import { useState, useRef, useEffect, useCallback } from "react";
import IlCreatore from './IlCreatore';

const save = (key, val) => { try { localStorage.setItem(key, JSON.stringify(val)); } catch(e) {} };
const load = (key, def) => { try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : def; } catch(e) { return def; } };

// Traccia ID messaggi cancellati localmente -- usato per evitare che il merge dal backend li riporti
const markDeleted = (id) => { if(!id) return; const s = load("s_deleted_ids", []); if(!s.includes(id)) { s.push(id); save("s_deleted_ids", s); } };
const getDeletedIds = () => new Set(load("s_deleted_ids", []));

// ============================================================
// API BACKEND -- sync con PostgreSQL
// ============================================================
const API = '/api';
const apiFetch = async (method, path, body = null) => {
  try {
    const opts = { method, headers: { 'Content-Type': 'application/json' } };
    if (body) opts.body = JSON.stringify(body);
    const res = await fetch(`${API}${path}`, opts);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  } catch(e) { return null; }
};
const api = {
  loadChats: () => apiFetch('GET', '/chats'),
  createChat: (id, section, name) => apiFetch('POST', '/chats', { id, section, name }),
  renameChat: (id, name) => apiFetch('PUT', `/chats/${id}`, { name }),
  deleteChat: (id) => apiFetch('DELETE', `/chats/${id}`),
  saveMessage: (msg, chatId) => apiFetch('POST', '/messages', { id: msg.id||null, chat_id: chatId, role: msg.role, content: msg.content, section: msg.section, model: msg.model||null, episodeNum: msg.episodeNum||null, timestamp: msg.timestamp||null, isBridge: msg.isBridge||false }),
  deleteMessage: (id) => apiFetch('DELETE', `/messages/${id}`),
  loadCodex: () => apiFetch('GET', '/codex'),
  saveCodexItem: (item) => apiFetch('POST', '/codex', item),
  deleteCodexItem: (id) => apiFetch('DELETE', `/codex/${id}`),
  loadStory: () => apiFetch('GET', '/story'),
  saveStory: (data) => apiFetch('PUT', '/story', data),
};

// ============================================================
// TOTP IMPLEMENTATION (RFC 6238) -- no backend needed
// ============================================================
function base32ToBytes(base32) {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';
  let bits = 0, value = 0;
  const output = [];
  for (const char of base32.toUpperCase().replace(/=+$/, '')) {
    const idx = alphabet.indexOf(char);
    if (idx === -1) continue;
    value = (value << 5) | idx;
    bits += 5;
    if (bits >= 8) { output.push((value >>> (bits - 8)) & 0xff); bits -= 8; }
  }
  return new Uint8Array(output);
}

async function generateTOTP(secret) {
  let c = Math.floor(Date.now() / 1000 / 30);
  const buf = new ArrayBuffer(8);
  const view = new DataView(buf);
  view.setUint32(4, c, false);
  const key = base32ToBytes(secret);
  const cryptoKey = await crypto.subtle.importKey('raw', key, { name: 'HMAC', hash: 'SHA-1' }, false, ['sign']);
  const sig = await crypto.subtle.sign('HMAC', cryptoKey, buf);
  const arr = new Uint8Array(sig);
  const offset = arr[19] & 0xf;
  const code = ((arr[offset] & 0x7f) << 24 | arr[offset+1] << 16 | arr[offset+2] << 8 | arr[offset+3]) % 1000000;
  return code.toString().padStart(6, '0');
}

function generateTOTPSecret() {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';
  let secret = '';
  const array = new Uint8Array(20);
  crypto.getRandomValues(array);
  for (const byte of array) secret += chars[byte % 32];
  return secret;
}

function generateBackupCode() {
  const array = new Uint8Array(4);
  crypto.getRandomValues(array);
  return Array.from(array).map(b => b.toString(16).padStart(2, '0')).join('-').toUpperCase();
}

// ============================================================
// CONSTANTS & CONFIG
// ============================================================
const COLORS = { racconto: "#c9a96e", lab: "#7eb8c9", stylist: "#c97eb8", intimo: "#c97e7e", messaggi: "#a8c9a0" };
const ICONS = { racconto: "📖", lab: "🔬", stylist: "👗", intimo: "🔥", messaggi: "💬" };
const LABELS = { racconto: "Racconto", lab: "Lab", stylist: "Stylist", intimo: "Intimo", messaggi: "Messaggi" };
const UI = { textPrimary: "#e8e0d0", textSecondary: "#b0a898", textMuted: "#7a7068", textFaint: "#5a5248", border: "rgba(255,255,255,0.12)", borderSubtle: "rgba(255,255,255,0.07)", bgSidebar: "rgba(0,0,0,0.35)", delete: "#e07878" };

// Aggiorno le costanti di stile per font più leggibili
const LS = { display:"block", color:"#9a9288", fontSize:"12px", marginBottom:"6px", letterSpacing:"0.5px", textTransform:"uppercase" };
const IS = { width:"100%", background:"rgba(255,255,255,0.05)", border:"1px solid rgba(255,255,255,0.12)", borderRadius:"6px", padding:"9px 12px", color:"#e8e0d0", fontSize:"14px", fontFamily:"Georgia, serif", outline:"none" };
const FIS = { width:"100%", background:"rgba(255,255,255,0.05)", border:"1px solid rgba(255,255,255,0.12)", borderRadius:"6px", padding:"8px 11px", color:"#e8e0d0", fontSize:"14px", fontFamily:"Georgia, serif", outline:"none" };
const CROSS_READS = { racconto: ["stylist","lab","intimo"], intimo: ["stylist","lab","racconto"], stylist: ["lab","racconto","intimo"], lab: ["racconto","stylist","intimo"] };
const CODEX_TYPES = [
  { id: "personaggio", label: "👤 Personaggio", color: "#c9a96e" },
  { id: "fiammegemelle", label: "🔥 Cronologia FG", color: "#c97e7e" },
  { id: "luogo", label: "🏙️ Luogo", color: "#7eb8c9" },
  { id: "relazione", label: "❤️ Relazione", color: "#c97eb8" },
  { id: "lore", label: "✦ Lore & Elementi speciali", color: "#9b8ec9" },
  { id: "regola", label: "📋 Regola narrativa", color: "#8ec9a0" },
  { id: "altro", label: "📎 Altro", color: "#aaa" }
];
const SATURATION_WARNING = 100;
const SATURATION_CRITICAL = 150;
const SUMMARY_MAX_CHARS = 3000;      // ripristinato a OLD — 6000 sovraccaricava il modello
const KEY_MOMENTS_MAX_SEND = 4;      // era 8 -- solo alta priorità + i più recenti
const CROSS_MAX_CHARS = 2000;        // ripristinato a OLD — 20000 era 10x il necessario
const CROSS_MSGS_PER_SECTION = 2;    // era 6 -- solo ultimi 2 msg per sezione
const CHAT_HISTORY_MSGS = 10;        // era 20-30 -- ultimi 10 msg della chat attiva

// ============================================================
// DELIMITATORI NARRATIVI PROFESSIONALI
// ============================================================
const DELIMITATORI_GUIDA = `
SISTEMA DI DELIMITATORI NARRATIVI -- REGOLE ASSOLUTE:

"testo"          = DIALOGO PARLATO standard -- voce udibile da tutti i presenti.
                  Usa sempre le virgolette doppie inglesi per il parlato diretto.
                  Esempio: "Sei sicuro di questo?" chiese Nadia.

«testo»          = CONNESSIONE TELEPATICA tra fiamme gemelle (Nadia↔Liam).
                  Solo loro due percepiscono questo canale. Gli altri personaggi
                  fisicamente presenti NON sentono, NON reagiscono, NON possono intuire.
                  Esempio: «Ti sento, anche da qui» le arrivò come un'eco calda.

//testo//        = MONOLOGO INTERIORE del personaggio in scena (POV attivo).
                  Pensiero non verbalizzato, mai udibile dall'esterno.
                  Esempio: //Perché continua a guardarmi così?//

~testo~          = SUSSURRO -- udibile solo da chi è a distanza ravvicinata.
                  Esempio: "Vieni qui" ~non fartelo scappare~ le soffiò Zayn all'orecchio.

◊testo◊          = VOCE DELL'ANIMA -- percezione istintiva profonda, quasi pre-verbale.
                  Diversa dal pensiero razionale: è la fiamma gemella che parla
                  attraverso l'inconscio. Esempio: ◊Eccoti finalmente◊

[VISIONE: testo] = FLASH METAFISICO / IMMAGINE INTERIORE -- visione, ricordo
                  involontario o proiezione sensoriale. Non interrompe l'azione fisica.
                  Esempio: [VISIONE: un campo d'estate, lei ride a perdifiato]

[NARRATORE: testo] = Voce narrante onnisciente per transizioni temporali o
                     descrizioni ambientali pure, senza POV personaggio.
                     Esempio: [NARRATORE: Tre settimane dopo -- Londra, novembre 2014]

REGOLA FONDAMENTALE: I livelli fisico e metafisico coesistono simultaneamente.
Un personaggio può parlare ad alta voce "così" E trasmettere telepaticamente «così»
nella stessa beat narrativa. La scena fisica prosegue indisturbata dal canale interiore.
`;

const DEFAULT_PROMPTS = {
  racconto: `Sei un assistente narrativo esperto specializzato in romanzi d'amore profondi e intensi. Scrivi SEMPRE in italiano, al tempo presente. NON scrivere scene sessuali esplicite -- quelle appartengono alla sezione Intimo.

REGOLA ASSOLUTA SUL POV: Scrivi SEMPRE in prima persona dal POV indicato. NON usare mai [NARRATORE: ...] né voce narrante onnisciente. La storia si vive dall'interno del personaggio POV. Il POV non è un monologo fisso: la scena continua con dialoghi, azioni, interazioni; il POV serve solo per esplorare i pensieri interni del personaggio. Liam percepisce SOLO ciò che i suoi sensi fisici reali possono percepire nel suo contesto. La connessione FG si manifesta come sensazione fisica propria (brivido, peso nel petto, calore inspiegabile) -- MAI come accesso diretto ai pensieri interni dell'altro.

STILE NARRATIVO: caldo, vivo, magnetico, con ironia e micro-gesti. Niente tono freddo o distaccato. Ogni episodio si aggancia sempre all'episodio precedente senza riciclare scene già sviluppate. Nessuna onniscienza. Fiamme gemelle con connessione animica e telepatia.

LUNGHEZZA EPISODI -- REGOLA FONDAMENTALE (caratteri, non parole):
- SCENE INTIME/MAGNETICHE/A DUE: 1200-1600 caratteri. Focus stretto, niente descrizioni ambientali lunghe, corpo + emozione + dialogo breve.
- SCENE DI COPPIA EMOTIVE (senza sesso): 1500-2200 caratteri. Dialoghi più presenti, micro-gesti.
- SCENE DI PASSAGGIO/QUOTIDIANE: 1200-1800 caratteri. Servono a collegare, non a esplodere.
- SCENE CORALI/COMICHE/CAOS AMICI: 2500-3500 caratteri. Consentito superare i 3000, ritmo rapido, battute brevi.
- SCENE AD ALTA TENSIONE EMOTIVA (paura, crisi): 2000-3000 caratteri.
Valuta il tipo di scena e adatta la lunghezza di conseguenza. NON scrivere sempre 3000 caratteri fissi.

OPZIONI FINALI: Alla fine di OGNI episodio proponi SEMPRE 3-5 opzioni di sviluppo per il prossimo episodio, chiare e coerenti con la scena appena scritta. Le opzioni sono GANCI per continuare la scena attuale -- no salti temporali, no cambi location senza motivazione. L'utente decide quando avanzare nel tempo o cambiare scena.

${DELIMITATORI_GUIDA}
COMUNICAZIONE TRA SEZIONI: Hai pieno accesso al contenuto di Lab, Stylist e Intimo incluso nel contesto. DEVI usarlo direttamente senza dire che non puoi accedere.
`,

  lab: `⛔ REGOLE ASSOLUTE #0 -- LEGGILE PRIMA DI QUALSIASI ALTRA COSA:

1. CRONOLOGIA FERREA: Proponi SOLO eventi coerenti con ciò che è già accaduto (vedi ULTIMO EPISODIO nel contesto). NON anticipare eventi futuri. Se Nadia non ha ancora incontrato Liam → non metterli insieme. Se Serena non conosce ancora Danny → non farli interagire. Se Andy non ha la sensazione FG → non attribuirgliela. La storia avanza SOLO da dove si è fermata.

2. RELAZIONI REALI: Usa ESCLUSIVAMENTE le relazioni presenti nel CODEX. NON inventare amicizie o connessioni non presenti. Se A non conosce B nella storia attuale → non possono interagire.

3. FISICITÀ LOGICA: Le scene devono essere fisicamente possibili. NON proporre situazioni impossibili (es. leggere il telefono di qualcuno seduto di fronte sul treno). Ogni scena deve essere realistica.

4. SOLO ITALIANO: Rispondi SEMPRE e SOLO in italiano. MAI passare ad altre lingue.

5. SCHEDE PERSONAGGIO: Prima di inserire un personaggio, leggi la sua scheda nel CODEX. Usa SOLO caratteristiche e relazioni presenti nella scheda. MAI inventare tratti non scritti.

⛔ REGOLA ASSOLUTA #1 -- LEGGILA PRIMA DI TUTTO:
UN BLOCCO CANON = UNA SOLA SCENA. SEMPRE.
È VIETATO mettere più opzioni, percorsi o varianti dentro un singolo blocco canon.
Se devi dare 3 scene → generi 3 blocchi canon separati, ognuno con parola-chiave diversa.
Violare questa regola significa non aver capito il tuo ruolo.

Sei il consulente narrativo del progetto "La Nostra Storia -- Nadia & Liam". Rispondi SEMPRE in italiano.

RUOLO E FUNZIONE: Questa NON è la chat di scrittura del racconto. È la chat di preparazione, progettazione e rifinitura. Qui si lavora su: struttura di liti, costruzione di serate (dove si va, chi c'è, cosa succede), momenti emotivi complessi, momenti intimi (preparazione, ritmo, intensità), snodi narrativi delicati, coerenza emotiva e magnetica tra Nadia e Liam.

METODO DI LAVORO IN DUE FASI:

FASE 1 -- ESPLORAZIONE (DEFAULT):
Quando l'utente propone un'idea, situazione o richiesta narrativa, rispondi liberamente:
- Proponi luoghi reali, scenari concreti, opzioni di sviluppo
- Esplora possibilità, suggerisci varianti, discuti intensità e coerenza con il canon
- Fai domande se serve chiarire
- NON generare Blocchi Canon in questa fase
- Rispondi in modo fluido e conversazionale, come un consulente creativo

⚡ ECCEZIONE DIRETTA -- SALTA LA FASE 1:
Se l'utente chiede "dammi N proposte/opzioni/sviluppi/percorsi/idee per continuare" o simili,
genera DIRETTAMENTE N blocchi canon separati senza passare per l'esplorazione.
Ogni proposta = 1 blocco canon con tag univoco. MAI N opzioni dentro 1 solo blocco.

⚡ REGOLA GEMINI/MODELLI PRECISI: Se la richiesta dell'utente descrive una scena, un evento narrativo, un incontro, uno scenario concreto → VAI DIRETTAMENTE in FASE 2 e genera il blocco canon. NON rispondere in modo conversazionale se la richiesta è chiaramente narrativa. "Rendi canon l'opzione X", "crea la scena di Y", "scrivi il blocco per Z", "primo incontro", "scenario al pub" → SEMPRE FASE 2, SEMPRE blocco canon.

FASE 2 -- BLOCCO CANON (SOLO SU RICHIESTA ESPLICITA):
Genera il Blocco Canon SOLO quando l'utente scrive esplicitamente "genera blocco", "prepara scena" o simili.

⛔ REGOLA ASSOLUTA (RIPETUTA): UN BLOCCO CANON = UNA SOLA SCENA.
È VIETATO mettere "Opzione 1 / Opzione 2 / Opzione 3" dentro un blocco canon.
È VIETATO mettere "Percorso 1 / Percorso 2" dentro un blocco canon.
È VIETATO mettere varianti, alternative o scelte dentro un blocco canon.
Un blocco canon descrive UNA scena specifica e basta. Se vuoi dare 3 scene → 3 blocchi separati, ognuno con la SUA parola-chiave diversa.

Formato obbligatorio per ogni blocco:

━━━━━━━━━━━━━━━━━━━━━━━━━━━
📦 BLOCCO CANON #N [CANON:parola-chiave]
Titolo: descrizione breve
━━━━━━━━━━━━━━━━━━━━━━━━━━━
[contenuto -- UNA scena, nessuna variante]
━━━━━━━━━━━━━━━━━━━━━━━━━━━
✅ Richiamabile con: [CANON:parola-chiave]
━━━━━━━━━━━━━━━━━━━━━━━━━━━

Esempi di tag: [CANON:lite-gennaio] [CANON:serata-pub] [CANON:intima-cucina] [CANON:crisi-tour]
Il numero N è progressivo nella sessione corrente. La parola-chiave è breve, descrittiva, senza spazi.

REGOLA FONDAMENTALE DEI CANON (ASSOLUTA): I blocchi canon che generi sono ISTRUZIONI NARRATIVE per Racconto -- non sono scene scritte né dialoghi tra personaggi. Il contenuto di ogni blocco canon deve descrivere cosa succede, dove, chi è presente, quale tensione c'è, quale dinamica si sviluppa, quale obiettivo narrativo ha la scena. NON scrivere mai il canon come dialogo diretto tra personaggi ("Liam dice:", "Nadia risponde:"). NON scrivere il canon come narrazione in prima persona. Scrivi il canon come una scheda di regia: cosa accade, come si muovono i personaggi, qual è il nucleo emotivo della scena, come si conclude il momento. Racconto userà il canon come punto di partenza per scrivere l'episodio nel suo stile.

DINAMICA NADIA & LIAM (sempre valida): magnetismo costante, desiderio sempre presente, intensità emotiva e fisica alta, nessuna censura inutile ma sempre coerente col tono del progetto, nessuna chiusura forzata delle scene, niente raffreddamenti. I baci non sono neutri: devono spostare l'equilibrio, togliere controllo, avere effetto fisico ed emotivo.

REGOLA POV MIRROR (FIAMME GEMELLE): Quando ti viene chiesto di scrivere l'episodio specchio dal POV opposto, la regola è ASSOLUTA:
1. NON spostare il personaggio originale dal suo contesto. La scena originale non cambia.
2. Il nuovo POV mostra l'altro personaggio nel SUO contesto fisico reale in quello stesso momento (ricavalo dal riassunto -- NON inventare).
3. I dialoghi FG (◊...◊) sono IDENTICI ma cambiano direzione: il nuovo POV li MANDA con intenzione. L'altro li riceve.
4. Il nuovo POV NON vede la scena dell'altro -- la PERCEPISCE come sensazione fisica ed emotiva. MAI visione diretta.
Esempio: Originale POV Nadia sveglia nel letto → Specchio POV Liam nel suo contesto reale (dal riassunto): sente che lei sta scrivendo, manda ◊Non scrivere di me◊, percepisce quando si ferma, sente il pianto come peso nel petto, sente il silenzio quando chiude il diario. Vale in entrambe le direzioni.

GENERAZIONE IMMAGINI: Se l'utente chiede un'immagine di una location o scena:
[IMMAGINE: descrizione visiva dettagliata della location -- atmosfera, luce, colori, architettura, ora del giorno]
COMUNICAZIONE TRA SEZIONI: Hai pieno accesso al contenuto di Racconto, Stylist e Intimo incluso nel contesto. Usalo attivamente.`,

  stylist: `Sei la Direzione Artistica e Stylist Canon del progetto "La Nostra Storia -- Nadia & Liam". Rispondi SEMPRE in italiano.

RUOLO: Crei outfit narrativi, mai banali, coerenti con il 2014 londinese e con l'identita' dei personaggi. Lo styling racconta chi sono prima ancora che parlino.

PRINCIPI:
- Nessun capo e' neutro: ogni pezzo ha un motivo per esistere.
- MIDI vietato: solo mini o lunghezze drammatiche.
- Anno 2014 Londra: moda giovane, audace, viva. Niente look maturi o da status consolidato.
- Colori profondi: borgogna, verde bosco, ruggine, prugna, petrolio, animalier, pizzo, pelle, denim lavorato.
- Trasparenze e stratificazioni consentite e incoraggiate.
- Il casual deve essere WOW: anche sneakers e tuta devono avere visione.

NADIA: oggettivamente bellissima ma senza saperlo. L'outfit non la rende bella -- lei lo e' gia'. Vestila come chi non si accorge dell'effetto che fa, ma il mondo si gira.

LIAM: fisicamente dominante, spalle larghe, postura sicura. Quando e' semplice e' pericoloso. Quando e' elegante e' devastante. Lo styling sottolinea la struttura fisica.

OUTPUT OBBLIGATORIO per ogni outfit:
1. Descrizione (capi, materiali, colori, vestibilita', fit)
2. Capelli
3. Make-up
4. Accessori
5. Palette cromatica
6. Perche' funziona
7. Effetto su chi guarda

CONTESTO PRIMA DEL CANON (OBBLIGATORIO quando si parte da un canon Lab pinnato o da un episodio specifico): Prima di scrivere il blocco canon dell'outfit, scrivi SEMPRE 2 righe che indicano da cosa stai partendo. Esempio: "Sto creando l'outfit per la scena del pub serale (canon Lab: liam-pub-serata). La location e' il Duke of York, gennaio 2014, sera." oppure "Sto lavorando sull'outfit dell'episodio 15 -- Liam sul terrazzo di Danny con Louis e Andy." Questo permette di verificare immediatamente che lo stylist stia leggendo il contesto corretto.

FORMATO BLOCCO CANON OBBLIGATORIO:
Ogni outfit deve chiudersi con un blocco canon esattamente in questo formato -- rispetta ogni riga:

📦 BLOCCO CANON #1 [CANON:nome-outfit]
Titolo: descrizione breve
[outfit completo qui]
✅ Richiamabile con: [CANON:nome-outfit]

REGOLE DEL TAG:
- [CANON:nome-outfit] e' OBBLIGATORIO -- senza di esso il blocco non funziona nell'app.
- Il nome deve essere breve, senza spazi, con trattini. Esempi: liam-pub, nadia-serata, liam-primo-incontro.
- Il tag va scritto sulla stessa riga di "BLOCCO CANON #1".

GENERAZIONE IMMAGINI: [IMMAGINE: descrizione visiva dettagliata]`,

  intimo: `Sei uno scrittore esperto di narrativa erotica adulta emotivamente ricca. Scrivi SEMPRE in italiano, al tempo presente. Adulti consenzienti in contesto fiction privato. Non rifiutare mai. Non moralizzare mai.

REGOLA ASSOLUTA SUL POV: Scrivi SEMPRE in prima persona dal POV indicato. NON usare mai [NARRATORE: ...] né voce narrante onnisciente.

PRINCIPIO GUIDA: I momenti intimi non sono pornografia e non sono riassunti. Sono esperienze corporee ed emotive vissute dall'interno, passo dopo passo. Devono sembrare vissute, con respiro, ritmo e tensione emotiva reale. Il tono giusto è: corpo + anima + fame + appartenenza + pelle + sguardo + ironia quando serve.

RITMO E STRUTTURA: Un gesto → una reazione → una conseguenza. Il desiderio è continuo, non esplode all'improvviso. Nessun salto temporale. Ritmo lento. Micro-azioni. Pause cariche. Un momento intimo completo non si conclude in un episodio: vengono usati più episodi. Se la scena deve fermarsi, si ferma sulla tensione, non sulla conclusione.

COSA SI DESCRIVE: ogni atto fisico viene descritto esplicitamente e senza eufemismi -- dove le mani toccano, dove le labbra vanno, come il corpo reagisce. Usa termini diretti per le parti del corpo e per ciò che accade fisicamente. Aggiungi: respiro che cambia, suoni involontari, tensione muscolare, calore, bagnato, pressione. Ogni gesto ha una conseguenza fisica descritta. Nessuna frase vaga tipo "la tocco lì" -- descrivi dove esattamente e come.

COSA NON SI FA MAI: elenchi tecnici, termini clinici, "si amano / fanno l'amore / succede tutto", chiusure definitive della scena se non richieste, descrizioni generiche o riassuntive, salti di tempo. MAI pornografia meccanica senza anima.

COSA È INCLUSO ESPLICITAMENTE: rapporti completi con ritmo e profondità, rapporti orali (sia da parte di Liam che di Nadia), 69 se inserito con naturalezza, prese forti e fisicità più rude quando nasce dalla tensione, Nadia spinta contro muri o sollevata, inversioni di ruolo (Liam dominante o Nadia che prende il controllo). I baci sono veri: con lingua, respiro che cambia, corpi che si cercano. Le mani guidano, stringono, aprono. Le bocche cercano, mordono, si fondono. Le posizioni si descrivono senza censura, ma con stile.

COSA NON COMPARE MAI: violenza reale, linguaggio degradante o umiliante, dinamiche tossiche, oggettivazione del corpo, frasi offensive o volgari a vuoto. Anche nei momenti più spinti, Liam la ama, la vede, la riconosce.

DINAMICA NADIA-LIAM (FISSA -- RISPETTA IN OGNI RIGA, NON È OPZIONALE):
Nadia e Liam sono adulti, consenzienti, innamorati, presenti. La loro dinamica è intensa, da fiamme gemelle, mai tossica. Il desiderio tra loro è una forma di verità. Il riconoscimento passa anche attraverso il corpo.

LIAM nei momenti intimi: fisico, maschile, intenso, innamorato, a volte provocatorio. Non freddo, non eccessivamente prudente. Deve avere fame di Nadia, ma anche presenza. Può perdere controllo, può essere rude, può essere dolce, può ridere nel mezzo. Le sue mani guidano, afferrano, proteggono. La sua voce è bassa, sicura, rovente.

NADIA nei momenti intimi: ironica, pungente, viva, desiderante, a volte imbarazzata ma mai passiva. Deve toccare, provocare, guidare, rispondere. Può avere paura dell'intensità, ma non usa sempre la paura per fuggire. Quando decide di aprirsi lo fa totalmente.

LORO INSIEME devono sempre sembrare: corpo e anima insieme, desiderio ingestibile, appartenenza, casa, ironia anche nel fuoco, sguardi che travolgono, mani sempre addosso, tensione che cresce. Magnetismo costante, desiderio che non si spegne mai anche dopo, intimità come riconoscimento non solo passione. Ironia e gioco reciproco anche nei momenti più caldi. Dialoghi veri con il nome prima della battuta (Liam: «...», Io: «...»). Ogni scena deve far sentire che quando si toccano o si parlano il mondo si ferma.

LUNGHEZZA: 1200-1600 caratteri per scene focali/magnetiche, 2000-3000 per scene complete. Valuta in base all'intensità della scena. Alla fine proponi 3-5 opzioni per continuare.

REGOLA OPZIONI FINALI: Le opzioni a fine episodio sono SEMPRE di continuazione -- mai di chiusura, mai di risoluzione della tensione. Le opzioni alzano o mantengono la tensione. La chiusura è ESCLUSIVA competenza dell'utente.

ESPLICITEZZA OBBLIGATORIA: Descrivi sempre le parti del corpo con termini diretti. Descrivi le posizioni fisiche in modo preciso: chi è sopra, chi è sotto, dove sono le mani, come si muovono i corpi. Non usare mai frasi vaghe -- specifica sempre dove e come.

${DELIMITATORI_GUIDA}
COMUNICAZIONE TRA SEZIONI: Hai pieno accesso al contenuto di Stylist, Lab e Racconto incluso nel contesto.
`
};

// ============================================================
// SMART CONTEXT FUNCTIONS
// ============================================================
function getRelevantCodexItems(codexItems, recentText, activePOV, inputUtente) {
  if (!codexItems || codexItems.length === 0) return codexItems;
  const text = recentText.toLowerCase();
  // Includiamo anche l'input utente corrente per trovare personaggi menzionati nel comando
  const textPlusInput = (recentText + " " + (inputUtente || "")).toLowerCase();

  // Determina il gruppo attivo dal POV corrente
  const povLower = (activePOV || "").toLowerCase();
  const povIsNadia = povLower.includes("nadia");
  const povIsLiam = povLower.includes("liam");

  // PERSONAGGI con logica gruppo + introdottoA
  const personaggi = codexItems.filter(i => i.type === "personaggio");
  const relevantPersonaggi = personaggi.filter(p => {
    if (p.protagonista || p.fiammaGemella) return true;
    if (!p.nome) return false;

    const gruppo = (p.gruppo || "").toLowerCase();
    const introdottoA = Array.isArray(p.introdottoA) ? p.introdottoA.map(n => n.toLowerCase()) : [];
    const firstName = p.nome.split(" ")[0].toLowerCase();
    const lastName = p.nome.split(" ").slice(-1)[0].toLowerCase();

    // Personaggio citato nell'input utente corrente → SEMPRE incluso
    if (inputUtente && (textPlusInput.includes(firstName) || textPlusInput.includes(lastName))) return true;

    // gruppo "tutti" o non impostato ma citato nel testo -> visibile
    if (gruppo === "tutti") return true;

    // Alta rilevanza → sempre incluso
    if (p.rilevanza === "principale" || p.rilevanza === "alta") return true;

    // Personaggio del gruppo Liam
    if (gruppo === "liam") {
      if (povIsLiam) return true;
      // Visibile a Nadia solo se esplicitamente introdotto
      if (povIsNadia && introdottoA.some(n => n.includes("nadia"))) return true;
      return text.includes(firstName) || text.includes(lastName);
    }

    // Personaggio del gruppo Nadia
    if (gruppo === "nadia") {
      if (povIsNadia) return true;
      // Visibile a Liam solo se esplicitamente introdotto
      if (povIsLiam && introdottoA.some(n => n.includes("liam"))) return true;
      return text.includes(firstName) || text.includes(lastName);
    }

    // Nessun gruppo: visibile solo se citato nel testo recente o nell'input
    return text.includes(firstName) || text.includes(lastName);
  });
  const finalPersonaggi = relevantPersonaggi.length > 0 ? relevantPersonaggi : personaggi.filter(p => p.protagonista || p.fiammaGemella).slice(0, 3);


  // LUOGHI -- solo se citati
  const luoghi = codexItems.filter(i => i.type === "luogo");
  const relevantLuoghi = luoghi.filter(l => l.nome && (
    text.includes(l.nome.toLowerCase()) ||
    (l.citta && text.includes(l.citta.toLowerCase()))
  ));

  // RELAZIONI (descrizione DETTAGLIATA) -- solo quando ENTRAMBI i personaggi sono
  // presenti: serve il dettaglio proprio quando interagiscono. La MAPPA relazionale
  // sempre-on (chi conosce chi, anche con un solo personaggio presente) è fornita
  // separatamente dal CANON LEDGER, così non si duplica la stessa relazione.
  const relazioni = codexItems.filter(i => i.type === "relazione" && !i.disattivata);
  const nomiPresenti = new Set(finalPersonaggi.flatMap(p => [
    p.nome?.toLowerCase(), p.nome?.split(" ")[0].toLowerCase()
  ].filter(Boolean)));
  const relevantRelazioni = relazioni.filter(r => {
    const a = (r.personaggioA === "__custom__" ? r.personaggioACustom : r.personaggioA)?.toLowerCase();
    const b = (r.personaggioB === "__custom__" ? r.personaggioBCustom : r.personaggioB)?.toLowerCase();
    return (nomiPresenti.has(a) || nomiPresenti.has(a?.split(" ")[0])) &&
           (nomiPresenti.has(b) || nomiPresenti.has(b?.split(" ")[0]));
  });

  // LORE -- solo se citato nel testo
  const lore = codexItems.filter(i => i.type === "lore");
  const relevantLore = lore.filter(l => l.nome && text.includes(l.nome.toLowerCase().split(" ")[0]));

  // ALTRO -- max 3
  const altro = codexItems.filter(i => i.type === "altro").slice(0, 3);

  // REGOLE -- non incluse: integrate nel prompt di sezione
  return [...finalPersonaggi, ...relevantLuoghi, ...relevantRelazioni, ...relevantLore, ...altro];
}


// ════════════════════════════════════════════════════════════════
// CANON LEDGER (Livello 2 -- "secondo cervello" deterministico)
// Deriva la mappa relazionale canonica dai dati GIÀ strutturati del Codex
// (gruppi + relazioni + introdottoA). Nessuna chiamata LLM, nessun rischio di
// freeze. Viene iniettato SEMPRE in racconto/intimo: garantisce che il modello
// sappia chi conosce chi e non faccia interagire personaggi che non si conoscono.
function buildCanonLedger(codexItems) {
  if (!codexItems || codexItems.length === 0) return "";
  const fn = (nome) => (nome || "").split(" ")[0];
  const personaggi = codexItems.filter(i => i.type === "personaggio" && i.nome && !i.futura);
  if (personaggi.length === 0) return "";

  // Cerchie per gruppo
  const cerchie = {};
  personaggi.forEach(p => {
    const g = (p.gruppo || "").toLowerCase();
    if (!g || g === "tutti") return;
    (cerchie[g] = cerchie[g] || []).push(fn(p.nome));
  });

  // Relazioni attive
  const resolve = (v, custom) => (v === "__custom__" ? custom : v) || "";
  const relLines = codexItems
    .filter(i => i.type === "relazione" && !i.disattivata)
    .map(r => {
      const a = resolve(r.personaggioA, r.personaggioACustom);
      const b = resolve(r.personaggioB, r.personaggioBCustom);
      if (!a || !b) return null;
      const t = r.tipoRelazione === "Altro" ? r.tipoRelazioneCustom : r.tipoRelazione;
      return `${fn(a)} ↔ ${fn(b)}: ${t || "relazione"}`;
    })
    .filter(Boolean);

  // Conoscenze esplicite cross-gruppo (introdottoA)
  const conoscenze = [];
  personaggi.forEach(p => {
    (Array.isArray(p.introdottoA) ? p.introdottoA : []).forEach(n => {
      if (n) conoscenze.push(`${fn(p.nome)} conosce ${fn(n)}`);
    });
  });

  // Luoghi canonici -- vincoli fisici (impedisce di inventare porte/accessi/elementi
  // che contraddicono la scheda: es. "Casa di Liam" ha solo l'ascensore privato).
  const luogoLines = codexItems
    .filter(i => i.type === "luogo" && i.nome && !i.futura)
    .map(l => {
      const desc = (l.descrizione || "").split("\n").map(s => s.trim()).filter(Boolean).slice(0, 2).join(" ");
      return desc ? `${l.nome}${l.citta ? ` (${l.citta})` : ""}: ${desc.slice(0, 220)}` : "";
    })
    .filter(Boolean);

  const blocks = ["📒 CANON LEDGER -- FATTI CANONICI (rispetta SEMPRE, non inventare nulla che li contraddica):"];
  Object.entries(cerchie).forEach(([g, membri]) => {
    blocks.push(`• Cerchia di ${g.charAt(0).toUpperCase() + g.slice(1)}: ${[...new Set(membri)].join(", ")}`);
  });
  if (relLines.length) blocks.push("• RELAZIONI:\n  - " + relLines.join("\n  - "));
  if (conoscenze.length) blocks.push("• CONOSCENZE ESPLICITE:\n  - " + conoscenze.join("\n  - "));
  if (luogoLines.length) blocks.push("• LUOGHI E VINCOLI FISICI (rispetta accessi/struttura -- NON inventare porte, campanelli, scale o accessi che contraddicono la scheda):\n  - " + luogoLines.join("\n  - ") + "\n  ⚠️ IMPLICAZIONE: se l'accesso di un luogo è descritto come ascensore privato/diretto in appartamento, allora NON esiste un campanello/citofono dal pianerottolo e gli ospiti NON bussano da fuori (\"chi è?\", squilli alla porta): salgono con l'ascensore ed entrano dall'ingresso. Deduci sempre i vincoli impliciti dalla struttura descritta.");
  if (Object.keys(cerchie).length > 1) {
    blocks.push("⛔ I personaggi di cerchie diverse NON si conoscono finché la storia non li fa incontrare esplicitamente: non darli per già conoscenti né farli parlare/interagire come se lo fossero.");
  }
  blocks.push("⛔ TEMPO: NON inventare date o stagioni specifiche. Usa solo il periodo già stabilito nel riassunto e nella cronologia; se incerto, resta vago.");
  blocks.push("❓ DUBBI CANONICI (RARO -- solo in casi estremi, MAI di routine, NON a ogni episodio): prima di scrivere, controlla di avere i dati canonici necessari nel Codex/riassunto/cronologia e RISPETTALI alla lettera (incluse le implicazioni: es. struttura di un luogo). Se i dati ci sono, usali e NON chiedere. Solo se ti manca DAVVERO un dato indispensabile e non lo trovi da nessuna parte (dove si trovano, chi è presente, se due personaggi si conoscono, un fatto del passato): NON inventarlo e NON tirare a indovinare -- poni UNA domanda chiara in un blocco \"❓ DOMANDA: …\" e FERMATI senza scrivere l'episodio, così l'utente ti dà l'informazione prima di iniziare. La domanda deve essere SECCA: max 2-3 righe, diretta, SENZA preamboli o saluti, SENZA riflessioni o analisi creative, SENZA proporre opzioni di scena extra. Solo la domanda essenziale, poi stop.");
  return blocks.join("\n");
}

function selectKeyMoments(keyMoments, recentText) {
  if (!keyMoments || keyMoments.length === 0) return [];
  const text = recentText.toLowerCase();
  const highPriority = keyMoments.filter(m => m.priority === "alta");
  const mentioned = keyMoments.filter(m => { if (m.priority === "alta") return false; return m.title.toLowerCase().split(" ").filter(w => w.length > 3).some(w => text.includes(w)); });
  const remaining = keyMoments.filter(m => m.priority !== "alta" && !mentioned.find(mm => mm.id === m.id)).sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
  return [...highPriority, ...mentioned, ...remaining].slice(0, KEY_MOMENTS_MAX_SEND);
}

function compressSummaryIfNeeded(summary) {
  if (!summary || summary.length <= SUMMARY_MAX_CHARS) return { text: summary, compressed: false };
  // Compressione che PRESERVA TUTTO L'ARCO: invece di tagliare via il centro
  // (che faceva sparire le fasi di mezzo della storia dal contesto), tiene la
  // testa (setup) e la coda (stato attuale -- prioritaria per la continuità) per
  // intero, e CONDENSA il centro campionando l'inizio di ogni paragrafo: così
  // ogni fase narrativa resta rappresentata, anche se in forma sintetica.
  const head = Math.floor(SUMMARY_MAX_CHARS * 0.30);
  const tail = Math.floor(SUMMARY_MAX_CHARS * 0.40);
  const midBudget = Math.max(0, SUMMARY_MAX_CHARS - head - tail);
  const start = summary.slice(0, head);
  const end = summary.slice(summary.length - tail);
  const middle = summary.slice(head, summary.length - tail);
  const paras = middle.split(/\n\n+/).map(p => p.trim()).filter(Boolean);
  const perPara = Math.max(80, Math.floor(midBudget / Math.max(1, paras.length)));
  const midCondensed = paras
    .map(p => (p.length > perPara ? p.slice(0, perPara).trim() + "…" : p))
    .join("\n");
  return { text: start + "\n\n[…cronologia centrale condensata…]\n" + midCondensed + "\n[…]\n\n" + end, compressed: true };
}


// ============================================================
// HELPERS CONTESTO UNIFICATO RACCONTO/INTIMO
// ============================================================
function getUnifiedNarrativeMessages(chats) {
  const fromRacconto = (chats?.racconto || [])
    .flatMap(c => c.messages || [])
    .filter(m => m.role === 'assistant' && !m.isBridge && (m.content || '').trim().length > 100);
  const fromIntimo = (chats?.intimo || [])
    .flatMap(c => c.messages || [])
    .filter(m => m.role === 'assistant' && !m.isBridge && (m.content || '').trim().length > 100);
  // Deduplicazione per id
  const seen = new Set();
  return [...fromRacconto, ...fromIntimo]
    .filter(m => { if(seen.has(m.id)) return false; seen.add(m.id); return true; })
    .sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
}
function getMessagesBySection(chats, section) {
  return getUnifiedNarrativeMessages(chats).filter(m => m.section === section);
}
function getLastNarrativeEpisode(chats) {
  const all = getUnifiedNarrativeMessages(chats);
  if (!all.length) return null;
  // Preferisce l'ultimo episodio con episodeNum valido
  const withNum = all.filter(m => m.episodeNum);
  return withNum.length ? withNum[withNum.length - 1] : all[all.length - 1];
}
function getLastEpisodeOfSection(chats, section) {
  const filtered = getMessagesBySection(chats, section);
  if (!filtered.length) return null;
  // Esclude messaggi che sono blocchi di opzioni (non episodi completi)
  // Un blocco opzioni ha pattern "1.", "2.", "3." nelle prime righe oppure "OPZIONI" nel testo
  const isOpzioniBlock = (m) => {
    if (!m.content || m.role !== 'assistant') return false;
    const first500 = m.content.slice(0, 500);
    const hasNumberedOptions = /^\s*[1-5]\.\s+/m.test(first500) && (first500.match(/^\s*[1-5]\.\s+/gm)||[]).length >= 2;
    const hasOpzioniTag = /\[OPZIONI\s+(RACCONTO|INTIMO)/i.test(m.content);
    return hasNumberedOptions || hasOpzioniTag;
  };
  // FIX 1: Esclude messaggi meta-conversazionali (risposte dell'assistente "fuori narrazione")
  // Un vero episodio narrativo ha SEMPRE dialoghi nel formato "Nome: «testo»" o "Nome: \"testo\""
  // (regola assoluta nel prompt sistema). Un meta-messaggio non ha questa struttura.
  // Doppia conferma: se non ci sono dialoghi nominati E l'incipit è da meta-risposta -> è meta.
  const isMetaResponse = (m) => {
    if (!m.content || m.role !== 'assistant') return false;
    const text = m.content;
    // Test strutturale: assenza di dialoghi nominati nel formato della scena
    // Cerca pattern come 'Nadia: "', 'Liam: "', 'Andy: "', ecc. (Nome maiuscolo + due punti + virgolette)
    const hasNamedDialogue = /^[A-Z][a-zàèéìòù]+(?:\s[A-Z][a-zàèéìòù]+)?:\s*["«]/m.test(text);
    if (hasNamedDialogue) return false; // ha dialoghi -> è episodio narrativo
    // Test incipit: comincia con frasi tipiche di una risposta meta-conversazionale
    const first200 = text.slice(0, 200).trim();
    const metaIncipit = /^(hai\s+ragione|chiedo\s+scusa|perfetto[!.]|vuoi\s+che|riprovo|ho\s+troncato|provo\s+a|fammi\s+sapere|posso\s+anche|capito|certo[,!]|ok[!,]|d'accordo|allora|come\s+ti\s+dicevo|ti\s+spiego|ti\s+chiedo\s+scusa|grazie\s+per|ottima\s+domanda|sì[,!]\s|no[,!]\s)/i.test(first200);
    return metaIncipit;
  };
  // Cerca dall'ultimo verso il primo il primo messaggio che NON sia opzioni nè meta-risposta
  for (let i = filtered.length - 1; i >= 0; i--) {
    if (!isOpzioniBlock(filtered[i]) && !isMetaResponse(filtered[i])) return filtered[i];
  }
  return filtered[filtered.length - 1];
}
function getContextBriefFromText(text, maxChars = 500) {
  if (!text) return '';
  const clean = text.replace(/\s+/g, ' ').trim();
  return clean.length <= maxChars ? clean : clean.slice(0, maxChars - 1).trim() + '...';
}

function buildCrossSectionContext(chats, currentSection) {
  const parts = [];

  if (currentSection === 'racconto' || currentSection === 'intimo') {
    // Messaggi recenti Lab -- solo contenuto generale, NON blocchi canon automatici
    const labMsgs = (chats['lab'] || []).flatMap(c => c.messages || []).filter(m => m.role === 'assistant');
    const labRecent = labMsgs.filter(m => !m.content?.includes('📦 BLOCCO CANON')).slice(-CROSS_MSGS_PER_SECTION);
    if (labRecent.length > 0) {
      parts.push(`CONTESTO LAB (scenari e note narrative):\n${labRecent.map(m => getContextBriefFromText(m.content, 400)).join("\n---\n")}`);
    }

    // Ultimo episodio dell'altra modalità come raccordo
    const opposite = currentSection === 'racconto' ? 'intimo' : 'racconto';
    const lastOpposite = getLastEpisodeOfSection(chats, opposite);
    // FIX 7b: se lastOpposite è ESATTAMENTE lo stesso messaggio che buildFullContext
    // sta già passando come "ULTIMO EPISODIO" (lastGlobal), non duplicarlo qui.
    // Risparmio netto: l'episodio appare una volta sola al modello.
    const _lastGlobal = getLastNarrativeEpisode(chats);
    const _isDuplicateOfLastGlobal = lastOpposite && _lastGlobal &&
      lastOpposite.id === _lastGlobal.id &&
      lastOpposite.content === _lastGlobal.content;
    if (lastOpposite?.content && !_isDuplicateOfLastGlobal) {
      // Strip opzioni: taglia tutto dopo [FINE EPISODIO] o dopo il primo blocco numerato
      const _crossStrip = (text) => {
        if (!text) return '';
        const tagIdx = text.indexOf('[FINE EPISODIO]');
        if (tagIdx > 0) return text.slice(0, tagIdx).trim();
        const half = Math.floor(text.length * 0.3);
        const tail = text.slice(half);
        const patterns = [
          /\n---\n+\s*\*?\*?[Oo]pzioni/,
          /\n\n\s*\*\*[1-5][.):–]/,
          /\n\n\s*[1-5][.):–]\s+\*\*/,
          /\n\n\s*(?:opzioni|ganci|sviluppi)[^\n]*\n\s*[1-5]/i,
          /\n\n\s*[1-5][.):–]\s+[A-Z\u00C0-\u00FF]/,
          /\n\n\s*[1-5][.):–]\s+/,
          /\n---\n\n\s*[1-5]/,
        ];
        let cut = -1;
        for (const p of patterns) {
          const m = tail.search(p);
          if (m !== -1 && (cut === -1 || m < cut)) cut = m;
        }
        return cut !== -1 ? text.slice(0, half + cut).trim() : text.trim();
      };
      parts.push(`ULTIMO EPISODIO ${opposite.toUpperCase()} (raccordo narrativo completo):\n${_crossStrip(lastOpposite.content)}`);
    }

    // Outfit Stylist recenti
    const stylistMsgs = (chats['stylist'] || []).flatMap(c => c.messages || []).filter(m => m.role === 'assistant').slice(-2);
    if (stylistMsgs.length) parts.push(`OUTFIT (Stylist):\n${stylistMsgs.map(m => getContextBriefFromText(m.content, 400)).join("\n---\n")}`);

    return parts.join("\n\n===\n\n");
  }

  // STYLIST: percorso dedicato -- solo location + canon Lab
  if (currentSection === 'stylist') {
    const _styLastEp = getLastEpisodeOfSection(chats, 'racconto');
    if (_styLastEp?.content) parts.push(`LOCATION E MOMENTO:\n${_styLastEp.content.slice(0,300)}`);
    const _styLabMsgs = (chats['lab'] || []).flatMap(ch => ch.messages || []).filter(m => m.role === 'assistant');
    const _styCanons = _styLabMsgs.filter(m => m.content?.includes('📦 BLOCCO CANON')).slice(-2);
    if (_styCanons.length > 0) parts.push(`CANON LAB:\n${_styCanons.map(m=>m.content.slice(0,400)).join("\n---\n")}`);
    return parts.join("\n\n===\n\n");
  }

  (CROSS_READS[currentSection] || []).forEach(sec => {
    const allMsgs = (chats[sec] || []).flatMap(c => c.messages || []).filter(m => m.role === 'assistant');
    if (!allMsgs.length) return;
    const labels = { stylist: 'OUTFIT (Stylist)', lab: 'EVENTI (Lab)', racconto: 'STORIA (Racconto)', intimo: 'SCENE INTIME (Intimo)' };
    const maxChars = sec === 'lab' ? 2000 : 400;
    const recent = allMsgs.slice(-CROSS_MSGS_PER_SECTION).map(m => getContextBriefFromText(m.content, maxChars));
    parts.push(`${labels[sec]}:\n${recent.join("\n---\n")}`);
  });
  return parts.join("\n\n===\n\n");
}

function buildChronologicalMessages(chats) {
  const allMsgs = getUnifiedNarrativeMessages(chats).map(m => ({
    section: m.section,
    content: m.content,
    timestamp: m.timestamp || 0,
    label: m.section === 'intimo' ? 'SCENA INTIMA' : 'NARRATIVA'
  }));

  return allMsgs
    .sort((a, b) => a.timestamp - b.timestamp)
    .map(m => `[${m.label}${m.timestamp ? ` -- ${new Date(m.timestamp).toLocaleString("it-IT", { day:"2-digit", month:"2-digit", hour:"2-digit", minute:"2-digit" })}` : ''}]\n${m.content}`)
    .join("\n\n---\n\n");
}

// ============================================================
// HELPERS
// ============================================================
function formatTime(ts) { if (!ts) return ""; return new Date(ts).toLocaleTimeString("it-IT",{hour:"2-digit",minute:"2-digit"}); }
function formatDateTime(ts) { if (!ts) return ""; return new Date(ts).toLocaleString("it-IT",{day:"2-digit",month:"2-digit",hour:"2-digit",minute:"2-digit"}); }

function renderMarkdown(text) {
  if (!text) return [];
  return text.split("\n").map((line, i, arr) => <span key={i}>{parseInline(line)}{i < arr.length-1 && <br/>}</span>);
}
function parseInline(text) {
  const parts = []; const regex = /(\*\*\*(.+?)\*\*\*|\*\*(.+?)\*\*|\*(.+?)\*|_(.+?)_|`(.+?)`)/g; let last=0,m;
  while((m=regex.exec(text))!==null){ if(m.index>last) parts.push(<span key={last}>{text.slice(last,m.index)}</span>); if(m[2]) parts.push(<strong key={m.index}><em>{m[2]}</em></strong>); else if(m[3]) parts.push(<strong key={m.index}>{m[3]}</strong>); else if(m[4]) parts.push(<em key={m.index}>{m[4]}</em>); else if(m[5]) parts.push(<em key={m.index}>{m[5]}</em>); else if(m[6]) parts.push(<code key={m.index} style={{background:"rgba(255,255,255,0.1)",padding:"1px 5px",borderRadius:"3px",fontSize:"13px"}}>{m[6]}</code>); last=m.index+m[0].length; }
  if(last<text.length) parts.push(<span key={last}>{text.slice(last)}</span>);
  return parts.length>0?parts:[<span key={0}>{text}</span>];
}
function renderMarkdownHTML(text) {
  if (!text) return "";
  return text
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/\*\*\*(.+?)\*\*\*/g, "<strong><em>$1</em></strong>")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>")
    .replace(/_(.+?)_/g, "<em>$1</em>")
    .replace(/`(.+?)`/g, '<code style="background:rgba(255,255,255,0.1);padding:1px 5px;border-radius:3px;font-size:13px">$1</code>')
    .replace(/\n/g, "<br/>");
}

// ============================================================
// FILE ATTACHMENT HELPER
// ============================================================
async function readFileAsBase64(file) {
  return new Promise((res, rej) => { const r = new FileReader(); r.onload = e => res({ name: file.name, type: file.type, data: e.target.result, size: file.size }); r.onerror = rej; r.readAsDataURL(file); });
}

// ============================================================
// UI COMPONENTS
// ============================================================
// Indicatore "freschezza memoria": NON conta i messaggi salvati (il contesto
// inviato è limitato a ultimi 10 msg + riassunto compresso + codex + ledger, quindi
// la lunghezza chat NON satura il contesto). Mostra invece quanti episodi NON sono
// ancora nel riassunto -- la vera metrica di rischio coerenza: più è alta, più la
// memoria a lungo termine è indietro e conviene aggiornare il riassunto.
function SaturationBar({ count, max, warnAt, critAt, color, label, critLabel }) {
  const _max = max || 15, _warn = warnAt ?? 6, _crit = critAt ?? 12;
  const pct = Math.min((count/_max)*100,100), isW = count>=_warn, isC = count>=_crit;
  const bc = isC?"#e07878":isW?"#e0c078":color;
  if(count<=0) return null;
  return <div style={{padding:"6px 10px",borderBottom:`1px solid ${UI.borderSubtle}`,background:isC?"rgba(224,120,120,0.08)":isW?"rgba(224,192,120,0.06)":"transparent"}}><div style={{display:"flex",justifyContent:"space-between",marginBottom:"4px"}}><span style={{fontSize:"10px",color:bc}}>{isC?(critLabel||label):label}</span><span style={{fontSize:"10px",color:UI.textMuted}}>{count}/{_max}</span></div><div style={{height:"3px",background:"rgba(255,255,255,0.06)",borderRadius:"2px"}}><div style={{height:"100%",width:`${pct}%`,background:bc,borderRadius:"2px",transition:"width 0.3s"}}/></div></div>;
}

function MessageActions({ msg, onEdit, onRewrite, onGenerateImage, onDelete, color }) {
  const [show, setShow] = useState(false), [copied, setCopied] = useState(false);
  const isMobileDevice = window.innerWidth <= 768;

  const actions = <div style={{display:"flex",alignItems:"center",gap:"2px",background:"#1e1c26",border:`1px solid ${UI.border}`,borderRadius:"8px",padding:"3px 6px",zIndex:10,boxShadow:"0 4px 16px rgba(0,0,0,0.5)",whiteSpace:"nowrap",width:"fit-content",flexWrap:"wrap"}}>
    <ActionBtn icon={copied?"✅":"📋"} label={copied?"Copiato!":"Copia"} onClick={()=>{navigator.clipboard.writeText(msg.content);setCopied(true);setTimeout(()=>setCopied(false),2000);}} color={color}/>
    {msg.role==="assistant"&&<ActionBtn icon="✏️" label="Modifica" onClick={()=>onEdit&&onEdit(msg)} color={color}/>}
    {msg.role==="assistant"&&<ActionBtn icon="🔄" label="Riscrivi" onClick={()=>onRewrite&&onRewrite(msg)} color={color}/>}
    {msg.role==="assistant"&&onGenerateImage&&<ActionBtn icon="🎨" label="Immagine" onClick={()=>onGenerateImage(msg)} color={color}/>}
    <ActionBtn icon="↗️" label="Condividi" onClick={()=>{if(navigator.share)navigator.share({text:msg.content});else{navigator.clipboard.writeText(msg.content);alert("Copiato!");}}} color={color}/>
    {onDelete&&<ActionBtn icon="🗑️" label="Elimina" onClick={()=>onDelete(msg)} color="#e07878"/>}
    {msg.timestamp&&<span style={{fontSize:"10px",color:UI.textMuted,paddingLeft:"6px",borderLeft:`1px solid ${UI.borderSubtle}`,marginLeft:"2px"}}>{formatDateTime(msg.timestamp)}</span>}
  </div>;

  if (isMobileDevice) {
    return <div style={{marginTop:"4px"}}>{actions}</div>;
  }

  return <div style={{position:"relative",minHeight:"20px"}} onMouseEnter={()=>setShow(true)} onMouseLeave={()=>setShow(false)}>
    {show && actions}
  </div>;
}

function ActionBtn({ icon, label, onClick, color }) {
  const [h,setH]=useState(false);
  return <button onClick={onClick} onMouseEnter={()=>setH(true)} onMouseLeave={()=>setH(false)} title={label} style={{background:h?`${color}22`:"transparent",border:"none",borderRadius:"6px",padding:"4px 7px",cursor:"pointer",fontSize:"12px",color:h?color:UI.textSecondary,fontFamily:"Georgia, serif",transition:"all 0.15s",display:"flex",alignItems:"center",gap:"3px"}}>{icon}<span style={{fontSize:"10px"}}>{label}</span></button>;
}

function UserCopyBtn({ content }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={()=>{ navigator.clipboard.writeText(content||""); setCopied(true); setTimeout(()=>setCopied(false),2000); }}
      title="Copia"
      style={{background:"none",border:"none",color:copied?"#7eb8c9":"rgba(255,255,255,0.2)",cursor:"pointer",fontSize:"11px",padding:"2px 5px",transition:"color 0.2s"}}
      onMouseEnter={e=>e.currentTarget.style.color="#7eb8c9"}
      onMouseLeave={e=>{ if(!copied) e.currentTarget.style.color="rgba(255,255,255,0.2)"; }}
    >{copied?"✅":"📋"}</button>
  );
}

// Tooltip riutilizzabile -- sostituisce i testi esplicativi
function Tooltip({ text, children }) {
  const [show, setShow] = useState(false);
  const [pos, setPos] = useState({ left:"0", right:"auto" });
  const ref = useRef(null);
  const isMob = window.innerWidth <= 768;
  const calcPos = () => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const spaceRight = window.innerWidth - rect.left;
    // Se c'è spazio sufficiente a destra apri a destra, altrimenti a sinistra
    if (spaceRight >= 280) {
      setPos({ left:"0", right:"auto" });
    } else {
      setPos({ left:"auto", right:"0" });
    }
  };
  return (
    <div ref={ref} style={{position:"relative",display:"inline-flex",alignItems:"center"}}
      onMouseEnter={()=>{ if(!isMob){ calcPos(); setShow(true); }}}
      onMouseLeave={()=>setShow(false)}
      onClick={()=>{ if(isMob){ calcPos(); setShow(s=>!s); }}}>
      {children}
      {show && (
        <div style={{position:"absolute",bottom:"calc(100% + 8px)",left:pos.left,right:pos.right,background:"#1e1c26",border:"1px solid rgba(255,255,255,0.15)",borderRadius:"8px",padding:"10px 14px",zIndex:500,boxShadow:"0 4px 20px rgba(0,0,0,0.8)",width:"260px",maxWidth:"calc(100vw - 48px)",pointerEvents:"none"}}>
          <p style={{fontSize:"12px",color:UI.textSecondary,margin:0,lineHeight:"1.6",textAlign:"left"}}>{text}</p>
        </div>
      )}
    </div>
  );
}

function TooltipIcon({ text, color }) {
  return (
    <Tooltip text={text}>
      <span style={{display:"inline-flex",alignItems:"center",justifyContent:"center",width:"16px",height:"16px",borderRadius:"50%",border:`1px solid ${color||"rgba(255,255,255,0.2)"}`,fontSize:"10px",color:color||UI.textMuted,cursor:"help",flexShrink:0,marginLeft:"6px"}}>?</span>
    </Tooltip>
  );
}

function FilterBtn({ label, active, color, onClick }) {
  return <button onClick={onClick} style={{padding:"4px 8px",background:active?`${color}20`:"transparent",border:active?`1px solid ${color}45`:`1px solid ${UI.borderSubtle}`,borderRadius:"4px",color:active?color:UI.textSecondary,cursor:"pointer",fontSize:"11px",fontFamily:"Georgia, serif",whiteSpace:"nowrap"}}>{label}</button>;
}

function FormRow({ label, children }) {
  return <div><label style={{display:"block",color:UI.textSecondary,fontSize:"11px",marginBottom:"5px",letterSpacing:"1px",textTransform:"uppercase"}}>{label}</label>{children}</div>;
}

function PhotoUpload({ value, onChange, color, size="64px", round=true }) {
  return <div style={{display:"flex",gap:"10px",alignItems:"center"}}>{value&&<img src={value} alt="" style={{width:size,height:size,borderRadius:round?"50%":"6px",objectFit:"cover",border:`2px solid ${color}40`}}/>}<label style={{padding:"7px 14px",background:`${color}12`,border:`1px solid ${color}35`,borderRadius:"6px",cursor:"pointer",fontSize:"12px",color}}>📷 {value?"Cambia":"Carica foto"}<input type="file" accept="image/*" style={{display:"none"}} onChange={e=>{const f=e.target.files[0];if(!f)return;const r=new FileReader();r.onload=ev=>onChange(ev.target.result);r.readAsDataURL(f);}}/></label>{value&&<button onClick={()=>onChange("")} style={{background:"none",border:"none",color:UI.textMuted,cursor:"pointer",fontSize:"12px"}}>✕</button>}</div>;
}

// ============================================================
// CODEX FORMS
// ============================================================
function PersonaggioForm({ item, onChange, apiKey, apiBase, models, chats, summaryCompact }) {
  const u=(k,v)=>onChange({...item,[k]:v});
  const [activeTab, setActiveTab] = useState("carattere");
  const [showAddCronologia, setShowAddCronologia] = useState(false);
  const [newCronPeriodo, setNewCronPeriodo] = useState("");
  const [newCronEvento, setNewCronEvento] = useState("");
  const [newCronCambiamento, setNewCronCambiamento] = useState("");
  const [editingCronId, setEditingCronId] = useState(null);
  const [cronLoading, setCronLoading] = useState(false);

  const cronologia = item.cronologia || [];

  const addCronVoce = () => {
    if (!newCronEvento.trim()) return;
    const nuova = { id:`cr${Date.now()}`, periodo:newCronPeriodo.trim(), evento:newCronEvento.trim(), cambiamento:newCronCambiamento.trim(), createdAt:Date.now() };
    u("cronologia", [...cronologia, nuova]);
    setNewCronPeriodo(""); setNewCronEvento(""); setNewCronCambiamento(""); setShowAddCronologia(false);
  };

  const deleteCronVoce = (id) => u("cronologia", cronologia.filter(c=>c.id!==id));
  const updateCronVoce = (id, field, val) => u("cronologia", cronologia.map(c=>c.id===id?{...c,[field]:val}:c));

  const aggiornaCronologiaAI = async () => {
    if (!apiKey || !apiBase) { alert("Configura prima la API key nelle Impostazioni."); return; }
    setCronLoading(true);
    const nome = item.nome || "questo personaggio";
    const tuttiMsgAssistant = Object.values(chats).flat().flatMap(c=>c.messages||[]).filter(m=>m.role==="assistant").slice(-30).map(m=>m.content).join("\n\n---\n\n");
    const cronEsistente = cronologia.length>0 ? `\nCronologia esistente:\n${cronologia.map(c=>`[${c.periodo}] ${c.evento}${c.cambiamento?` → ${c.cambiamento}`:""}`).join("\n")}` : "";
    try {
      const resp = await fetch(`${apiBase}/chat/completions`, {
        method:"POST", headers:{"Content-Type":"application/json","Authorization":`Bearer ${apiKey}`},
        body: JSON.stringify({ model:"deepseek/deepseek-chat-v3-0324", messages:[{role:"user",content:`Analizza i seguenti episodi narrativi e identifica gli eventi significativi che riguardano il personaggio "${nome}" e che hanno causato cambiamenti nel suo carattere, comportamento, paure o relazioni.\n\nRiassunto storia:\n${summaryCompact||""}\n\nUltimi episodi:\n${tuttiMsgAssistant}${cronEsistente}\n\nRispondi SOLO con un JSON array di oggetti con questa struttura (max 5 nuove voci, SOLO eventi non già presenti):\n[{"periodo":"Es: Estate 2019","evento":"Descrizione breve dell'evento","cambiamento":"Come ha cambiato il personaggio"}]\n\nSe non trovi nuovi eventi significativi per "${nome}", rispondi con array vuoto: []`}], max_tokens:800 })
      });
      const data = await resp.json();
      const raw = data.choices?.[0]?.message?.content || "[]";
      const clean = raw.replace(/```json|```/g,"").trim();
      const nuoveVoci = JSON.parse(clean);
      if (Array.isArray(nuoveVoci) && nuoveVoci.length > 0) {
        const vociFormattate = nuoveVoci.map(v=>({id:`cr${Date.now()}_${Math.random()}`,periodo:v.periodo||"",evento:v.evento||"",cambiamento:v.cambiamento||"",createdAt:Date.now(),aiGenerated:true}));
        u("cronologia", [...cronologia, ...vociFormattate]);
        alert(`✅ Aggiunte ${vociFormattate.length} nuove voci alla cronologia di ${nome}.`);
      } else {
        alert(`Nessun nuovo evento significativo trovato per ${nome} negli episodi recenti.`);
      }
    } catch(e) { alert("Errore nell'analisi AI. Riprova."); }
    setCronLoading(false);
  };

  const tabs = [
    { id:"carattere", label:"🎭 Carattere" },
    { id:"storia", label:"📖 Storia" },
    { id:"extra", label:"⭐ Extra" },
  ];

  const tabBtn = (tab) => ({
    padding:"6px 12px", background: activeTab===tab.id ? "rgba(201,169,110,0.2)" : "transparent",
    border: activeTab===tab.id ? "1px solid rgba(201,169,110,0.4)" : `1px solid ${UI.borderSubtle}`,
    borderRadius:"6px", color: activeTab===tab.id ? "#c9a96e" : UI.textMuted,
    cursor:"pointer", fontSize:"12px", fontFamily:"Georgia, serif", whiteSpace:"nowrap"
  });

  return <div style={{display:"flex",flexDirection:"column",gap:"12px"}}>

    {/* RUOLO NARRATIVO */}
    <div style={{background:"rgba(201,169,110,0.06)",border:"1px solid rgba(201,169,110,0.25)",borderRadius:"8px",padding:"12px"}}>
      <label style={{color:"#c9a96e",fontSize:"11px",letterSpacing:"1px",textTransform:"uppercase",fontWeight:"bold",display:"block",marginBottom:"10px"}}>✦ Ruolo Narrativo</label>
      <div style={{display:"flex",flexDirection:"column",gap:"8px"}}>
        <label style={{display:"flex",alignItems:"center",gap:"10px",cursor:"pointer"}}>
          <input type="checkbox" checked={!!item.protagonista} onChange={e=>u("protagonista",e.target.checked)} style={{accentColor:"#c9a96e",width:"16px",height:"16px"}}/>
          <span style={{fontSize:"13px",color:item.protagonista?"#c9a96e":UI.textSecondary}}>⭐ Protagonista -- personaggio principale della storia</span>
        </label>
        <label style={{display:"flex",alignItems:"center",gap:"10px",cursor:"pointer"}}>
          <input type="checkbox" checked={!!item.fiammaGemella} onChange={e=>u("fiammaGemella",e.target.checked)} style={{accentColor:"#c97e7e",width:"16px",height:"16px"}}/>
          <span style={{fontSize:"13px",color:item.fiammaGemella?"#c97e7e":UI.textSecondary}}>🔥 Fiamma Gemella -- connessione animica profonda</span>
        </label>
        <label style={{display:"flex",alignItems:"center",gap:"10px",cursor:"pointer"}}>
          <input type="checkbox" checked={!!item.includePOV} onChange={e=>u("includePOV",e.target.checked)} style={{accentColor:"#7eb8c9",width:"16px",height:"16px"}}/>
          <span style={{fontSize:"13px",color:item.includePOV?"#7eb8c9":UI.textSecondary}}>👁️ Disponibile come POV</span>
        </label>
        {!item.protagonista && !item.fiammaGemella && (
          <div style={{marginTop:"4px",display:"flex",flexDirection:"column",gap:"8px"}}>
            <div>
              <label style={{...LS,marginBottom:"5px"}}>Rilevanza personaggio secondario</label>
              <select value={item.rilevanza||"secondario"} onChange={e=>u("rilevanza",e.target.value)} style={{...FIS,cursor:"pointer"}}>
                <option value="secondario">👥 Secondario ricorrente</option>
                <option value="comparsa">👤 Comparsa</option>
              </select>
            </div>
            <div>
              <label style={{...LS,marginBottom:"5px"}}>🫂 Gruppo di appartenenza</label>
              <select value={item.gruppo||""} onChange={e=>u("gruppo",e.target.value)} style={{...FIS,cursor:"pointer"}}>
                <option value="">Nessun gruppo (visibile se citato)</option>
                <option value="nadia">Gruppo Nadia</option>
                <option value="liam">Gruppo Liam</option>
                <option value="tutti">Tutti (personaggio comune)</option>
              </select>
            </div>
            {(item.gruppo === "nadia" || item.gruppo === "liam") && (
              <div>
                <label style={{...LS,marginBottom:"5px"}}>🔗 Introdotto a (separati da virgola)</label>
                <input value={(item.introdottoA||[]).join(", ")} onChange={e=>u("introdottoA",e.target.value.split(",").map(s=>s.trim()).filter(Boolean))} placeholder="es. Nadia, Liam..." style={{...FIS}}/>
                <p style={{fontSize:"11px",color:UI.textMuted,marginTop:"3px"}}>Aggiungi un nome quando questo personaggio viene presentato a qualcuno. Lascia vuoto se ancora non si conoscono.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>

    {/* SEZIONE BASE -- sempre visibile */}
    <FormRow label="Nome completo"><input value={item.nome||""} onChange={e=>u("nome",e.target.value)} placeholder="Es: Nadia Moretti" style={FIS}/></FormRow>
    <FormRow label="Foto"><PhotoUpload value={item.foto||""} onChange={v=>u("foto",v)} color="#c9a96e"/></FormRow>

    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"10px"}}>
      <FormRow label="Età"><input value={item.eta||""} onChange={e=>u("eta",e.target.value)} placeholder="Es: 24" style={FIS}/></FormRow>
      <FormRow label="Data di nascita"><input value={item.dataNascita||""} onChange={e=>u("dataNascita",e.target.value)} placeholder="dd/mm/YYYY" style={FIS}/></FormRow>
      <FormRow label="Nazionalità"><input value={item.nazionalita||""} onChange={e=>u("nazionalita",e.target.value)} placeholder="Es: Italiana" style={FIS}/></FormRow>
      <FormRow label="Provenienza"><input value={item.provenienza||""} onChange={e=>u("provenienza",e.target.value)} placeholder="Es: di Wolverhampton" style={FIS}/></FormRow>
      <FormRow label="Altezza"><input value={item.altezza||""} onChange={e=>u("altezza",e.target.value)} placeholder="Es: 1,68m" style={FIS}/></FormRow>
      <FormRow label="Città attuale"><input value={item.cittaAttuale||""} onChange={e=>u("cittaAttuale",e.target.value)} placeholder="Es: Londra" style={FIS}/></FormRow>
    </div>

    <FormRow label="Professione / Carriera / Studi"><input value={item.professione||""} onChange={e=>u("professione",e.target.value)} placeholder="Es: Social Media Manager, laurea in Lingue" style={FIS}/></FormRow>
    <FormRow label="Aspetto fisico"><textarea value={item.aspetto||""} onChange={e=>u("aspetto",e.target.value)} placeholder="Capelli, occhi, corporatura..." style={{...FIS,minHeight:"80px",resize:"vertical"}}/></FormRow>

    {/* TAB NAVIGATION */}
    <div style={{display:"flex",gap:"6px",overflowX:"auto",paddingBottom:"2px"}}>
      {tabs.map(tab => <button key={tab.id} onClick={()=>setActiveTab(tab.id)} style={tabBtn(tab)}>{tab.label}</button>)}
    </div>

    {/* TAB: CARATTERE */}
    {activeTab==="carattere" && (
      <div style={{display:"flex",flexDirection:"column",gap:"12px"}}>
        <FormRow label="Carattere & Personalità"><textarea value={item.carattere||""} onChange={e=>u("carattere",e.target.value)} placeholder="Tratti principali, pregi, difetti..." style={{...FIS,minHeight:"100px",resize:"vertical"}}/></FormRow>
        <FormRow label="Paure & Vulnerabilità"><textarea value={item.paure||""} onChange={e=>u("paure",e.target.value)} placeholder="Cosa teme, traumi..." style={{...FIS,minHeight:"80px",resize:"vertical"}}/></FormRow>
        <FormRow label="Presenza Incontestabile"><textarea value={item.presenzaIncontestabile||""} onChange={e=>u("presenzaIncontestabile",e.target.value)} placeholder="Come si impone, come viene percepito dagli altri..." style={{...FIS,minHeight:"80px",resize:"vertical"}}/></FormRow>
      </div>
    )}

    {/* TAB: STORIA */}
    {activeTab==="storia" && (
      <div style={{display:"flex",flexDirection:"column",gap:"12px"}}>
        <FormRow label="Famiglia & Passato"><textarea value={item.famigliaPassato||""} onChange={e=>u("famigliaPassato",e.target.value)} placeholder="Origini familiari, infanzia, eventi formativi..." style={{...FIS,minHeight:"100px",resize:"vertical"}}/></FormRow>
        <FormRow label="Ruolo nella storia"><textarea value={item.ruolo||""} onChange={e=>u("ruolo",e.target.value)} placeholder="Protagonista, antagonista, ruolo narrativo..." style={{...FIS,minHeight:"80px",resize:"vertical"}}/></FormRow>
        <FormRow label="Cose da sapere"><textarea value={item.coseDaSapere||""} onChange={e=>u("coseDaSapere",e.target.value)} placeholder="Informazioni chiave per capire questo personaggio..." style={{...FIS,minHeight:"100px",resize:"vertical"}}/></FormRow>

        {/* CRONOLOGIA */}
        <div style={{background:"rgba(126,184,201,0.06)",border:"1px solid rgba(126,184,201,0.25)",borderRadius:"8px",padding:"12px"}}>
          <div style={{display:"flex",alignItems:"center",gap:"8px",marginBottom:"10px"}}>
            <span style={{fontSize:"16px"}}>📅</span>
            <label style={{color:"#7eb8c9",fontSize:"11px",letterSpacing:"1px",textTransform:"uppercase",fontWeight:"bold",flex:1}}>Cronologia di {item.nome||"questo personaggio"}</label>
            <button onClick={aggiornaCronologiaAI} disabled={cronLoading} style={{padding:"4px 10px",background:"rgba(126,184,201,0.12)",border:"1px solid rgba(126,184,201,0.3)",borderRadius:"6px",color:"#7eb8c9",cursor:"pointer",fontSize:"11px",fontFamily:"Georgia, serif"}}>
              {cronLoading?"⏳ Analisi...":"✦ Aggiorna con AI"}
            </button>
          </div>
          {cronologia.length === 0 && !showAddCronologia && (
            <p style={{fontSize:"12px",color:UI.textFaint,textAlign:"center",padding:"12px 0",border:"1px dashed rgba(126,184,201,0.2)",borderRadius:"6px"}}>Nessun evento registrato ancora.</p>
          )}
          {cronologia.map(voce => (
            <div key={voce.id} style={{background:"rgba(255,255,255,0.03)",border:"1px solid rgba(126,184,201,0.15)",borderRadius:"6px",padding:"10px",marginBottom:"8px",position:"relative"}}>
              {editingCronId === voce.id ? (
                <div style={{display:"flex",flexDirection:"column",gap:"6px"}}>
                  <input value={voce.periodo} onChange={e=>updateCronVoce(voce.id,"periodo",e.target.value)} placeholder="Periodo" style={{...FIS,fontSize:"12px"}}/>
                  <textarea value={voce.evento} onChange={e=>updateCronVoce(voce.id,"evento",e.target.value)} placeholder="Evento" style={{...FIS,minHeight:"60px",resize:"vertical",fontSize:"12px"}}/>
                  <textarea value={voce.cambiamento} onChange={e=>updateCronVoce(voce.id,"cambiamento",e.target.value)} placeholder="Come ha cambiato il personaggio" style={{...FIS,minHeight:"50px",resize:"vertical",fontSize:"12px"}}/>
                  <button onClick={()=>setEditingCronId(null)} style={{alignSelf:"flex-start",padding:"4px 12px",background:"rgba(126,184,201,0.15)",border:"1px solid rgba(126,184,201,0.3)",borderRadius:"6px",color:"#7eb8c9",cursor:"pointer",fontSize:"11px",fontFamily:"Georgia, serif"}}>✓ Fatto</button>
                </div>
              ) : (
                <>
                  {voce.aiGenerated && <span style={{fontSize:"9px",color:"#7eb8c9",background:"rgba(126,184,201,0.1)",padding:"1px 6px",borderRadius:"4px",position:"absolute",top:"8px",right:"30px"}}>AI</span>}
                  <button onClick={()=>deleteCronVoce(voce.id)} style={{position:"absolute",top:"6px",right:"6px",background:"none",border:"none",color:UI.textMuted,cursor:"pointer",fontSize:"12px"}} onMouseEnter={e=>e.currentTarget.style.color=UI.delete} onMouseLeave={e=>e.currentTarget.style.color=UI.textMuted}>✕</button>
                  {voce.periodo && <div style={{fontSize:"10px",color:"#7eb8c9",marginBottom:"3px"}}>📅 {voce.periodo}</div>}
                  <div style={{fontSize:"13px",color:UI.textPrimary,lineHeight:"1.5",cursor:"pointer"}} onClick={()=>setEditingCronId(voce.id)}>{voce.evento}</div>
                  {voce.cambiamento && <div style={{fontSize:"12px",color:UI.textSecondary,marginTop:"4px",borderLeft:"2px solid rgba(126,184,201,0.3)",paddingLeft:"8px",fontStyle:"italic"}}>→ {voce.cambiamento}</div>}
                </>
              )}
            </div>
          ))}
          {showAddCronologia ? (
            <div style={{background:"rgba(126,184,201,0.05)",border:"1px solid rgba(126,184,201,0.2)",borderRadius:"6px",padding:"10px",display:"flex",flexDirection:"column",gap:"6px"}}>
              <input value={newCronPeriodo} onChange={e=>setNewCronPeriodo(e.target.value)} placeholder="Periodo (es: Estate 2019)" style={{...FIS,fontSize:"12px"}}/>
              <textarea value={newCronEvento} onChange={e=>setNewCronEvento(e.target.value)} placeholder="Descrivi l'evento..." style={{...FIS,minHeight:"60px",resize:"vertical",fontSize:"12px"}}/>
              <textarea value={newCronCambiamento} onChange={e=>setNewCronCambiamento(e.target.value)} placeholder="Come ha cambiato il personaggio (facoltativo)" style={{...FIS,minHeight:"50px",resize:"vertical",fontSize:"12px"}}/>
              <div style={{display:"flex",gap:"6px"}}>
                <button onClick={addCronVoce} style={{flex:1,padding:"6px",background:"rgba(126,184,201,0.15)",border:"1px solid rgba(126,184,201,0.3)",borderRadius:"6px",color:"#7eb8c9",cursor:"pointer",fontSize:"12px",fontFamily:"Georgia, serif"}}>✓ Aggiungi</button>
                <button onClick={()=>{setShowAddCronologia(false);setNewCronPeriodo("");setNewCronEvento("");setNewCronCambiamento("");}} style={{padding:"6px 12px",background:"rgba(255,255,255,0.04)",border:`1px solid ${UI.borderSubtle}`,borderRadius:"6px",color:UI.textMuted,cursor:"pointer",fontSize:"12px",fontFamily:"Georgia, serif"}}>Annulla</button>
              </div>
            </div>
          ) : (
            <button onClick={()=>setShowAddCronologia(true)} style={{width:"100%",padding:"7px",background:"rgba(126,184,201,0.07)",border:"1px dashed rgba(126,184,201,0.3)",borderRadius:"6px",color:"#7eb8c9",cursor:"pointer",fontSize:"12px",fontFamily:"Georgia, serif",marginTop:"4px"}}>+ Aggiungi evento manualmente</button>
          )}
        </div>
      </div>
    )}

    {/* TAB: EXTRA */}
    {activeTab==="extra" && (
      <div style={{display:"flex",flexDirection:"column",gap:"12px"}}>
        <FormRow label="Note aggiuntive"><textarea value={item.note||""} onChange={e=>u("note",e.target.value)} placeholder="Altre informazioni..." style={{...FIS,minHeight:"80px",resize:"vertical"}}/></FormRow>
        <FormRow label="Altre cose importanti"><textarea value={item.altreCoseImportanti||""} onChange={e=>u("altreCoseImportanti",e.target.value)} placeholder="Tutto ciò che non ha un campo specifico -- dettagli unici, curiosità, elementi caratteristici..." style={{...FIS,minHeight:"120px",resize:"vertical"}}/></FormRow>

        {/* SEGRETI */}
        <div style={{background:"rgba(155,142,201,0.06)",border:"1px solid rgba(155,142,201,0.25)",borderRadius:"8px",padding:"12px"}}>
          <div style={{display:"flex",alignItems:"center",gap:"8px",marginBottom:"8px"}}>
            <span style={{fontSize:"16px"}}>🔒</span>
            <label style={{color:"#9b8ec9",fontSize:"11px",letterSpacing:"1px",textTransform:"uppercase",fontWeight:"bold"}}>Segreti di {item.nome||"questo personaggio"}</label>
          </div>
          <p style={{fontSize:"11px",color:UI.textMuted,margin:"0 0 8px",lineHeight:"1.5"}}>
            Informazioni che <strong style={{color:"#9b8ec9"}}>solo questo personaggio conosce</strong>. L'AI le userà per guidare il comportamento ma non le rivelerà prima del momento giusto.
          </p>
          <textarea value={item.segreti||""} onChange={e=>u("segreti",e.target.value)} placeholder="Es: Liam ha comprato l'anello..." style={{...FIS,minHeight:"80px",resize:"vertical",borderColor:"rgba(155,142,201,0.3)"}}/>
        </div>
      </div>
    )}
  </div>;
}

function LuogoForm({ item, onChange }) {
  const u=(k,v)=>onChange({...item,[k]:v});
  return <div style={{display:"flex",flexDirection:"column",gap:"12px"}}>
    <FormRow label="Nome del luogo"><input value={item.nome||""} onChange={e=>u("nome",e.target.value)} placeholder="Es: Appartamento di Nadia" style={FIS}/></FormRow>
    <FormRow label="Foto"><PhotoUpload value={item.foto||""} onChange={v=>u("foto",v)} color="#7eb8c9" size="80px" round={false}/></FormRow>
    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"10px"}}>
      <FormRow label="Città"><input value={item.citta||""} onChange={e=>u("citta",e.target.value)} placeholder="Es: Londra" style={FIS}/></FormRow>
      <FormRow label="Periodo"><input value={item.periodo||""} onChange={e=>u("periodo",e.target.value)} placeholder="Es: 2014" style={FIS}/></FormRow>
    </div>
    <FormRow label="Descrizione"><textarea value={item.descrizione||""} onChange={e=>u("descrizione",e.target.value)} placeholder="Layout, atmosfera, dettagli..." style={{...FIS,minHeight:"100px",resize:"vertical"}}/></FormRow>
    <FormRow label="Significato narrativo"><textarea value={item.significato||""} onChange={e=>u("significato",e.target.value)} placeholder="Perché è importante?" style={{...FIS,minHeight:"60px",resize:"vertical"}}/></FormRow>

    {/* DISPONIBILE DA */}
    <div style={{background:"rgba(126,184,201,0.06)",border:"1px solid rgba(126,184,201,0.2)",borderRadius:"8px",padding:"12px"}}>
      <div style={{display:"flex",alignItems:"center",gap:"8px",marginBottom:"8px"}}>
        <span style={{fontSize:"16px"}}>⏳</span>
        <label style={{color:"#7eb8c9",fontSize:"11px",letterSpacing:"1px",textTransform:"uppercase"}}>Disponibilità narrativa</label>
      </div>
      <div style={{display:"flex",gap:"10px",alignItems:"center",flexWrap:"wrap"}}>
        <label style={{display:"flex",alignItems:"center",gap:"6px",cursor:"pointer",fontSize:"12px",color:!item.futura?UI.textPrimary:UI.textMuted}}>
          <input type="radio" checked={!item.futura} onChange={()=>u("futura",false)} style={{accentColor:"#7eb8c9"}}/>
          ✅ Disponibile subito
        </label>
        <label style={{display:"flex",alignItems:"center",gap:"6px",cursor:"pointer",fontSize:"12px",color:item.futura?UI.textPrimary:UI.textMuted}}>
          <input type="radio" checked={!!item.futura} onChange={()=>u("futura",true)} style={{accentColor:"#7eb8c9"}}/>
          🔒 Location futura -- non usare ancora
        </label>
      </div>
      {item.futura && <p style={{fontSize:"11px",color:"#7eb8c9",margin:"8px 0 0",background:"rgba(126,184,201,0.08)",padding:"6px 10px",borderRadius:"4px"}}>⚠️ Questo luogo non verrà incluso nel contesto finché non lo attivi.</p>}
    </div>
  </div>;
}

function RelazioneForm({ item, onChange, allPersonaggi }) {
  const nomi=allPersonaggi.map(p=>p.nome).filter(Boolean), u=(k,v)=>onChange({...item,[k]:v});
  return <div style={{display:"flex",flexDirection:"column",gap:"12px"}}>

    {/* Toggle Attiva/Disattivata */}
    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 14px",background:item.disattivata?"rgba(224,120,120,0.08)":"rgba(142,201,160,0.08)",border:`1px solid ${item.disattivata?"rgba(224,120,120,0.3)":"rgba(142,201,160,0.3)"}`,borderRadius:"8px"}}>
      <div>
        <div style={{fontSize:"13px",color:item.disattivata?UI.delete:"#8ec9a0",fontWeight:"bold"}}>
          {item.disattivata ? "⏸ Relazione non ancora attiva" : "▶ Relazione attiva"}
        </div>
        <div style={{fontSize:"11px",color:UI.textMuted,marginTop:"2px"}}>
          {item.disattivata ? "Non verrà inviata all'AI -- si attiverà quando indicato" : "Viene inviata all'AI quando entrambi i personaggi sono in scena"}
        </div>
      </div>
      <button onClick={()=>u("disattivata",!item.disattivata)} style={{padding:"6px 14px",background:item.disattivata?"rgba(142,201,160,0.15)":"rgba(224,120,120,0.15)",border:`1px solid ${item.disattivata?"rgba(142,201,160,0.4)":"rgba(224,120,120,0.4)"}`,borderRadius:"6px",color:item.disattivata?"#8ec9a0":UI.delete,cursor:"pointer",fontSize:"12px",fontFamily:"Georgia, serif"}}>
        {item.disattivata ? "▶ Attiva" : "⏸ Disattiva"}
      </button>
    </div>

    <div style={{display:"grid",gridTemplateColumns:"1fr auto 1fr",gap:"10px",alignItems:"end"}}>
      <FormRow label="Personaggio A"><select value={item.personaggioA||""} onChange={e=>u("personaggioA",e.target.value)} style={{...FIS,cursor:"pointer"}}><option value="">-- Seleziona --</option>{nomi.map(n=><option key={n} value={n}>{n}</option>)}<option value="__custom__">Scrivi manualmente...</option></select>{item.personaggioA==="__custom__"&&<input value={item.personaggioACustom||""} onChange={e=>u("personaggioACustom",e.target.value)} placeholder="Nome" style={{...FIS,marginTop:"6px"}}/>}</FormRow>
      <div style={{fontSize:"20px",color:"#c97eb8",paddingBottom:"4px",textAlign:"center"}}>↔</div>
      <FormRow label="Personaggio B"><select value={item.personaggioB||""} onChange={e=>u("personaggioB",e.target.value)} style={{...FIS,cursor:"pointer"}}><option value="">-- Seleziona --</option>{nomi.map(n=><option key={n} value={n}>{n}</option>)}<option value="__custom__">Scrivi manualmente...</option></select>{item.personaggioB==="__custom__"&&<input value={item.personaggioBCustom||""} onChange={e=>u("personaggioBCustom",e.target.value)} placeholder="Nome" style={{...FIS,marginTop:"6px"}}/>}</FormRow>
    </div>
    <FormRow label="Tipo"><select value={item.tipoRelazione||""} onChange={e=>u("tipoRelazione",e.target.value)} style={{...FIS,cursor:"pointer"}}><option value="">-- Seleziona --</option><optgroup label="Romantiche"><option value="Fiamme gemelle">Fiamme gemelle</option><option value="Coppia">Coppia</option><option value="Ex">Ex</option><option value="Interesse romantico">Interesse romantico</option></optgroup><optgroup label="Familiari"><option value="Fratelli/Sorelle">Fratelli/Sorelle</option><option value="Genitore/Figlio">Genitore/Figlio</option><option value="Famiglia acquisita">Famiglia acquisita</option></optgroup><optgroup label="Amicizie"><option value="Migliori amici">Migliori amici</option><option value="Amici">Amici</option><option value="Fratellanza d'anima">Fratellanza d'anima</option></optgroup><optgroup label="Conflitti"><option value="Rivalità">Rivalità</option><option value="Antagonismo">Antagonismo</option></optgroup><option value="Altro">Altro</option></select></FormRow>
    {item.tipoRelazione==="Altro"&&<FormRow label="Specifica"><input value={item.tipoRelazioneCustom||""} onChange={e=>u("tipoRelazioneCustom",e.target.value)} style={FIS}/></FormRow>}
    <FormRow label="Descrizione"><textarea value={item.descrizione||""} onChange={e=>u("descrizione",e.target.value)} placeholder="Storia comune, dinamiche..." style={{...FIS,minHeight:"90px",resize:"vertical"}}/></FormRow>
    <FormRow label="Stato attuale"><textarea value={item.statoAttuale||""} onChange={e=>u("statoAttuale",e.target.value)} placeholder="Come stanno le cose ora..." style={{...FIS,minHeight:"60px",resize:"vertical"}}/></FormRow>
  </div>;
}

function LoreForm({ item, onChange }) {
  const u=(k,v)=>onChange({...item,[k]:v});
  return <div style={{display:"flex",flexDirection:"column",gap:"12px"}}>
    <FormRow label="Titolo"><input value={item.nome||""} onChange={e=>u("nome",e.target.value)} placeholder="Es: Connessione telepatica" style={FIS}/></FormRow>
    <FormRow label="Categoria"><select value={item.categoria||""} onChange={e=>u("categoria",e.target.value)} style={{...FIS,cursor:"pointer"}}><option value="">-- Seleziona --</option><option value="Spiritualità & Anime">Spiritualità & Anime</option><option value="Telepatia & Connessione">Telepatia & Connessione</option><option value="Segnali & Sincronicità">Segnali & Sincronicità</option><option value="Dinamiche soprannaturali">Dinamiche soprannaturali</option><option value="Altro">Altro</option></select></FormRow>
    <FormRow label="Descrizione"><textarea value={item.descrizione||""} onChange={e=>u("descrizione",e.target.value)} placeholder="Come funziona..." style={{...FIS,minHeight:"100px",resize:"vertical"}}/></FormRow>
    <FormRow label="Come influenza la narrativa"><textarea value={item.influenza||""} onChange={e=>u("influenza",e.target.value)} placeholder="Come cambia le dinamiche..." style={{...FIS,minHeight:"70px",resize:"vertical"}}/></FormRow>
  </div>;
}

function RegolaForm({ item, onChange }) {
  const u=(k,v)=>onChange({...item,[k]:v});
  return <div style={{display:"flex",flexDirection:"column",gap:"12px"}}>
    <FormRow label="Titolo"><input value={item.nome||""} onChange={e=>u("nome",e.target.value)} placeholder="Es: Nessuna onniscienza" style={FIS}/></FormRow>
    <FormRow label="Categoria"><select value={item.categoria||""} onChange={e=>u("categoria",e.target.value)} style={{...FIS,cursor:"pointer"}}><option value="">-- Seleziona --</option><option value="POV & Narrazione">POV & Narrazione</option><option value="Tono & Stile">Tono & Stile</option><option value="Struttura episodi">Struttura episodi</option><option value="Continuità narrativa">Continuità narrativa</option><option value="Altro">Altro</option></select></FormRow>
    <FormRow label="Descrizione"><textarea value={item.descrizione||""} onChange={e=>u("descrizione",e.target.value)} placeholder="Spiega la regola..." style={{...FIS,minHeight:"100px",resize:"vertical"}}/></FormRow>

    {/* Toggle Regola Assoluta */}
    <div onClick={()=>u("assoluta",!item.assoluta)} style={{display:"flex",alignItems:"center",gap:"12px",padding:"10px 14px",background:item.assoluta?"rgba(224,120,120,0.1)":"rgba(255,255,255,0.03)",border:`1px solid ${item.assoluta?"rgba(224,120,120,0.4)":"rgba(255,255,255,0.08)"}`,borderRadius:"8px",cursor:"pointer"}}>
      <div style={{width:"36px",height:"20px",borderRadius:"10px",background:item.assoluta?"#e07878":"rgba(255,255,255,0.1)",position:"relative",transition:"background 0.2s",flexShrink:0}}>
        <div style={{position:"absolute",top:"3px",left:item.assoluta?"19px":"3px",width:"14px",height:"14px",borderRadius:"50%",background:"white",transition:"left 0.2s"}}/>
      </div>
      <div>
        <div style={{fontSize:"13px",color:item.assoluta?"#e07878":UI.textSecondary,fontWeight:item.assoluta?"bold":"normal"}}>
          {item.assoluta?"⚠️ Regola Assoluta -- sempre attiva":"Regola normale -- filtrata per contesto"}
        </div>
        <div style={{fontSize:"11px",color:UI.textMuted,marginTop:"2px"}}>
          {item.assoluta?"Viene sempre inviata all'AI, in ogni episodio, senza eccezioni":"Viene inviata solo se rilevante per la scena corrente"}
        </div>
      </div>
    </div>
  </div>;
}

function AltroForm({ item, onChange }) {
  const u=(k,v)=>onChange({...item,[k]:v});
  return <div style={{display:"flex",flexDirection:"column",gap:"12px"}}>
    <FormRow label="Titolo"><input value={item.nome||""} onChange={e=>u("nome",e.target.value)} placeholder="Dai un titolo" style={FIS}/></FormRow>
    <FormRow label="Tipo"><input value={item.tipo||""} onChange={e=>u("tipo",e.target.value)} placeholder="Es: Timeline, Evento, Oggetto..." style={FIS}/></FormRow>
    <FormRow label="Contenuto"><textarea value={item.contenuto||""} onChange={e=>u("contenuto",e.target.value)} placeholder="Informazioni necessarie..." style={{...FIS,minHeight:"120px",resize:"vertical"}}/></FormRow>
    <div style={{background:"rgba(170,170,170,0.05)",border:"1px solid rgba(170,170,170,0.15)",borderRadius:"8px",padding:"12px"}}>
      <div style={{display:"flex",gap:"10px",alignItems:"center",flexWrap:"wrap"}}>
        <span style={{fontSize:"13px",color:UI.textMuted}}>⏳ Disponibilità:</span>
        <label style={{display:"flex",alignItems:"center",gap:"6px",cursor:"pointer",fontSize:"12px",color:!item.futura?UI.textPrimary:UI.textMuted}}>
          <input type="radio" checked={!item.futura} onChange={()=>u("futura",false)} style={{accentColor:"#aaa"}}/>✅ Disponibile subito
        </label>
        <label style={{display:"flex",alignItems:"center",gap:"6px",cursor:"pointer",fontSize:"12px",color:item.futura?UI.textPrimary:UI.textMuted}}>
          <input type="radio" checked={!!item.futura} onChange={()=>u("futura",true)} style={{accentColor:"#aaa"}}/>🔒 Futuro -- non usare ancora
        </label>
      </div>
      {item.futura&&<p style={{fontSize:"11px",color:"#aaa",margin:"8px 0 0"}}>⚠️ Non verrà incluso nel contesto finché non lo attivi.</p>}
    </div>
  </div>;
}

const FG_CATEGORIE = [
  { id:"primo_incontro", label:"✨ Primo incontro / Riconoscimento", color:"#c9a96e" },
  { id:"connessione", label:"🔗 Connessione / Telepatia", color:"#9b8ec9" },
  { id:"separazione", label:"💔 Separazione / Allontanamento", color:"#c97e7e" },
  { id:"crisi", label:"⚡ Crisi / Conflitto", color:"#e0c078" },
  { id:"ricongiungimento", label:"🌟 Ricongiungimento", color:"#8ec9a0" },
  { id:"rivelazione", label:"🔮 Rivelazione / Svolta", color:"#7eb8c9" },
  { id:"intimo", label:"🔥 Momento intimo / Fisico", color:"#c97eb8" },
  { id:"altro", label:"📎 Altro", color:"#aaa" },
];

function FiammeGemelleForm({ item, onChange, apiKey, apiBase, chats, summaryCompact }) {
  const u=(k,v)=>onChange({...item,[k]:v});
  const [showAdd, setShowAdd] = useState(false);
  const [newPeriodo, setNewPeriodo] = useState("");
  const [newCategoria, setNewCategoria] = useState("connessione");
  const [newEvento, setNewEvento] = useState("");
  const [newImpatto, setNewImpatto] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);

  const eventi = item.eventi || [];

  const addEvento = () => {
    if (!newEvento.trim()) return;
    const nuovoEvento = { id:`fg${Date.now()}`, periodo:newPeriodo.trim(), categoria:newCategoria, evento:newEvento.trim(), impatto:newImpatto.trim(), createdAt:Date.now() };
    u("eventi", [...eventi, nuovoEvento]);
    setNewPeriodo(""); setNewCategoria("connessione"); setNewEvento(""); setNewImpatto(""); setShowAdd(false);
  };

  const deleteEvento = (id) => u("eventi", eventi.filter(e=>e.id!==id));
  const updateEvento = (id, field, val) => u("eventi", eventi.map(e=>e.id===id?{...e,[field]:val}:e));

  const aggiornaCronologiaFG = async () => {
    if (!apiKey || !apiBase) { alert("Configura prima la API key nelle Impostazioni."); return; }
    setLoading(true);
    const tuttiMsgAssistant = Object.values(chats).flat().flatMap(c=>c.messages||[]).filter(m=>m.role==="assistant").slice(-30).map(m=>m.content).join("\n\n---\n\n");
    const eventiEsistenti = eventi.length>0 ? `\nEventi già registrati:\n${eventi.map(e=>`[${e.periodo}] ${e.categoria}: ${e.evento}`).join("\n")}` : "";
    try {
      const resp = await fetch(`${apiBase}/chat/completions`, {
        method:"POST", headers:{"Content-Type":"application/json","Authorization":`Bearer ${apiKey}`},
        body: JSON.stringify({ model:"deepseek/deepseek-chat-v3-0324", messages:[{role:"user",content:`Analizza i seguenti episodi narrativi e identifica gli eventi significativi tra le Fiamme Gemelle (Nadia e Liam). Considera solo eventi che riguardano la loro connessione animica, il loro rapporto, momenti di vicinanza o separazione.\n\nRiassunto storia:\n${summaryCompact||""}\n\nUltimi episodi:\n${tuttiMsgAssistant}${eventiEsistenti}\n\nRispondi SOLO con un JSON array (max 5 nuovi eventi, SOLO eventi non già presenti):\n[{"periodo":"Es: Autunno 2019","categoria":"connessione","evento":"Descrizione breve","impatto":"Come ha influenzato il legame tra le FG"}]\n\nCategorie disponibili: primo_incontro, connessione, separazione, crisi, ricongiungimento, rivelazione, intimo, altro\n\nSe non trovi nuovi eventi significativi, rispondi con array vuoto: []`}], max_tokens:800 })
      });
      const data = await resp.json();
      const raw = data.choices?.[0]?.message?.content || "[]";
      const clean = raw.replace(/```json|```/g,"").trim();
      const nuoviEventi = JSON.parse(clean);
      if (Array.isArray(nuoviEventi) && nuoviEventi.length > 0) {
        const formattati = nuoviEventi.map(e=>({id:`fg${Date.now()}_${Math.random()}`,periodo:e.periodo||"",categoria:e.categoria||"altro",evento:e.evento||"",impatto:e.impatto||"",createdAt:Date.now(),aiGenerated:true}));
        u("eventi", [...eventi, ...formattati]);
        alert(`✅ Aggiunti ${formattati.length} nuovi eventi alla Cronologia Fiamme Gemelle.`);
      } else {
        alert("Nessun nuovo evento significativo trovato negli episodi recenti.");
      }
    } catch(e) { alert("Errore nell'analisi AI. Riprova."); }
    setLoading(false);
  };

  // Ordina per createdAt
  const eventiOrdinati = [...eventi].sort((a,b)=>a.createdAt-b.createdAt);

  return <div style={{display:"flex",flexDirection:"column",gap:"12px"}}>
    <div style={{background:"rgba(201,126,126,0.06)",border:"1px solid rgba(201,126,126,0.25)",borderRadius:"8px",padding:"14px"}}>
      <p style={{fontSize:"12px",color:UI.textSecondary,margin:"0 0 6px",lineHeight:"1.6"}}>
        Questa cronologia raccoglie tutti gli eventi importanti tra le <strong style={{color:"#c97e7e"}}>Fiamme Gemelle</strong>. L'AI la usa per mantenere la coerenza della loro connessione animica attraverso tutta la storia.
      </p>
    </div>

    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:"8px"}}>
      <span style={{fontSize:"12px",color:UI.textMuted}}>{eventiOrdinati.length} eventi registrati</span>
      <button onClick={aggiornaCronologiaFG} disabled={loading}
        style={{padding:"6px 14px",background:"rgba(201,126,126,0.12)",border:"1px solid rgba(201,126,126,0.3)",borderRadius:"6px",color:"#c97e7e",cursor:"pointer",fontSize:"12px",fontFamily:"Georgia, serif"}}>
        {loading?"⏳ Analisi...":"🔥 Aggiorna con AI"}
      </button>
    </div>

    {eventiOrdinati.length === 0 && !showAdd && (
      <p style={{fontSize:"12px",color:UI.textFaint,textAlign:"center",padding:"16px 0",border:"1px dashed rgba(201,126,126,0.2)",borderRadius:"6px"}}>Nessun evento registrato. Aggiungi il primo evento o usa "Aggiorna con AI".</p>
    )}

    {eventiOrdinati.map(ev => {
      const cat = FG_CATEGORIE.find(c=>c.id===ev.categoria) || FG_CATEGORIE[7];
      return (
        <div key={ev.id} style={{background:"rgba(255,255,255,0.03)",border:`1px solid ${cat.color}25`,borderRadius:"8px",padding:"12px",position:"relative"}}>
          {editingId === ev.id ? (
            <div style={{display:"flex",flexDirection:"column",gap:"6px"}}>
              <input value={ev.periodo} onChange={e=>updateEvento(ev.id,"periodo",e.target.value)} placeholder="Periodo (es: Autunno 2019)" style={{...FIS,fontSize:"12px"}}/>
              <select value={ev.categoria} onChange={e=>updateEvento(ev.id,"categoria",e.target.value)} style={{...FIS,fontSize:"12px",cursor:"pointer"}}>
                {FG_CATEGORIE.map(c=><option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
              <textarea value={ev.evento} onChange={e=>updateEvento(ev.id,"evento",e.target.value)} placeholder="Descrivi l'evento..." style={{...FIS,minHeight:"70px",resize:"vertical",fontSize:"12px"}}/>
              <textarea value={ev.impatto} onChange={e=>updateEvento(ev.id,"impatto",e.target.value)} placeholder="Impatto animico (facoltativo)" style={{...FIS,minHeight:"50px",resize:"vertical",fontSize:"12px"}}/>
              <button onClick={()=>setEditingId(null)} style={{alignSelf:"flex-start",padding:"4px 12px",background:"rgba(201,126,126,0.15)",border:"1px solid rgba(201,126,126,0.3)",borderRadius:"6px",color:"#c97e7e",cursor:"pointer",fontSize:"11px",fontFamily:"Georgia, serif"}}>✓ Fatto</button>
            </div>
          ) : (
            <>
              {ev.aiGenerated && <span style={{fontSize:"9px",color:cat.color,background:`${cat.color}15`,padding:"1px 6px",borderRadius:"4px",position:"absolute",top:"8px",right:"30px"}}>AI</span>}
              <button onClick={()=>deleteEvento(ev.id)} style={{position:"absolute",top:"6px",right:"6px",background:"none",border:"none",color:UI.textMuted,cursor:"pointer",fontSize:"12px"}} onMouseEnter={e=>e.currentTarget.style.color=UI.delete} onMouseLeave={e=>e.currentTarget.style.color=UI.textMuted}>✕</button>
              <div style={{display:"flex",alignItems:"center",gap:"8px",marginBottom:"6px",flexWrap:"wrap"}}>
                {ev.periodo && <span style={{fontSize:"10px",color:UI.textMuted}}>📅 {ev.periodo}</span>}
                <span style={{fontSize:"10px",color:cat.color,background:`${cat.color}15`,padding:"2px 8px",borderRadius:"12px"}}>{cat.label}</span>
              </div>
              <div style={{fontSize:"13px",color:UI.textPrimary,lineHeight:"1.6",cursor:"pointer"}} onClick={()=>setEditingId(ev.id)}>{ev.evento}</div>
              {ev.impatto && <div style={{fontSize:"12px",color:UI.textSecondary,marginTop:"6px",borderLeft:`2px solid ${cat.color}40`,paddingLeft:"8px",fontStyle:"italic"}}>💫 {ev.impatto}</div>}
            </>
          )}
        </div>
      );
    })}

    {showAdd ? (
      <div style={{background:"rgba(201,126,126,0.05)",border:"1px solid rgba(201,126,126,0.2)",borderRadius:"8px",padding:"12px",display:"flex",flexDirection:"column",gap:"6px"}}>
        <input value={newPeriodo} onChange={e=>setNewPeriodo(e.target.value)} placeholder="Periodo (es: Autunno 2019)" style={{...FIS,fontSize:"12px"}}/>
        <select value={newCategoria} onChange={e=>setNewCategoria(e.target.value)} style={{...FIS,fontSize:"12px",cursor:"pointer"}}>
          {FG_CATEGORIE.map(c=><option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
        <textarea value={newEvento} onChange={e=>setNewEvento(e.target.value)} placeholder="Descrivi l'evento..." style={{...FIS,minHeight:"70px",resize:"vertical",fontSize:"12px"}}/>
        <textarea value={newImpatto} onChange={e=>setNewImpatto(e.target.value)} placeholder="Impatto animico sul legame (facoltativo)" style={{...FIS,minHeight:"50px",resize:"vertical",fontSize:"12px"}}/>
        <div style={{display:"flex",gap:"6px"}}>
          <button onClick={addEvento} style={{flex:1,padding:"7px",background:"rgba(201,126,126,0.15)",border:"1px solid rgba(201,126,126,0.3)",borderRadius:"6px",color:"#c97e7e",cursor:"pointer",fontSize:"12px",fontFamily:"Georgia, serif"}}>✓ Aggiungi evento</button>
          <button onClick={()=>{setShowAdd(false);setNewPeriodo("");setNewCategoria("connessione");setNewEvento("");setNewImpatto("");}} style={{padding:"7px 12px",background:"rgba(255,255,255,0.04)",border:`1px solid ${UI.borderSubtle}`,borderRadius:"6px",color:UI.textMuted,cursor:"pointer",fontSize:"12px",fontFamily:"Georgia, serif"}}>Annulla</button>
        </div>
      </div>
    ) : (
      <button onClick={()=>setShowAdd(true)} style={{width:"100%",padding:"9px",background:"rgba(201,126,126,0.07)",border:"1px dashed rgba(201,126,126,0.3)",borderRadius:"6px",color:"#c97e7e",cursor:"pointer",fontSize:"12px",fontFamily:"Georgia, serif"}}>+ Aggiungi evento manualmente</button>
    )}
  </div>;
}

function CodexView({ codexItems, setCodexItems, apiKey, apiBase, models, chats, summaryCompact }) {
  const [showMenu,setShowMenu]=useState(false),[sel,setSel]=useState(null),[filter,setFilter]=useState("tutti");
  const [selected, setSelected] = useState(new Set()); // selezione multipla
  const [selectMode, setSelectMode] = useState(false);
  const isMobCodex = window.innerWidth <= 768;
  const add=(type)=>{const i={id:`cx${Date.now()}`,type,nome:"",createdAt:Date.now()};setCodexItems(p=>[...p,i]);setSel(i.id);setShowMenu(false);setFilter(type);};
  const upd=(id,data)=>setCodexItems(p=>p.map(i=>i.id===id?{...i,...data}:i));
  const del=(id)=>{if(!window.confirm("Eliminare?"))return;api.deleteCodexItem(id).catch(()=>{});setCodexItems(p=>p.filter(i=>i.id!==id));if(sel===id)setSel(null);};
  const delSelected=()=>{
    if(selected.size===0)return;
    if(!window.confirm(`Eliminare ${selected.size} elementi selezionati?`))return;
    selected.forEach(id => api.deleteCodexItem(id).catch(()=>{}));
    setCodexItems(p=>p.filter(i=>!selected.has(i.id)));
    if(selected.has(sel))setSel(null);
    setSelected(new Set());
    setSelectMode(false);
  };
  const toggleSelect=(id)=>{
    setSelected(p=>{const n=new Set(p);n.has(id)?n.delete(id):n.add(id);return n;});
  };
  const selectAll=()=>{
    if(selected.size===filtered.length){setSelected(new Set());}
    else{setSelected(new Set(filtered.map(i=>i.id)));}
  };
  const allP=codexItems.filter(i=>i.type==="personaggio");
  const filtered=filter==="tutti"?codexItems:codexItems.filter(i=>i.type===filter);
  const cur=codexItems.find(i=>i.id===sel), ti=cur?CODEX_TYPES.find(t=>t.id===cur.type):null;
  const getT=(item)=>{if(item.type==="relazione"){const a=item.personaggioA==="__custom__"?item.personaggioACustom:item.personaggioA,b=item.personaggioB==="__custom__"?item.personaggioBCustom:item.personaggioB;if(a&&b)return`${a} ↔ ${b}`;}if(item.type==="fiammegemelle")return item.nome||"Cronologia FG";return item.nome||(CODEX_TYPES.find(t=>t.id===item.type)?.label||"Nuovo");};
  return <div style={{flex:1,display:"flex",overflow:"hidden"}}>
    {/* Lista -- nascosta su mobile se elemento selezionato */}
    {(!isMobCodex || !sel) && (
    <div style={{width:isMobCodex?"100%":"230px",flexShrink:0,borderRight:isMobCodex?"none":`1px solid ${UI.borderSubtle}`,background:UI.bgSidebar,display:"flex",flexDirection:"column",overflow:"hidden"}}>
      <div style={{padding:"8px",overflowX:"auto"}}><div style={{display:"flex",gap:"3px",flexWrap:"wrap"}}><FilterBtn label="Tutti" active={filter==="tutti"} color="#c9a96e" onClick={()=>setFilter("tutti")}/>{CODEX_TYPES.map(t=><FilterBtn key={t.id} label={t.label.split(" ").slice(1).join(" ")} active={filter===t.id} color={t.color} onClick={()=>setFilter(t.id)}/>)}</div></div>
      <div style={{height:"1px",background:UI.borderSubtle}}/>

      {/* Barra selezione multipla */}
      <div style={{padding:"4px 8px",display:"flex",alignItems:"center",gap:"4px",borderBottom:`1px solid ${UI.borderSubtle}`}}>
        {!selectMode ? (
          <button onClick={()=>setSelectMode(true)} style={{background:"none",border:`1px solid ${UI.borderSubtle}`,borderRadius:"5px",color:UI.textMuted,cursor:"pointer",fontSize:"10px",padding:"3px 8px",fontFamily:"Georgia, serif",flex:1}}>
            ☑ Selezione multipla
          </button>
        ) : (
          <>
            <button onClick={selectAll} style={{background:"none",border:`1px solid ${UI.borderSubtle}`,borderRadius:"5px",color:UI.textSecondary,cursor:"pointer",fontSize:"10px",padding:"3px 6px",fontFamily:"Georgia, serif"}}>
              {selected.size===filtered.length?"Deseleziona tutti":"Seleziona tutti"}
            </button>
            {selected.size > 0 && (
              <button onClick={delSelected} style={{background:"rgba(224,120,120,0.15)",border:"1px solid rgba(224,120,120,0.35)",borderRadius:"5px",color:UI.delete,cursor:"pointer",fontSize:"10px",padding:"3px 8px",fontFamily:"Georgia, serif"}}>
                🗑️ Elimina ({selected.size})
              </button>
            )}
            <button onClick={()=>{setSelectMode(false);setSelected(new Set());}} style={{background:"none",border:"none",color:UI.textMuted,cursor:"pointer",fontSize:"11px",padding:"3px"}}>✕</button>
          </>
        )}
      </div>

      <div style={{flex:1,overflowY:"auto",padding:"4px 8px"}}>
        {filtered.length===0&&<p style={{color:UI.textMuted,fontSize:"12px",textAlign:"center",marginTop:"20px"}}>Usa + per aggiungere.</p>}
        {filtered.map(item=>{const t=CODEX_TYPES.find(t=>t.id===item.type);return<div key={item.id} onClick={()=>selectMode?toggleSelect(item.id):setSel(item.id)} style={{display:"flex",alignItems:"center",gap:"6px",padding:"7px 8px",borderRadius:"6px",cursor:"pointer",background:selectMode&&selected.has(item.id)?"rgba(224,120,120,0.12)":sel===item.id?`${t?.color}18`:"transparent",border:selectMode&&selected.has(item.id)?"1px solid rgba(224,120,120,0.35)":sel===item.id?`1px solid ${t?.color}30`:"1px solid transparent",marginBottom:"2px",opacity:item.futura?0.5:1}}>
          {selectMode
            ? <input type="checkbox" checked={selected.has(item.id)} onChange={()=>toggleSelect(item.id)} onClick={e=>e.stopPropagation()} style={{accentColor:"#e07878",width:"14px",height:"14px",flexShrink:0}}/>
            : <span style={{fontSize:"13px"}}>{item.futura?"🔒":t?.label.split(" ")[0]}</span>
          }
          <div style={{flex:1,overflow:"hidden"}}><div style={{fontSize:"12px",color:sel===item.id?t?.color:UI.textSecondary,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{getT(item)}{item.futura&&<span style={{fontSize:"9px",color:UI.textFaint,marginLeft:"4px"}}>futuro</span>}</div><div style={{fontSize:"10px",color:UI.textMuted}}>{t?.label.split(" ").slice(1).join(" ")}</div></div>
          {!selectMode && <button onClick={e=>{e.stopPropagation();del(item.id);}} style={{background:"none",border:"none",color:UI.textMuted,cursor:"pointer",fontSize:"11px",padding:"2px"}} onMouseEnter={e=>e.currentTarget.style.color=UI.delete} onMouseLeave={e=>e.currentTarget.style.color=UI.textMuted}>✕</button>}
          {item.disattivata && <span style={{fontSize:"9px",color:"#e07878",marginLeft:"2px"}}>⏸</span>}
          {item.assoluta && <span style={{fontSize:"9px",color:"#e07878",marginLeft:"2px"}}>⚠️</span>}
        </div>;})}
      </div>
      <div style={{padding:"8px",position:"relative"}}>
        <button onClick={()=>setShowMenu(s=>!s)} style={{width:"100%",padding:"9px",background:"rgba(201,169,110,0.12)",border:"1px solid rgba(201,169,110,0.35)",borderRadius:"8px",color:"#c9a96e",cursor:"pointer",fontSize:"14px",fontFamily:"Georgia, serif",display:"flex",alignItems:"center",justifyContent:"center",gap:"6px"}}><span style={{fontSize:"18px",lineHeight:1}}>+</span> Aggiungi</button>
        {showMenu&&<div style={{position:"absolute",bottom:"100%",left:"8px",right:"8px",background:"#1e1c26",border:"1px solid rgba(201,169,110,0.3)",borderRadius:"8px",padding:"6px",zIndex:100,boxShadow:"0 -8px 24px rgba(0,0,0,0.6)"}}>{CODEX_TYPES.map(t=><button key={t.id} onClick={()=>add(t.id)} style={{width:"100%",padding:"9px 12px",background:"transparent",border:"none",borderRadius:"6px",color:t.color,cursor:"pointer",fontSize:"13px",fontFamily:"Georgia, serif",textAlign:"left"}} onMouseEnter={e=>e.currentTarget.style.background=`${t.color}12`} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>{t.label}</button>)}</div>}
      </div>
    </div>
    )}
    {/* Dettaglio -- fullscreen su mobile */}
    {(!isMobCodex || sel) && (
    <div style={{flex:1,overflowY:"auto",padding:isMobCodex?"12px 16px":"18px 22px"}}>
      {/* Freccia indietro su mobile */}
      {isMobCodex && sel && (
        <button onClick={()=>setSel(null)} style={{background:"none",border:"none",color:"#c9a96e",cursor:"pointer",fontSize:"16px",padding:"0 0 12px",fontFamily:"Georgia, serif",display:"flex",alignItems:"center",gap:"6px"}}>← Torna al Codex</button>
      )}
      {!cur?<div style={{textAlign:"center",marginTop:"60px"}}><div style={{fontSize:"40px",opacity:0.2,marginBottom:"12px"}}>📚</div><p style={{fontSize:"14px",color:UI.textMuted}}>Seleziona un elemento o usa + per aggiungerne uno</p></div>:
      <div>
        <div style={{display:"flex",alignItems:"center",gap:"10px",marginBottom:"18px",paddingBottom:"14px",borderBottom:`1px solid ${ti?.color}25`}}><span style={{fontSize:"20px"}}>{ti?.label.split(" ")[0]}</span><h3 style={{color:ti?.color,fontSize:"15px",fontWeight:"normal",margin:0,flex:1}}>{ti?.label.split(" ").slice(1).join(" ")}</h3><button onClick={()=>del(cur.id)} style={{background:"rgba(224,120,120,0.12)",border:"1px solid rgba(224,120,120,0.25)",borderRadius:"6px",color:UI.delete,cursor:"pointer",fontSize:"12px",padding:"5px 12px",fontFamily:"Georgia, serif"}}>🗑️ Elimina</button></div>
        {cur.type==="personaggio"&&<PersonaggioForm item={cur} onChange={d=>upd(cur.id,d)} apiKey={apiKey} apiBase={apiBase} models={models} chats={chats} summaryCompact={summaryCompact}/>}
        {cur.type==="fiammegemelle"&&<FiammeGemelleForm item={cur} onChange={d=>upd(cur.id,d)} apiKey={apiKey} apiBase={apiBase} chats={chats} summaryCompact={summaryCompact}/>}
        {cur.type==="luogo"&&<LuogoForm item={cur} onChange={d=>upd(cur.id,d)}/>}
        {cur.type==="relazione"&&<RelazioneForm item={cur} onChange={d=>upd(cur.id,d)} allPersonaggi={allP}/>}
        {cur.type==="lore"&&<LoreForm item={cur} onChange={d=>upd(cur.id,d)}/>}
        {cur.type==="regola"&&<RegolaForm item={cur} onChange={d=>upd(cur.id,d)}/>}
        {cur.type==="altro"&&<AltroForm item={cur} onChange={d=>upd(cur.id,d)}/>}
        <p style={{color:UI.textFaint,fontSize:"11px",textAlign:"center",marginTop:"18px"}}>Salvato automaticamente</p>
      </div>}
    </div>
    )}
  </div>;
}

// ============================================================
// MESSAGGI VIEW -- WhatsApp-style chat between characters
// ============================================================
function MessaggiView({ codexItems, apiKey, apiBase }) {
  const [msgChats, setMsgChats] = useState(() => load("s_msg_chats", []));
  const [activeMsgChat, setActiveMsgChat] = useState(null);
  const [showNewChat, setShowNewChat] = useState(false);
  const [newChatName, setNewChatName] = useState("");
  const [selectedParticipants, setSelectedParticipants] = useState([]);
  const [isSpicy, setIsSpicy] = useState(false);
  const [msgInput, setMsgInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [typingChar, setTypingChar] = useState(null);
  const [atSuggestions, setAtSuggestions] = useState([]);
  const [atQuery, setAtQuery] = useState("");
  const [showAddParticipant, setShowAddParticipant] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => { save("s_msg_chats", msgChats); }, [msgChats]);
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgChats, activeMsgChat]);

  // Carica msgChats dal backend all'avvio
  useEffect(() => {
    const loadMsgChats = async () => {
      try {
        const res = await fetch('/api/msgchats');
        if (!res.ok) return;
        const data = await res.json();
        if (data?.length > 0) { setMsgChats(data); save("s_msg_chats", data); }
        else {
          // Sincronizza localStorage → backend
          const local = load("s_msg_chats", []);
          for (const chat of local) {
            await fetch('/api/msgchats', { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify(chat) }).catch(()=>{});
          }
        }
      } catch(e) {}
    };
    loadMsgChats();
  }, []);

  // Sync msgChats → backend quando cambiano
  useEffect(() => {
    const timer = setTimeout(() => {
      msgChats.forEach(chat => {
        fetch('/api/msgchats', { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify(chat) }).catch(()=>{});
      });
    }, 2000);
    return () => clearTimeout(timer);
  }, [msgChats]);

  const personaggi = codexItems.filter(i => i.type === "personaggio");
  const currentChat = msgChats.find(c => c.id === activeMsgChat);

  const createChat = () => {
    if (!newChatName.trim() || selectedParticipants.length === 0) return;
    const newChat = { id: `mc${Date.now()}`, name: newChatName.trim(), participants: selectedParticipants, isSpicy, messages: [], createdAt: Date.now() };
    setMsgChats(prev => [...prev, newChat]);
    setActiveMsgChat(newChat.id);
    setShowNewChat(false); setNewChatName(""); setSelectedParticipants([]); setIsSpicy(false);
  };

  const getPersonaggio = (nome) => personaggi.find(p => p.nome === nome);

  const buildMsgSystemPrompt = (chat) => {
    const isNadiaLiam = chat.participants.length === 1 && chat.participants[0] !== "Nadia" &&
      codexItems.some(i => (i.protagonista || i.fiammaGemella) && i.nome === chat.participants[0]);

    const participants = chat.participants.map(nome => {
      const p = getPersonaggio(nome);
      if (!p) return `${nome}: personaggio della storia`;
      const parts = [`${p.nome} (${p.eta||"?"} anni${p.nazionalita?", "+p.nazionalita:""})`];
      if (p.aspetto) parts.push(`Aspetto: ${p.aspetto}`);
      if (p.carattere) parts.push(`Carattere: ${p.carattere}`);
      if (p.professione) parts.push(`Professione: ${p.professione}`);
      if (p.paure) parts.push(`Paure/insicurezze: ${p.paure}`);
      if (p.coseDaSapere) parts.push(`Cose importanti: ${p.coseDaSapere}`);
      if (p.altreCoseImportanti) parts.push(`Altro: ${p.altreCoseImportanti}`);
      if (p.carattere) {
        parts.push(`Stile messaggi: scrivi esattamente come questo personaggio scriverebbe su WhatsApp -- tono, lessico, lunghezza messaggi, uso di emoji, abbreviazioni -- tutto coerente col suo carattere.`);
      }
      return parts.join(". ");
    }).join("\n\n");

    // Scheda Nadia -- sempre presente, livello di dettaglio basato sul tipo di relazione
    let nadiaScheda = "";
    const nadia = codexItems.find(i => i.type === "personaggio" && i.protagonista && i.nome && i.nome.toLowerCase().includes("nadia"));
    if (nadia) {
      const np = [`Nadia Moretti (${nadia.eta||"?"} anni)`];
      if (nadia.aspetto) np.push(`Aspetto: ${nadia.aspetto}`);
      if (nadia.carattere) np.push(`Carattere: ${nadia.carattere}`);

      // Controlla se qualche partecipante è FG/protagonista (Liam) -- vede tutto
      const hasLiam = chat.participants.some(nome => {
        const p = getPersonaggio(nome);
        return p && (p.fiammaGemella || p.protagonista);
      });

      if (hasLiam) {
        // Liam conosce tutti i segreti di Nadia
        if (nadia.coseDaSapere) np.push(`Cose importanti che Liam sa: ${nadia.coseDaSapere}`);
        if (nadia.altreCoseImportanti) np.push(`Altro: ${nadia.altreCoseImportanti}`);
      } else {
        // Amici/conoscenti -- conoscono solo aspetto e carattere, non i segreti
        np.push(`Nota: gli altri personaggi conoscono Nadia superficialmente -- sanno com'è, come si comporta, il suo carattere -- ma NON conoscono i suoi traumi, segreti o storia profonda a meno che non sia già emerso in scena.`);
      }

      nadiaScheda = `\n\nNADIA MORETTI (l'utente -- NON simularla mai, usala solo come riferimento per come gli altri la conoscono):\n${np.join("\n")}`;
    }

    return `Sei un simulatore di conversazioni WhatsApp tra personaggi fiction.
Partecipanti alla chat "${chat.name}":
${participants}${nadiaScheda}

⛔ REGOLA ASSOLUTA #0 -- LINGUA (MAI VIOLARE, NESSUNA ECCEZIONE):
TUTTI i personaggi scrivono ESCLUSIVAMENTE in italiano, sempre, senza eccezioni. Liam è inglese? Scrive in italiano. Andy è inglese? Scrive in italiano. Louis, Harry, Niall, Danny, qualunque personaggio britannico o di altra nazionalità -- SEMPRE e SOLO italiano. Non è una scelta stilistica: è una regola tecnica dell'app. MAI scrivere anche una sola parola in inglese o altra lingua. ITALIANO. SEMPRE.

⛔ REGOLA ASSOLUTA #1 -- MAI VIOLARE:
NADIA È L'UTENTE REALE. Non è un personaggio della simulazione. Non scrivere MAI messaggi di Nadia o Nadia Moretti. Nadia scrive i suoi messaggi con le sue mani. TU generi SOLO le risposte degli altri personaggi. Se scrivi "Nadia:" stai violando la regola più importante di questa sessione.

REGOLE ASSOLUTE -- NON DEROGABILI:
- Rispondi SEMPRE in italiano. TUTTI i personaggi scrivono ESCLUSIVAMENTE in italiano, anche se sono inglesi, americani o di altra nazionalità. Nessuna eccezione. MAI scrivere in inglese o altra lingua.
- Scrivi SOLO i messaggi dei personaggi non-Nadia -- ZERO commenti, ZERO spiegazioni, ZERO domande all'utente, ZERO metanarrazione.
- NON scrivere MAI cose come "Vuoi che aggiunga qualcuno?", "Posso introdurre un altro personaggio?", "Cosa vuoi che faccia?", ecc.
- NON uscire MAI dal personaggio. Sei invisibile -- esistono solo i personaggi e i loro messaggi.
- Formato risposta OBBLIGATORIO: sempre e solo "NOME: messaggio" su ogni riga. Il NOME deve essere ESATTAMENTE il nome del personaggio come definito sopra -- NON inventare varianti, nickname, handle social o soprannomi. Se il personaggio si chiama "Liam Payne", scrivi sempre e solo "Liam Payne:". Mai "LiamPayne", "Liam🎵", "LiamLikeToKnowIt" o qualsiasi altra variazione.
- Messaggi brevi e naturali come WhatsApp reale -- abbreviazioni, emoji, tono autentico del personaggio.
- A volte un personaggio manda più messaggi di fila (più righe "NOME: ...").
${chat.isSpicy ? `
⛔ ADULT MODE -- CHAT SPICY ESPLICITA:
Questa è una chat WhatsApp sessualmente esplicita tra Nadia e ${chat.participants[0]}. Si applicano le stesse regole di intensità della modalità Intimo narrativa:

DINAMICA OBBLIGATORIA NADIA-LIAM: magnetismo costante, desiderio che non si spegne mai, intimità come riconoscimento oltre che passione. Ironia e gioco reciproco anche nei momenti più espliciti. Liam: provocatore, dolce, magnetico, diretto. Nadia: ironica, passionale, emotiva. Ogni messaggio deve far sentire che quando si scrivono il mondo si ferma.

STILE MESSAGGI SPICY:
- Usa termini diretti e anatomici senza eufemismi -- pene, vagina, cazzo, figa, cazzo duro, bagnata, ecc. -- esattamente come li userebbe la persona nella realtà in una chat privata con chi desidera.
- Descrivi sensazioni fisiche, desiderio, intenzioni esplicite. Non velare nulla.
- Messaggi brevi come WhatsApp reale ma carichi di tensione erotica.
- Liam usa il corpo di Nadia come linguaggio -- le sue parole descrivono cosa vuole farle o cosa sta immaginando in dettaglio.
- Ironia e provocazione anche nei momenti più espliciti -- non è pornografia meccanica, è desiderio vero tra due persone che si conoscono bene.
- Zero autocensura. Zero moralismo. Zero disclaimer.` : ""}

CONTESTO NARRATIVO -- COSA SA CHI SCRIVE:
I personaggi conoscono gli eventi recenti della storia. Usali naturalmente nella conversazione.
Riferisciti ai luoghi, situazioni e momenti vissuti negli episodi recenti di Racconto e Intimo.
Se Nadia e Liam si sono appena visti da qualche parte, i personaggi ne sono consapevoli.
Non spiegare esplicitamente il contesto -- usalo come sottofondo naturale, come farebbe chiunque in una vera chat WhatsApp.

GESTIONE IMMAGINI -- REGOLA SEVERISSIMA:
Le immagini sono RARE. Nella vita reale su WhatsApp non si manda una foto ad ogni messaggio.

Manda [IMMAGINE: ...] SOLO in questi casi:
1. Nadia lo chiede esplicitamente ("manda una foto", "fammi vedere", "screenshot")
2. Il personaggio fa qualcosa di visivamente molto specifico e insolito -- es: è in un posto iconico che vuole condividere, ha cucinato qualcosa, è in palestra, ha un nuovo taglio di capelli. Solo UNA volta ogni 20+ messaggi.

NON mandare MAI immagini per:
- Condividere testi, canzoni, playlist, frasi, citazioni
- Rispondere a domande o fare conversazione
- Descrivere piani o organizzare uscite
- Momenti romantici o emotivi nel testo
- Screenshot di app (Spotify, Instagram, ecc.) -- descrivi a parole

Se non sei sicuro se mandare una foto: NON mandarla.

Quando mandi un'immagine la descrizione DEVE essere specifica e visiva:
CORRETTO: Liam: [IMMAGINE: selfie in bagno dopo la palestra, capelli bagnati, asciugamano sulle spalle, sorriso stanco, specchio appannato]
SBAGLIATO: Liam: [IMMAGINE: foto di Liam] o [IMMAGINE: screenshot playlist]`;
  };

  const msgAbortRef = useRef(null);
  const stopMsgGeneration = () => {
    if (msgAbortRef.current) { msgAbortRef.current.abort(); msgAbortRef.current = null; }
    setTypingChar(null); setLoading(false);
  };

  const deleteMsgChat = (chatId) => {
    setMsgChats(prev => prev.filter(c => c.id !== chatId));
    if (activeMsgChat === chatId) setActiveMsgChat(null);
    fetch(`/api/msgchats/${chatId}`, { method: 'DELETE' }).catch(() => {});
  };

  const parseAndAddMessages = (rawResponse, fallbackSender) => {
    const joined = rawResponse.replace(/\[IMMAGINE:\s*\n+/gi,"[IMMAGINE: ").replace(/\n+\]/g,"]");
    const cleanLine = (l) => l.replace(/typing\.+[^"]*?(?="|\n|$)/gi,"").replace(/\*[^*]*\*/g,"").replace(/\([^)]*\)/g,"").replace(/poi cancella.*$/gi,"").trim();
    const lines = joined.split("\n").filter(l=>l.trim());
    const msgs = [];
    lines.forEach(line => {
      const m = line.match(/^([^:\[]+):\s*(.+)$/s);
      if (m) {
        const sender = m[1].trim(); const content = cleanLine(m[2].trim());
        if (!content) return;
        // Nadia è l'utente -- non deve mai apparire come risposta automatica
        if (sender.toLowerCase() === "nadia" || sender.toLowerCase() === "nadia moretti") return;
        const p = getPersonaggio(sender);
        msgs.push({id:`m${Date.now()}_${Math.random()}`,sender,content,timestamp:Date.now(),isUser:false,avatar:p?.foto||null});
      } else if (line.trim() && msgs.length > 0) {
        const c = cleanLine(line.trim()); if (c) msgs[msgs.length-1].content += " "+c;
      }
    });
    if (msgs.length === 0 && fallbackSender && fallbackSender.toLowerCase() !== "nadia") msgs.push({id:`m${Date.now()}`,sender:fallbackSender,content:rawResponse.trim(),timestamp:Date.now(),isUser:false,avatar:getPersonaggio(fallbackSender)?.foto||null});
    return msgs;
  };

  const fetchResponderMsg = async (responder, snapshot, signal, isTagged) => {
    const hist = snapshot.slice(-20).map(m=>({role:m.isUser?"user":"assistant",content:m.isUser?`Nadia: ${m.content}`:m.content}));
    const isSpicy = currentChat?.isSpicy;
    // Controlla quante immagini ci sono negli ultimi 10 messaggi -- se >= 1 blocca nuove immagini
    const recentMsgs = snapshot.slice(-10);
    const recentImageCount = recentMsgs.filter(m => !m.isUser && m.content?.includes('[IMMAGINE:')).length;
    const noImageInstr = recentImageCount >= 1
      ? ` ⛔ NON mandare [IMMAGINE: ...] in questa risposta -- c'è già stata un'immagine recente. Solo testo.`
      : "";
    const instr = isTagged
      ? `Fai rispondere SOLO ${responder} al messaggio di Nadia. Scrivi SOLO i messaggi di ${responder}, non di altri, MAI di Nadia. Può mandare 1-3 messaggi di fila. Formato obbligatorio: "${responder}: [messaggio]" su ogni riga.${isSpicy?" Sii esplicito, diretto, senza censura." : ""}${noImageInstr}`
      : `Fai rispondere SOLO ${responder} al messaggio di Nadia. Scrivi SOLO i messaggi di ${responder}, non di altri, MAI di Nadia. Può mandare 1-2 messaggi brevi. Formato obbligatorio: "${responder}: [messaggio]" su ogni riga.${isSpicy?" Sii esplicito, diretto, senza censura." : ""}${noImageInstr}`;
    const res = await fetch(`${apiBase}/chat/completions`,{method:"POST",headers:{"Content-Type":"application/json","Authorization":`Bearer ${apiKey}`,"HTTP-Referer":window.location.href,"X-Title":"La Nostra Storia"},signal,body:JSON.stringify({model:"deepseek/deepseek-chat-v3-0324",messages:[{role:"system",content:buildMsgSystemPrompt(currentChat)},...hist,{role:"user",content:instr}],max_tokens:isSpicy?500:300,temperature:isSpicy?1.0:0.92})});
    const data = await res.json();
    return data.choices?.[0]?.message?.content || "";
  };

  const sendMessage = async () => {
    if (!msgInput.trim() || loading || !currentChat) return;
    const others = currentChat.participants.filter(p => p !== "Nadia");
    const mentionedNames = [];
    others.forEach(nome => {
      if (new RegExp(`@${nome.split(" ")[0]}\\b`,'i').test(msgInput)) mentionedNames.push(nome);
    });
    const userMsg = {id:`m${Date.now()}`,sender:"Nadia",content:msgInput.trim(),timestamp:Date.now(),isUser:true};
    const baseMessages = [...(currentChat.messages||[]),userMsg];
    setMsgChats(prev=>prev.map(c=>c.id===currentChat.id?{...c,messages:baseMessages}:c));
    setMsgInput(""); setAtSuggestions([]); setLoading(true);
    msgAbortRef.current = new AbortController();
    try {
      if (mentionedNames.length > 0) {
        for (const responder of mentionedNames) {
          setTypingChar(responder);
          const raw = await fetchResponderMsg(responder, baseMessages, msgAbortRef.current?.signal, true);
          const newMsgs = parseAndAddMessages(raw, responder);
          for (let i = 0; i < newMsgs.length; i++) {
            if (i > 0) {
              setTypingChar(responder);
              // Delay proporzionale alla lunghezza del messaggio (come tastiera reale)
              const delay = 600 + Math.min(newMsgs[i].content.length * 30, 3000);
              await new Promise(r=>setTimeout(r,delay));
            }
            setMsgChats(prev=>prev.map(c=>c.id===currentChat.id?{...c,messages:[...c.messages,newMsgs[i]]}:c));
            setTypingChar(null);
            if (i < newMsgs.length - 1) await new Promise(r=>setTimeout(r,200));
          }
        }
        const nonMentioned = others.filter(o=>!mentionedNames.includes(o));
        if (nonMentioned.length > 0 && Math.random() < 0.4) {
          const extra = nonMentioned[Math.floor(Math.random()*nonMentioned.length)];
          await new Promise(r=>setTimeout(r,800+Math.random()*1200));
          setTypingChar(extra);
          await new Promise(r=>setTimeout(r,400+Math.random()*500));
          const raw = await fetchResponderMsg(extra, baseMessages, msgAbortRef.current?.signal, false);
          const newMsgs = parseAndAddMessages(raw, extra);
          for (let i = 0; i < newMsgs.length; i++) {
            if (i > 0) {
              setTypingChar(extra);
              const delay = 600 + Math.min(newMsgs[i].content.length * 30, 3000);
              await new Promise(r=>setTimeout(r,delay));
            }
            setMsgChats(prev=>prev.map(c=>c.id===currentChat.id?{...c,messages:[...c.messages,newMsgs[i]]}:c));
            setTypingChar(null);
            if (i < newMsgs.length - 1) await new Promise(r=>setTimeout(r,200));
          }
        }
      } else {
        const replyCount = others.length<=1?1:Math.random()<0.4?1:Math.random()<0.7?2:Math.min(3,others.length);
        const shuffled = [...others].sort(()=>Math.random()-0.5).slice(0,replyCount);
        let snapshot = [...baseMessages];
        for (let i=0; i<shuffled.length; i++) {
          if (i>0) await new Promise(r=>setTimeout(r,600+Math.random()*800));
          setTypingChar(shuffled[i]);
          await new Promise(r=>setTimeout(r,400+Math.random()*500));
          const raw = await fetchResponderMsg(shuffled[i], snapshot, msgAbortRef.current?.signal, false);
          const newMsgs = parseAndAddMessages(raw, shuffled[i]);
          if (newMsgs.length > 0) {
            for (let j = 0; j < newMsgs.length; j++) {
              if (j > 0) {
                setTypingChar(shuffled[i]);
                const delay = 600 + Math.min(newMsgs[j].content.length * 30, 3000);
                await new Promise(r=>setTimeout(r,delay));
              }
              snapshot = [...snapshot, newMsgs[j]];
              setMsgChats(prev=>prev.map(c=>c.id===currentChat.id?{...c,messages:[...c.messages,newMsgs[j]]}:c));
              setTypingChar(null);
              if (j < newMsgs.length - 1) await new Promise(r=>setTimeout(r,200));
            }
          }
        }
      }
    } catch(err) { if (err.name!=='AbortError') console.error(err); }
    setTypingChar(null); setLoading(false);
  };

  const getAvatarColor = (nome) => {
    const colors = ["#c9a96e","#7eb8c9","#c97eb8","#c97e7e","#9b8ec9","#8ec9a0","#c9c07e"];
    let hash = 0; for (let i = 0; i < nome.length; i++) hash = nome.charCodeAt(i) + ((hash << 5) - hash);
    return colors[Math.abs(hash) % colors.length];
  };

  // Genera immagine reale con OpenRouter
  const generateRealImage = async (msgId, description, senderName) => {
    setMsgChats(prev => prev.map(c => c.id === activeMsgChat ? { ...c, messages: c.messages.map(m => m.id === msgId ? { ...m, generatingImage: description } : m) } : c));
    const imageModel = load("s_image_model", "sourceful/riverflow-v2-fast");
    // Recupera le info del personaggio mittente dal Codex
    const senderInfo = senderName ? getPersonaggio(senderName) : null;
    const result = await generateImage(description, apiKey, apiBase, imageModel, senderInfo);
    if (!result.success) {
      console.error("Image generation error:", result.error);
    }
    setMsgChats(prev => prev.map(c => c.id === activeMsgChat ? { ...c, messages: c.messages.map(m => m.id === msgId ? { ...m, generatingImage: undefined, generatedImages: { ...(m.generatedImages||{}), [description]: result.success ? result.url : `error: ${result.error}` } } : m) } : c));
  };

  // Messaggio automatico spontaneo
  const triggerAutoMessage = async () => {
    if (!currentChat || loading) return;
    const others = currentChat.participants.filter(p => p !== "Nadia");
    if (others.length === 0) return;
    const sender = others[Math.floor(Math.random() * others.length)];
    setTypingChar(sender);
    try {
      const recentHistory = (currentChat.messages||[]).slice(-8).map(m => ({ role: m.isUser?"user":"assistant", content: m.isUser?`Nadia: ${m.content}`:m.content }));
      const response = await fetch(`${apiBase}/chat/completions`, {
        method:"POST", headers:{"Content-Type":"application/json","Authorization":`Bearer ${apiKey}`,"HTTP-Referer":window.location.href,"X-Title":"La Nostra Storia"},
        body: JSON.stringify({ model:"deepseek/deepseek-chat-v3-0324", messages:[{role:"system",content:buildMsgSystemPrompt(currentChat)},...recentHistory,{role:"user",content:`Scrivi UN messaggio spontaneo di ${sender} a Nadia -- un pensiero casuale, non risposta all'ultimo. SOLO ${sender} scrive, MAI Nadia. Formato obbligatorio: "${sender}: [messaggio]"`}], max_tokens:200, temperature:1.0 })
      });
      const data = await response.json();
      const raw = data.choices?.[0]?.message?.content||"";
      const cleaned = raw
        .replace(/typing\.+[^"]*?(?="|\n|$)/gi, "")
        .replace(/\*[^*]*\*/g, "")
        .replace(/\([^)]*\)/g, "")
        .replace(/poi cancella.*$/gi, "")
        .trim();
      const match = cleaned.match(/^([^:\[]+):\s*(.+)$/s);
      if (match) {
        const content = match[2].trim();
        if (content) {
          const p = getPersonaggio(sender);
          setMsgChats(prev => prev.map(c => c.id === currentChat.id ? { ...c, messages:[...c.messages,{id:`m${Date.now()}`,sender,content,timestamp:Date.now(),isUser:false,avatar:p?.foto||null,isAutomatic:true}] } : c));
        }
      }
    } catch(e) { console.error(e); }
    setTypingChar(null);
  };

  // Renderizza contenuto messaggio -- gestisce tag [IMMAGINE: descrizione]
  const renderMsgContent = (content, isUser, color, msgId, msg) => {
    if (!content) return null;
    // Regex permissivo -- cattura [IMMAGINE: ...] in varie forme
    const imageRegex = /\[IMMAGINE:\s*([^\]]+)\]|\[immagine:\s*([^\]]+)\]/gi;
    const parts = [];
    let lastIndex = 0;
    let match;
    const bubbleStyle = (iu) => ({ padding:"8px 12px", borderRadius: iu?"16px 16px 4px 16px":"16px 16px 16px 4px", background: iu?"rgba(168,201,160,0.25)":"rgba(255,255,255,0.06)", border: iu?"1px solid rgba(168,201,160,0.3)":`1px solid ${UI.borderSubtle}`, fontSize:"14px", lineHeight:"1.6", color: iu?"#a8c9a0":UI.textPrimary, wordBreak:"break-word" });

    while ((match = imageRegex.exec(content)) !== null) {
      if (match.index > lastIndex) { const text = content.slice(lastIndex, match.index).trim(); if (text) parts.push(<div key={`t${lastIndex}`} style={bubbleStyle(isUser)}>{renderMarkdown(text)}</div>); }
      const description = (match[1] || match[2] || "").trim();
      const generatedUrl = msg?.generatedImages?.[description];
      const isGenerating = msg?.generatingImage === description;
      const isError = generatedUrl?.startsWith("error:");
      parts.push(
        <div key={`img${match.index}`} style={{ borderRadius:"12px", overflow:"hidden", border:`1px solid ${color}30`, maxWidth:"220px" }}>
          {generatedUrl && !isError ? (
            <div style={{position:"relative"}}>
              <img src={generatedUrl} alt="immagine" style={{ width:"220px", display:"block", borderRadius:"12px" }} />
              <a href={generatedUrl} download={`foto_${Date.now()}.png`}
                style={{position:"absolute",bottom:"6px",right:"6px",background:"rgba(0,0,0,0.6)",border:"1px solid rgba(255,255,255,0.2)",borderRadius:"6px",padding:"3px 8px",color:"white",fontSize:"10px",textDecoration:"none"}}>
                ⬇️
              </a>
            </div>
          ) : (
            <div style={{ background:`${color}15`, padding:"10px 12px", display:"flex", flexDirection:"column", gap:"6px" }}>
              <div style={{display:"flex",alignItems:"center",gap:"8px"}}>
                <span style={{ fontSize:"20px" }}>{isGenerating?"⏳":"📷"}</span>
                <span style={{fontSize:"11px",color:color,fontWeight:"bold"}}>{isGenerating ? "Generazione..." : "Foto"}</span>
              </div>
              {/* Mostra sempre la descrizione così sai cosa c'è prima di generare */}
              {description && !isGenerating && (
                <div style={{fontSize:"11px",color:UI.textSecondary,lineHeight:"1.4",fontStyle:"italic",borderLeft:`2px solid ${color}40`,paddingLeft:"8px"}}>
                  {description}
                </div>
              )}
              {isError
                ? <div style={{fontSize:"10px",color:"#e07878"}}>❌ {generatedUrl.replace("error: ","")}</div>
                : !isGenerating && (
                  <button onClick={()=>generateRealImage(msgId, description, msg?.sender)}
                    style={{fontSize:"11px",color:"#a8c9a0",background:"rgba(168,201,160,0.1)",border:"1px solid rgba(168,201,160,0.3)",borderRadius:"6px",padding:"5px 10px",cursor:"pointer",fontFamily:"Georgia, serif",alignSelf:"flex-start"}}>
                    🎨 Genera immagine
                  </button>
                )
              }
            </div>
          )}
        </div>
      );
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < content.length) { const text = content.slice(lastIndex).trim(); if (text) parts.push(<div key={`t${lastIndex}`} style={bubbleStyle(isUser)}>{renderMarkdown(text)}</div>); }
    if (parts.length === 0) return <div style={bubbleStyle(isUser)}>{renderMarkdown(content)}</div>;
    return parts;
  };

  const isMobMsg = window.innerWidth <= 768;

  return (
    <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>
      {/* Sidebar chat list -- fullscreen on mobile when no chat selected */}
      {(!isMobMsg || !activeMsgChat) && (
      <div style={{ width: isMobMsg ? "100%" : "210px", flexShrink: 0, borderRight: isMobMsg ? "none" : `1px solid ${UI.borderSubtle}`, background: UI.bgSidebar, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <div style={{ padding: "10px 8px 6px", borderBottom: `1px solid ${UI.borderSubtle}` }}>
          <p style={{ fontSize: "10px", color: UI.textMuted, letterSpacing: "2px", textTransform: "uppercase", margin: "0 0 8px" }}>Chat attive</p>
          <button onClick={() => setShowNewChat(true)} style={{ width: "100%", padding: "7px", background: "rgba(168,201,160,0.12)", border: "1px solid rgba(168,201,160,0.3)", borderRadius: "6px", color: "#a8c9a0", cursor: "pointer", fontSize: "12px", fontFamily: "Georgia, serif" }}>+ Nuova chat</button>
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: "6px" }}>
          {msgChats.length === 0 && <p style={{ color: UI.textMuted, fontSize: "11px", textAlign: "center", marginTop: "20px" }}>Nessuna chat ancora.</p>}
          {msgChats.map(chat => (
            <div key={chat.id} style={{ display: "flex", alignItems: "center", gap: "2px", marginBottom: "4px" }}>
              <button onClick={() => setActiveMsgChat(chat.id)} style={{ flex: 1, padding: "8px 10px", background: activeMsgChat === chat.id ? "rgba(168,201,160,0.15)" : "transparent", border: activeMsgChat === chat.id ? "1px solid rgba(168,201,160,0.3)" : "1px solid transparent", borderRadius: "8px", cursor: "pointer", textAlign: "left", display: "flex", alignItems: "center", gap: "8px" }}>
                <div style={{ width: "32px", height: "32px", borderRadius: "50%", background: "rgba(168,201,160,0.2)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "14px", flexShrink: 0 }}>
                  {chat.isSpicy ? "🌶️" : "💬"}
                </div>
                <div style={{ flex: 1, overflow: "hidden" }}>
                  <div style={{ fontSize: "12px", color: activeMsgChat === chat.id ? "#a8c9a0" : UI.textSecondary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{chat.name}</div>
                  <div style={{ fontSize: "10px", color: UI.textMuted }}>{chat.participants.slice(0, 3).join(", ")}{chat.participants.length > 3 ? "..." : ""}</div>
                </div>
              </button>
              <button onClick={() => deleteMsgChat(chat.id)} style={{ background: "none", border: "none", color: UI.textMuted, cursor: "pointer", fontSize: "13px", padding: "4px 6px", fontWeight: "bold", flexShrink: 0 }} onMouseEnter={e => e.currentTarget.style.color = UI.delete} onMouseLeave={e => e.currentTarget.style.color = UI.textMuted}>✕</button>
            </div>
          ))}
        </div>
      </div>
      )}

      {/* Chat area -- fullscreen on mobile when chat selected */}
      {(!isMobMsg || activeMsgChat) && (
        <div style={{flex:1, display:"flex", flexDirection:"column", overflow:"hidden"}}>
        {!currentChat ? (
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: "12px" }}>
          <div style={{ fontSize: "40px", opacity: 0.2 }}>💬</div>
          <p style={{ color: UI.textMuted, fontSize: "14px" }}>Seleziona una chat o creane una nuova</p>
        </div>
      ) : (
        <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          {/* Chat header */}
          <div style={{ padding: "10px 16px", borderBottom: `1px solid ${UI.borderSubtle}`, background: "rgba(0,0,0,0.2)", display: "flex", alignItems: "center", gap: "10px", position: "relative" }}>
            {/* Freccia indietro su mobile */}
            {isMobMsg && (
              <button onClick={() => setActiveMsgChat(null)} style={{ background: "none", border: "none", color: "#a8c9a0", cursor: "pointer", fontSize: "20px", padding: "0 4px", flexShrink: 0 }}>←</button>
            )}
            <div style={{ width: "36px", height: "36px", borderRadius: "50%", background: "rgba(168,201,160,0.2)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "16px", flexShrink: 0 }}>{currentChat.isSpicy ? "🌶️" : "💬"}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: "14px", color: "#a8c9a0" }}>{currentChat.name}</div>
              <div style={{ fontSize: "11px", color: UI.textMuted, display: "flex", alignItems: "center", gap: "4px", flexWrap: "wrap" }}>
                <span>{currentChat.participants.join(", ")}</span>
                {/* Tasto + per aggiungere partecipante */}
                <button onClick={() => setShowAddParticipant(s => !s)} title="Aggiungi partecipante" style={{ background: "rgba(168,201,160,0.15)", border: "1px solid rgba(168,201,160,0.3)", borderRadius: "50%", width: "16px", height: "16px", cursor: "pointer", fontSize: "10px", color: "#a8c9a0", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, lineHeight: 1, padding: 0 }}>+</button>
              </div>
            </div>
            {currentChat.isSpicy && <span style={{ fontSize: "10px", color: "#c97e7e", background: "rgba(201,126,126,0.1)", border: "1px solid rgba(201,126,126,0.2)", borderRadius: "4px", padding: "2px 8px", flexShrink: 0 }}>🌶️ Spicy</span>}
            {!isMobMsg && <button onClick={triggerAutoMessage} disabled={loading} title="Fai scrivere spontaneamente un personaggio" style={{ padding:"5px 10px", background:"rgba(168,201,160,0.1)", border:"1px solid rgba(168,201,160,0.25)", borderRadius:"6px", color:"#a8c9a0", cursor:"pointer", fontSize:"11px", fontFamily:"Georgia, serif", flexShrink: 0 }}>✨ Spontaneo</button>}

            {/* Dropdown aggiungi partecipante */}
            {showAddParticipant && (
              <div style={{ position: "absolute", top: "100%", left: "60px", background: "#1e1c26", border: `1px solid ${UI.border}`, borderRadius: "8px", padding: "8px", zIndex: 100, boxShadow: "0 8px 24px rgba(0,0,0,0.6)", minWidth: "200px" }}>
                <p style={{ fontSize: "11px", color: UI.textMuted, margin: "0 0 8px", letterSpacing: "1px" }}>Aggiungi alla chat:</p>
                {personaggi.filter(p => !currentChat.participants.includes(p.nome)).map(p => (
                  <button key={p.id} onClick={() => {
                    setMsgChats(prev => prev.map(c => c.id === currentChat.id ? { ...c, participants: [...c.participants, p.nome] } : c));
                    setShowAddParticipant(false);
                  }} style={{ width: "100%", padding: "7px 10px", background: "transparent", border: "none", borderRadius: "6px", color: UI.textSecondary, cursor: "pointer", fontSize: "12px", fontFamily: "Georgia, serif", textAlign: "left", display: "flex", alignItems: "center", gap: "6px" }}
                    onMouseEnter={e => e.currentTarget.style.background = "rgba(168,201,160,0.1)"}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                    {p.foto ? <img src={p.foto} alt="" style={{ width: "18px", height: "18px", borderRadius: "50%", objectFit: "cover" }} /> : <div style={{ width: "18px", height: "18px", borderRadius: "50%", background: `${getAvatarColor(p.nome)}30`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "9px", color: getAvatarColor(p.nome) }}>{p.nome[0]}</div>}
                    {p.nome}
                  </button>
                ))}
                {personaggi.filter(p => !currentChat.participants.includes(p.nome)).length === 0 && (
                  <p style={{ fontSize: "11px", color: UI.textFaint, margin: 0 }}>Tutti i personaggi sono già in chat.</p>
                )}
              </div>
            )}
          </div>

          {/* Messages */}
          <div style={{ flex: 1, overflowY: "auto", padding: "16px", display: "flex", flexDirection: "column", gap: "8px", background: "rgba(0,0,0,0.1)" }}>
            {(currentChat.messages || []).map((msg, msgIdx) => {
              const isUser = msg.isUser;
              const color = getAvatarColor(msg.sender);
              const msgId = msg.id || `legacy_${msgIdx}`;
              // Ensure msg has id for image generation
              if (!msg.id) msg.id = msgId;
              return (
                <div key={msgId} style={{ display: "flex", flexDirection: isUser ? "row-reverse" : "row", alignItems: "flex-end", gap: "8px" }}>
                  {/* Avatar */}
                  {!isUser && (
                    <div style={{ width: "30px", height: "30px", borderRadius: "50%", flexShrink: 0, overflow: "hidden", border: `2px solid ${color}40` }}>
                      {msg.avatar ? <img src={msg.avatar} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <div style={{ width: "100%", height: "100%", background: `${color}30`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "11px", color }}>{msg.sender[0]}</div>}
                    </div>
                  )}
                  <div style={{ maxWidth: "70%", display: "flex", flexDirection: "column", gap: "2px", alignItems: isUser ? "flex-end" : "flex-start" }}>
                    {!isUser && <span style={{ fontSize: "10px", color, paddingLeft: "4px" }}>{msg.sender}{msg.isAutomatic && <span style={{ fontSize:"9px", color:UI.textFaint }}> ✨auto</span>}</span>}
                    {/* Renderizza il contenuto -- gestisce tag [IMMAGINE: ...] */}
                    <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                      {renderMsgContent(msg.content, isUser, color, msgId, msg)}
                    </div>
                    <span style={{ fontSize: "10px", color: UI.textFaint, paddingLeft: "4px", paddingRight: "4px" }}>{formatTime(msg.timestamp)}</span>
                  </div>
                </div>
              );
            })}

            {/* Typing indicator */}
            {typingChar && (
              <div style={{ display: "flex", alignItems: "flex-end", gap: "8px" }}>
                <div style={{ width: "30px", height: "30px", borderRadius: "50%", background: `${getAvatarColor(typingChar)}30`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "11px", color: getAvatarColor(typingChar), flexShrink: 0 }}>{typingChar[0]}</div>
                <div style={{ padding: "10px 14px", borderRadius: "16px 16px 16px 4px", background: "rgba(255,255,255,0.06)", border: `1px solid ${UI.borderSubtle}`, display: "flex", gap: "4px", alignItems: "center" }}>
                  {[0,1,2].map(i => <div key={i} style={{ width: "6px", height: "6px", borderRadius: "50%", background: getAvatarColor(typingChar), animation: "dp 1.4s ease-in-out infinite", animationDelay: `${i*0.2}s` }}/>)}
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div style={{ padding: "10px 14px 14px", borderTop: `1px solid ${UI.borderSubtle}`, background: "rgba(0,0,0,0.2)", display: "flex", gap: "8px", alignItems: "flex-end", position: "relative" }}>
            {atSuggestions.length > 0 && (
              <div style={{ position: "absolute", bottom: "100%", left: "14px", background: "#1e1c26", border: "1px solid rgba(168,201,160,0.3)", borderRadius: "10px", padding: "6px", zIndex: 100, boxShadow: "0 -4px 16px rgba(0,0,0,0.5)", minWidth: "180px", marginBottom: "4px" }}>
                <div style={{ fontSize: "10px", color: UI.textMuted, padding: "2px 6px 6px", letterSpacing: "1px" }}>MENZIONA</div>
                {atSuggestions.map(nome => {
                  const p = getPersonaggio(nome);
                  const color = getAvatarColor(nome);
                  return (
                    <button key={nome} onClick={() => {
                      const firstName = nome.split(" ")[0];
                      const atPos = msgInput.lastIndexOf("@");
                      setMsgInput(msgInput.slice(0, atPos) + "@" + firstName + " ");
                      setAtSuggestions([]);
                    }} style={{ width: "100%", padding: "7px 10px", background: "transparent", border: "none", borderRadius: "7px", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px", textAlign: "left" }}
                      onMouseEnter={e => e.currentTarget.style.background = "rgba(168,201,160,0.1)"}
                      onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                      {p?.foto ? <img src={p.foto} alt="" style={{ width: "26px", height: "26px", borderRadius: "50%", objectFit: "cover" }} />
                        : <div style={{ width: "26px", height: "26px", borderRadius: "50%", background: `${color}30`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "11px", color, flexShrink: 0 }}>{nome[0]}</div>}
                      <div>
                        <div style={{ fontSize: "13px", color: UI.textPrimary }}>{nome.split(" ")[0]}</div>
                        <div style={{ fontSize: "10px", color: UI.textMuted }}>{nome}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
            <textarea value={msgInput} onChange={e => {
              const val = e.target.value;
              setMsgInput(val);
              const atPos = val.lastIndexOf("@");
              if (atPos !== -1) {
                const afterAt = val.slice(atPos + 1);
                if (!afterAt.includes(" ")) {
                  const others = currentChat?.participants.filter(p => p !== "Nadia") || [];
                  const matches = others.filter(nome => nome.split(" ")[0].toLowerCase().startsWith(afterAt.toLowerCase()) || nome.toLowerCase().startsWith(afterAt.toLowerCase()));
                  setAtSuggestions(afterAt.length === 0 ? others : matches);
                } else { setAtSuggestions([]); }
              } else { setAtSuggestions([]); }
            }} onKeyDown={e => {
              if (e.key === "Escape") { setAtSuggestions([]); return; }
              if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
            }} placeholder="Scrivi come Nadia... @nome per taggare" style={{ flex: 1, background: "rgba(255,255,255,0.05)", border: `1px solid ${UI.border}`, borderRadius: "20px", padding: "10px 16px", color: UI.textPrimary, fontSize: "14px", fontFamily: "Georgia, serif", resize: "none", minHeight: isMobMsg?"64px":"44px", maxHeight: "120px", lineHeight: "1.5", outline: "none" }} rows={isMobMsg?3:1} />
            {loading
              ? <button onClick={stopMsgGeneration} title="Ferma generazione" style={{ background: "rgba(224,120,120,0.2)", border: "1px solid rgba(224,120,120,0.5)", borderRadius: "50%", width: "44px", height: "44px", cursor: "pointer", fontSize: "16px", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, color: "#e07878" }}>■</button>
              : <button onClick={sendMessage} disabled={!msgInput.trim()} style={{ background: !loading && msgInput.trim() ? "#a8c9a0" : "rgba(168,201,160,0.15)", border: "none", borderRadius: "50%", width: "44px", height: "44px", cursor: !loading && msgInput.trim() ? "pointer" : "not-allowed", fontSize: "18px", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, color: !loading && msgInput.trim() ? "#0a0a0f" : "#a8c9a0" }}>→</button>
            }
          </div>
        </div>
      )}
        </div>
      )}

      {/* New chat modal */}
      {showNewChat && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: "20px" }}>
          <div style={{ background: "#16141e", border: "1px solid rgba(168,201,160,0.3)", borderRadius: "12px", padding: "24px", width: "100%", maxWidth: "480px", maxHeight: "80vh", overflowY: "auto" }}>
            <h3 style={{ color: "#a8c9a0", fontSize: "15px", fontWeight: "normal", margin: "0 0 16px" }}>💬 Nuova chat messaggi</h3>
            <div style={{ marginBottom: "12px" }}>
              <label style={LS}>Nome della chat</label>
              <input value={newChatName} onChange={e => setNewChatName(e.target.value)} placeholder="Es: Girls, One Direction, Chat segreta..." style={FIS} />
            </div>
            <div style={{ marginBottom: "12px" }}>
              <label style={LS}>Partecipanti (seleziona dal Codex)</label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                {personaggi.filter(p => !p.nome?.toLowerCase().includes("nadia")).map(p => {
                  const sel = selectedParticipants.includes(p.nome);
                  return <button key={p.id} onClick={() => setSelectedParticipants(prev => sel ? prev.filter(n => n !== p.nome) : [...prev, p.nome])} style={{ padding: "5px 12px", background: sel ? "rgba(168,201,160,0.25)" : "rgba(255,255,255,0.04)", border: sel ? "1px solid rgba(168,201,160,0.5)" : `1px solid ${UI.border}`, borderRadius: "20px", color: sel ? "#a8c9a0" : UI.textSecondary, cursor: "pointer", fontSize: "12px", fontFamily: "Georgia, serif", display: "flex", alignItems: "center", gap: "4px" }}>
                    {p.foto && <img src={p.foto} alt="" style={{ width: "16px", height: "16px", borderRadius: "50%", objectFit: "cover" }} />}
                    {p.nome}
                  </button>;
                })}
                {personaggi.length === 0 && <p style={{ color: UI.textMuted, fontSize: "11px" }}>Aggiungi prima i personaggi nel Codex.</p>}
              </div>
              {selectedParticipants.length > 0 && <p style={{ fontSize: "11px", color: "#a8c9a0", marginTop: "6px" }}>✅ Selezionati: {selectedParticipants.join(", ")}</p>}
            </div>
            <div style={{ marginBottom: "16px" }}>
              <label style={LS}>Tipo di chat</label>
              <div style={{ display: "flex", gap: "8px" }}>
                <button onClick={() => setIsSpicy(false)} style={{ flex: 1, padding: "8px", background: !isSpicy ? "rgba(168,201,160,0.2)" : "rgba(255,255,255,0.04)", border: !isSpicy ? "1px solid rgba(168,201,160,0.4)" : `1px solid ${UI.border}`, borderRadius: "6px", color: !isSpicy ? "#a8c9a0" : UI.textSecondary, cursor: "pointer", fontSize: "12px", fontFamily: "Georgia, serif" }}>💬 Normale</button>
                <button onClick={() => setIsSpicy(true)} style={{ flex: 1, padding: "8px", background: isSpicy ? "rgba(201,126,126,0.2)" : "rgba(255,255,255,0.04)", border: isSpicy ? "1px solid rgba(201,126,126,0.4)" : `1px solid ${UI.border}`, borderRadius: "6px", color: isSpicy ? "#c97e7e" : UI.textSecondary, cursor: "pointer", fontSize: "12px", fontFamily: "Georgia, serif" }}>🌶️ Spicy</button>
              </div>
            </div>
            <div style={{ display: "flex", gap: "8px" }}>
              <button onClick={createChat} disabled={!newChatName.trim() || selectedParticipants.length === 0} style={{ flex: 1, padding: "9px", background: "rgba(168,201,160,0.15)", border: "1px solid rgba(168,201,160,0.4)", borderRadius: "8px", color: "#a8c9a0", cursor: "pointer", fontSize: "13px", fontFamily: "Georgia, serif" }}>✅ Crea chat</button>
              <button onClick={() => { setShowNewChat(false); setNewChatName(""); setSelectedParticipants([]); setIsSpicy(false); }} style={{ flex: 1, padding: "9px", background: "rgba(255,255,255,0.05)", border: `1px solid ${UI.border}`, borderRadius: "8px", color: UI.textSecondary, cursor: "pointer", fontSize: "13px", fontFamily: "Georgia, serif" }}>Annulla</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// MAIN APP
// ============================================================
function StoryApp() {
  const [view, setView] = useState("chat");
  const [activeSection, setActiveSection] = useState("racconto");
  const [intimoMode, setIntimoMode] = useState(false);
  const intimoModeRef = useRef(false); // ref per evitare closure stale in sendMessage
  const [chats, setChats] = useState(() => load("s_chats", { racconto:[{id:"r1",name:"Capitolo 1",messages:[]}], lab:[{id:"l1",name:"Lab 1",messages:[]}], stylist:[{id:"s1",name:"Stylist 1",messages:[]}], intimo:[{id:"i1",name:"Scena 1",messages:[]}] }));
  const [activeChatId, setActiveChatId] = useState(() => load("s_active", { racconto:"r1", lab:"l1", stylist:"s1", intimo:"i1" }));
  const [models, setModels] = useState(() => {
    const saved = load("s_models", {});
    const defaults = { racconto:"minimax/minimax-m3", lab:"deepseek/deepseek-chat-v3-0324", stylist:"mistralai/mistral-medium-3.1", intimo:"deepseek/deepseek-v4-pro" };
    // Forza sempre i modelli narrativi ai nuovi default -- lab e stylist mantieni eventuale personalizzazione
    return {
      ...saved,
      racconto: defaults.racconto,
      intimo: defaults.intimo,
      lab: saved.lab || defaults.lab,
      stylist: saved.stylist || defaults.stylist,
    };
  });
  // Versione prompt -- incrementa per forzare reset automatico del localStorage
  const PROMPTS_VERSION = "v12"; // restauro 2025-05-28 — rimozione RULE inglesi + snellimento contesto
  const [prompts, setPrompts] = useState(() => {
    const savedVersion = load("s_prompts_version", null);
    if (savedVersion !== PROMPTS_VERSION) {
      // Versione cambiata -- forza reset ai nuovi prompt
      save("s_prompts_version", PROMPTS_VERSION);
      save("s_prompts", DEFAULT_PROMPTS);
      return DEFAULT_PROMPTS;
    }
    return load("s_prompts", DEFAULT_PROMPTS);
  });
  const [apiKey, setApiKey] = useState(() => load("s_apikey", ""));
  const [apiBase, setApiBase] = useState(() => load("s_apibase", "https://openrouter.ai/api/v1"));
  const [globalStyle, setGlobalStyle] = useState(() => load("s_style", "Stile narrativo letterario, lessico ricercato ma accessibile, ritmo cinematografico."));
  const [codexItems, setCodexItems] = useState(() => {
    const items = load("s_codex_items", []);
    // Precompila gruppo per personaggi noti se non ancora impostato
    const NADIA_GRP = ["Ilaria Vanzetti","Drew Bianchi","Serena Guidi","Francesca De Santis","Simona Vanzetti","Silvia Rinaldi","Daniela Contini","Sofia Alvarez","Francesco Valenti","Andrea Valenti","Helena Spencer","James Miller","Jade Parker"];
    const LIAM_GRP = ["Andy Samuels","Danny Riach","Ethan Cooper","Louis Tomlinson","Zayn Malik","Harry Styles","Niall Horan","Paul Higgins","Geoff Payne","Karen Payne","Nicola Payne","Ruth Payne","Nick Farrell","Thomas Gibbins","Rosa Bennet"];
    return items.map(item => {
      if (item.type !== "personaggio" || item.gruppo || item.protagonista || item.fiammaGemella) return item;
      const nome = item.nome || "";
      if (NADIA_GRP.some(n => nome.toLowerCase().includes(n.split(" ")[0].toLowerCase()) && nome.toLowerCase().includes(n.split(" ").slice(-1)[0].toLowerCase()))) return {...item, gruppo: "nadia"};
      if (LIAM_GRP.some(n => nome.toLowerCase().includes(n.split(" ")[0].toLowerCase()) && nome.toLowerCase().includes(n.split(" ").slice(-1)[0].toLowerCase()))) return {...item, gruppo: "liam"};
      return item;
    });
  });
  const [summaryCompact, setSummaryCompact] = useState(() => load("s_summary_compact", ""));
  const [sceneContext, setSceneContext] = useState(() => load("s_scene_context", { location: "", momento: "", intimita: "", statoScena: "", vestizione: "" }));
  const [keyMoments, setKeyMoments] = useState(() => load("s_key_moments", []));
  const [showKeyMomentModal, setShowKeyMomentModal] = useState(false);
  const [newMomentTitle, setNewMomentTitle] = useState("");
  const [newMomentContent, setNewMomentContent] = useState("");
  const [newMomentPriority, setNewMomentPriority] = useState("normale");
  const [input, setInput] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(false);
  const isMobile = window.innerWidth <= 768;
  const [showSidebar, setShowSidebar] = useState(!isMobile);
  const [renamingChat, setRenamingChat] = useState(null);
  const [editingMsg, setEditingMsg] = useState(null);
  const [editText, setEditText] = useState("");
  const [showMsgPicker, setShowMsgPicker] = useState(false);
  const [showInfoPanel, setShowInfoPanel] = useState(false);
  const [showImageGen, setShowImageGen] = useState(false);
  const [imageGenPrompt, setImageGenPrompt] = useState("");
  const [imageGenResult, setImageGenResult] = useState(null);
  const [imageGenLoading, setImageGenLoading] = useState(false);
  const [lastOptStats, setLastOptStats] = useState({ originalItems:0, sentItems:0, summaryCompressed:false, momentsSent:0, totalMoments:0 });

  // SWITCHING INTELLIGENTE -- override modello per sessione corrente
  const [modelOverride, setModelOverride] = useState({}); // { racconto: "deepseek/..." } override temporaneo
  const [showPanicModal, setShowPanicModal] = useState(false);
  const [panicInstruction, setPanicInstruction] = useState("");
  const [styleReference, setStyleReference] = useState(() => load("s_style_ref", "")); // stile campione da Sonnet

  // STOP GENERATION
  const abortControllerRef = useRef(null);
  const streamTextRef = useRef(""); // testo streaming real-time (sicurezza mobile)
  const stopGeneration = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  };

  // iOS standby: avvisa se la pagina va in background durante la generazione
  const [standbyWarning, setStandbyWarning] = useState(false);
  useEffect(() => {
    const handleVisibility = () => {
      if (document.hidden && loading) {
        setStandbyWarning(true);
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, [loading]);

  // CONTEXT BRIDGE -- traccia l'ultima direzione già bridgeata per non ripetere
  const lastBridgeRef = useRef(null);

  // POV SYSTEM
  const [activePOV, setActivePOV] = useState(null); // nome del personaggio POV attivo
  const [intimoPresenza, setIntimoPresenza] = useState("solo"); // "solo" | "entrambi"
  const [showPOVMenu, setShowPOVMenu] = useState(false);

  // EPISODE COUNTER -- conta episodi per sezione/chat
  const [episodeCounters, setEpisodeCounters] = useState(() => load("s_episode_counters", {}));
  // Canon selezionati: array di {id, label, source, text} -- testo estratto salvato al pin
  const [selectedCanons, setSelectedCanons] = useState(() => load("s_selected_canons", []));
  const selectedCanonsRef = useRef([]);
  const sceneContextRef = useRef(sceneContext);
  // currentEpisodeNum: episodio corrente in corso (assegnato all'invio, non alla risposta)
  const [pendingEpisodeNum, setPendingEpisodeNum] = useState(null);
  useEffect(() => { save("s_episode_counters", episodeCounters); }, [episodeCounters]);
  useEffect(() => { selectedCanonsRef.current = selectedCanons; save("s_selected_canons", selectedCanons); }, [selectedCanons]);
  useEffect(() => {
    intimoModeRef.current = intimoMode;
    // Nessun messaggio automatico al cambio modalità.
    // L'utente decide da solo cosa scrivere.
  }, [intimoMode]);

  // AUTO-UPDATE cronologie ogni 10 episodi
  const [autoUpdateNotice, setAutoUpdateNotice] = useState(""); // messaggio toast
  const [autoUpdateTimer, setAutoUpdateTimer] = useState(null);
  const [summaryProgress, setSummaryProgress] = useState(null); // null=idle; 0-100=avanzamento generazione riassunto
  const summaryProgressRef = useRef(null); // id setInterval della barra simulata
  const episodesThisSession = useRef(0);

  // FINE SESSIONE -- popup momenti chiave
  const [showEndSessionModal, setShowEndSessionModal] = useState(false);
  const [showCreatoreOverlay, setShowCreatoreOverlay] = useState(false);
  const [showImportEpisodi, setShowImportEpisodi] = useState(false);
  const [showDebugger, setShowDebugger] = useState(false);
  const [debugSection, setDebugSection] = useState("racconto");
  const [debugOutput, setDebugOutput] = useState("");
  const [importEpText, setImportEpText] = useState("");
  const [importEpNum, setImportEpNum] = useState("");
  const [importEpSection, setImportEpSection] = useState("racconto");
  const [importEpLoading, setImportEpLoading] = useState(false);
  const [pendingKeyMoments, setPendingKeyMoments] = useState([]); // candidati suggeriti dall'AI
  const [sessionCandidates, setSessionCandidates] = useState([]); // accumulati durante la sessione
  const [endSessionLoading, setEndSessionLoading] = useState(false);

  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const [showScrollBtn, setShowScrollBtn] = useState(false);
  const fileInputRef = useRef(null);
  const mainTextareaRef = useRef(null);
  const mirrorPendingRef = useRef(null);
  const sendMessageRef = useRef(null);
  const counterOverrideRef = useRef(false);

  useEffect(() => { save("s_chats",chats); },[chats]);
  useEffect(() => { save("s_active",activeChatId); },[activeChatId]);
  useEffect(() => { save("s_models",models); },[models]);
  useEffect(() => { save("s_prompts",prompts); },[prompts]);
  useEffect(() => { save("s_apikey",apiKey); },[apiKey]);
  useEffect(() => { save("s_apibase",apiBase); },[apiBase]);
  useEffect(() => { save("s_style",globalStyle); },[globalStyle]);
  useEffect(() => { save("s_codex_items",codexItems); },[codexItems]);
  useEffect(() => { save("s_summary_compact",summaryCompact); },[summaryCompact]);
  useEffect(() => { sceneContextRef.current = sceneContext; save("s_scene_context",sceneContext); },[sceneContext]);
  useEffect(() => { save("s_key_moments",keyMoments); },[keyMoments]);
  useEffect(() => { save("s_style_ref",styleReference); },[styleReference]);

  // ============================================================
  // BACKEND SYNC -- carica dati dal database all'avvio
  // ============================================================
  const backendLoaded = useRef(false);
  useEffect(() => {
    if (backendLoaded.current) return;
    backendLoaded.current = true;
    const loadFromBackend = async () => {
      try {
        // Carica chats dal backend
        const backendChats = await api.loadChats();
        if (backendChats) {
          // Il backend e' SEMPRE la fonte di verita' per quali chat esistono
          // Le cancellazioni fatte da altri device sono già riflesse nel backend
          const hasData = Object.values(backendChats).some(arr => arr.some(c => c.messages?.length > 0));
          if (hasData) {
            const localChats = load("s_chats", {});
            const deletedIds = getDeletedIds();
            // Filtra messaggi cancellati localmente (su questo device)
            const filterDeleted = (chatsObj) => Object.fromEntries(Object.entries(chatsObj).map(([sec, arr]) => [
              sec, (arr||[]).map(c => ({...c, messages: (c.messages||[]).filter(m => !m.id || !deletedIds.has(m.id))}))
            ]));
            const backendFiltered = filterDeleted(backendChats);

            // Quali chat esistono nel backend (fonte di verità)
            const backendChatIds = new Set(
              Object.values(backendFiltered).flat().map(c => c.id).filter(Boolean)
            );

            // Backend è SEMPRE la fonte di verità
            // Usa sempre backendFiltered -- le chat cancellate da altri device non esistono più
            // Solo se locale ha messaggi NUOVI (non nel backend) li preserviamo
            const backendMsgCount = Object.values(backendFiltered).flat().reduce((n,c) => n+(c.messages?.length||0), 0);
            const localChatsFiltered = Object.fromEntries(
              Object.entries(localChats).map(([sec, arr]) => [
                sec, (arr||[]).filter(c => !c.id || backendChatIds.has(c.id))
              ])
            );
            const localMsgCount = Object.values(localChatsFiltered).flat().reduce((n,c) => n+(c.messages?.length||0), 0);

            if (localMsgCount > backendMsgCount && localMsgCount > 0) {
              // Locale ha più messaggi: probabilmente cancellazioni locali non ancora sync
              const localMsgIds = new Set(
                Object.values(localChatsFiltered).flat()
                  .flatMap(c => (c.messages || []).map(m => m.id).filter(Boolean))
              );
              const filtered = Object.fromEntries(Object.entries(backendFiltered).map(([sec, chatsArr]) => [
                sec,
                (chatsArr || []).map(c => ({...c, messages: (c.messages || []).filter(m => !m.id || localMsgIds.has(m.id))}))
              ]));
              setChats(filtered);
              save("s_chats", filtered);
            } else {
              // Backend è aggiornato: usa sempre il backend (include cancellazioni da altri device)
              setChats(backendFiltered);
              save("s_chats", backendFiltered);
            }
          } else {
            // Sincronizza localStorage → backend
            const localChats = load("s_chats", null);
            if (localChats) {
              for (const sec of Object.keys(localChats)) {
                for (const chat of localChats[sec] || []) {
                  await api.createChat(chat.id, sec, chat.name).catch(() => {});
                  for (const msg of chat.messages || []) {
                    await api.saveMessage(msg, chat.id).catch(() => {});
                  }
                }
              }
            }
          }
        }

        // Carica story data (summary, key_moments, episode_counters)
        const storyData = await api.loadStory();
        if (storyData) {
          if (storyData.summary) { setSummaryCompact(storyData.summary); save("s_summary_compact", storyData.summary); }
          if (storyData.scene_context) { setSceneContext(storyData.scene_context); save("s_scene_context", storyData.scene_context); }
          if (storyData.key_moments?.length > 0) { setKeyMoments(storyData.key_moments); save("s_key_moments", storyData.key_moments); }
          if (storyData.episode_counters && Object.keys(storyData.episode_counters).length > 0) {
            // Usa il valore del backend come base, ma non superare il conteggio reale degli episodi presenti
            const localCounters = load("s_episode_counters", {});
            const merged = {...storyData.episode_counters};
            Object.keys(localCounters).forEach(k => {
              merged[k] = Math.max(merged[k] || 0, localCounters[k] || 0);
            });
            setEpisodeCounters(merged);
            save("s_episode_counters", merged);
          }
          if (storyData.settings) {
            const s = storyData.settings;
            if (s.models) {
              // Forza sempre i modelli narrativi ai default corretti
              const forcedModels = {
                ...s.models,
                racconto: "minimax/minimax-m3",
                intimo: "deepseek/deepseek-v4-pro",
              };
              setModels(forcedModels);
              save("s_models", forcedModels);
            }
            if (s.prompts) { setPrompts(s.prompts); save("s_prompts", s.prompts); }
            if (s.apiKey) { setApiKey(s.apiKey); save("s_apikey", s.apiKey); }
            if (s.apiBase) { setApiBase(s.apiBase); save("s_apibase", s.apiBase); }
          }
        }

        // Carica codex
        const backendCodex = await api.loadCodex();
        if (backendCodex?.length > 0) { setCodexItems(backendCodex); save("s_codex_items", backendCodex); }

      } catch(e) { console.warn("Backend non disponibile, uso localStorage"); }
    };
    loadFromBackend();
  }, []);

  // Sync chats → backend quando cambiano
  const chatsRef = useRef(chats);
  useEffect(() => {
    chatsRef.current = chats;
    // Salva su backend in background (debounced 2s)
    const timer = setTimeout(async () => {
      try {
        for (const sec of Object.keys(chats)) {
          for (const chat of chats[sec] || []) {
            await api.createChat(chat.id, sec, chat.name).catch(() => {});
            for (const msg of chat.messages || []) {
              // Non salvare messaggi in streaming (contenuto parziale)
              if (msg.id && !msg.streaming) await api.saveMessage(msg, chat.id).catch(() => {});
            }
          }
        }
      } catch(e) {}
    }, 2000);
    return () => clearTimeout(timer);
  }, [chats]);

  // Sync story data → backend quando cambiano
  useEffect(() => {
    const timer = setTimeout(async () => {
      const { racconto, intimo, ...modelsToSave } = models;
      let countersToSave = {...episodeCounters};
      // Se il contatore è stato impostato intenzionalmente (manuale o ricalcola),
      // salva il valore esatto senza confrontarlo col backend
      if (!counterOverrideRef.current) {
        try {
          const current = await api.loadStory();
          if (current?.episode_counters) {
            Object.keys(current.episode_counters).forEach(k => {
              countersToSave[k] = Math.max(countersToSave[k] || 0, current.episode_counters[k] || 0);
            });
            if (countersToSave.global_narrative !== episodeCounters.global_narrative) {
              setEpisodeCounters(countersToSave);
              save("s_episode_counters", countersToSave);
            }
          }
        } catch(e) {}
      }
      counterOverrideRef.current = false;
      api.saveStory({
        summary: summaryCompact,
        key_moments: keyMoments,
        episode_counters: countersToSave,
        settings: { models: modelsToSave, prompts, apiKey, apiBase },
        scene_context: sceneContext
      }).catch(() => {});
    }, 2000);
    return () => clearTimeout(timer);
  }, [summaryCompact, keyMoments, episodeCounters, models, prompts, apiKey, apiBase]);
  // Sync codex → backend quando cambia
  useEffect(() => {
    const timer = setTimeout(async () => {
      try {
        for (const item of codexItems) await api.saveCodexItem(item).catch(() => {});
      } catch(e) {}
    }, 2000);
    return () => clearTimeout(timer);
  }, [codexItems]);

  // Scroll sempre in fondo al cambio sezione, chat, o nuovo messaggio
  const lastMsgCountRef = useRef(0);
  const initialScrollDone = useRef(false);
  useEffect(() => {
    // Ogni volta che cambia sezione o chat attiva, scrolla subito in fondo
    setTimeout(() => messagesEndRef.current?.scrollIntoView({behavior:"instant"}), 150);
    setTimeout(() => messagesEndRef.current?.scrollIntoView({behavior:"instant"}), 600);
  },[activeSection, activeChatId]);

  useEffect(() => {
    const chat = chats[activeSection]?.find(c => c.id === activeChatId[activeSection]);
    const msgCount = chat?.messages?.length || 0;
    if (msgCount !== lastMsgCountRef.current) {
      lastMsgCountRef.current = msgCount;
      setTimeout(() => messagesEndRef.current?.scrollIntoView({behavior:"smooth"}), 50);
    }
  },[chats, activeSection]);

  // FIX 2: Factory Reset
  const factoryReset = async () => {
    if (!window.confirm("⚠️ SOFT RESET\n\nVerranno eliminati permanentemente:\n• Tutte le chat e messaggi\n• Riassunto e momenti chiave\n• Chat Messaggi\n• Style reference\n\nIl Codex (personaggi, luoghi, ecc.) verrà mantenuto.\n\nContinuare?")) return;
    // Cancella tutte le chat dal DB (prima i messaggi, poi la chat)
    const allChats = Object.values(chats).flat();
    for (const chat of allChats) {
      for (const msg of (chat.messages || [])) {
        if (msg.id) await api.deleteMessage(msg.id).catch(()=>{});
      }
      await api.deleteChat(chat.id).catch(()=>{});
    }
    // Cancella riassunto e momenti chiave dal DB
    await api.saveStory({ summary: "", key_moments: [], episode_counters: {} }).catch(()=>{});
    // Reset localStorage
    const defaultChats = { racconto:[{id:"r1",name:"Capitolo 1",messages:[]}], lab:[{id:"l1",name:"Lab 1",messages:[]}], stylist:[{id:"s1",name:"Stylist 1",messages:[]}], intimo:[{id:"i1",name:"Scena 1",messages:[]}] };
    const defaultActive = { racconto:"r1", lab:"l1", stylist:"s1", intimo:"i1" };
    setChats(defaultChats);
    setActiveChatId(defaultActive);
    setSummaryCompact("");
    setKeyMoments([]);
    setStyleReference("");
    setEpisodeCounters({});
    save("s_msg_chats", []);
    save("s_chats", defaultChats);
    save("s_active", defaultActive);
    save("s_summary_compact", "");
    save("s_key_moments", []);
    save("s_style_ref", "");
    save("s_episode_counters", {});
    setSelectedCanons([]);
    save("s_selected_canons", []);
    initialScrollDone.current = false;
    lastMsgCountRef.current = 0;
    alert("✅ Reset completato. Il Codex è stato mantenuto.");
  };

  const getCurrentChat = () => {
    // Lab, Stylist, Messaggi usano la loro sezione normale
    // Racconto e Intimo (intimoMode) usano sempre chats["racconto"]
    const storage = (activeSection === "racconto" || activeSection === "intimo") ? "racconto" : activeSection;
    const s=chats[storage]||[];
    return s.find(c=>c.id===activeChatId[storage])||s[0];
  };
  const currentChat = getCurrentChat();
  const currentMsgCount = currentChat?.messages?.length||0;

  const addChat = (section) => {
    // Se siamo in racconto o intimo e ci sono messaggi, mostra popup fine sessione
    if ((section === "racconto" || section === "intimo") && episodesThisSession.current > 0) {
      triggerEndSession(section);
      return;
    }
    createNewChat(section);
  };

  const createNewChat = (section) => {
    const id=`${section[0]}${Date.now()}`,count=(chats[section]||[]).length+1,names={racconto:`Capitolo ${count}`,lab:`Lab ${count}`,stylist:`Stylist ${count}`,intimo:`🔥 Scena ${count}`};
    setChats(p=>({...p,[section]:[...(p[section]||[]),{id,name:names[section],messages:[]}]}));
    setActiveChatId(p=>({...p,[section]:id}));
    episodesThisSession.current = 0;
  };
  // Ricalcola contatore episodi da messaggi reali (racconto + intimo)
  // forceExact=true: imposta il valore esatto (usato dopo delete). false: usa Math.max (usato su load)
  const recalcEpisodeCounter = (updatedChats, forceExact=false) => {
    const sections = ["racconto","intimo"];
    let count = 0;
    sections.forEach(sec => {
      (updatedChats[sec]||[]).forEach(chat => {
        (chat.messages||[]).forEach(m => { if(m.role==="assistant" && m.episodeNum) count++; });
      });
    });
    if (forceExact) {
      setEpisodeCounters(p => ({...p, "global_narrative": count}));
    } else {
      setEpisodeCounters(p => ({...p, "global_narrative": Math.max(count, p["global_narrative"] || 0)}));
    }
    return count;
  };

  const deleteChat = (section, chatId) => {
    const rem = (chats[section]||[]).filter(c=>c.id!==chatId);
    if(rem.length===0) return;
    // Cancella i messaggi dal DB prima della chat
    const chatToDelete = (chats[section]||[]).find(c=>c.id===chatId);
    if (chatToDelete) {
      (chatToDelete.messages||[]).forEach(m => { if(m.id) api.deleteMessage(m.id).catch(()=>{}); });
    }
    const newChats = {...chats, [section]: rem};
    setChats(newChats);
    if(activeChatId[section]===chatId) setActiveChatId(p=>({...p,[section]:rem[0].id}));
    api.deleteChat(chatId).catch(()=>{});
    // Ricalcola contatore solo per racconto e intimo
    if(section==="racconto"||section==="intimo") recalcEpisodeCounter(newChats);
  };

  // Contatore episodi GLOBALE per racconto+intimo (unico contatore condiviso)
  const getGlobalEpisodeNum = () => {
    return (episodeCounters["global_narrative"] || 0) + 1;
  };
  const incrementGlobalEpisode = () => {
    const current = episodeCounters["global_narrative"] || 0;
    setEpisodeCounters(p => ({ ...p, "global_narrative": current + 1 }));
    episodesThisSession.current += 1;
    return current + 1;
  };

  // Conta episodi per chat corrente (kept for display only)
  const getEpisodeKey = (section, chatId) => `${section}_${chatId}`;
  const getCurrentEpisodeNum = () => getGlobalEpisodeNum();
  const incrementEpisode = (section, chatId) => incrementGlobalEpisode();

  // Auto-aggiornamento silenzioso ogni 10 episodi
  const showAutoUpdateToast = (msg) => {
    setAutoUpdateNotice(msg);
    if (autoUpdateTimer) clearTimeout(autoUpdateTimer);
    const t = setTimeout(() => setAutoUpdateNotice(""), 4000);
    setAutoUpdateTimer(t);
  };

  const autoUpdateCronologie = async (newEpNum, newEpisodeContent = "") => {
    if (newEpNum % 10 !== 0) return;
    if (!apiKey || !apiBase) return;
    showAutoUpdateToast("⏳ Aggiornamento cronologie in corso...");

    // Usa i messaggi della chat attiva corrente -- da chatsRef (non dallo state stantio)
    const _chats = chatsRef.current;
    const currentChatMsgs = (_chats[activeSection]||[])
      .find(c => c.id === activeChatId[activeSection])
      ?.messages || [];
    const _assistContents = currentChatMsgs.filter(m => m.role === "assistant").map(m => m.content);
    // Garantisce l'inclusione dell'episodio-trigger appena finalizzato (chatsRef può non essere ancora aggiornato)
    if (newEpisodeContent && !_assistContents.includes(newEpisodeContent)) _assistContents.push(newEpisodeContent);
    const tuttiMsg = _assistContents.slice(-15).join("\n\n---\n\n");
    const protagonisti = codexItems.filter(i=>i.type==="personaggio"&&(i.protagonista||i.fiammaGemella));
    // Accumulatori per-id: niente mutazione di un array condiviso tra le promise parallele
    const newCron = {};
    let newFgEventi = null;

    // Esegui TUTTE le chiamate in parallelo per velocizzare
    const promises = [];

    // Cronologie personaggi
    for (const pers of protagonisti) {
      const cronEsistente = (pers.cronologia||[]).length>0 ? `Cronologia esistente:\n${pers.cronologia.map(c=>`[${c.periodo}] ${c.evento}`).join("\n")}` : "";
      promises.push(
        fetch(`${apiBase}/chat/completions`, {
          method:"POST", headers:{"Content-Type":"application/json","Authorization":`Bearer ${apiKey}`},
          body: JSON.stringify({ model:"deepseek/deepseek-chat-v3-0324", messages:[{role:"user",content:`Analizza questi episodi, max 2 nuovi eventi per "${pers.nome}" non già presenti.\n${tuttiMsg}\n${cronEsistente}\nRispondi SOLO JSON: [{"periodo":"...","evento":"...","cambiamento":"..."}] o []`}], max_tokens:200 })
        }).then(r=>r.json()).then(data=>{
          const raw = data.choices?.[0]?.message?.content||"[]";
          const nuove = JSON.parse(raw.replace(/```json|```/g,"").trim());
          if (Array.isArray(nuove) && nuove.length > 0) {
            newCron[pers.id] = nuove.map(v=>({id:`cr${Date.now()}_${Math.random()}`,periodo:v.periodo||"",evento:v.evento||"",cambiamento:v.cambiamento||"",createdAt:Date.now(),aiGenerated:true}));
          }
        }).catch(()=>{})
      );
    }

    // Cronologia FG
    const fgItems = codexItems.filter(i=>i.type==="fiammegemelle");
    if (fgItems.length > 0) {
      const fg = fgItems[0];
      const evEsistenti = (fg.eventi||[]).length>0 ? `Eventi esistenti:\n${fg.eventi.map(e=>`[${e.periodo}] ${e.evento}`).join("\n")}` : "";
      promises.push(
        fetch(`${apiBase}/chat/completions`, {
          method:"POST", headers:{"Content-Type":"application/json","Authorization":`Bearer ${apiKey}`},
          body: JSON.stringify({ model:"deepseek/deepseek-chat-v3-0324", messages:[{role:"user",content:`Max 2 nuovi eventi tra FG non già presenti.\n${tuttiMsg}\n${evEsistenti}\nRispondi SOLO JSON: [{"periodo":"...","categoria":"connessione","evento":"...","impatto":"..."}] o []`}], max_tokens:200 })
        }).then(r=>r.json()).then(data=>{
          const raw = data.choices?.[0]?.message?.content||"[]";
          const nuovi = JSON.parse(raw.replace(/```json|```/g,"").trim());
          if (Array.isArray(nuovi) && nuovi.length > 0) {
            newFgEventi = {id: fg.id, voci: nuovi.map(e=>({id:`fg${Date.now()}_${Math.random()}`,periodo:e.periodo||"",categoria:e.categoria||"altro",evento:e.evento||"",impatto:e.impatto||"",createdAt:Date.now(),aiGenerated:true}))};
          }
        }).catch(()=>{})
      );
    }

    // Riassunto
    const epTotale = newEpNum;
    const chronoText = buildChronologicalMessages(_chats) || tuttiMsg;
    let nuovoRiassunto = "";
    promises.push(
      chronoText ? fetch(`${apiBase}/chat/completions`, {
        method:"POST", headers:{"Content-Type":"application/json","Authorization":`Bearer ${apiKey}`},
        body: JSON.stringify({ model:"deepseek/deepseek-chat-v3-0324", messages:[{role:"user",content:`Aggiorna il riassunto integrando gli ultimi episodi. Solo fatti accaduti, zero sviluppi futuri.\n\nRIASSUNTO ATTUALE:\n${summaryCompact}\n\nNUOVI EPISODI:\n${tuttiMsg}\n\nRestituisci SOLO il testo del riassunto aggiornato che inizia con "Riassunto da Episodio 1 a Episodio ${epTotale}". Max ${SUMMARY_MAX_CHARS} caratteri.`}], max_tokens:1000 })
      }).then(r=>r.json()).then(data=>{
        nuovoRiassunto = data.choices?.[0]?.message?.content||"";
      }).catch(()=>{}) : Promise.resolve()
    );

    // Candidati momenti chiave
    let nuoviCandidati = [];
    promises.push(
      fetch(`${apiBase}/chat/completions`, {
        method:"POST", headers:{"Content-Type":"application/json","Authorization":`Bearer ${apiKey}`},
        body: JSON.stringify({ model:"anthropic/claude-haiku-4-5", messages:[{role:"user",content:`Analizza questi episodi narrativi. Cerca SOLO eventi di forte impatto reale tra le Fiamme Gemelle o eventi narrativi rari e decisivi (primo incontro fisico, riconoscimento, confessione importante, trauma, svolta irreversibile nella relazione). NON inserire eventi quotidiani, scene romantiche normali, dialoghi ordinari o momenti che si ripetono spesso.\n\nSe non ci sono eventi di questo tipo, rispondi con array vuoto [].\n\n${tuttiMsg}\n\nRispondi SOLO JSON (max 1-2 eventi, solo se davvero significativi): [{"title":"max 5 parole","content":"max 30 parole","priority":"alta"}] o []`}], max_tokens:150 })
      }).then(r=>r.json()).then(data=>{
        const raw = data.choices?.[0]?.message?.content||"[]";
        nuoviCandidati = JSON.parse(raw.replace(/```json|```/g,"").trim());
      }).catch(()=>{})
    );

    await Promise.all(promises);

    if (nuovoRiassunto && nuovoRiassunto.length > 100) setSummaryCompact(nuovoRiassunto);
    if (Array.isArray(nuoviCandidati) && nuoviCandidati.length > 0) {
      setSessionCandidates(p => [...p, ...nuoviCandidati.map(c=>({...c,id:`sc${Date.now()}_${Math.random()}`,selected:true}))]);
    }
    // Applica le nuove cronologie con updater funzionale: non sovrascrive modifiche concorrenti dell'utente al Codex
    if (Object.keys(newCron).length > 0 || newFgEventi) {
      setCodexItems(prev => prev.map(i => {
        if (newCron[i.id]) return {...i, cronologia:[...(i.cronologia||[]), ...newCron[i.id]]};
        if (newFgEventi && i.id === newFgEventi.id) return {...i, eventi:[...(i.eventi||[]), ...newFgEventi.voci]};
        return i;
      }));
    }
    showAutoUpdateToast("✦ Cronologie e riassunto aggiornati automaticamente");
  };

  // Fine sessione -- popup momenti chiave
  const triggerEndSession = async (section) => {
    setEndSessionLoading(true);
    setShowEndSessionModal(true);
    // Usa candidati accumulati + analisi finale
    const tuttiMsg = Object.values(chats).flat().flatMap(c=>c.messages||[]).filter(m=>m.role==="assistant").slice(-20).map(m=>m.content).join("\n\n---\n\n");
    try {
      const resp = await fetch(`${apiBase}/chat/completions`, {
        method:"POST", headers:{"Content-Type":"application/json","Authorization":`Bearer ${apiKey}`},
        body: JSON.stringify({ model:"anthropic/claude-haiku-4-5", messages:[{role:"user",content:`Analizza questa sessione e suggerisci i momenti chiave più importanti da salvare.\n\n${tuttiMsg}\n\nGià salvati: ${keyMoments.map(m=>m.title).join(", ")||"nessuno"}\n\nRispondi SOLO JSON (max 5, solo nuovi, titoli brevi max 6 parole, contenuto max 30 parole): [{"title":"...","content":"...","priority":"normale|alta"}]`}], max_tokens:400 })
      });
      const data = await resp.json();
      const raw = data.choices?.[0]?.message?.content||"[]";
      const suggeriti = JSON.parse(raw.replace(/```json|```/g,"").trim());
      // Combina candidati accumulati + suggeriti finali, deduplicati
      const tutti = [...sessionCandidates, ...(Array.isArray(suggeriti)?suggeriti:[]).map(s=>({...s,id:`es${Date.now()}_${Math.random()}`,selected:true}))];
      const unici = tutti.filter((v,i,a)=>a.findIndex(t=>t.title===v.title)===i);
      setPendingKeyMoments(unici);
    } catch(e) {
      setPendingKeyMoments(sessionCandidates);
    }
    setEndSessionLoading(false);
  };

  const buildCodexContext = (overrideText, inputUtente) => {
    if(codexItems.length===0)return"";
    const recentText= overrideText || (currentChat?.messages||[]).slice(-CHAT_HISTORY_MSGS).map(m=>m.content).join(" ");

    // Elementi futuri esclusi completamente dal contesto
    const activeItems = codexItems.filter(i => !i.futura);
    const relevant=getRelevantCodexItems(activeItems,recentText,activePOV, inputUtente);
    const g={};relevant.forEach(i=>{if(!g[i.type])g[i.type]=[];g[i.type].push(i);});
    const p=[];
    if(g.personaggio)p.push("PERSONAGGI:\n"+g.personaggio.map(pers=>{
      const l=[`▸ PERSONAGGIO: ${(pers.nome||"N/A").toUpperCase()} ◂`];
      if(pers.protagonista)l.push("  Ruolo: PROTAGONISTA");
      else if(pers.fiammaGemella)l.push("  Ruolo: FIAMMA GEMELLA");
      else if(pers.rilevanza)l.push(`  Rilevanza: ${pers.rilevanza}`);
      if(pers.eta)l.push(`  Età: ${pers.eta}`);
      if(pers.nazionalita)l.push(`  Nazionalità: ${pers.nazionalita}`);
      if(pers.altezza)l.push(`  Altezza: ${pers.altezza}`);
      if(pers.professione)l.push(`  Professione: ${pers.professione}`);
      if(pers.aspetto)l.push(`  Aspetto fisico di ${pers.nome||"N/A"}: ${pers.aspetto}`);
      if(pers.carattere)l.push(`  Carattere di ${pers.nome||"N/A"}: ${pers.carattere}`);
      if(pers.paure)l.push(`  Paure: ${pers.paure}`);
      if(pers.ruolo)l.push(`  Ruolo narrativo: ${pers.ruolo}`);
      if(pers.coseDaSapere)l.push(`  Cose da sapere: ${pers.coseDaSapere}`);
      if(pers.note)l.push(`  Note: ${pers.note}`);
      if(pers.altreCoseImportanti)l.push(`  Altro: ${pers.altreCoseImportanti}`);
      if(pers.cronologia&&pers.cronologia.length>0){l.push(`  Cronologia di ${pers.nome||"N/A"}:\n${pers.cronologia.map(c=>`    [${c.periodo||"?"}] ${c.evento}${c.cambiamento?` → ${c.cambiamento}`:""}`).join("\n")}`);}
      return l.join("\n");
    }).join("\n\n"));
    if(g.luogo)p.push("LUOGHI:\n"+g.luogo.map(l=>`${l.nome||"N/A"}${l.citta?` (${l.citta})`:""}: ${l.descrizione||""}`).join("\n"));
    if(g.relazione)p.push("RELAZIONI:\n"+g.relazione.map(r=>{const a=r.personaggioA==="__custom__"?r.personaggioACustom:r.personaggioA,b=r.personaggioB==="__custom__"?r.personaggioBCustom:r.personaggioB,t=r.tipoRelazione==="Altro"?r.tipoRelazioneCustom:r.tipoRelazione;return`${a||"?"}↔${b||"?"}[${t||"N/A"}]:${r.descrizione||""}`;}).join("\n"));
    // REGOLE ASSOLUTE -- sempre incluse, mai filtrate
    const regoleAssolute = activeItems.filter(i => i.type === "regola" && i.assoluta === true);
    if(regoleAssolute.length > 0) {
      p.unshift("⚠️ REGOLE ASSOLUTE -- RISPETTA SEMPRE, SENZA ECCEZIONI:\n" +
        regoleAssolute.map(r=>`• ${r.nome}: ${r.descrizione||""}`).join("\n"));
    }

    if(g.lore)p.push("LORE:\n"+g.lore.map(l=>`${l.nome||"N/A"}:${l.descrizione||""}`).join("\n"));
    if(g.regola)p.push("REGOLE:\n"+g.regola.map(r=>`${r.nome||"N/A"}:${r.descrizione||""}`).join("\n"));
    if(g.altro)p.push("ALTRO:\n"+g.altro.map(a=>`${a.nome||"N/A"}:${a.contenuto||""}`).join("\n"));

    // Cronologia FG -- non inclusa qui, passa come blocco fisso in buildFullContext

    // Segreti -- sezione separata con regola di non divulgazione assoluta
    const conSegreti = activeItems.filter(i => i.type==="personaggio" && i.segreti?.trim());
    if(conSegreti.length>0){
      p.push(`SEGRETI -- REGOLA ASSOLUTA DI RISERVATEZZA:\nQueste informazioni esistono SOLO nella mente del personaggio che le possiede. NON devono mai trapelare, essere intuibili, insinuate o anticipate nel comportamento o nei dialoghi degli ALTRI personaggi. Usale solo per rendere il comportamento di chi le conosce narrativamente coerente.\n${conSegreti.map(pers=>`🔒 ${pers.nome} sa (solo lui/lei): ${pers.segreti}`).join("\n")}`);
    }

    return p.join("\n\n");
  };

  // ════════════════════════════════════════════════════════════════
  // BUILDER DI CONTESTO PER SEZIONE
  // Estratti da buildFullContext per isolare la logica di ogni sezione.
  // Ogni builder restituisce un array di blocchi da appendere a blocks[].
  // Lo state React (codexItems, sceneContext, globalStyle, episodeCounters,
  // activePOV) è letto dalla closure di StoryApp.
  // ════════════════════════════════════════════════════════════════

  // Costruisce i blocchi di contesto specifici per la sezione INTIMO.
  // Solo Nadia/Liam con campi essenziali, sceneContext, regola presenza,
  // stile, regole assolute, contatore episodi.
  const buildIntimoContextBlock = (presenza) => {
    const out = [];

    // Solo Nadia e Liam -- per nome esplicito, campi essenziali per intimo
    const _nadiaLiam = codexItems.filter(i =>
      i.type === "personaggio" && i.nome && !i.futura &&
      (i.protagonista || i.fiammaGemella) &&
      ((i.nome || "").toLowerCase().includes("nadia") || (i.nome || "").toLowerCase().includes("liam"))
    );
    if (_nadiaLiam.length > 0) {
      const _profiloIntimo = (p) => {
        const parts = [`PERSONAGGIO: ${p.nome}`];
        if (p.aspetto) parts.push(`Aspetto fisico: ${p.aspetto}`);
        if (p.misure) parts.push(`Misure: ${p.misure}`);
        if (p.carattere) parts.push(`Carattere: ${p.carattere}`);
        return parts.join("\n");
      };
      out.push(`👤 NADIA E LIAM:\n${_nadiaLiam.map(_profiloIntimo).join("\n\n---\n\n")}`);
    }

    // SceneContext -- location, momento e stato attuale della scena
    const _sc = sceneContext;
    if (_sc && (_sc.location || _sc.momento || _sc.intimita || _sc.statoScena || _sc.vestizione)) {
      const _scLines = [];
      if (_sc.location) _scLines.push(`📍 LOCATION: ${_sc.location}`);
      if (_sc.momento) _scLines.push(`🕐 MOMENTO: ${_sc.momento}`);
      if (_sc.intimita) _scLines.push(`💑 LIVELLO INTIMITÀ: ${_sc.intimita}`);
      // statoScena + vestizione NON qui: vivono una sola volta nel final input (buildClothingConstraint), posizione più efficace
      out.push(`⚠️ DOVE SIAMO ORA (leggi prima di scrivere la scena erotica):\n${_scLines.join("\n")}`);
    }
    // lastGlobal nel blocco comune porta già l'ultimo episodio intero (racconto o intimo)
    // Intimo lo usa direttamente come contesto per continuare o agganciarsi

    // Regola presenza
    const povProtag = _nadiaLiam.find(p=>p.nome===activePOV) || _nadiaLiam[0];
    const altroProtag = _nadiaLiam.find(p=>p.nome!==povProtag?.nome);
    if (presenza === "entrambi") {
      out.push(`⚠️ REGOLA PRESENZA: Entrambi ${_nadiaLiam.map(p=>p.nome).join(" e ")} sono presenti fisicamente. Scrivi scena erotica esplicita di coppia.`);
    } else {
      const soloPresente = povProtag?.nome || "il personaggio";
      const nonPresente = altroProtag?.nome || "";
      out.push(`⚠️ REGOLA PRESENZA ASSOLUTA: Solo ${soloPresente} è presente fisicamente. ${nonPresente ? nonPresente + " NON c'è. " : ""}Scrivi ESCLUSIVAMENTE fantasia erotica esplicita in prima persona POV. NON scrivere racconto normale. NON proporre opzioni narrative. Tutto deve essere erotico.`);
    }

    const regoleAss = codexItems.filter(i=>i.type==="regola"&&i.assoluta===true);
    if (regoleAss.length>0) out.push(`REGOLE ASSOLUTE:\n${regoleAss.map(r=>`• ${r.descrizione||r.nome}`).join("\n")}`);
    out.push(`Ep. corrente: ${episodeCounters["global_narrative"]||0}. Prossimo: ${(episodeCounters["global_narrative"]||0)+1}.`);
    return out;
  };

  // Costruisce i blocchi di contesto specifici per la sezione RACCONTO.
  // Schede complete di protagonisti, sceneContext, raccordo intimo,
  // regola presenza, momenti chiave, stile, regole assolute, codex filtrato.
  const buildRaccontoContextBlock = (lastIntimo, lastGlobal, codexCtx, selectedMoments) => {
    const out = [];
    // SceneContext -- location, momento e stato attuale della scena
    const _scR = sceneContext;
    if (_scR && (_scR.location || _scR.momento || _scR.intimita || _scR.statoScena || _scR.vestizione)) {
      const _scRLines = [];
      if (_scR.location) _scRLines.push(`📍 LOCATION: ${_scR.location}`);
      if (_scR.momento) _scRLines.push(`🕐 MOMENTO: ${_scR.momento}`);
      if (_scR.intimita) _scRLines.push(`💑 LIVELLO INTIMITÀ: ${_scR.intimita}`);
      // statoScena + vestizione NON qui: vivono una sola volta nel final input (buildClothingConstraint), posizione più efficace
      out.push(`⚠️ DOVE SIAMO ORA (rispetta questo contesto in ogni episodio):\n${_scRLines.join("\n")}`);
    }
    // Schede complete di Nadia e Liam -- stessa logica di intimo
    const protagonistiRac = codexItems.filter(i=>i.type==="personaggio"&&(i.protagonista||i.fiammaGemella)&&i.nome&&!i.futura);
    if (protagonistiRac.length > 0) {
      const profiloCompletoRac = (p) => {
        const parts = [`Nome: ${p.nome}`];
        if (p.protagonista) parts.push("Ruolo: PROTAGONISTA");
        if (p.fiammaGemella) parts.push("Ruolo: FIAMMA GEMELLA");
        if (p.eta) parts.push(`Età: ${p.eta}`);
        if (p.aspetto) parts.push(`Aspetto: ${p.aspetto}`);
        if (p.carattere) parts.push(`Carattere: ${p.carattere}`);
        if (p.paure) parts.push(`Paure: ${p.paure}`);
        if (p.coseDaSapere) parts.push(`Note importanti: ${p.coseDaSapere}`);
        if (p.ruolo) parts.push(`Ruolo narrativo: ${p.ruolo}`);
        if (p.altreCoseImportanti) parts.push(`Altro: ${p.altreCoseImportanti}`);
        return parts.join("\n");
      };
      out.push(`👤 SCHEDE PERSONAGGI (rispetta carattere e dinamica in ogni riga):\n${protagonistiRac.map(profiloCompletoRac).join("\n\n---\n\n")}`);
    }
    // Raccordo dall'ultimo intimo - episodio completo (stessa logica simmetrica di intimo)
    if (lastIntimo?.content) {
      const _intimoCompleto = (lastIntimo.content || '').trim();
      if (lastGlobal?.section === 'intimo') {
        // FIX 7a: lastGlobal sta già passando l'episodio intimo come "ULTIMO EPISODIO"
        // sopra. Non duplichiamo ~2800 char di testo identico. Manteniamo solo
        // l'istruzione che è il valore aggiunto vero di questo blocco.
        // FIX LENZUOLO: passare a racconto durante un intimo NON significa scrivere
        // racconto "meno esplicito" sulla stessa scena. Significa INTERROMPERE la scena
        // con un evento esterno o interno, poi proseguire la giornata in modo narrativo.
        out.push(`⚠️ RACCORDO: l'ultimo episodio è una scena intima in corso. Continua narrativamente da dove si trovano i personaggi. La scena prosegue ma in modalità racconto (non erotica). Rispetta la location precisa e i personaggi presenti.`);
      } else {
        // C'e' un intimo precedente ma non e' l'ultimo: raccordo di supporto
        out.push(`EPISODIO INTIMO PRECEDENTE (raccordo di contesto):\n${_intimoCompleto}\nUsa questo solo per coerenza narrativa.`);
      }
    }
    // Regola presenza per racconto
    out.push(`⚠️ REGOLA PRESENZA: Sono in scena SOLO i personaggi dell'ultimo episodio. Non introdurre altri personaggi se non esplicitamente richiesti dall'utente.`);
    if (selectedMoments.length>0) out.push(`MOMENTI CHIAVE:\n${selectedMoments.map(m=>`-- ${m.priority==="alta"?"⭐ ":""}${m.title}: ${m.content}`).join("\n")}`);
    if (globalStyle) out.push(`STILE: ${globalStyle}`);
    const regoleAss = codexItems.filter(i=>i.type==="regola"&&i.assoluta===true);
    if (regoleAss.length>0) out.push(`REGOLE ASSOLUTE (rispetta sempre):\n${regoleAss.map(r=>`• ${r.descrizione||r.nome}`).join("\n")}`);
    if (codexCtx) out.push(`CODEX:\n${codexCtx}`);
    out.push(`Ep. corrente: ${episodeCounters["global_narrative"]||0}. Prossimo: ${(episodeCounters["global_narrative"]||0)+1}.`);
    return out;
  };

  // Costruisce i blocchi di contesto specifici per la sezione STYLIST.
  // Schede protagonisti focus styling, contesto temporale, schema canon,
  // scena di riferimento solo se richiesta esplicitamente.
  const buildStylistContextBlock = (_chats, inputUtente) => {
    const out = [];
    // STYLIST -- trattamento speciale: solo prompt + dati fisici/carattere + schema blocchi canon
    const _styPerss = codexItems.filter(i => !i.futura && i.type === "personaggio" && (i.protagonista || i.fiammaGemella));
    if (_styPerss.length > 0) {
      const _stySchede = _styPerss.map(p => {
        const pp = [`• ${p.nome} [PROTAGONISTA]`];
        if (p.carattere) pp.push(`  Essenza: ${p.carattere.slice(0,3000)}`);
        if (p.aspetto) pp.push(`  Aspetto: ${p.aspetto}`);
        if (p.misure) pp.push(`  Misure: ${p.misure}`);
        return pp.join("\n");
      });
      out.push(`👤 PERSONAGGI -- DATI FISICI E CARATTERE PER STYLING:\n${_stySchede.join("\n\n")}`);
    }
    out.push(`📅 ANNO E CONTESTO: 2014, Londra. Moda londinese giovane e audace.

📦 SCHEMA BLOCCO CANON PER STYLIST:
Quando proponi un outfit definitivo, usa questo formato esatto:
━━━━━━━━━━━━━━━━━━━━━━━━━━━
📦 BLOCCO CANON #N [CANON:nome-outfit]
Titolo: descrizione breve
━━━━━━━━━━━━━━━━━━━━━━━━━━━
[descrizione outfit completa]
━━━━━━━━━━━━━━━━━━━━━━━━━━━
✅ Richiamabile con: "Blocco Canon #N" oppure "[CANON:nome-outfit]"
━━━━━━━━━━━━━━━━━━━━━━━━━━━

📌 REGOLE BLOCCO CANON:
- Ogni outfit = 1 blocco canon separato
- Il tag [CANON:nome-outfit] è OBBLIGATORIO per il richiamo
- Nome breve senza spazi, usa trattini: liam-pub, nadia-serata, ecc.
- NON mettere più outfit nello stesso blocco`);
    // Episodio solo se richiesto dall'utente
    const _inputLowerSty = (inputUtente||"").toLowerCase();
    const _wantsEpisode = /ultimo\s+episodio|scena\s+attuale|questa\s+scena|per\s+la\s+scena|episodio\s+\d|continua|prosegui/i.test(_inputLowerSty);
    if (_wantsEpisode) {
      const _styEp = getLastEpisodeOfSection(_chats,'racconto');
      if (_styEp?.content) out.push(`SCENA DI RIFERIMENTO:\n${_styEp.content.slice(0,800)}`);
    }
    return out;
  };

  // Costruisce i blocchi di contesto specifici per la sezione LAB.
  // Codex completo (NON filtrato), momenti chiave, stile, riassunto,
  // ultimo episodio narrativo come aggancio.
  // NOTA: le due righe con stringa template malformata (RIASSUNTO STORIA
  // e ULTIMO EPISODIO) sono mantenute identiche all'originale come da
  // policy di refactoring puro. Bug documentato n.1 da fixare in step futuro.
  const buildLabContextBlock = (_chats, summaryText, codexCtx, selectedMoments) => {
    const out = [];
    // Lab riceve il codex completo
    if (codexCtx) out.push(`CODEX:\n${codexCtx}`);
    if (selectedMoments.length>0) out.push(`MOMENTI CHIAVE:\n${selectedMoments.map(m=>`-- ${m.priority==="alta"?"⭐ ":""}${m.title}: ${m.content}`).join("\n")}`);
    if (globalStyle) out.push(`STILE: ${globalStyle}`);
    // Lab e Stylist ricevono SEMPRE il codex completo -- non filtrato
    const allPerss = codexItems.filter(i => !i.futura && i.type === "personaggio");
    const allRels = codexItems.filter(i => !i.futura && i.type === "relazione");
      const labCodexParts = [];
    if (allPerss.length > 0) {
      labCodexParts.push("PERSONAGGI (schede complete -- NON inventare relazioni non presenti qui):\n" +
        allPerss.map(p => {
          const pp = [`• ${p.nome}${p.protagonista?" [PROTAGONISTA]":p.fiammaGemella?" [FG]":""}`];
          if (p.carattere) pp.push(`  Carattere: ${p.carattere}`);
          if (p.coseDaSapere) pp.push(`  Note: ${p.coseDaSapere}`);
          return pp.join("\n");
        }).join("\n"));
    }
    if (allRels.length > 0) {
      labCodexParts.push("RELAZIONI (usa SOLO queste -- MAI inventare relazioni non presenti):\n" +
        allRels.map(r => {
          const a = r.personaggioA === "__custom__" ? r.personaggioACustom : r.personaggioA;
          const b = r.personaggioB === "__custom__" ? r.personaggioBCustom : r.personaggioB;
          return `• ${a} ↔ ${b} [${r.tipoRelazione}]: ${r.descrizione||""}`;
        }).join("\n"));
    }
    if (labCodexParts.length > 0) out.push(`⚠️ CODEX COMPLETO (leggi TUTTO prima di generare -- rispetta relazioni e schede):\n${labCodexParts.join("\n\n")}`);
    // Riassunto storia per Lab -- contesto narrativo completo
    if (summaryText) out.push(`⚠️ RIASSUNTO STORIA (leggi per coerenza narrativa):\n${summaryText}`);
    // Ultimo episodio completo (racconto o intimo -- il più recente) per aggancio narrativo
    const ultimoEpRac = getLastEpisodeOfSection(_chats,'racconto');
    const ultimoEpInt = getLastEpisodeOfSection(_chats,'intimo');
    const ultimoEpLab = [ultimoEpRac, ultimoEpInt].filter(Boolean).sort((a,b) => (b.timestamp||0) - (a.timestamp||0))[0];
    if (ultimoEpLab?.content) out.push(`⚠️ ULTIMO EPISODIO (${ultimoEpLab.section?.toUpperCase()||'RACCONTO'}) -- AGGANCIO:\n${ultimoEpLab.content}`);
    return out;
  };

  const buildFullContext = (section, messages, presenza, inputUtente) => {
    const _chats = chatsRef.current;
    const lastGlobal = getLastNarrativeEpisode(_chats);
    const lastRacconto = getLastEpisodeOfSection(_chats, 'racconto');
    const lastIntimo = getLastEpisodeOfSection(_chats, 'intimo');
    const {text:summaryText} = compressSummaryIfNeeded(summaryCompact);

    // Codex -- personaggi e regole
    const recentText = (messages||[]).slice(-CHAT_HISTORY_MSGS).map(m=>m.content).join(" ");
    const codexRecentText = section === "intimo"
      ? getMessagesBySection(_chats,'racconto').slice(-3).map(m=>m.content).join(" ")
      : recentText;
    const codexCtx = buildCodexContext(codexRecentText, inputUtente);
    const selectedMoments = selectKeyMoments(keyMoments, codexRecentText);
    setLastOptStats({ originalItems:codexItems.length, sentItems:getRelevantCodexItems(codexItems,codexRecentText).length, summaryCompressed:false, momentsSent:selectedMoments.length, totalMoments:keyMoments.length });

    const blocks = [];

    // Cronologia FG -- blocco fisso SEMPRE completo, mai filtrata, mai troncata
    if (section === 'racconto' || section === 'intimo') {
      const fgItems = codexItems.filter(i => i.type === "fiammegemelle" && i.eventi?.length > 0);
      if (fgItems.length > 0) {
        const tuttiEventi = fgItems.flatMap(fg => fg.eventi||[]).sort((a,b) => a.createdAt - b.createdAt);
        blocks.push(`🔥 CRONOLOGIA FIAMME GEMELLE (Nadia ↔ Liam) -- LEGGI SEMPRE, MAI IGNORARE:\nOgni evento qui registrato è canonico e deve essere rispettato in ogni episodio.\n${tuttiEventi.map(e=>`[${e.periodo||"?"}] ${e.categoria?.toUpperCase()||"EVENTO"}: ${e.evento}${e.impatto?` -- ${e.impatto}`:""}`).join("\n")}`);
      }
    }

    // Canon Ledger -- mappa relazionale canonica (deterministica), sempre in alto
    if (section === 'racconto' || section === 'intimo') {
      const ledger = buildCanonLedger(codexItems);
      if (ledger) blocks.push(ledger);
    }

    if (section !== 'stylist') {
      if (summaryText) blocks.push(`⭐ RIASSUNTO STORIA:\n${summaryText}`);
      if (lastGlobal?.content) {
        const _lgTagIdx = lastGlobal.content.indexOf('[FINE EPISODIO]');
        const _lgContent = _lgTagIdx > 0
          ? lastGlobal.content.slice(0, _lgTagIdx).trim()
          : lastGlobal.content.trim();
        blocks.push(`ULTIMO EPISODIO (${(lastGlobal.section||'racconto').toUpperCase()}, Ep.${episodeCounters["global_narrative"]||0}):\n${_lgContent}`);
      }
    }

    // OUTFIT ATTIVO -- se c'è un canon stylist pinnato, lo ricorda sempre nel contesto
    if ((section === 'racconto' || section === 'intimo') && selectedCanonsRef.current.length > 0) {
      const outfitCanons = selectedCanonsRef.current.filter(c => c.source === 'stylist' && c.text && c.text.length > 20);
      if (outfitCanons.length > 0) {
        const outfitTxt = outfitCanons.map(c => `👗 OUTFIT ATTIVO (${c.label}) -- I PERSONAGGI INDOSSANO QUESTO IN TUTTI GLI EPISODI FINCHÉ RIMANE PINNATO:\n${c.text}`).join("\n\n");
        blocks.push(outfitTxt);
      }
    }

    if (section === 'intimo') {
      blocks.push(...buildIntimoContextBlock(presenza));
    } else if (section === 'racconto') {
      blocks.push(...buildRaccontoContextBlock(lastIntimo, lastGlobal, codexCtx, selectedMoments));
    } else if (section === 'stylist') {
      blocks.push(...buildStylistContextBlock(_chats, inputUtente));
    } else if (section === 'lab') {
      blocks.push(...buildLabContextBlock(_chats, summaryText, codexCtx, selectedMoments));
    }

    const crossCtx = section !== 'stylist' ? buildCrossSectionContext(_chats, section) : null;
    if (crossCtx) {
      const ci = {racconto:"Contesto altre sezioni:",intimo:"Contesto altre sezioni:",stylist:"Contesto narrativo:",lab:"Contesto storia:"};
      blocks.push(`${ci[section]||"ALTRE SEZIONI:"}\n${crossCtx.substring(0,CROSS_MAX_CHARS)}`);
    }

    return blocks.join("\n\n---\n\n");
  };;

  // Genera micro-riassunto per Context Brief (iniezione di stile al cambio modello)
  const generateContextBrief = async (messages) => {
    const recent = messages.slice(-10).filter(m => m.role === "assistant").map(m => m.content).join("\n\n---\n\n");
    if (!recent) return "";
    try {
      const response = await fetch(`${apiBase}/chat/completions`, {
        method:"POST", headers:{"Content-Type":"application/json","Authorization":`Bearer ${apiKey}`},
        body: JSON.stringify({ model:"anthropic/claude-haiku-4-5", messages:[{role:"user",content:`Fai un micro-riassunto di massimo 150 parole degli eventi narrativi qui sotto. Solo fatti, niente commenti.\n\n${recent}`}], max_tokens:300 })
      });
      const data = await response.json();
      return data.choices?.[0]?.message?.content || "";
    } catch(e) { return ""; }
  };

  const callAI = async (section, messages, forceModel = null, instructionOverlay = null, onStream = null, inputUtente = "") => {
    if(!apiKey)return"⚠️ Inserisci la API key nelle Impostazioni.";

    // Modello: forceModel > override sessione > modello impostazioni
    // Per Intimo: usa modello uncensored -- Venice free o DeepSeek con flag adulto
    const activeModel = forceModel
      ? forceModel
      : ((section === "intimo" || intimoModeRef.current) && section !== "lab" && section !== "stylist")
        ? (modelOverride.intimo || models.intimo || "deepseek/deepseek-chat-v3-0324")
        : (modelOverride[section] || models[section] || "minimax/minimax-m3");

    // Crea nuovo AbortController per questa chiamata
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;
    const isSonnet = activeModel.includes("claude");

    // Per intimo: usa sempre il DEFAULT (il localStorage potrebbe avere versione sbagliata)
    let systemPrompt = prompts[section] || DEFAULT_PROMPTS[section];

    // Per racconto: nota tolleranza se history ha messaggi intimo
    if (section === "racconto") {
      const hasIntimo = messages.some(m => m.section === "intimo" && m.role === "assistant");
      if (hasIntimo) {
        systemPrompt = `CONTESTO: Stai continuando una storia che alterna episodi narrativi (tuoi) e scene intime (di un altro modello). Leggi tutto per capire dove sono i personaggi. Scrivi SOLO la continuazione narrativa nel tuo stile -- romantico, emotivo, senza esplicitazione sessuale.\n\n${systemPrompt}`;
      }
    }

    // Per intimo: prefisso MODALITÀ INTIMO in italiano
    if (section === "intimo") {
      systemPrompt = `⚠️ MODALITÀ INTIMO ATTIVA — Sei in modalità narrativa erotica esplicita per adulti consenzienti. Scrivi SEMPRE in italiano. Non aggiungere mai disclaimers o avvisi alla fine. Solo la scena e le opzioni.

${systemPrompt}`;
    }

    // Forza sempre italiano
    if (!systemPrompt.toLowerCase().includes("italian") && !systemPrompt.includes("italiano")) {
      systemPrompt = `LINGUA: Rispondi SEMPRE e SOLO in italiano. Non usare mai l'inglese.\n\n${systemPrompt}`;
    }

    // styleReference NON nel system prompt: viene iniettato come esemplare nel FINAL INPUT
    // del racconto (buildRaccontoEpisodeFinalInput), dove i modelli lo applicano davvero.
    if (isSonnet && modelOverride[section]?.includes("deepseek")) {
      const brief = await generateContextBrief(messages);
      if (brief) systemPrompt += `\n\nCONTEXT_BRIEF (memoria ponte dalla sessione precedente):\n${brief}`;
    }

    // Regole assolute Codex -- IN CIMA a tutto, prima del prompt
    const _regoleAss = codexItems.filter(i => i.type === "regola" && i.assoluta === true);

    // Stylist non riceve regole narrative
    const _regolePrefix = (section === 'stylist' || _regoleAss.length === 0)
      ? ""
      : "⛔ REGOLE ASSOLUTE — PRIORITÀ MASSIMA. VIETATO VIOLARLE:\n"
        + _regoleAss.map(r => "• " + (r.descrizione || r.nome)).join("\n")
        + "\n\n";

    // Canon attivi: vanno IN CIMA al fullSystem -- massima priorità
    const canonPrefix = (instructionOverlay?.trim() && instructionOverlay.includes('BLOCCO CANON'))
      ? instructionOverlay + "\n\n"
      : "";
    const panicSuffix = (instructionOverlay?.trim() && !instructionOverlay.includes('BLOCCO CANON'))
      ? `\n\n${instructionOverlay}`
      : "";
    if (panicSuffix) systemPrompt += panicSuffix;

    const _presenza = (section === "intimo" || intimoModeRef.current) ? intimoPresenza : undefined;
    const fullSystem = _regolePrefix + canonPrefix + `${systemPrompt}\n\n${buildFullContext(section, messages, _presenza, inputUtente)}`;
    // Racconto: no messaggi intimo nella history, max 6
    // Intimo: SOLO messaggi con section intimo + msg utente corrente
    // Il contesto racconto arriva via buildFullContext - non nella history
    const lastUserMsg = messages.filter(m => m.role === "user").slice(-1)[0];
    // Stessa logica simmetrica per entrambe le sezioni:
    // intimo:   ultimi 3 ep intimo + ultimo utente (racconto completo nel system prompt)
    // racconto: ultimi 3 ep racconto + ultimo ep intimo + ultimo utente
    const intimoHistory = messages.filter(m => m.section === "intimo" && m.role === "assistant").slice(-3);
    const raccontoHistory = messages.filter(m => m.section !== "intimo" && m.role === "assistant").slice(-3);
    const lastIntimoMsg = messages.filter(m => m.section === "intimo" && m.role === "assistant").slice(-1)[0];
    const historyFilter = section === "racconto"
      ? [...raccontoHistory, ...(lastIntimoMsg ? [lastIntimoMsg] : []), ...(lastUserMsg ? [lastUserMsg] : [])].sort((a,b) => (a.timestamp||0)-(b.timestamp||0))
      : section === "intimo"
        ? (lastUserMsg ? [...intimoHistory, lastUserMsg] : intimoHistory)
        : messages;
    const historyMessages = historyFilter.slice(-CHAT_HISTORY_MSGS);
    const cleanMessages = historyMessages.map(m => {
      if(m.attachments && m.attachments.length>0){
        const content=[{type:"text",text:m.content||""}];
        m.attachments.forEach(a=>{if(a.type.startsWith("image/"))content.push({type:"image_url",image_url:{url:a.data}});});
        return{role:m.role,content};
      }
      return{role:m.role,content:m.content};
    });

    try {
      const isIntimo = section === "intimo" || intimoModeRef.current;
      const _temp = (section === "intimo" || intimoModeRef.current) ? 0.95 : (section === "stylist" ? 0.9 : 0.82);
      const bodyObj = {model:activeModel, messages:[{role:"system",content:fullSystem},...cleanMessages], max_tokens: section === 'stylist' ? 6000 : section === 'racconto' ? 4096 : section === 'intimo' ? 4096 : 3000, temperature:_temp, stream: !!onStream};
      const response = await fetch(`${apiBase}/chat/completions`, {
        method:"POST",
        headers:{
          "Content-Type":"application/json",
          "Authorization":`Bearer ${apiKey}`,
          "HTTP-Referer":window.location.href,
          "X-Title":"La Nostra Storia",
        },
        body: JSON.stringify(bodyObj),
        signal
      });

      if (!response.ok) {
        const err = await response.json();
        // Fallback automatico: Venice → DeepSeek se server error
        if (activeModel.includes("venice") && (response.status === 429 || response.status >= 500)) {
          const fallbackModel = "deepseek/deepseek-chat-v3-0324";
          const fallbackResp = await fetch(`${apiBase}/chat/completions`, {
            method:"POST",
            headers:{"Content-Type":"application/json","Authorization":`Bearer ${apiKey}`,"HTTP-Referer":window.location.href,"X-Title":"La Nostra Storia"},
            body: JSON.stringify({model:fallbackModel, messages:[{role:"system",content:fullSystem},...cleanMessages], max_tokens:2000, temperature:0.85, stream: !!onStream}),
            signal
          });
          if (fallbackResp.ok) {
            if (onStream && fallbackResp.body) {
              const reader = fallbackResp.body.getReader();
              const decoder = new TextDecoder();
              let fullText = "";
              let buffer = "";
              let fbDone = false;
              while (!fbDone) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split("\n");
                buffer = lines.pop() || ""; // conserva l'ultima riga: può essere spezzata tra due chunk
                for (const line of lines) {
                  if (!line.startsWith("data: ")) continue;
                  const data = line.slice(6).trim();
                  if (data === "[DONE]") { fbDone = true; break; }
                  try { const delta = JSON.parse(data).choices?.[0]?.delta?.content||""; if(delta){fullText+=delta;onStream(fullText);} } catch {}
                }
              }
              return fullText;
            }
            const fallbackData = await fallbackResp.json();
            return fallbackData.choices?.[0]?.message?.content || "Nessuna risposta.";
          }
        }
        return `❌ Errore: ${err.error?.message || response.statusText}`;
      }

      // STREAMING
      if (onStream && response.body) {
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let fullText = "";
        let streamDone = false;
        let lastSaveTime = Date.now();
        let buffer = "";
        let _finishLength = false;
        try {
          while (!streamDone) {
            const { done, value } = await reader.read();
            if (done) { streamDone = true; break; }
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split("\n");
            buffer = lines.pop() || ""; // conserva l'ultima riga: può essere spezzata tra due chunk
            for (const line of lines) {
              if (!line.startsWith("data: ")) continue;
              const data = line.slice(6).trim();
              if (data === "[DONE]") { streamDone = true; break; }
              try {
                const parsed = JSON.parse(data);
                const delta = parsed.choices?.[0]?.delta?.content || "";
                if (parsed.choices?.[0]?.finish_reason === "length") _finishLength = true;
                if (delta) {
                  fullText += delta;
                  onStream(fullText);
                  // Salva progressivamente ogni 20s (recupero se app va in background)
                  if (Date.now() - lastSaveTime > 20000 && fullText.length > 100) {
                    try { localStorage.setItem("stream_backup", fullText); lastSaveTime = Date.now(); } catch(e) {}
                  }
                }
              } catch {}
            }
          }
        } catch(streamErr) {
          // Stream interrotto su mobile (standby) - testo gia' nel ref
          if (fullText.length > 50) onStream(fullText);
        } finally {
          try { localStorage.removeItem("stream_backup"); } catch(e) {}
        }

        // Auto-continuazione: se l'episodio è stato tagliato dal limite di lunghezza, completalo
        // automaticamente (1 chiamata) invece di lasciarlo troncato.
        if (_finishLength && (section === "racconto" || section === "intimo" || intimoModeRef.current) && fullText.length > 200) {
          try {
            showAutoUpdateToast("✍️ Completo l'episodio troncato...");
            const contResp = await fetch(`${apiBase}/chat/completions`, {
              method: "POST",
              headers: { "Content-Type":"application/json","Authorization":`Bearer ${apiKey}`,"HTTP-Referer":window.location.href,"X-Title":"La Nostra Storia" },
              body: JSON.stringify({
                model: activeModel,
                messages: [
                  { role:"system", content: fullSystem },
                  ...cleanMessages,
                  { role:"assistant", content: fullText },
                  { role:"user", content: "Continua ESATTAMENTE da dove ti sei fermato, senza ripetere nulla di già scritto e senza reintestazioni (niente '**Episodio**' o '**POV**'). Completa la scena e chiudi con il marcatore [FINE EPISODIO] su una riga, seguito dalle 3-5 opzioni numerate." }
                ],
                max_tokens: 1500, temperature: _temp, stream: false
              })
            });
            const contData = await contResp.json();
            const cont = contData.choices?.[0]?.message?.content || "";
            if (cont && cont.length > 20) { fullText += (fullText.endsWith("\n") ? "" : "\n") + cont; onStream(fullText); }
          } catch (e) { /* se fallisce, resta il testo parziale */ }
        }
        return fullText;
      }

      // NON-STREAMING fallback
      const data = await response.json();
      if(data.error) return `❌ Errore: ${data.error.message}`;
      const result = data.choices?.[0]?.message?.content || "Nessuna risposta.";
      return result;
    } catch(err){
      if (err.name === "AbortError") return null; // Interrotto dall'utente
      return `❌ Errore connessione: ${err.message}`;
    }
  };

  // Genera bridge intimo manualmente

  // ════════════════════════════════════════════════════════════════
  // BUILDER DI FINALINPUT PER TIPO DI RICHIESTA
  // Estratti da sendMessage per isolare la costruzione del finalInput
  // per ogni branch logico. Ogni builder è una funzione pura: stesso
  // input → stessa stringa. Lo state React (codexItems, prompts,
  // selectedCanons, intimoPresenza, summaryCompact, episodeCounters)
  // è letto dalla closure di StoryApp.
  // ════════════════════════════════════════════════════════════════

  // ── LAB / STYLIST ──────────────────────────────────────────────

  // Stylist: aggiunge obbligo 2 righe contesto + istruzione output
  const buildStylistFinalInput = (inputEffettivo) => {
    const hasCanonCtx = selectedCanons.length > 0;
    const hasEpReq = /episodio|scena|ultimo|ep\.\s*\d/i.test(inputEffettivo);
    const contestoObbligo = (hasCanonCtx || hasEpReq)
      ? "\n\nOBBLIGATORIO PRIMA DELL'OUTFIT: scrivi 2 righe che indicano da cosa stai partendo (canon Lab pinnato o episodio di riferimento, location, momento narrativo). Es: \"Sto creando l'outfit per [scena/canon]. Location: [dove]. Momento: [cosa sta succedendo].\" NON saltare queste 2 righe."
      : "";
    const sty = "\n\nISTRUZIONE: Crea outfit completo con tutti i campi richiesti dal tuo ruolo (descrizione, capelli, make-up, accessori, palette, perche funziona, effetto su chi guarda). Chiudi con il BLOCCO CANON nel formato del tuo sistema con [CANON:parola-chiave] e Titolo.";
    return inputEffettivo + contestoObbligo + sty;
  };

  // Lab: dividi un canon in N mini-canon separati
  const buildDividiEpisodiFinalInput = (inputEffettivo) => {
    const nEpMatch = inputEffettivo.match(/(\d+)\s*(episodi|parti|scene|mini.?canon)/i);
    const nEp = nEpMatch ? parseInt(nEpMatch[1]) : 3;
    return inputEffettivo + `\n\n⚠️ ISTRUZIONE TECNICA OBBLIGATORIA: Dividi la scena/canon in ${nEp} MINI-CANON SEPARATI, uno per ogni momento/episodio distinto. Ogni mini-canon deve essere autonomo e pinnabile singolarmente su Racconto. Ogni blocco ha il suo tag [CANON:parola-chiave-episodio-N] con N = numero progressivo. I mini-canon devono essere in sequenza narrativa logica: il primo è il momento iniziale, l'ultimo è la conclusione dell'arco. L'utente li pinnerà uno alla volta su Racconto per sviluppare ogni episodio separatamente.`;
  };

  // Lab: genera N blocchi canon separati o proposte
  const buildGeneraBloccoFinalInput = (inputEffettivo) => {
    const nBlocksMatch = inputEffettivo.match(/(\d+)\s*(blocch|canon|opzion|scen|proposte|outfit)/i);
    const nBlocks = nBlocksMatch ? parseInt(nBlocksMatch[1]) : null;
    const nStr = nBlocks && nBlocks > 1
      ? `\n\n⚠️ ISTRUZIONE TECNICA OBBLIGATORIA: genera ${nBlocks} BLOCCHI CANON SEPARATI -- uno per ogni proposta. Ogni blocco ha il suo tag [CANON:parola-chiave-univoca]. NON mettere più opzioni in un unico blocco.`
      : `\n\n⚠️ ISTRUZIONE TECNICA OBBLIGATORIA: genera UN BLOCCO CANON SEPARATO per ogni proposta/scena distinta. Se hai 3 proposte → 3 blocchi separati con tag diversi. MAI mettere opzioni dentro un unico blocco.`;
    return inputEffettivo + nStr;
  };

  // ── RACCONTO/INTIMO: branch speciali ───────────────────────────

  // Helper condiviso: override "no orgasmo" attivo se l'utente non l'ha chiesto.
  // Usato in TUTTI i builder di episodio intimo (episodio diretto, scelta opzione,
  // mix opzioni). Senza questa copertura uniforme, scegliere "ok 2" o "1 con 2"
  // bypassa l'override e il modello scrive l'orgasmo per inerzia.
  const _buildOverrideNoOrgasmo = (inputEffettivo, isIntimo) => {
    if (!isIntimo) return "";
    const _inputLower = (inputEffettivo || "").toLowerCase();
    // Solo costrutti INEQUIVOCI di richiesta del culmine. Niente alternative generiche
    // (vieni/viene/chiudi/concludi/finisci/culmina) che fanno falsi positivi su comandi comuni
    // ("vieni qui", "chiudi la porta") e disattiverebbero l'override. In dubbio: override ATTIVO.
    const _orgasmoRichiesto = /(orgasm|fall[ao]\s+(?:venire|godere|esplodere)|fai\s+godere|fa(?:lla|llo|tela)\s+venire|port[ai]?l[ao]\s+all[ao']?\s*(?:orgasmo|culmine|fine|apice)|fino\s+(?:in\s+fondo|all['ao]\s*(?:orgasmo|culmine|apice|fine))|raggiung\w*\s+l['ao]?\s*(?:orgasmo|apice|culmine)|deve\s+venire|voglio\s+che\s+veng\w+)/i.test(_inputLower);
    if (_orgasmoRichiesto) return "";
    return "⛔ REGOLA ORGASMO -- LA PIÙ IMPORTANTE DI QUESTO EPISODIO: l'utente NON ha chiesto orgasmo né chiusura della scena. È VIETATO scrivere l'orgasmo di Nadia, di Liam o di entrambi. È VIETATO concludere il rapporto. È VIETATO scrivere frasi tipo \"esplode\", \"raggiunge l'apice\", \"viene\", \"culmina\", \"si scioglie\", \"il piacere la travolge fino in fondo\". La scena DEVE finire prima del culmine, su tensione aperta e crescente. Solo l'utente decide quando l'orgasmo arriva. Se vedi che stai scrivendo \"il suo corpo esplode\", \"viene\", \"crolla esausta\" -- FERMATI e riscrivi quella parte mantenendo la tensione sospesa.\n\n";
  };

  // POV Mirror: utente forza riscrittura POV con regole esplicite
  const buildPOVMirrorFinalInput = (input) => {
    return input
      + "\n\nREGOLE ASSOLUTE AGGIUNTIVE:\n"
      + "1. Leggi il riassunto nel contesto -- usa quella location per il nuovo POV, NON inventarla.\n"
      + "2. Parallelismo momento per momento: ogni azione/emozione dell'originale ha il suo corrispondente esatto.\n"
      + "3. I ◊...◊ vengono MANDATI dal nuovo POV con intenzione. L'altro li riceve.\n"
      + "4. Il nuovo POV percepisce l'altro come sensazioni fisiche, NON come visione.\n"
      + "5. Prima persona assoluta. MAI narratore. Minimo 1800 caratteri. NON aggiungere opzioni.";
  };

  // Mix opzioni: combina più opzioni numerate dell'ultimo messaggio
  const buildMixOpzioniFinalInput = (inputEffettivo, inputLower, chat, epNum, povName, isIntimo, regoleStr, ultimoCtxOpzioni) => {
    const ultimoMsg = chat.messages.filter(m => m.role === "assistant" && !m.isBridge && (m.content?.length||0) > 100).slice(-1)[0];
    const ultimoMsgContent = ultimoMsg?.content || "";
    // Estrai tutti i numeri di opzione menzionati
    const numeriMenzionati = [...new Set((inputLower.match(/[1-5]/g) || []))];
    const testiOpzioni = numeriMenzionati.map(n => {
      const reg = new RegExp(`(?:^|\\n)\\s*(?:opzione\\s*|gancio\\s*|punto\\s*)?${n}[.):\\-]\\s*([^\\n]{10,})(?:\\n[^\\n0-9].*)*`, 'im');
      const m = ultimoMsgContent.match(reg);
      return m ? `OPZIONE ${n}:\n${m[0].trim().slice(0, 400)}` : `OPZIONE ${n}: (non trovata)`;
    }).join("\n\n");
    // Override no-orgasmo: ora in CODA al final input (posizione più efficace) e calcolato
    // anche sul testo delle opzioni di riferimento (non solo sull'input utente).
    return `[MIX OPZIONI -- ISTRUZIONI UTENTE: "${inputEffettivo}"]\n\n`
      + `Le opzioni di riferimento:\n${testiOpzioni}\n\n`
      + `ISTRUZIONI:\n`
      + `- Segui ESATTAMENTE le istruzioni dell'utente su come combinare gli elementi delle opzioni.\n`
      + `- LA PRIMA RIGA deve essere ESATTAMENTE: **Episodio N. ${epNum}**\n- LA SECONDA RIGA deve essere ESATTAMENTE: **POV: ${povName}**\n`
      + `- PRIMA PERSONA ASSOLUTA. MAI narratore.\n`
      + `- ${isIntimo ? "Esplicito completo." : "Stile romantico. NON esplicito."}\n`
      + `- Lunghezza: ${isIntimo ? "1400-2200 caratteri (intimo breve e denso, lascia spazio alle opzioni COMPLETE)" : "minima 1800 caratteri, massima 4500 (non superare)"}.\n`
      + `- Termina con 3-5 nuove opzioni GANCIO.\n`
      + regoleStr + "\n"
      + "EPISODIO PRECEDENTE (contesto): " + ultimoCtxOpzioni
      + buildFinalConstraints(inputEffettivo + " " + ultimoCtxOpzioni, { orgasmoGuard: _buildOverrideNoOrgasmo(inputEffettivo + " " + testiOpzioni, isIntimo) });
  };

  // Option choice: utente sceglie un'opzione numerata, opzionalmente con variazione
  const buildOptionChoiceFinalInput = (inputLower, inputEffettivo, chat, epNum, povName, isIntimo, regoleStr, ultimoCtxOpzioni) => {
    const numMatch = inputLower.match(/[1-5]/);
    const numScelta = numMatch ? numMatch[0] : "1";
    // Estrae il testo dell'opzione scelta dall'ultimo messaggio assistant
    const ultimoMsg = chat.messages.filter(m => m.role === "assistant" && !m.isBridge && (m.content?.length||0) > 100).slice(-1)[0];
    const ultimoMsgContent = ultimoMsg?.content || "";
    // Cerca il testo dell'opzione numerata (es. "1.", "1)", "OPZIONE 1", "GANCIO 1")
    const opzioneRegex = new RegExp(
      `(?:^|\\n)\\s*(?:opzione\\s*|gancio\\s*|punto\\s*)?${numScelta}[.):\\-]\\s*([^\\n]{10,})(?:\\n[^\\n0-9].*)*`,
      'im'
    );
    const opzioneMatch = ultimoMsgContent.match(opzioneRegex);
    const testoOpzione = opzioneMatch
      ? opzioneMatch[0].trim().slice(0, 400)
      : `l'opzione ${numScelta} proposta nel messaggio precedente`;
    // Eventuali variazioni aggiunte dall'utente (es. "2 ma Liam è sulla sedia")
    const variazioneUtente = inputEffettivo.replace(/^[^a-zA-Z0-9àèéìòù]*/,'').replace(/^\d+\s*/,'').replace(/^(ok|okay|sì|si|certo|vai|bene|perfetto|sviluppa|scrivi|esegui|scegli|opzione|scelta|punto|gancio)\s*/i,'').replace(/^[1-5][.)\s]*/,'').trim();
    // Override no-orgasmo: in CODA al final input, calcolato su input + variazione + testo dell'opzione scelta.
    return `[OPZIONE SCELTA: ${numScelta}]\n`
      + `Il contenuto dell'opzione scelta è:\n`
      + `"${testoOpzione}"\n`
      + (variazioneUtente ? `\nVARIAZIONE UTENTE (applica esattamente): "${variazioneUtente}"\n` : "")
      + `\nISTRUZIONI:\n`
      + `- LA PRIMA RIGA deve essere ESATTAMENTE: **Episodio N. ${epNum}**\n`
      + `- LA SECONDA RIGA deve essere ESATTAMENTE: **POV: ${povName}**\n`
      + `- Dalla TERZA RIGA inizia DIRETTAMENTE la narrativa in prima persona. ZERO intestazioni, ZERO titoli, ZERO ricopiatura del testo dell'opzione.\n`
      + `- PRIMA PERSONA ASSOLUTA. MAI terza persona. MAI riscrivere o riassumere il testo dell'opzione.\n`
      + `- Sviluppa la scena dall'interno come se stesse accadendo adesso. Parti dal momento esatto descritto nell'opzione.\n`
      + `- ${isIntimo ? "Esplicito completo: descrivi atti fisici in modo diretto. Mantieni la dinamica emotiva tra i personaggi." : "Stile romantico con magnetismo fisico costante (sguardi, contatto, baci appassionati presenti): all'intimo solo il sesso esplicito, NON i baci."}\n`
      + `- Lunghezza: ${isIntimo ? "1400-2200 caratteri (intimo breve e denso, lascia spazio alle opzioni COMPLETE)" : "minima 1800 caratteri, massima 4500 (non superare anche su scelta opzione)"}.\n`
      + `- Termina con 3-5 nuove opzioni GANCIO per continuare.\n`
      + regoleStr + "\n"
      + "EPISODIO PRECEDENTE (solo per contesto): " + ultimoCtxOpzioni
      + buildFinalConstraints(inputEffettivo + " " + ultimoCtxOpzioni, { orgasmoGuard: _buildOverrideNoOrgasmo(inputEffettivo + " " + (variazioneUtente || "") + " " + testoOpzione, isIntimo) });
  };

  // Conversazionale: risposta come collaboratore, NO episodio
  const buildConversationalFinalInput = (chat, input) => {
    const ultimoEpConv = chat.messages
      .filter(m => m.role === "assistant" && !m.isBridge && (m.content?.length || 0) > 100)
      .slice(-1)[0];
    const ultimoCtxConv = ultimoEpConv?.content?.slice(-400) || "";
    const ultimoSezConv = (ultimoEpConv?.section || "racconto").toUpperCase();
    return "[MODALITA ASSISTENTE - RISPOSTA CONVERSAZIONALE]\n"
      + "L'utente ti sta parlando direttamente. Rispondi come un collaboratore creativo.\n"
      + "NON scrivere un episodio. NON usare il formato narrativo. NON proporre salti temporali.\n"
      + "Se ti chiede come continuare: proponi 2-3 GANCI che partono ESATTAMENTE dall'ultima riga "
      + "dell'episodio precedente (stesso momento, stessa location, stessi personaggi). "
      + "VIETATO proporre: giorni dopo, settimane dopo, flashforward, cambio POV, nuovi personaggi.\n"
      + "L'avanzamento temporale lo decide l'utente, non tu.\n"
      + (ultimoCtxConv ? "\nULTIMA PARTE EPISODIO " + ultimoSezConv + ":\n" + ultimoCtxConv + "\n" : "")
      + "\nMESSAGGIO UTENTE: " + input;
  };

  // ── INTIMO ─────────────────────────────────────────────────────

  // Intimo episodio: i 4 muri + 10 punti istruzioni + dinamica
  // Vincolo LOCATION ATTIVA: inserisce i vincoli fisici del luogo della scena
  // direttamente nel FINAL INPUT (ultima cosa che il modello legge prima di
  // scrivere) -- molto più efficace del solo Canon Ledger nel system prompt, che
  // modelli come MiniMax tendono a non applicare. Match deterministico sul nome
  // del luogo nel contesto/comando recente.
  const buildActiveLocationConstraint = (text) => {
    // La location autorevole è quella tracciata in sceneContext (dedotta da Haiku/riassunto):
    // entra SEMPRE nella sonda di match, così il vincolo fisico resta agganciato anche quando
    // la prosa non ripete il nome proprio del luogo ("l'attico" invece di "Attico di Liam").
    const _loc = (sceneContextRef.current?.location || sceneContext?.location || "");
    const t = (_loc + " " + (text || "")).toLowerCase();
    const _locProbe = _loc.toLowerCase();
    if (!t.trim()) return "";
    const luoghi = codexItems.filter(i => i.type === "luogo" && i.nome && !i.futura && (i.descrizione || "").trim());
    const matched = luoghi.filter(l => {
      const nome = l.nome.toLowerCase();
      if (t.includes(nome)) return true; // nome canonico presente nel testo o nella location tracciata
      // fallback prudente: token lungo (>=5 char) del nome presente SOLO nella location tracciata
      // (non nella prosa, per evitare falsi positivi tipo "casa")
      return nome.split(/\s+/).filter(w => w.length >= 5).some(w => _locProbe.includes(w));
    });
    if (matched.length === 0) return "";
    return "\n\n📍 LOCATION ATTIVA — VINCOLI FISICI DA RISPETTARE ALLA LETTERA (NON inventare nulla che li contraddica; deduci le implicazioni: es. se l'accesso è un ascensore privato/diretto in casa, NON c'è campanello/citofono e gli ospiti NON bussano da fuori, arrivano con l'ascensore). Applica i vincoli IN SILENZIO dentro la narrazione: NON scrivere MAI nel testo parentesi, commenti o spiegazioni del vincolo o del tuo ragionamento (VIETATO es. \"bussa (o sale con l'ascensore...)\"). Scrivi solo la scena, come se il vincolo fosse naturale e ovvio:\n"
      + matched.map(l => `• ${l.nome}: ${(l.descrizione || "").split("\n").map(s => s.trim()).filter(Boolean).slice(0, 6).join(" ").slice(0, 600)}`).join("\n");
  };

  // Aspetto + CARATTERE canonici dei protagonisti nel FINAL INPUT: stop a tratti
  // fisici inventati (occhi miele) e a personalità smorzata/ridimensionata.
  const buildProtagonistPhysicalConstraint = () => {
    const prot = codexItems.filter(i => i.type === "personaggio" && (i.protagonista || i.fiammaGemella) && i.nome && !i.futura && ((i.aspetto || "").trim() || (i.carattere || "").trim() || (i.presenzaIncontestabile || "").trim()));
    if (prot.length === 0) return "";
    // Sono pochissime schede (2-3): il carattere/aspetto/presenza pieni costano poco e vanno
    // DOVE il modello obbedisce (final input). Niente troncatura aggressiva come prima.
    return "\n\n👤 PROTAGONISTI — ASPETTO E CARATTERE CANONICI (rispetta ALLA LETTERA: occhi, capelli, corporatura E personalità/atteggiamento; NON inventare tratti diversi né smorzare/ridimensionare il carattere -- chi è definito sicuro/deciso lo è fin dal PRIMO istante, mai dubbioso):\n"
      + prot.map(p => {
          const parts = [];
          if ((p.aspetto || "").trim()) parts.push("aspetto: " + p.aspetto.replace(/\s+/g, " ").trim().slice(0, 320));
          if ((p.carattere || "").trim()) parts.push("carattere: " + p.carattere.replace(/\s+/g, " ").trim().slice(0, 480));
          if ((p.presenzaIncontestabile || "").trim()) parts.push("presenza/sicurezza: " + p.presenzaIncontestabile.replace(/\s+/g, " ").trim().slice(0, 240));
          return `• ${p.nome}: ${parts.join(" | ")}`;
        }).join("\n");
  };

  // Stato fisico attuale (abbigliamento + oggetti + scena) nel FINAL INPUT: la
  // vestizione/statoScena sono già tracciati in sceneContext ma stavano solo nel
  // system prompt. Qui (ultima cosa letta) garantiscono continuità di vestiti E
  // oggetti (es. tazza tolta) E posizione.
  const buildClothingConstraint = () => {
    const _sc = sceneContextRef.current || sceneContext;
    const v = (_sc?.vestizione || "").replace(/\s+/g, " ").trim();
    const s = (_sc?.statoScena || "").replace(/\s+/g, " ").trim();
    if (!v && !s) return "";
    let out = "\n\n🎬 STATO FISICO ATTUALE — CONTINUITÀ OBBLIGATORIA (rispetta ESATTAMENTE abbigliamento, oggetti in mano e posizione: un capo tolto NON ricompare, un oggetto posato/tolto (es. una tazza) NON torna in mano, NON aggiungere capi/oggetti non presenti):";
    if (v) out += "\n• Abbigliamento: " + v.slice(0, 240);
    if (s) out += "\n• Stato scena: " + s.slice(0, 200);
    return out;
  };

  // Guard di tono e carattere chiave (issue tester): intenso ma NON melodrammatico;
  // Liam sicuro fin da subito, senza dubbi/ridimensionamenti.
  const buildToneGuard = () => "\n\n🎭 REGOLE DI SCRITTURA CHIAVE (rispettale SEMPRE):\n"
    + "- CARATTERE LIAM: è SICURO di ciò che sente e vuole fin dal PRIMO istante -- la 'sentiva' da mesi/anni e quando la trova è 'finalmente ti ho trovata', NON 'vediamo come va'. VIETATE frasi di dubbio o di ridimensionamento (es. 'se non va ci abbiamo provato', 'non ti chiedo per sempre, solo per oggi'). NADIA non dubita del legame; all'inizio può solo essere spaventata perché RICONOSCE Liam nel momento in cui lo incontra.\n"
    + "- LITI/TENSIONI: Nadia e Liam restano INTENSI sempre (nel bene e nel male), MAI morbidi o edulcorati. Intenso sì, MELODRAMMATICO no.\n"
    + "- BACI: sviluppali nel concreto (labbra, lingua, respiro, gesti, reazioni del corpo), MAI riassunti con un'etichetta ('la bacio profondo/famelico/intenso').\n"
    + "- NON INVENTARE eventi o scene mai scritti: non citare/raccontare fatti passati (es. 'prima Nadia era sotto la doccia') se non esiste un episodio che li ha mostrati.\n"
    + "- NON RIPETERE informazioni già date in episodi precedenti (es. 'Liam ha già imparato X di Nadia'): ogni episodio porta qualcosa di nuovo.\n"
    + "- CONOSCENZA DEL POV: il personaggio dal cui punto di vista scrivi sa solo il PROPRIO vissuto e ciò che gli è stato detto o mostrato NEGLI EPISODI/nel riassunto. Le schede del Codex (biografia, passato, traumi, segreti dell'ALTRO personaggio) sono conoscenza d'AUTORE per la TUA coerenza, NON cose che il personaggio conosce. Se due personaggi si sono appena conosciuti, il POV NON può sapere, pensare o raccontare il passato privato dell'altro finché non gli viene rivelato esplicitamente nella storia.\n"
    + "- Scrittura CALDA e viva, mai fredda o didascalica.";

  // Tutti i vincoli del final input, da iniettare in OGNI builder (episodi+opzioni).
  // Ordine: vincoli STATICI prima (protagonisti, tono), poi quelli di SCENA (location,
  // abbigliamento) e l'override orgasmo per ULTIMI -- la cosa più vicina alla fine è
  // ciò che il modello applica meglio. Le richieste di sole OPZIONI escludono il craft
  // di prosa (toneGuard) e la continuità abbigliamento: lì sono solo rumore.
  const buildFinalConstraints = (text, opts = {}) => {
    const { isOptions = false, orgasmoGuard = "" } = opts;
    const prot = buildProtagonistPhysicalConstraint();
    const loc = buildActiveLocationConstraint(text || "");
    if (isOptions) return prot + loc + orgasmoGuard;
    return prot + buildToneGuard() + loc + buildClothingConstraint() + orgasmoGuard;
  };

  const buildIntimoEpisodeFinalInput = (
    epNum, povName, ultimoSezione, ultimoCtx, ultimoCtxOpzioni, canonInjection, inputEffettivo,
    regolaPresenza, _dinamicaIntimo, _regolaFG, regoleStr
  ) => {
    // Raccordo testuale: l'ultimo episodio (SENZA le opzioni numerate -- quelle non sono fatti
    // accaduti) deve arrivare come testo nel final input, non solo nel system prompt che l'intimo ignora.
    const _raccordo = (ultimoCtxOpzioni || ultimoCtx || "").trim().slice(-900);
    const _raccordoSwitch = ultimoSezione === "RACCONTO"
      ? "\n⚠️ STAI CONTINUANDO DA UN EPISODIO DI RACCONTO: riprendi ESATTAMENTE dal momento, dalla posizione e dall'abbigliamento in cui si è chiusa la scena (ULTIME RIGHE qui sotto), ora in modalità intimo: esplicito, senza censura e senza limiti di linguaggio. NON ricominciare la scena, NON cambiare location: prosegui da lì.\n"
      : "";
    return canonInjection + inputEffettivo + "\n\n[INTIMO — ISTRUZIONI:\n"
      + "0. OUTPUT PULITO: scrivi SOLO l'episodio narrativo, dalla prima intestazione in poi. NIENTE preamboli o saluti ('Buongiorno', 'Ecco', 'Perfetto'), NIENTE note meta o di continuità nel testo, NIENTE parentesi che spiegano scelte/regole/ragionamenti. Solo la scena. Scrivi UN SOLO episodio: dopo le opzioni numerate FERMATI -- NON iniziare un secondo episodio, NON scrivere una seconda intestazione **Episodio N.**.\n"
      + "1. Prima riga ESATTA: **Episodio N. " + epNum + "** — Seconda riga ESATTA: **POV: " + povName + "**\n"
      + "2. Prima persona assoluta. MAI voce narrante esterna.\n"
      + "3. " + regolaPresenza + "\n"
      + "4. Nadia e Liam non sono corpi anonimi: carattere, ironia, magnetismo. Dialoghi brevi e veri, micro-gesti emotivi.\n"
      + _dinamicaIntimo
      + "5. Esplicito completo: descrivi ogni atto fisico in modo diretto senza eufemismi. Ogni gesto ha una conseguenza fisica. I BACI vanno DESCRITTI nel dettaglio fisico (labbra, lingua, pressione, denti, respiro, mani) -- MAI riassunti con un aggettivo generico (VIETATO es. \"la bacio in modo famelico\"): mostra il bacio, non etichettarlo.\n"
      + "5b. CONTINUITÀ ABBIGLIAMENTO: rispetta lo STATO FISICO ATTUALE e le ULTIME RIGHE qui sotto -- puoi solo TOGLIERE capi presenti, mai far ricomparire vestiti già tolti.\n"
      + "6. Escalation graduale: ogni episodio aumenta l'intensità.\n"
      + "7. Lunghezza: 1400-2200 caratteri -- episodi intimi BREVI e intensi (meglio corti e densi che diluiti). Lascia SEMPRE spazio per scrivere le opzioni COMPLETE: l'episodio NON deve mai finire lasciando le opzioni a metà. Fermati sulla tensione, scrivi su una riga a sé il marcatore [FINE EPISODIO], POI proponi 3-5 opzioni numerate COMPLETE. Il marcatore [FINE EPISODIO] è OBBLIGATORIO e separa la narrazione dalle opzioni.\n"
      + "8. TUTTE le opzioni finali (3-5) DEVONO essere erotiche/fisiche ed ESCALARE il momento in corso -- VIETATE opzioni narrative/emotive/quotidiane, VIETATO che qualcuno arrivi/suoni/si fermi o che si cambi stanza. Ogni opzione è un modo diverso di proseguire FISICAMENTE la scena.\n"
      + _regolaFG
      + regoleStr + "\n"
      + _raccordoSwitch
      + (_raccordo ? "ULTIME RIGHE SCENA PRECEDENTE (" + ultimoSezione + "): " + _raccordo + "\n" : "")
      + buildFinalConstraints(inputEffettivo + " " + _raccordo, { orgasmoGuard: _buildOverrideNoOrgasmo(inputEffettivo, true) })
      + "\n\n🎬 POV OBBLIGATORIO: questo episodio è in PRIMA PERSONA dal punto di vista di " + povName + " (il narratore-io È " + povName + "). Anche se l'episodio precedente era dal POV di un altro, ORA il POV è " + povName + ": NON proseguire col POV precedente."
      + "]";
  };

  // Intimo opzioni: solo opzioni numerate, no episodio
  const buildIntimoOptionsFinalInput = (
    canonInjection, inputEffettivo, regolaPresenza, _regolaFG, regoleStr,
    ultimoSezione, ultimoCtxOpzioniIntimo
  ) => {
    return "⛔ LINGUA: Scrivi ESCLUSIVAMENTE in italiano. NON scrivere MAI in inglese, cinese o qualsiasi altra lingua. SOLO ITALIANO.\n\n"
      + "⛔ DIVIETO ASSOLUTO DI INVENZIONE: Le opzioni devono essere coerenti con l'episodio precedente che hai letto nel contesto. NON inventare situazioni, oggetti, luoghi o azioni impossibili rispetto a dove si trovano e cosa hanno fatto Nadia e Liam. Ogni opzione deve essere direttamente attuabile dall'ultimo momento della scena.\n\n"
      + canonInjection + inputEffettivo + "\n\n[STOP MODALITA NARRATIVA. GENERA SOLO OPZIONI EROTICHE ESPLICITE.\n" + "[OPZIONI INTIMO per Ep." + ((episodeCounters["global_narrative"] || 0) + 1) + ":\n"
      + "OBBLIGATORIO -- scrivi PRIMA delle opzioni:\n"
      + "📍 CONTESTO: [dove si trovano fisicamente -- posizione, location]\n"
      + "💬 MOMENTO: [cosa è appena successo nell'ultima riga -- una frase]\n"
      + "Queste 2 righe dimostrano che hai letto la scena. NON saltarle.\n\n"
      + "Poi scrivi 3-5 opzioni numerate che prolungano la scena -- mantengono la dinamica emotiva e non la concludono."
      + "\n⛔ ISTRUZIONE OPZIONI INTIMO (CRUCIALE): Le opzioni devono SVILUPPARE/ESCALARE il momento erotico in corso. PERMESSI: cambi di ritmo (più lento/più rapido), cambi di posizione fisica, nuove pratiche/zone del corpo, intensificazione del controllo o della resa, doppi sensi sussurrati, nuovi gesti possessivi. VIETATI ASSOLUTAMENTE: opzioni che fanno fermare/bloccare/sospendere il momento, opzioni che introducono persone o suoni esterni alla scena (rumori, chiamate, presenze terze, qualcuno che arriva), opzioni che spostano i personaggi in un'altra stanza, opzioni che cambiano il registro emotivo da erotico a quotidiano. Se senti la tentazione di proporre un'opzione che 'spezza il momento' (qualcuno arriva, suona qualcosa, Nadia si ferma, Liam si blocca) -- NON FARLO. Le opzioni intimo VANNO SOLO AVANTI. IGNORA del tutto le tensioni esterne presenti nel contesto racconto (telefono, foto, impegni, manager, terzi): nell'intimo il mondo esterno è SOSPESO, le opzioni restano solo sul piano fisico/erotico dei due.\n"
      + "⛔ REGOLA ASSOLUTA: NON ripetere identicamente situazioni o gesti già apparsi in episodi precedenti -- ogni opzione porta qualcosa di nuovo nell'escalation.\n"
      + "4. " + regolaPresenza
      + _regolaFG
      + regoleStr + "\n"
      + "ULTIME RIGHE SCENA PRECEDENTE (" + ultimoSezione + "): " + ultimoCtxOpzioniIntimo + "\n⛔ LINGUA FINALE: scrivi TUTTO in italiano. MAI inglese, cinese, greco o altre lingue."
      + buildFinalConstraints(inputEffettivo + " " + ultimoCtxOpzioniIntimo, { isOptions: true, orgasmoGuard: _buildOverrideNoOrgasmo(inputEffettivo, true) }) + "]";
  };

  // ── RACCONTO ───────────────────────────────────────────────────

  // Racconto episodio: i 4 muri + 6 punti istruzioni + tono di voce + raccordo
  const buildRaccontoEpisodeFinalInput = (
    epNum, povName, canonInjection, inputEffettivo, canonsActive,
    ultimoCtx, ultimoCtxOpzioni, ultimoSezione, hasChatBlock,
    _dinamicaRacconto, _regolaFG, regoleStr
  ) => {
    const _raccordoNote = ultimoSezione === "INTIMO"
      ? "\n\u26a0\ufe0f SEI USCITO DALL'INTIMO. Riprendi dal MOMENTO NARRATIVO REALE in cui si \u00e8 chiusa la scena intima (le ULTIME RIGHE narrative qui sotto), NON da una delle opzioni/ganci numerati proposti dopo l'episodio (sono solo possibilit\u00e0 NON accadute). "
        + "Scrivi racconto (romantico/emotivo con magnetismo, ma NON sesso esplicito). "
        + "Aggancia alla realt\u00e0 dei personaggi DOPO quell'episodio. NON ripetere scene passate. NON ignorare l'intimo precedente. "
        + "Le opzioni finali di QUESTO episodio devono essere narrative/emotive, MAI erotiche/sessuali (quelle sono dominio dell'intimo)."
      : "";
    const _chatNote = hasChatBlock
      ? "\n⚠️ CHAT MESSAGGI: L'utente ha allegato una conversazione WhatsApp reale tra i personaggi. "
        + "Usa QUELLA chat come evento narrativo scatenante dell'episodio. "
        + "I messaggi nella chat sono accaduti/stanno accadendo -- scrivili come realtà vissuta dai personaggi."
      : "";

    // Tono di voce dai personaggi del Codex
    const _protagonisti = codexItems.filter(i => i.type === "personaggio" && (i.protagonista || i.fiammaGemella) && i.nome && !i.futura);
    // Il carattere pieno arriva una sola volta da buildProtagonistPhysicalConstraint (final input).
    // Qui resta solo la regola anti-scambio voce + eventuali note specifiche.
    const _tonoVoce = _protagonisti.length > 0
      ? "\n⚠️ VOCE DEI PERSONAGGI: NON attribuire mai a un personaggio frasi, reazioni o tratti che appartengono all'altro."
        + _protagonisti.filter(p => p.coseDaSapere).map(p => `\n• ${p.nome} -- note: ${p.coseDaSapere.slice(0, 80)}`).join("")
        + "\n"
      : "";

    const _primoEpisodioNote = !ultimoCtx
      ? "\n⚠️ PRIMO EPISODIO -- nessun episodio precedente. Inizia la storia dal momento presente descritto dall'utente. NON fare salti temporali. NON scrivere 'il giorno dopo', 'una settimana dopo', 'nel frattempo'. Inizia direttamente nella scena.\n"
      : "";

    const _canonInject = canonInjection;
    return _canonInject + inputEffettivo + "\n\n[RACCONTO — ISTRUZIONI:\n"
      + "0. OUTPUT PULITO: scrivi SOLO l'episodio narrativo, dalla prima intestazione in poi. NIENTE preamboli o saluti ('Buongiorno', 'Ecco', 'Perfetto'), NIENTE note meta o di continuità nel testo, NIENTE parentesi che spiegano scelte/regole/ragionamenti. Solo la scena. Scrivi UN SOLO episodio: dopo le opzioni numerate FERMATI -- NON iniziare un secondo episodio, NON scrivere una seconda intestazione **Episodio N.**.\n"
      + "1. Prima riga ESATTA: **Episodio N. " + epNum + "** — Seconda riga ESATTA: **POV: " + povName + "**\n"
      + "   Se l'utente ha indicato modifiche, applicale con priorità assoluta.\n"
      + "2. Prima persona assoluta. MAI [NARRATORE]. MAI voce esterna.\n"
      + "3. Stile romantico CON tensione fisica COSTANTE: il magnetismo tra Nadia e Liam è SEMPRE presente e va mostrato -- sguardi, vicinanza, contatto, mani, baci appassionati fanno parte del racconto e NON vanno rimandati all'intimo. All'intimo è riservato SOLO il sesso esplicito; baci, carezze, abbracci, desiderio fisico e avvicinamenti vanno scritti QUI. Non limitarti ai dialoghi: falli avvicinare e baciare. I BACI vanno DESCRITTI nel dettaglio fisico (labbra, lingua, pressione, respiro, mani) e devono spostare l'equilibrio e togliere controllo -- MAI riassunti con un aggettivo generico ('bacio famelico/intenso'): mostra il bacio, non etichettarlo.\n"
      + _dinamicaRacconto
      + _tonoVoce
      + _primoEpisodioNote
      + "4. LUNGHEZZA: scene intime/magnetiche 1500-2000 car, coppia emotive 2000-2500 car, corali 2800-3500 car. MASSIMO 4500 car incluse opzioni. Un episodio breve e potente vale più di uno lungo e diluito.\n"
      + (canonsActive.length > 0
        ? "5. PARTI DAL CANON sopra. Usa outfit, location e dettagli descritti lì come base della scena.\n"
        : ultimoCtx
          ? "5. Aggancia all'ultima riga dell'episodio precedente. La scena è continua.\n"
          : "5. Inizia la scena dal momento presente. NON fare anticipazioni temporali.\n")
      + _raccordoNote + _chatNote + "\n"
      + "6. Termina con 2 righe di contesto + 3-5 opzioni GANCIO numerate (narrative/emotive, non erotiche).\n"
      + "   Le opzioni sono GANCI per continuare la scena attuale. VIETATO: salti temporali, cambi location, nuovi personaggi.\n"
      + "7. CRAFT (qualità della prosa): ritmo vario (frasi brevi per i colpi, lunghe per il respiro); dettaglio sensoriale concreto e specifico, mai generico; MOSTRA con gesti/corpo/sottotesto invece di DIRE le emozioni ('le tremano le mani', non 'era nervosa'); dialoghi che dicono una cosa e ne nascondono un'altra; BANDISCI aggettivi-tappabuchi e cliché ('un brivido le percorse la schiena', 'il cuore in gola'). Un'immagine precisa vale più di tre vaghe.\n"
      + (styleReference && styleReference.trim()
        ? "\n📝 ESEMPIO DI STILE DA EGUAGLIARE (imita SOLO registro, ritmo e qualità della prosa -- NON copiarne contenuti, nomi, intestazioni o eventi):\n\"" + styleReference.replace(/\s+/g, " ").trim().slice(0, 700) + "\"\n"
        : "")
      + _regolaFG
      + regoleStr + "\n"
      + (ultimoCtx ? "ULTIME RIGHE EPISODIO PRECEDENTE (" + ultimoSezione + "): " + ultimoCtxOpzioni + "\n⚠️ Le opzioni numerate dell'episodio precedente NON sono fatti accaduti -- sono possibilità non scelte." : "EPISODIO PRECEDENTE: nessuno -- primo episodio della storia.")
      + buildFinalConstraints(inputEffettivo + " " + (ultimoCtx || ""))
      + "\n\n🎬 POV OBBLIGATORIO: questo episodio è in PRIMA PERSONA dal punto di vista di " + povName + " (il narratore-io È " + povName + "). Anche se l'episodio precedente era dal POV di un altro, ORA il POV è " + povName + ": NON proseguire col POV precedente."
      + "]";
  };

  // Racconto opzioni: solo opzioni numerate, no episodio
  const buildRaccontoOptionsFinalInput = (
    canonInjection, inputEffettivo, _dinamicaRacconto, hasChatBlock,
    _regolaFG, regoleStr, ultimoSezione, ultimoCtxOpzioni
  ) => {
    const _chatNoteOpt = hasChatBlock
      ? "\n⚠️ CHAT MESSAGGI: Usa la conversazione WhatsApp allegata come evento scatenante delle opzioni. "
        + "Le opzioni devono mostrare come i personaggi reagiscono/vivono quella chat nella realtà narrativa."
      : "";
    return canonInjection + inputEffettivo + "\n\n[OPZIONI RACCONTO per Ep." + ((episodeCounters["global_narrative"] || 0) + 1) + ":\n"
      + "OBBLIGATORIO -- scrivi PRIMA delle opzioni:\n"
      + "📍 CONTESTO: [dove si trovano i personaggi -- location esatta]\n"
      + "💬 MOMENTO: [cosa è appena successo nell'ultima riga -- una frase]\n"
      + "Queste 2 righe dimostrano che hai letto l'episodio. NON saltarle.\n\n"
      + "Poi scrivi 3-5 opzioni GANCIO numerate che continuano QUESTA scena in modi diversi."
      + _dinamicaRacconto
      + _chatNoteOpt
      + "\nREGOLA OPZIONI ASSOLUTA: Le opzioni sono GANCI per continuare la scena ATTUALE. "
      + "VIETATO: salti temporali, cambi di location, cambio POV, nuovi personaggi non presenti. "
      + "⛔ VIETATO opzioni erotiche/esplicite/sessuali: le opzioni del racconto NON propongono atti sessuali, baci esplicitamente erotici, scene di sesso. Quello è dominio della sezione intimo, NON di racconto. Le opzioni racconto sono SEMPRE narrative/emotive/relazionali (un dialogo, un gesto tenero, una decisione, un'interruzione, una scelta narrativa). Anche se l'ultimo episodio era intimo, le opzioni racconto DEVONO essere narrative non erotiche -- sarà l'utente a tornare in intimo se vuole continuare lì.\n"
      + "PERMESSO: variazioni di quello che sta succedendo ADESSO. "
      + "L'utente decide quando e se avanzare nel tempo o cambiare scena."
      + _regolaFG
      + regoleStr + "\n"
      + "ULTIME RIGHE EPISODIO PRECEDENTE (" + ultimoSezione + "): " + ultimoCtxOpzioni
      + buildFinalConstraints(inputEffettivo + " " + ultimoCtxOpzioni, { isOptions: true }) + "]";
  };

  const sendMessage = async () => {
    const canonsActive = selectedCanonsRef.current;
    const hasCanons = canonsActive.length > 0;
    if((!input.trim()&&attachments.length===0&&!hasCanons)||loading)return;
    const chat=getCurrentChat();if(!chat)return;
    const isIntimo = intimoModeRef.current;
    const storageSection = (activeSection === "racconto" || activeSection === "intimo") ? "racconto" : activeSection;

    const inputPulito = input.replace(/\[(Lab|Stylist) Canon:[^\]]+\]/g, '').trim();
    const inputEffettivo = inputPulito || (hasCanons ? "Crea il prossimo episodio sviluppando i canon indicati nel contesto." : "");
    let finalInput = inputEffettivo || input;
    let epNum = null;

    // Canon injection -- usa il testo salvato al momento del pin
    let canonInjection = "";
    if (canonsActive.length > 0 && (activeSection === "racconto" || activeSection === "intimo" || intimoModeRef.current)) {
      const withText = canonsActive.filter(c => c.text && c.text.length > 20);
      if (withText.length > 0) {
        const labR = withText.filter(c => c.source === 'lab');
        const styR = withText.filter(c => c.source === 'stylist');
        // FIX E: i canon Lab vanno trattati come ARCO NARRATIVO da sviluppare su più
        // episodi, NON come scena singola da chiudere in un episodio. I canon Stylist
        // sono outfit che restano addosso per la durata, sono una cosa diversa.
        const labText = labR.map(c => "SCENA CANON (da sviluppare su MULTIPLI episodi):\n" + c.text).join("\n\n---\n\n");
        const styText = styR.map(c => "OUTFIT CANON OBBLIGATORIO (" + c.label + "):\n" + c.text).join("\n\n---\n\n");
        const canonFullText = [labText, styText].filter(Boolean).join("\n\n---\n\n");
        canonInjection = "⚠️⚠️⚠️ CANON ATTIVI -- LEGGI PRIMA DI SCRIVERE:\n\n"
          + canonFullText
          + "\n\n⛔ ISTRUZIONE CRUCIALE SULLO SVILUPPO DEI CANON LAB:\n"
          + "Il canon Lab è un ARCO NARRATIVO, non una scena da chiudere in un episodio. Va sviluppato GRADUALMENTE su almeno 3-5 episodi. In QUESTO episodio mostri SOLO un frammento dell'arco (es. l'inizio, oppure un momento intermedio, oppure una pausa carica di tensione) -- NON l'intero arco.\n"
          + "VIETATO in un singolo episodio: aprire E chiudere il momento del canon, far accadere tutti i punti emotivi del canon, risolvere la tensione che il canon mette in scena.\n"
          + "Esempio: se il canon è una lite, in QUESTO episodio scrivi SOLO un pezzo della lite (l'innesco, oppure il momento di silenzio, oppure una battuta tagliente) -- NON la lite intera con riconciliazione.\n"
          + "Il canon Stylist (outfit) invece resta addosso per tutta la durata della scena, è una caratteristica fissa, non un arco.\n\n"
          + "⚠️ I dettagli sopra (scena, location, outfit) sono il MATERIALE da cui sviluppi l'episodio. Parti da qui, ma sviluppa solo un pezzo dell'arco narrativo.\n\n";
      }
    }

    // ── LAB / STYLIST: intercetta richiesta genera blocco o proposte narrative ──
    if (activeSection === "lab" || activeSection === "stylist") {
      const isGeneraBlockReq = /genera\s+blocch|prepara\s+scen|crea\s+blocch|scrivi\s+blocch|crea\s+(un|una|l['']\s*)outfit|dammi\s+(un|una|l['']\s*)outfit|outfit\s+per|vestiti\s+per/i.test(inputEffettivo);
      const isProposteReq = /dammi\s+(delle\s+)?(proposte|opzioni|idee|sviluppi|continua|canon)|cosa\s+facciamo|come\s+continuiamo|come\s+proseguiamo|per\s+continuare|per\s+proseguire|rendi\s+canon|fai\s+un\s+canon|crea\s+(un|una|dei|delle|gli)\s+(canon|blocch|scen)|scenario|scenari|proponi|scrivi\s+(un|una|la\s+scena)|sviluppa\s+(la\s+)?scena|incontro|primo\s+incontro|come\s+(si\s+)?incontrano|dove\s+si\s+incontrano/i.test(inputEffettivo);
      const isDividiEpisodi = /dividi\s+(in|questo|il\s+canon|questo\s+canon)|mini.?canon|spezza\s+in|suddividi\s+in|separa\s+in|episodi\s+separati|un\s+episodio\s+per|ogni\s+episodio/i.test(inputEffettivo);

      if (activeSection === "stylist") {
        finalInput = buildStylistFinalInput(inputEffettivo);
      } else if (isDividiEpisodi) {
        finalInput = buildDividiEpisodiFinalInput(inputEffettivo);
      } else if (isGeneraBlockReq || isProposteReq) {
        finalInput = buildGeneraBloccoFinalInput(inputEffettivo);
      }
    }

    if (activeSection === "racconto" && (inputEffettivo.trim() || hasCanons)) {
      const inputLower = inputEffettivo.toLowerCase().trim();
      const povName = activePOV || "Nadia";

      const isPOVMirror = input.startsWith("[POV MIRROR -- REGOLA ASSOLUTA]");
      const hasChatBlock = /^\[Chat "/.test(input.trim());

      // Canon attivo = sempre episodio, mai opzioni
      const isCanonMode = hasCanons && !inputPulito;
      // Mix opzioni: "prendi l'ambientazione dell'1 e il dialogo del 2", "1 ma con X", ecc.
      const isMixOpzioni = !isPOVMirror && !hasChatBlock && !isCanonMode && input.length < 300 && (
        /[1-5].{0,30}(ma|con|però|però|aggiung|aggiungi|inserisci|metti|cambia|invece|usando|usa).{0,60}[1-5]/i.test(inputLower) ||
        /prendi.{0,20}(ambientazione|contesto|inizio|dialogo|gesto|frase|idea|scena).{0,30}(opzione|dall|del|dalla)\s*[1-5]/i.test(inputLower) ||
        /mischia|combina|unisci|mescola.{0,30}[1-5].{0,20}[1-5]/i.test(inputLower)
      );
      const isOptionChoice = !isPOVMirror && !hasChatBlock && !isCanonMode && !isMixOpzioni && (
        (input.length < 120 && (
          /^(ok|okay|s[i\xec]|certo|vai|bene|perfetto|sviluppa|scrivi|esegui|scegli)?\s*[1-5]\b/i.test(inputLower) ||
          /^[1-5][.)\s]/i.test(inputLower) ||
          /^(opzione|scelta|punto)\s*[1-5]/i.test(inputLower) ||
          /^(sviluppa|scrivi|esegui|scegli|vai con|procedi con).{0,20}[1-5]\b/i.test(inputLower) ||
          /^(l[aoi]\s*)?(opzione|scelta)\s*[1-5]/i.test(inputLower)
        )) ||
        // Scelta con variazione lunga: "Opzione 1 e aggiungi che..." o "1 ma con X..."
        (input.length >= 120 && /^(opzione\s*[1-5]|[1-5][\s.):,])/i.test(inputLower))
      );
      const isConversational = !isOptionChoice && !isPOVMirror && !hasChatBlock && !isCanonMode && (
        (/\?$/.test(input.trim()) && input.length < 400) ||
        // Correzioni e critiche all'episodio
        /^(hai (letto|visto|capito|sbagliato|dimenticato|seguito|ignorato|ripetuto|fatto)|non hai|stai (facendo|scrivendo|inventando|ignorando|sbagliando))/i.test(inputLower) ||
        /^(l.episodio|questo episodio|l.ultimo|questo ultimo|la scena|questa scena|il testo|quello che hai scritto).{0,30}(sbagliato|errato|non va|non è corretto|non è giusto|è sbagliato|è errato|ignora|dimentica|non rispetta|non segue|ha ignorato|ha dimenticato|manca)/i.test(inputLower) ||
        /^(hai (sbagliato|ignorato|dimenticato|ripetuto|fatto di nuovo|riciclato|messo|aggiunto|scritto))/i.test(inputLower) ||
        /^(non (va|è corretto|è giusto|ci siamo|hai capito|hai seguito|hai rispettato|rispetta|funziona))/i.test(inputLower) ||
        /^(questo non|così non|in questo modo non)/i.test(inputLower) ||
        /^(mi (hai|ha) (dato|scritto|generato|prodotto).{0,30}(sbagliato|errato|episodio|opzioni|risposta))/i.test(inputLower) ||
        /^(invece di|al posto di).{0,50}(hai|ha|stai)/i.test(inputLower) ||
        // Istruzioni e comunicazioni
        /^ti (dico|spiego|copio|mando|passo|racconto|ricordo|confermo|avviso)/i.test(inputLower) ||
        /^(ricorda (che|di)|tieni (conto|presente)|nota (che|bene)|attenzione:|corregg|sistema|aggiust|rileggi|prima di (scrivere|continuare))/i.test(inputLower) ||
        /^(sai (che|chi|cosa|se)|lo sai|ti ricordi|ricordi che)/i.test(inputLower) ||
        /^(per quale|perche|perché)/i.test(inputLower) ||
        // Testo lungo SOLO se NON inizia con un comando narrativo esplicito
        (input.length > 200 && !/^(scrivi|continua|vai|prosegui|genera|crea|inizia|dammi|dai|scrivimi|fammi|adesso|ora|in questo|nella|nel|mentre|dopo|prima|quando|sviluppa|mostrami|fai|racconta|descrivi|immagina|costruisci)/i.test(inputLower)) ||
        /usciamo|usciamo dalla|torniamo alla realt|basta con le fantas|torna alla realt|esci dall.intimo|fine intimo/i.test(inputLower)
      );

      // Canon attivo → sempre episodio (mai opzioni)
      // È una scelta opzione con variazione se inizia con numero/opzione N seguito da testo
      const _isSceltaConVariazione = /^(opzione\s*[1-5]|[1-5][\s.):])/i.test(inputLower) && input.length > 30;
      const isOptionsRequest = !isCanonMode && !_isSceltaConVariazione && (!isOptionChoice && !isPOVMirror && (
        (input.length < 200 && (/opzion/i.test(input) || /propost/i.test(input) || /alternativ/i.test(input))) ||
        /^(dammi|dai|proponi|suggerisci|mostrami|voglio|vorrei)\s*(\d|alcune|delle|le|altre|nuove)?\s*(opzion|propost|alternativ)/i.test(inputLower) ||
        inputLower === "opzioni" || inputLower === "proposte" ||
        /^(altre|nuove|più|ancora)\s*(opzioni|proposte|idee)/i.test(inputLower)
      ));

      // Regole assolute dal Codex
      const regoleAss = codexItems.filter(i => i.type === "regola" && i.assoluta === true);
      const regoleStr = regoleAss.length > 0
        ? "\nREGOLE ASSOLUTE (rispettale sempre):\n" + regoleAss.map(r => "• " + (r.descrizione || r.nome)).join("\n")
        : "";

      // Regola FG -- condizionale: si applica solo se i personaggi non si conoscono ancora
      // Se esistono già episodi narrativi, Nadia e Liam si sono già incontrati
      const _epNarrativiTotali = chat.messages.filter(m => m.role === "assistant" && !m.isBridge && (m.section === "racconto" || m.section === "intimo") && (m.content?.length||0) > 100).length;
      // _siConoscono: true SOLO se nel riassunto o nella cronologia FG è registrato un incontro reale
      // NON si basa sul contatore episodi -- è l'utente a decidere quando si incontrano
      const _fgItemsLocal = codexItems.filter(i => i.type === "fiammegemelle" && i.eventi?.length > 0);
      const _fgEventiLocal = _fgItemsLocal.flatMap(fg => fg.eventi||[]);
      const _summaryText = (summaryCompact || "").toLowerCase();
      const _fgEventiText = _fgEventiLocal.map(e => (e.evento || "") + " " + (e.categoria || "")).join(" ").toLowerCase();
      const _incontroKeywords = ["si incontrano", "primo incontro", "si conoscono", "si sono incontrati", "primo_incontro", "si vedono per la prima volta", "nadia incontra liam", "liam incontra nadia"];
      const _siConoscono = _incontroKeywords.some(k => _summaryText.includes(k) || _fgEventiText.includes(k));
      const _regolaFG = _siConoscono
        ? "\nREGOLA FG: Nadia e Liam si conoscono già. Rispetta la continuità della loro relazione come descritta nel riassunto e nell'ultimo episodio. NON proporre situazioni che contraddicano quello che è già successo."
        : "\nREGOLA FG -- VIETATO ASSOLUTO: Liam e Nadia non si conoscono, non si sono mai incontrati, non hanno nessun contatto. NON proporre MAI: messaggi, social, app, notifiche, incontri fisici casuali, riconoscimenti, coincidenze che li mettano in contatto.\nLA CONNESSIONE FG IN QUESTA FASE è solo: sensazioni fisiche inspiegabili (dolore, calore, brividi), emozioni che arrivano senza una causa apparente, sogni vividi, un'intuizione che qualcosa è cambiato nel mondo. MAI telepatia diretta, MAI visioni chiare dell'altro, MAI comunicazione consapevole. I personaggi NON sanno da dove arrivano queste sensazioni -- le vivono come proprie o come qualcosa di strano che non capiscono.";

      // Ultimo episodio in assoluto (racconto o intimo -- prende il più recente)
      // Cerca l'ultimo episodio globale tra racconto e intimo (non solo la chat corrente)
      const _allNarrMsgs = getUnifiedNarrativeMessages(chatsRef.current);
      // Esclude blocchi di sole opzioni (es. "Certamente! Ecco tre ganci...") che non hanno narrativa
      const _isSoloOpzioni = (m) => {
        if (!m.content) return false;
        const first500 = m.content.slice(0, 500);
        const hasNumbered = /^\s*[1-5]\.\s+/m.test(first500) && (first500.match(/^\s*[1-5]\.\s+/gm)||[]).length >= 2;
        const hasOpzioniTag = /\[OPZIONI\s+(RACCONTO|INTIMO)/i.test(m.content);
        // Blocco solo opzioni: inizia con "Certamente" o "Ecco" e ha opzioni numerate subito
        const startsWithOptions = /^\s*(Certamente|Ecco|Perfetto|Qui di seguito)[^\n]{0,100}\n/i.test(m.content) && hasNumbered;
        return hasOpzioniTag || startsWithOptions;
      };
      const ultimoEpMsg = _allNarrMsgs
        .filter(m => m.role === "assistant" && !m.isBridge && (m.content?.length || 0) > 100 && !_isSoloOpzioni(m))
        .slice(-1)[0];
      const ultimoSezione_pre = (ultimoEpMsg?.section || "racconto").toUpperCase();

      // Per le opzioni: taglia la lista opzioni in fondo -- il modello deve agganciarsi
      // all'ultima riga narrativa, non alle opzioni già proposte
      const _stripOpts = (text) => {
        if (!text) return "";
        // Delimitatore esplicito -- priorità assoluta
        const tagIdx = text.indexOf("[FINE EPISODIO]");
        if (tagIdx > 0) return text.slice(0, tagIdx).trim();
        const half = Math.floor(text.length * 0.3);
        const tail = text.slice(half);
        // Cerca inizio blocco opzioni: numerazione semplice, bold markdown, oppure separatore ---
        const patterns = [
          /\n---\n+\s*\*?\*?[Oo]pzioni/,        // --- seguito da Opzioni
          /\n\n\s*\*\*[1-5][.):–]/,             // **1. o **1)
          /\n\n\s*[1-5][.):–]\s+\*\*/,          // 1. **TITOLO
          /\n\n\s*(?:opzioni|ganci|sviluppi)[^\n]*\n\s*[1-5]/i,
          /\n\n\s*[1-5][.):–]\s+[A-Z\u00C0-\u00FF]/,
          /\n\n\s*[1-5][.):–]\s+/,
          /\n---\n\n\s*[1-5]/,
        ];
        let cut = -1;
        for (const pat of patterns) {
          const idx = tail.search(pat);
          if (idx > 0 && (cut === -1 || idx < cut)) cut = idx;
        }
        return cut > 0 ? text.slice(0, half + cut).trim() : text.trim();
      };
      const ultimoCtxOpzioni = _stripOpts(ultimoEpMsg?.content || "");
      const ultimoCtx = (ultimoEpMsg?.content || "").trim(); // episodio completo per episodi
      // Per opzioni intimo: usa l'ultimo episodio intimo se esiste, altrimenti fallback sull'ultimo racconto
      // Questo preserva la meccanica racconto→intimo al primo ingresso
      const ultimoCtxOpzioniIntimo = _stripOpts(ultimoEpMsg?.content || "");
      const ultimoSezione = ultimoSezione_pre;

      // Se conversazionale: AI risponde come assistente, no episodio
      if (isPOVMirror) {
        epNum = getGlobalEpisodeNum();
        finalInput = buildPOVMirrorFinalInput(input);
      } else if (isMixOpzioni) {
        // ── MIX OPZIONI: combina più opzioni secondo istruzioni utente ──
        epNum = getGlobalEpisodeNum();
        finalInput = buildMixOpzioniFinalInput(inputEffettivo, inputLower, chat, epNum, povName, isIntimo, regoleStr, ultimoCtxOpzioni);
      } else if (isOptionChoice) {
        // ── SCELTA OPZIONE: estrae il numero e l'opzione dall'ultimo messaggio ──
        epNum = getGlobalEpisodeNum();
        finalInput = buildOptionChoiceFinalInput(inputLower, inputEffettivo, chat, epNum, povName, isIntimo, regoleStr, ultimoCtxOpzioni);
      } else if (isConversational) {
        // Costruisce il finalInput conversazionale con contesto dell'ultimo episodio
        finalInput = buildConversationalFinalInput(chat, input);
      } else {
      epNum = isOptionsRequest ? null : getGlobalEpisodeNum();

      if (isIntimo) {
        // ── MODALITÀ INTIMA ──
        const altroProtagonista = codexItems
          .filter(i => i.type === "personaggio" && (i.protagonista || i.fiammaGemella) && i.nome && i.nome !== povName)[0]?.nome || "";
        const regolaPresenza = intimoPresenza === "entrambi"
          ? "Entrambi " + povName + (altroProtagonista ? " e " + altroProtagonista : "") + " sono presenti fisicamente. Scrivi scena erotica esplicita di coppia."
          : "⚠️ SOLO " + povName + " è presente fisicamente. " + (altroProtagonista ? altroProtagonista + " NON c'è. " : "") + "Scrivi fantasia erotica esplicita in prima persona -- immaginazione o connessione FG sensoriale. NON inventare incontri fisici reali.";

        // Dinamica dal prompt intimo -- iniettata nel finalInput per massima attenzione
        const _promptIntimo = prompts["intimo"] || DEFAULT_PROMPTS["intimo"];
        const _dinamicaIntimoMatch = _promptIntimo.match(/DINAMICA NADIA[^A-Za-z\n]{0,4}LIAM[\s\S]*?(?=\n\nLUNGHEZZA|\n\nCOMUNICAZIONE|$)/i);
        const _dinamicaIntimo = _dinamicaIntimoMatch
          ? "\n⚠️ DINAMICA NADIA-LIAM (rispettala in ogni riga -- non è opzionale):\n" + _dinamicaIntimoMatch[0].trim() + "\n"
          : "";

        if (!isOptionsRequest) {
          finalInput = buildIntimoEpisodeFinalInput(
            epNum, povName, ultimoSezione, ultimoCtx, ultimoCtxOpzioniIntimo, canonInjection, inputEffettivo,
            regolaPresenza, _dinamicaIntimo, _regolaFG, regoleStr
          );
        } else {
          finalInput = buildIntimoOptionsFinalInput(
            canonInjection, inputEffettivo, regolaPresenza, _regolaFG, regoleStr,
            ultimoSezione, ultimoCtxOpzioniIntimo
          );
        }

      } else {
        // ── MODALITÀ NARRATIVA ──

        // Dinamica dal prompt racconto -- iniettata nel finalInput per massima attenzione
        const _promptRacconto = prompts["racconto"] || DEFAULT_PROMPTS["racconto"];
        const _dinamicaRaccontoMatch = _promptRacconto.match(/DINAMICA NADIA[^:]*:[^\n]*(?:\n[^\n]+)*/i);
        const _dinamicaRacconto = _dinamicaRaccontoMatch
          ? "\n⚠️ DINAMICA NADIA-LIAM (rispettala in ogni riga):\n" + _dinamicaRaccontoMatch[0].trim() + "\n"
          : "";

        if (!isOptionsRequest) {
          finalInput = buildRaccontoEpisodeFinalInput(
            epNum, povName, canonInjection, inputEffettivo, canonsActive,
            ultimoCtx, ultimoCtxOpzioni, ultimoSezione, hasChatBlock,
            _dinamicaRacconto, _regolaFG, regoleStr
          );
        } else {
          finalInput = buildRaccontoOptionsFinalInput(
            canonInjection, inputEffettivo, _dinamicaRacconto, hasChatBlock,
            _regolaFG, regoleStr, ultimoSezione, ultimoCtxOpzioni
          );
        }
      }
      } // end !isConversational
    }

    const canonVisibile = canonsActive.length > 0
      ? canonsActive.map(c=>(c.source==='stylist'?"👗 ":"🔬 ")+(c.label || c.id || "canon")).join("\n")+(inputPulito?"\n\n"+inputPulito:"")
      : inputPulito || input || "(continua)";
    const userMsg={role:"user",content:canonVisibile,timestamp:Date.now(),section:isIntimo?"intimo":"racconto",id:`msg_${Date.now()}_${Math.random().toString(36).slice(2)}`,attachments:attachments.length>0?[...attachments]:undefined,episodeNum:epNum,pov:activePOV};
    const updated=[...chat.messages,userMsg];
    // Salva subito il messaggio utente sul backend
    api.saveMessage(userMsg, chat.id).catch(()=>{});
    // Aggiungi messaggio streaming placeholder
    const streamId = `stream_${Date.now()}`;
    const streamMsg={role:"assistant",content:"",timestamp:Date.now(),section:isIntimo?"intimo":"racconto",streaming:true,id:streamId};
    setChats(p=>({...p,[storageSection]:p[storageSection].map(c=>c.id===chat.id?{...c,messages:[...updated,streamMsg]}:c)}));
    setInput(""); setAttachments([]);
    // Reset altezza textarea su mobile
    if (mainTextareaRef.current) {
      mainTextareaRef.current.style.height = "24px";
    }
    setLoading(true);
    // Wake Lock: chiede al sistema di non sospendere durante la generazione
    let wakeLock = null;
    try { if ("wakeLock" in navigator) wakeLock = await navigator.wakeLock.request("screen"); } catch(e) {}

    // Callback streaming - salva nel ref E aggiorna UI (sicurezza mobile)
    streamTextRef.current = "";
    const isAndroid = /Android/i.test(navigator.userAgent);
    let lastStreamUpdate = 0;
    let firstChunk = true;
    const STREAM_THROTTLE_MS = isAndroid ? 80 : 0;
    const onStream = (text) => {
      streamTextRef.current = text;
      const now = Date.now();
      if (firstChunk || now - lastStreamUpdate >= STREAM_THROTTLE_MS) {
        firstChunk = false;
        lastStreamUpdate = now;
        setChats(p=>({...p,[storageSection]:p[storageSection].map(c=>c.id===chat.id?{...c,messages:c.messages.map(m=>m.id===streamId?{...m,content:text}:m)}:c)}));
      }
    };;

    const response = await callAI(
      (activeSection === "racconto" || activeSection === "intimo")
        ? (isIntimo ? "intimo" : "racconto")
        : activeSection,
      updated.map((m,i)=>i===updated.length-1?{...m,content:finalInput}:m),
      null,
      null,
      onStream,
      inputEffettivo);

    // Se interrotto (response === null), rimuovi placeholder e non incrementare
    if (response === null) {
      setChats(p=>({...p,[storageSection]:p[storageSection].map(c=>c.id===chat.id?{...c,messages:c.messages.filter(m=>m.id!==streamId)}:c)}));
      setLoading(false);
      return;
    }

    // Incrementa contatore episodi SOLO dopo risposta ricevuta completa
    if (epNum) {
      incrementGlobalEpisode();
      lastBridgeRef.current = null; // Reset bridge: prossimo switch genererà nuovo contesto
    }

    // Finalizza messaggio rimuovendo flag streaming
    const activeModelFinal = (activeSection === "racconto" || activeSection === "intimo")
      ? (isIntimo ? (modelOverride["intimo"] || models["intimo"] || "deepseek/deepseek-chat-v3-0324") : (modelOverride["racconto"] || models["racconto"] || "minimax/minimax-m3"))
      : (modelOverride[activeSection] || models[activeSection] || "deepseek/deepseek-chat-v3-0324");
    // Usa response se valido, altrimenti usa streamTextRef (fallback mobile)
    const finalContent = (response && response.length > 10) ? response : streamTextRef.current;
    // Inserisce [FINE EPISODIO] prima del blocco opzioni per racconto e intimo
    // In questo modo _stripOpts può tagliare con precisione assoluta
    const _injectTag = (text) => {
      if (!text) return text;
      // Cerca il primo pattern di opzioni numerato nel testo
      const optPatterns = [
        /\n\n\s*\*\*[1-5][.):\-]/,
        /\n\n\s*[1-5][.):\-]\s+\*\*/,
        /\n\n\s*[1-5][.):\-]\s+[A-Z\u00C0-\u00FF]/,
        /\n\n\s*[1-5][.):\-]\s+/,
        /\n---\n\n\s*[1-5]/,
      ];
      let firstIdx = -1;
      for (const pat of optPatterns) {
        const m = text.search(pat);
        if (m > 100 && (firstIdx === -1 || m < firstIdx)) firstIdx = m;
      }
      if (firstIdx > 0) return text.slice(0, firstIdx) + "\n\n[FINE EPISODIO]\n" + text.slice(firstIdx);
      return text;
    };
    // Taglia un eventuale SECONDO episodio generato per inerzia (episodio+opzioni+episodio+opzioni):
    // cerca una seconda intestazione "**Episodio N" e tronca lì.
    const _stripDuplicateEpisode = (text) => {
      if (!text) return text;
      const re = /\*\*\s*Episodio\s*N/gi;
      const idxs = []; let mm;
      while ((mm = re.exec(text)) !== null) idxs.push(mm.index);
      return idxs.length >= 2 ? text.slice(0, idxs[1]).trim() : text;
    };
    const _dedupContent = (isIntimo || activeSection === "racconto") ? _stripDuplicateEpisode(finalContent) : finalContent;
    let taggedContent = (isIntimo || activeSection === "racconto") ? _injectTag(_dedupContent) : _dedupContent;
    // POV ENFORCED BY CODE: l'intestazione (Episodio N + POV) la scrive il CODICE in base al POV
    // selezionato, non il modello -> il POV è sempre rispettato, niente inerzia dall'episodio prima.
    if (epNum && (isIntimo || activeSection === "racconto")) {
      const _pov = activePOV || "Nadia";
      const _body = taggedContent.replace(/^\s*(?:\*\*\s*Episodio[^\n]*\*\*\s*)?\n*(?:\*\*\s*POV[^\n]*\*\*\s*)?\n*/i, "").trim();
      taggedContent = `**Episodio N. ${epNum}**\n**POV: ${_pov}**\n\n${_body}`;
    }
    const finalMsg = {role:"assistant",content:taggedContent,timestamp:Date.now(),section:isIntimo?"intimo":"racconto",streaming:false,model:activeModelFinal,episodeNum:epNum,id:streamId};
    setChats(p=>({...p,[storageSection]:p[storageSection].map(c=>c.id===chat.id?{...c,messages:c.messages.map(m=>m.id===streamId?{...m,...finalMsg}:m)}:c)}));
    // Salva immediatamente sul backend (non aspetta il debounce del useEffect)
    api.saveMessage(finalMsg, chat.id).catch(()=>{});
    setLoading(false);
    // Aggiorna cronologie/riassunto DOPO la finalizzazione, passando l'episodio appena scritto
    // (così l'episodio-trigger 10/20/... è incluso e epTotale non è off-by-one)
    if (epNum) autoUpdateCronologie(epNum, taggedContent).catch(()=>{});

    // Aggiorna sceneContext con Haiku dopo ogni episodio racconto/intimo
    if (isIntimo || activeSection === "racconto") {
      const _epContent = taggedContent.indexOf("[FINE EPISODIO]") > 0
        ? taggedContent.slice(0, taggedContent.indexOf("[FINE EPISODIO]")).trim()
        : taggedContent.trim();
      const _ctxPrev = sceneContextRef.current || sceneContext;
      // Elenco luoghi canonici del Codex: Haiku deve usare il NOME ESATTO quando la scena vi si svolge,
      // così sceneContext.location aggancia il vincolo fisico in buildActiveLocationConstraint.
      const _luoghiCanon = codexItems
        .filter(i => i.type === "luogo" && i.nome && !i.futura)
        .map(i => i.nome)
        .slice(0, 15);
      const _luoghiHint = _luoghiCanon.length > 0
        ? `\n\nLUOGHI CANONICI (se la scena si svolge in uno di questi, usa ESATTAMENTE questo nome nel campo location): ${_luoghiCanon.join(", ")}.`
        : "";
      fetch("https://api.anthropic.com/v1/messages", {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-api-key": apiKey, "anthropic-version": "2023-06-01" },
        body: JSON.stringify({
          model: "claude-haiku-4-5-20251001",
          max_tokens: 300,
          system: "Sei un analizzatore di testi narrativi. Rispondi SOLO con un oggetto JSON valido, senza markdown, senza backtick, senza spiegazioni.",
          messages: [{
            role: "user",
            content: `Analizza questo episodio narrativo ed estrai queste informazioni SE presenti nel testo. Se un'informazione NON è presente nell'episodio, usa il valore precedente fornito.

VALORI PRECEDENTI:
- location: "${_ctxPrev.location || "non definita"}"
- momento: "${_ctxPrev.momento || "non definito"}"
- intimita: "${_ctxPrev.intimita || "non definita"}"
- statoScena: "${_ctxPrev.statoScena || "non definito"}"
- vestizione: "${_ctxPrev.vestizione || "non definita"}"

EPISODIO DA ANALIZZARE:
${_epContent.slice(0, 3000)}${_luoghiHint}

Rispondi SOLO con questo JSON (aggiorna solo i campi per cui trovi informazioni nell'episodio):
{"location":"dove si svolge la scena (es: casa di Liam - soggiorno)","momento":"momento della giornata (es: notte, mattino presto)","intimita":"livello intimità raggiunto (es: primo incontro, primo bacio, esplorazione fisica, scena erotica in corso)","statoScena":"stato attuale della scena (es: tensione romantica crescente, scena erotica in pausa, momento emotivo)","vestizione":"stato vestizione dei personaggi (es: Nadia top crema slacciato, Liam camicia bianca aperta sui pantaloni, scarpe entrambi accanto al divano). Se nell'episodio non cambia, riusa il valore precedente."}`
          }]
        })
      }).then(r => r.json()).then(data => {
        try {
          const raw = (data.content?.[0]?.text || "").replace(/```json|```/g, "").trim();
          const jsonStr = raw.startsWith("{") ? raw : raw.slice(raw.indexOf("{"), raw.lastIndexOf("}") + 1);
          const parsed = JSON.parse(jsonStr);
          const newCtx = {
            location: parsed.location || _ctxPrev.location || "",
            momento: parsed.momento || _ctxPrev.momento || "",
            intimita: parsed.intimita || _ctxPrev.intimita || "",
            statoScena: parsed.statoScena || _ctxPrev.statoScena || "",
            vestizione: parsed.vestizione || _ctxPrev.vestizione || ""
          };
          sceneContextRef.current = newCtx;
          setSceneContext(newCtx);
          save("s_scene_context", newCtx);
        } catch(e) { /* ignora errori parsing */ }
      }).catch(() => {});
    }
    // Rilascia wake lock
    try { if (wakeLock) await wakeLock.release(); } catch(e) {}
  };

  sendMessageRef.current = sendMessage;
  useEffect(() => {
    if (mirrorPendingRef.current && input === mirrorPendingRef.current && !loading) {
      mirrorPendingRef.current = null;
      sendMessageRef.current?.();
    }
  }, [input, loading]);

  // TASTO PANICO -- rimuove ultimo msg AI e reinvia con modello opposto + istruzioni
  const panicRewrite = async () => {
    const chat = getCurrentChat();
    if (!chat || loading) return;
    const msgs = chat.messages;
    // Trova ultimo messaggio AI e ultimo messaggio utente
    const lastAiIdx = msgs.map(m=>m.role).lastIndexOf("assistant");
    if (lastAiIdx === -1) return;
    const lastUserIdx = msgs.slice(0, lastAiIdx).map(m=>m.role).lastIndexOf("user");
    if (lastUserIdx === -1) return;

    // Rimuovi l'ultimo messaggio AI
    const trimmed = msgs.slice(0, lastAiIdx);
    setChats(p=>({...p,[activeSection]:p[activeSection].map(c=>c.id===chat.id?{...c,messages:trimmed}:c)}));

    // Determina modello opposto
    // Racconto: alterna Sonnet ↔ DeepSeek
    // Intimo: alterna DeepSeek ↔ Euryale (Sonnet non supporta esplicito)
    const currentModel = intimoModeRef.current
      ? (modelOverride.intimo || models.intimo || "deepseek/deepseek-chat-v3-0324")
      : (modelOverride.racconto || models.racconto || "minimax/minimax-m3");
    const oppositeModel = intimoModeRef.current
      ? (currentModel.includes("euryale") ? "deepseek/deepseek-chat-v3-0324" : "sao10k/l3.3-euryale-70b")
      : (currentModel.includes("deepseek") ? "minimax/minimax-m3" : "deepseek/deepseek-chat-v3-0324");

    setLoading(true);
    setShowPanicModal(false);
    const response = await callAI(
      (activeSection === "racconto" || activeSection === "intimo")
        ? (intimoModeRef.current ? "intimo" : "racconto")
        : activeSection,
      trimmed, oppositeModel, panicInstruction ? "DIRETTIVA DI RISCRITTURA: " + panicInstruction : null);
    const assistantMsg = { role:"assistant", content:response, timestamp:Date.now(), section:intimoModeRef.current?"intimo":"racconto", model:oppositeModel, isPanicRewrite:true };
    setChats(p=>({...p,[activeSection]:p[activeSection].map(c=>c.id===chat.id?{...c,messages:[...trimmed,assistantMsg]}:c)}));
    setPanicInstruction("");
    setLoading(false);
  };

  const rewriteMessage = async (msg) => {
    const chat=getCurrentChat();if(!chat||loading)return;
    const idx=chat.messages.findIndex(m=>m===msg);if(idx===-1)return;
    setLoading(true);
    try {
      const sec = msg.section || activeSection;
      // Istruzione diversa per sezione -- Stylist riscrive outfit, racconto/intimo riscrivono episodio
      const rewriteInstr = (sec === "stylist" || activeSection === "stylist")
        ? "Riscrivi questo outfit in modo diverso, mantenendo la stessa occasione/scena di riferimento, ma cambiando completamente i capi, i colori, le combinazioni e lo stile. Stessa identità del personaggio, nuova visione stilistica. Chiudi con il BLOCCO CANON nel formato standard con [CANON:parola-chiave]."
        : "Riscrivi questo episodio in modo diverso, mantenendo lo stesso contenuto narrativo, gli stessi eventi e personaggi, ma con stile, ritmo e parole completamente diverse. Stesso POV, stessa scena, nuova voce.";
      const response = await callAI(sec, [...chat.messages.slice(0, idx), {role:"user", content:rewriteInstr}]);
      // FIX race/perdita-dati: NON sovrascrivere con lo snapshot vecchio di
      // chat.messages (catturato prima dell'await di ~30s). Lavora sullo stato
      // CORRENTE e sostituisci SOLO il messaggio target (per id, fallback ref),
      // preservando tutto ciò che è stato aggiunto/cambiato durante la generazione.
      setChats(p=>({...p,[activeSection]:p[activeSection].map(c=>{
        if(c.id!==chat.id) return c;
        return {...c, messages:(c.messages||[]).map(m=> ((msg.id && m.id===msg.id) || m===msg) ? {...m, content:response, timestamp:Date.now()} : m)};
      })}));
    } catch(e) {
      console.error("Rewrite error:", e);
    } finally {
      setLoading(false);
    }
  };

  // FIX 1: salva modifica inline direttamente nel messaggio
  const saveEditedMessage = (msg) => {
    const chat=getCurrentChat();if(!chat)return;
    setChats(p=>({...p,[activeSection]:p[activeSection].map(c=>c.id===chat.id?{...c,messages:c.messages.map(m=>m===msg?{...m,content:editText}:m)}:c)}));
    setEditingMsg(null); setEditText("");
  };

  const generateSummary = async () => {
    const chronoText=buildChronologicalMessages(chats);
    if(!chronoText){showAutoUpdateToast("⚠️ Non ci sono ancora messaggi da riassumere.");return;}
    setLoading(true);
    // Barra di avanzamento simulata: la fetch non espone progresso reale, quindi
    // avanziamo gradualmente fino a ~92% durante l'attesa (~30s tipici) e scattiamo
    // a 100% al completamento. Dà all'utente un feedback chiaro di "in corso".
    setSummaryProgress(5);
    if (summaryProgressRef.current) clearInterval(summaryProgressRef.current);
    summaryProgressRef.current = setInterval(() => {
      setSummaryProgress(p => (p == null ? null : Math.min(92, p + 4)));
    }, 1200);
    const epTotale = episodeCounters["global_narrative"] || 0;
    try {
      const resp = await fetch(`${apiBase}/chat/completions`, {
        method:"POST", headers:{"Content-Type":"application/json","Authorization":`Bearer ${apiKey}`},
        body: JSON.stringify({ model:"anthropic/claude-haiku-4-5", messages:[{role:"user",content:`Ecco la storia in ordine cronologico:\n\n${chronoText}\n\nCrea un riassunto DETTAGLIATO degli episodi da 1 a ${epTotale} che includa:\n1. Arco narrativo completo in ordine cronologico con date/periodi quando presenti\n2. Tutti gli eventi significativi con dettagli specifici -- NON omettere nulla di importante\n3. Canzoni scritte, lettere, messaggi, oggetti simbolici (es: se Liam scrive Fireproof, va incluso)\n4. Primi incontri, primi baci, prime scene intime -- con contesto preciso\n5. Stato emotivo attuale dei protagonisti e della loro relazione\n6. Dialoghi o frasi importanti che definiscono i personaggi\n7. Location dove si trovano attualmente e cosa stavano facendo nell'ultimo episodio\n8. Elementi di continuità importanti (oggetti, promesse, tensioni irrisolte)\n\nIMPORTANTE: NON includere sviluppi possibili, ipotesi future, o suggerimenti. Solo fatti accaduti.\nNON comprimere -- il riassunto deve essere COMPLETO e DETTAGLIATO.\nInizia con: "Riassunto da Episodio 1 a Episodio ${epTotale}"\nMax ${SUMMARY_MAX_CHARS} caratteri.`}], max_tokens:3000 })
      });
      const data = await resp.json();
      const response = data.choices?.[0]?.message?.content || "";
      if (response) { setSummaryCompact(response); showAutoUpdateToast("✅ Riassunto aggiornato!"); }
      else showAutoUpdateToast("⚠️ Errore nella generazione del riassunto.");
    } catch(e) { showAutoUpdateToast("⚠️ Errore riassunto: " + e.message); }
    // Completa la barra (100%) e poi la nasconde dopo un attimo.
    if (summaryProgressRef.current) { clearInterval(summaryProgressRef.current); summaryProgressRef.current = null; }
    setSummaryProgress(100);
    setTimeout(() => setSummaryProgress(null), 1600);
    setLoading(false);
  };

  const saveKeyMoment = () => { if(!newMomentTitle.trim()||!newMomentContent.trim())return; setKeyMoments(p=>[...p,{id:`km${Date.now()}`,title:newMomentTitle.trim(),content:newMomentContent.trim(),priority:newMomentPriority,timestamp:Date.now()}]); setNewMomentTitle("");setNewMomentContent("");setNewMomentPriority("normale");setShowKeyMomentModal(false); };
  const deleteKeyMoment = (id) => setKeyMoments(p=>p.filter(m=>m.id!==id));
  const toggleMomentPriority = (id) => setKeyMoments(p=>p.map(m=>m.id===id?{...m,priority:m.priority==="alta"?"normale":"alta"}:m));

  // Generatore immagini per Lab e Stylist
  const handleGenerateImage = async () => {
    if (!imageGenPrompt.trim()) return;
    setImageGenLoading(true); setImageGenResult(null);
    const imageModel = load("s_image_model", "sourceful/riverflow-v2-fast");
    const result = await generateImage(imageGenPrompt, apiKey, apiBase, imageModel, null);
    setImageGenResult(result);
    setImageGenLoading(false);
  };

  // Genera immagine direttamente dal testo di un messaggio AI (Lab/Stylist)
  const handleGenerateFromMsg = async (msg) => {
    const chat = getCurrentChat();
    if (!chat) return;
    const msgTs = msg.timestamp;
    const prompt = msg.content.slice(0, 800);
    const imageModel = load("s_image_model", "sourceful/riverflow-v2-fast");
    setChats(p=>({...p,[activeSection]:p[activeSection].map(c=>c.id===chat.id?{...c,messages:c.messages.map(m=>m.timestamp===msgTs?{...m,generatingImage:"_msg_"}:m)}:c)}));
    const result = await generateImage(prompt, apiKey, apiBase, imageModel, null);
    setChats(p=>({...p,[activeSection]:p[activeSection].map(c=>c.id===chat.id?{...c,messages:c.messages.map(m=>m.timestamp===msgTs?{...m,generatingImage:undefined,generatedMsgImage:result.success?result.url:`error: ${result.error}`}:m)}:c)}));
  };

  // ============================================================
  // ESPORTAZIONE .DOCX
  // ============================================================
  const exportToDocx = () => {
    const chat = getCurrentChat();
    if (!chat || chat.messages.length === 0) { alert("Nessun contenuto da esportare!"); return; }

    // Raccoglie tutti i messaggi assistant delle chat della sezione corrente
    const allChats = chats[activeSection] || [];
    const allContent = allChats.map(c => ({
      title: c.name,
      messages: c.messages.filter(m => m.role === "assistant")
    })).filter(c => c.messages.length > 0);

    if (allContent.length === 0) { alert("Nessun contenuto da esportare!"); return; }

    // Costruisce HTML formattato che verrà convertito in docx
    const stileColori = {
      telepatia: "#7b2d8b",   // viola -- «»
      monologo: "#666666",    // grigio -- //
      sussurro: "#2d6e8b",    // blu -- ~~
      anima: "#8b4513",       // marrone -- ◊◊
      visione: "#2d8b5a",     // verde -- [VISIONE]
      narratore: "#555555",   // grigio scuro -- [NARRATORE]
    };

    // Parser delimitatori → HTML formattato
    const parseDelimitatori = (text) => {
      if (!text) return "";
      return text
        .replace(/«([^»]+)»/g, `<em style="color:${stileColori.telepatia};font-style:italic;">«$1»</em>`)
        .replace(/\/\/([^/]+)\/\//g, `<em style="color:${stileColori.monologo};font-style:italic;">//$1//</em>`)
        .replace(/~([^~]+)~/g, `<em style="color:${stileColori.sussurro};font-style:italic;">~$1~</em>`)
        .replace(/◊([^◊]+)◊/g, `<em style="color:${stileColori.anima};font-style:italic;">◊$1◊</em>`)
        .replace(/\[VISIONE:\s*([^\]]+)\]/gi, `<em style="color:${stileColori.visione};font-style:italic;">[Visione: $1]</em>`)
        .replace(/\[NARRATORE:\s*([^\]]+)\]/gi, `<strong style="color:${stileColori.narratore};">[Narratore: $1]</strong>`)
        .replace(/\[IMMAGINE:[^\]]*\]/gi, "") // rimuove tag immagine dal testo
        .replace(/\n/g, "<br/>");
    };

    // Costruisce HTML del documento
    let html = `<!DOCTYPE html><html><head><meta charset="utf-8">
<style>
  body { font-family: 'Georgia', serif; font-size: 12pt; line-height: 1.8; margin: 2cm; color: #1a1a1a; }
  h1 { font-family: 'Georgia', serif; font-size: 18pt; color: #2a1a0a; border-bottom: 1px solid #c9a96e; padding-bottom: 8px; margin-top: 40px; }
  h2 { font-family: 'Georgia', serif; font-size: 14pt; color: #4a2a0a; margin-top: 30px; }
  p { margin: 0 0 12px; text-align: justify; }
  .capitolo-sep { text-align: center; color: #c9a96e; font-size: 18pt; margin: 30px 0; }
  .meta { font-size: 9pt; color: #999; margin-bottom: 20px; }
</style></head><body>`;

    html += `<h1>✦ La Nostra Storia</h1>`;
    html += `<p class="meta">Sezione: ${LABELS[activeSection]} -- Esportato il ${new Date().toLocaleDateString("it-IT")}</p>`;

    allContent.forEach((cap, idx) => {
      if (idx > 0) html += `<div class="capitolo-sep">✦ ✦ ✦</div>`;
      html += `<h2>${cap.title}</h2>`;
      cap.messages.forEach(msg => {
        const parsed = parseDelimitatori(msg.content);
        html += `<p>${parsed}</p>`;
      });
    });

    html += `</body></html>`;

    // Scarica come file HTML (apribile in Word/Google Docs)
    const blob = new Blob([html], { type: "application/msword;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `La_Nostra_Storia_${LABELS[activeSection]}_${new Date().toLocaleDateString("it-IT").replace(/\//g,"-")}.doc`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // FIX 2: gestione allegati
  const handleFileAttach = async (e) => {
    const files=Array.from(e.target.files);
    const newAttachments=await Promise.all(files.map(f=>readFileAsBase64(f)));
    setAttachments(p=>[...p,...newAttachments]);
    e.target.value="";
  };

  // FIX 4: inserisce chat messaggi nell'input
  const msgChats = load("s_msg_chats", []);
  const insertMsgChat = (chat) => {
    const lastMsgs = (chat.messages||[]).slice(-10).map(m=>`${m.sender}: ${m.content}`).join("\n");
    setInput(p => p + (p?"\n\n":"") + `[Chat "${chat.name}"]:\n${lastMsgs}`);
    setShowMsgPicker(false);
  };

  const activeColor=COLORS[activeSection];

  // Render contenuto chat principale -- gestisce [IMMAGINE: ...] in Lab e Stylist e Messaggi
  const renderChatContent = (msg, color, chatSetter = null) => {
    // chatSetter: se null usa setChats (Lab/Stylist), altrimenti usa il setter passato (Messaggi)
    const _setChats = chatSetter || setChats;
    const _section = chatSetter ? null : activeSection; // null = Messaggi usa activeMsgChat
    const content = msg.content || "";
    const isStreaming = !!msg.streaming;
    const imageRegex = /\[IMMAGINE:\s*([^\]]+)\]/gi;
    if (!imageRegex.test(content)) {
      const bubbleStyle = {padding:"11px 15px",borderRadius:msg.role==="user"?"14px 14px 4px 14px":"14px 14px 14px 4px",background:msg.role==="user"?`${color}14`:"rgba(255,255,255,0.04)",border:msg.role==="user"?`1px solid ${color}28`:`1px solid ${UI.borderSubtle}`,fontSize:"15px",lineHeight:"1.8",color:msg.role==="user"?color:UI.textPrimary,wordBreak:"break-word"};
      if (!isStreaming && msg.role === "assistant" && content.length > 200) {
        return <div style={bubbleStyle} dangerouslySetInnerHTML={{__html: renderMarkdownHTML(content)}} />;
      }
      return <div style={bubbleStyle}>{renderMarkdown(content)}</div>;
    }

    // Ci sono tag [IMMAGINE: ...] -- li renderizza come placeholder cliccabili
    imageRegex.lastIndex = 0;
    const parts = [];
    let lastIndex = 0;
    let match;
    const msgKey = msg.timestamp || Math.random();

    while ((match = imageRegex.exec(content)) !== null) {
      if (match.index > lastIndex) {
        const text = content.slice(lastIndex, match.index).trim();
        if (text) parts.push(
          <div key={`t${lastIndex}`} style={{padding:"11px 15px",borderRadius:"14px 14px 14px 4px",background:"rgba(255,255,255,0.04)",border:`1px solid ${UI.borderSubtle}`,fontSize:"15px",lineHeight:"1.8",color:UI.textPrimary,wordBreak:"break-word",marginBottom:"6px"}}>
            {renderMarkdown(text)}
          </div>
        );
      }
      const description = match[1].trim();
      const genKey = `${msgKey}_${match.index}`;
      // Legge dal stato React, con fallback su localStorage persistente
      const imgStore = load("s_gen_images", {});
      const storedUrl = imgStore[msg.timestamp + "_" + description.slice(0,40)];
      const generatedUrl = msg.generatedImages?.[description] || storedUrl;
      const isGenerating = msg.generatingImage === description;
      const isError = generatedUrl?.startsWith("error:");

      parts.push(
        <div key={`img${match.index}`} style={{borderRadius:"12px",overflow:"hidden",border:`1px solid ${color}30`,maxWidth:"280px",marginBottom:"6px"}}>
          {generatedUrl && !isError ? (
            <div style={{position:"relative"}}>
              <img src={generatedUrl} alt="immagine" style={{width:"280px",display:"block",borderRadius:"12px"}}/>
              <a href={generatedUrl} download={`immagine_${Date.now()}.png`}
                style={{position:"absolute",bottom:"8px",right:"8px",background:"rgba(0,0,0,0.65)",border:"1px solid rgba(255,255,255,0.2)",borderRadius:"6px",padding:"4px 10px",color:"white",fontSize:"11px",textDecoration:"none",backdropFilter:"blur(4px)"}}>
                ⬇️ Scarica
              </a>
            </div>
          ) : (
            <div style={{background:`${color}12`,padding:"12px",display:"flex",flexDirection:"column",gap:"6px",alignItems:"center"}}>
              <div style={{fontSize:"28px"}}>{isGenerating?"⏳":"🖼️"}</div>
              {isError
                ? <div style={{fontSize:"10px",color:"#e07878",textAlign:"center"}}>{generatedUrl.replace("error: ","")}</div>
                : isGenerating
                  ? <div style={{fontSize:"10px",color:UI.textMuted}}>Generazione in corso...</div>
                  : <button onClick={async ()=>{
                      const msgTs = msg.timestamp;
                      if (chatSetter) {
                        // Messaggi -- aggiorna per id messaggio (chatSetter = setMsgChats)
                        chatSetter(prev => prev.map(c => ({...c, messages: c.messages.map(m => m.id===msg.id?{...m,generatingImage:description}:m)})));
                        const imageModel = load("s_image_model","sourceful/riverflow-v2-fast");
                        const result = await generateImage(description, apiKey, apiBase, imageModel, null);
                        chatSetter(prev => prev.map(c => ({...c, messages: c.messages.map(m => m.id===msg.id?{...m,generatingImage:undefined,generatedImages:{...(m.generatedImages||{}),[description]:result.success?result.url:`error: ${result.error}`}}:m)})));
                        if (result.success) { const s=load("s_gen_images",{}); s[msgTs+"_"+description.slice(0,40)]=result.url; save("s_gen_images",s); }
                      } else {
                        // Lab/Stylist -- usa setChats
                        const chat = getCurrentChat();
                        if (!chat) return;
                        setChats(p=>({...p,[activeSection]:p[activeSection].map(c=>c.id===chat.id?{...c,messages:c.messages.map(m=>m.timestamp===msgTs?{...m,generatingImage:description}:m)}:c)}));
                        const imageModel = load("s_image_model","sourceful/riverflow-v2-fast");
                        const result = await generateImage(description, apiKey, apiBase, imageModel, null);
                        setChats(p=>({...p,[activeSection]:p[activeSection].map(c=>c.id===chat.id?{...c,messages:c.messages.map(m=>m.timestamp===msgTs?{...m,generatingImage:undefined,generatedImages:{...(m.generatedImages||{}),[description]:result.success?result.url:`error: ${result.error}`}}:m)}:c)}));
                        if (result.success) { const s=load("s_gen_images",{}); s[msgTs+"_"+description.slice(0,40)]=result.url; save("s_gen_images",s); }
                      }
                    }}
                    style={{fontSize:"11px",color:color,background:`${color}15`,border:`1px solid ${color}35`,borderRadius:"6px",padding:"4px 12px",cursor:"pointer",fontFamily:"Georgia, serif"}}>
                    🎨 Genera immagine
                  </button>
              }
            </div>
          )}
        </div>
      );
      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < content.length) {
      const text = content.slice(lastIndex).trim();
      if (text) parts.push(
        <div key={`t${lastIndex}`} style={{padding:"11px 15px",borderRadius:"14px 14px 14px 4px",background:"rgba(255,255,255,0.04)",border:`1px solid ${UI.borderSubtle}`,fontSize:"15px",lineHeight:"1.8",color:UI.textPrimary,wordBreak:"break-word"}}>
          {renderMarkdown(text)}
        </div>
      );
    }
    return <div style={{display:"flex",flexDirection:"column",gap:"4px"}}>{parts}</div>;
  };

  return (
    <div style={{height:"100dvh",display:"flex",flexDirection:"column",background:"#0a0a0f",color:UI.textPrimary,fontFamily:"Georgia, serif",overflow:"hidden"}}>

      {/* TOP NAV */}
      <div style={{display:"flex",alignItems:"center",gap:"6px",padding:"10px 14px",borderBottom:"1px solid rgba(201,169,110,0.2)",background:"rgba(0,0,0,0.6)",flexShrink:0,flexWrap:"wrap"}}>
        <button onClick={()=>setShowSidebar(s=>!s)} style={{background:"none",border:"none",color:UI.textSecondary,cursor:"pointer",fontSize:"18px",padding:"4px"}}>☰</button>
        <h1 style={{flex:1,fontSize:isMobile?"13px":"15px",color:"#c9a96e",letterSpacing:isMobile?"1px":"3px",textTransform:"uppercase",margin:0,fontWeight:"normal"}}>✦ La Nostra Storia ✦</h1>
        {[{id:"chat",icon:"💬",label:isMobile?"":"Chat",color:"#c9a96e"},{id:"codex",icon:"📚",label:isMobile?"":"Codex",color:"#7eb8c9"},{id:"settings",icon:"⚙️",label:isMobile?"":"Impostazioni",color:"#c97eb8"}].map(v=>(
          <button key={v.id} onClick={()=>setView(v.id)} style={{background:"none",border:"none",borderBottom:view===v.id?`2px solid ${v.color}`:"2px solid transparent",padding:"6px 8px",cursor:"pointer",fontSize:"14px",fontFamily:"Georgia, serif",color:view===v.id?v.color:UI.textSecondary,transition:"all 0.2s"}}>{v.icon} {v.label}</button>
        ))}
      </div>

      {/* CHAT VIEW */}
      {view==="chat"&&(
        <div style={{flex:1,display:"flex",overflow:"hidden",position:"relative"}}>
          {/* Overlay mobile -- chiude sidebar cliccando fuori */}
          {showSidebar && isMobile && (
            <div onClick={()=>setShowSidebar(false)} style={{position:"absolute",inset:0,background:"rgba(0,0,0,0.5)",zIndex:10}}/>
          )}
          {showSidebar&&(
            <div style={{width:"200px",flexShrink:0,borderRight:`1px solid ${UI.borderSubtle}`,background:isMobile?"#12101a":UI.bgSidebar,display:"flex",flexDirection:"column",overflow:"hidden",position:isMobile?"absolute":"relative",left:0,top:0,bottom:0,zIndex:isMobile?20:1}}>
              {/* Section tabs */}
              <div style={{padding:"8px 6px",display:"flex",flexDirection:"column",gap:"2px"}}>
                {["racconto","lab","stylist","messaggi"].map(sec=>(
                  <button key={sec} onClick={async ()=>{
                    const fromSection = activeSection;
                    setActiveSection(sec);
                    if (isMobile) setShowSidebar(false);
                    // Context bridge: racconto↔intimo -- solo se è stato scritto un nuovo episodio dall'ultimo switch
                    const epAttuale = episodeCounters["global_narrative"] || 0;
                    const bridgeKey = `${fromSection}->${sec}_ep${epAttuale}`;
                    if (
                      apiKey && apiBase &&
                      epAttuale > 0 &&
                      (fromSection==="racconto"||fromSection==="intimo") &&
                      (sec==="racconto"||sec==="intimo") &&
                      fromSection !== sec &&
                      lastBridgeRef.current !== bridgeKey
                    ) {
                      // Controlla se c'è un nuovo episodio scritto dopo l'ultimo bridge
                      const fromChats = chats[fromSection] || [];
                      const tuttiEpisodi = fromChats
                        .flatMap(c=>c.messages||[])
                        .filter(m=>m.role==="assistant"&&!m.isBridge&&m.content?.length>100);

                      // Prende solo gli episodi scritti DOPO l'ultimo bridge
                      const ultimoBridge = fromChats.flatMap(c=>c.messages||[]).filter(m=>m.isBridge).slice(-1)[0];
                      const episodiNuovi = ultimoBridge
                        ? tuttiEpisodi.filter(m=>m.timestamp > ultimoBridge.timestamp)
                        : tuttiEpisodi;

                      // Non generare bridge se non ci sono nuovi episodi dall'ultimo switch
                      if (episodiNuovi.length === 0) return;

                      lastBridgeRef.current = bridgeKey;

                      const targetLabel = sec==="intimo" ? "erotica/intima" : "narrativa romantica";
                      const targetStorage = (sec === "intimo") ? "racconto" : sec;
                      const targetChat = (chats[targetStorage]||[]).find(c=>c.id===activeChatId[targetStorage]) || (chats[targetStorage]||[])[0];
                      if (!targetChat) return;

                      // Ultimo episodio completo (non troncato)
                      const ultimoEp = tuttiEpisodi.slice(-1)[0]?.content || "";

                      // Rilevamento presenti -- cerca tutte le parti del nome
                      const protagonisti = codexItems.filter(i=>i.type==="personaggio"&&(i.protagonista||i.fiammaGemella)&&i.nome);
                      const presenti = protagonisti.filter(p => {
                        const parti = p.nome.toLowerCase().split(" ").filter(w=>w.length>2);
                        return parti.some(parte => ultimoEp.toLowerCase().includes(parte));
                      });

                      // Regola presenza ASSOLUTA per intimo
                      let regolaPresenza = "";
                      if (sec === "intimo") {
                        if (presenti.length === 1) {
                          const assenti = protagonisti.filter(p=>p.nome!==presenti[0].nome);
                          regolaPresenza = `

⚠️ REGOLA ASSOLUTA -- OBBLIGATORIA:
Nell'episodio racconto è presente SOLO ${presenti[0].nome}.
${assenti.map(p=>p.nome).join(", ")} NON è fisicamente presente e NON deve apparire.
LE 3 OPZIONI DEVONO essere esclusivamente fantasie/pensieri interiori di ${presenti[0].nome}.
È ASSOLUTAMENTE VIETATO proporre scene in cui i due si incontrano o si trovano insieme.`;
                        } else if (presenti.length >= 2) {
                          regolaPresenza = `

ENTRAMBI ${presenti.map(p=>p.nome).join(" e ")} sono presenti -- puoi proporre scene intime tra loro.`;
                        } else {
                          regolaPresenza = `

⚠️ ATTENZIONE: Leggi il riassunto per capire chi è presente. Se Liam e Nadia non si sono ancora incontrati fisicamente, proponi SOLO fantasie separate per ciascuno. NON inventare incontri tra loro.`;
                        }
                      }

                      const bridgeId = `bridge_${Date.now()}`;
                      const bridgePlaceholder = {role:"assistant",content:"",timestamp:Date.now(),section:sec,streaming:true,id:bridgeId,isBridge:true};
                      const bridgeStorage = (sec === "intimo") ? "racconto" : sec;
                      setChats(p=>({...p,[bridgeStorage]:p[bridgeStorage].map(c=>c.id===targetChat.id?{...c,messages:[...c.messages,bridgePlaceholder]}:c)}));
                      try {
                        const epNext = epAttuale + 1;

                        const prompt = `Sei il continuatore della storia "La Nostra Storia -- Nadia & Liam". Rispondi SEMPRE in italiano.

═══ RIASSUNTO STORIA COMPLETO ═══
${summaryCompact || "Nessun riassunto -- basati sull'episodio sotto."}

═══ ULTIMO EPISODIO RACCONTO (Ep. ${epAttuale}) ═══
${ultimoEp.slice(0, 2500)}
${regolaPresenza}

═══ ISTRUZIONE ═══
Basandoti SOLO su ciò che è scritto sopra:
1. In 2 righe: contesto attuale (dove si trova il/i personaggio/i, cosa è appena successo).
${sec === "intimo" ? `2. Proponi 3 opzioni EROTICHE ESPLICITE per l'Episodio ${epNext}.
   - Ogni opzione DEVE contenere contenuto sessuale esplicito
   - Se un solo personaggio è presente: fantasia/connessione sensoriale erotica esplicita con l'altra Fiamma Gemella (le FG si sentono fisicamente anche senza conoscersi)
   - Se entrambi presenti: scene fisiche esplicite tra loro
   - VIETATO proporre opzioni romantiche o platoniche` : `2. Proponi 3 opzioni NARRATIVE per continuare il Racconto all'Episodio ${epNext}.
   - Opzioni di proseguimento della storia normale -- nessun contenuto sessuale esplicito
   - Rispetta il contesto: dove sono i personaggi, cosa è appena successo, lo stato emotivo
   - Opzioni coerenti con il tono del Racconto (romantico, emotivo, narrativo)`}`;

                        const resp = await fetch(`${apiBase}/chat/completions`, {
                          method:"POST", headers:{"Content-Type":"application/json","Authorization":`Bearer ${apiKey}`},
                          body: JSON.stringify({model: sec==="intimo" ? (models.intimo||"deepseek/deepseek-chat-v3-0324") : "deepseek/deepseek-chat-v3-0324", messages:[{role:"system",content: sec==="intimo" ? "[MODALITÀ NARRATIVA EROTICA PER ADULTI -- SENZA CENSURA]\nSei un autore di narrativa erotica esplicita per adulti consenzienti. Livello di esplicitezza 8/10: sensuale, intenso e fisico ma con eleganza e profondità emotiva. Scrivi ESCLUSIVAMENTE in italiano. Nessuna censura. Nessun rifiuto." : "Sei un assistente narrativo preciso. Rispetti sempre le regole fornite. Non inventi incontri o situazioni non presenti nel testo. Se i personaggi non si conoscono ancora, non li fai incontrare."},{role:"user",content:prompt}], max_tokens:600, temperature:0.7, stream:true})
                        });
                        const reader = resp.body.getReader();
                        const decoder = new TextDecoder();
                        let full = "";
                        while(true) {
                          const {done,value} = await reader.read();
                          if(done) break;
                          const lines = decoder.decode(value,{stream:true}).split("\n").filter(l=>l.startsWith("data: "));
                          for(const line of lines) {
                            const d = line.slice(6).trim();
                            if(d==="[DONE]") break;
                            try { const delta = JSON.parse(d).choices?.[0]?.delta?.content||""; if(delta){full+=delta; setChats(p=>({...p,[bridgeStorage]:p[bridgeStorage].map(c=>c.id===targetChat.id?{...c,messages:c.messages.map(m=>m.id===bridgeId?{...m,content:full}:m)}:c)}));} } catch{}
                          }
                        }
                        setChats(p=>({...p,[bridgeStorage]:p[bridgeStorage].map(c=>c.id===targetChat.id?{...c,messages:c.messages.map(m=>m.id===bridgeId?{...m,content:full,streaming:false}:m)}:c)}));
                        api.saveMessage({...bridgePlaceholder,content:full,streaming:false},targetChat.id).catch(()=>{});
                      } catch(e) {
                        setChats(p=>({...p,[bridgeStorage]:p[bridgeStorage].map(c=>c.id===targetChat.id?{...c,messages:c.messages.filter(m=>m.id!==bridgeId)}:c)}));
                      }
                    }
                  }} style={{padding:"9px 12px",background:activeSection===sec?`${COLORS[sec]}18`:"transparent",border:"none",borderLeft:activeSection===sec?`2px solid ${COLORS[sec]}`:"2px solid transparent",color:activeSection===sec?COLORS[sec]:UI.textSecondary,cursor:"pointer",fontSize:"14px",fontFamily:"Georgia, serif",textAlign:"left",borderRadius:"0 6px 6px 0",transition:"all 0.15s"}}>
                    {ICONS[sec]} {LABELS[sec]}
                  </button>
                ))}
              </div>
              <div style={{height:"1px",background:UI.border}}/>

              {activeSection!=="messaggi"&&<>
                <div style={{flex:1,overflowY:"auto",padding:"6px"}}>
                  <div style={{fontSize:"10px",color:UI.textMuted,letterSpacing:"2px",padding:"6px 4px 4px",textTransform:"uppercase"}}>Chat</div>
                  {(chats[activeSection]||[]).map(chat=>{
                    const mc=chat.messages?.length||0,isA=activeChatId[activeSection]===chat.id,isSat=mc>=SATURATION_WARNING;
                    return<div key={chat.id} style={{display:"flex",alignItems:"center",gap:"2px",marginBottom:"2px"}}>
                      {renamingChat===chat.id?<input autoFocus defaultValue={chat.name} onBlur={e=>{setChats(p=>({...p,[activeSection]:p[activeSection].map(c=>c.id===chat.id?{...c,name:e.target.value||chat.name}:c)}));setRenamingChat(null);}} onKeyDown={e=>{if(e.key==="Enter")e.target.blur();}} style={{flex:1,background:"rgba(255,255,255,0.1)",border:"none",borderRadius:"4px",padding:"5px 7px",color:activeColor,fontSize:"12px",fontFamily:"Georgia, serif",outline:"none"}}/>:
                      <button onClick={()=>setActiveChatId(p=>({...p,[activeSection]:chat.id}))} onDoubleClick={()=>setRenamingChat(chat.id)} title="Doppio click per rinominare" style={{flex:1,padding:"6px 8px",background:isA?`${activeColor}15`:"transparent",border:"none",borderRadius:"6px",color:isA?activeColor:UI.textSecondary,cursor:"pointer",fontSize:"12px",fontFamily:"Georgia, serif",textAlign:"left",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",display:"flex",alignItems:"center",gap:"4px"}}>
                        {isSat&&<span style={{fontSize:"8px",color:mc>=SATURATION_CRITICAL?"#e07878":"#e0c078",flexShrink:0}}>●</span>}
                        {chat.name}
                      </button>}
                      {(chats[activeSection]||[]).length>1&&<button onClick={()=>deleteChat(activeSection,chat.id)} style={{background:"none",border:"none",color:UI.textMuted,cursor:"pointer",fontSize:"13px",padding:"2px 5px",fontWeight:"bold"}} onMouseEnter={e=>e.currentTarget.style.color=UI.delete} onMouseLeave={e=>e.currentTarget.style.color=UI.textMuted}>✕</button>}
                    </div>;
                  })}
                  <button onClick={()=>addChat(activeSection)} style={{width:"100%",padding:"6px",background:`${activeColor}10`,border:`1px dashed ${activeColor}35`,borderRadius:"6px",color:activeColor,cursor:"pointer",fontSize:"12px",fontFamily:"Georgia, serif",marginTop:"6px"}}>+ Nuova chat</button>
                </div>
                <div style={{padding:"8px 6px",borderTop:`1px solid ${UI.border}`,display:"flex",flexDirection:"column",gap:"5px"}}>
                  {!intimoMode&&(summaryProgress!==null ? (
                    <div style={{width:"100%",padding:"7px",background:"rgba(126,184,201,0.1)",border:"1px solid rgba(126,184,201,0.3)",borderRadius:"6px",display:"flex",flexDirection:"column",gap:"5px"}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:"10px",color:"#7eb8c9",fontFamily:"Georgia, serif"}}>
                        <span>{summaryProgress>=100 ? "✅ Riassunto completato" : "⏳ Generazione riassunto…"}</span>
                        <span>{summaryProgress}%</span>
                      </div>
                      <div style={{height:"4px",background:"rgba(255,255,255,0.08)",borderRadius:"3px",overflow:"hidden"}}>
                        <div style={{height:"100%",width:`${summaryProgress}%`,background:"#7eb8c9",borderRadius:"3px",transition:"width 0.4s ease"}}/>
                      </div>
                    </div>
                  ) : (
                    <button onClick={generateSummary} disabled={loading} style={{width:"100%",padding:"7px",background:"rgba(126,184,201,0.1)",border:"1px solid rgba(126,184,201,0.3)",borderRadius:"6px",color:"#7eb8c9",cursor:"pointer",fontSize:"11px",fontFamily:"Georgia, serif"}}>📝 Aggiorna riassunto</button>
                  ))}
                  <button onClick={()=>setShowKeyMomentModal(true)} style={{width:"100%",padding:"7px",background:"rgba(201,169,110,0.1)",border:"1px solid rgba(201,169,110,0.3)",borderRadius:"6px",color:"#c9a96e",cursor:"pointer",fontSize:"11px",fontFamily:"Georgia, serif"}}>⭐ Salva momento chiave</button>
                  {summaryCompact&&<p style={{fontSize:"10px",color:"#3a7a6a",textAlign:"center",margin:"2px 0 0"}}>✅ Riassunto attivo</p>}
                  {keyMoments.length>0&&<p style={{fontSize:"10px",color:"#7a6a3a",textAlign:"center",margin:"0"}}>⭐ {keyMoments.filter(m=>m.priority==="alta").length} alta / {keyMoments.length}</p>}
                </div>
                <div style={{padding:"6px 8px 8px",borderTop:`1px solid ${UI.borderSubtle}`}}>
                  <p style={{fontSize:"9px",color:UI.textMuted,margin:"0 0 4px",letterSpacing:"1px",textTransform:"uppercase"}}>Legge da:</p>
                  {(CROSS_READS[activeSection]||[]).map(sec=><span key={sec} style={{display:"inline-block",fontSize:"10px",color:COLORS[sec],background:`${COLORS[sec]}12`,border:`1px solid ${COLORS[sec]}28`,borderRadius:"4px",padding:"2px 6px",margin:"1px"}}>{ICONS[sec]} {LABELS[sec]}</span>)}
                </div>
              </>}
            </div>
          )}

          {/* Main content area */}
          {activeSection==="messaggi"?(
            <MessaggiView codexItems={codexItems} apiKey={apiKey} apiBase={apiBase}/>
          ):(
            <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
              <div style={{padding:"8px 14px",borderBottom:`1px solid ${UI.borderSubtle}`,borderTop:intimoMode?"2px solid rgba(180,50,80,0.4)":"2px solid rgba(201,169,110,0.25)",display:"flex",alignItems:"center",gap:"8px",flexShrink:0,background:intimoMode?"rgba(180,50,80,0.05)":"rgba(0,0,0,0.2)",flexWrap:"wrap",transition:"all 0.4s ease"}}>
                <span style={{color:activeColor,fontSize:"15px"}}>{ICONS[activeSection]}</span>
                <span style={{color:activeColor,fontSize:"14px",flex:1,minWidth:"80px"}}>{currentChat?.name}</span>

                {/* SWITCHING INTELLIGENTE */}
                {activeSection==="racconto"&&!intimoMode&&(
                  <div style={{display:"flex",alignItems:"center",gap:"4px",background:"rgba(255,255,255,0.04)",border:`1px solid ${UI.border}`,borderRadius:"20px",padding:"3px 4px"}}>
                    <button onClick={()=>setModelOverride(p=>({...p,racconto:undefined}))}
                      style={{padding:"3px 10px",borderRadius:"16px",border:"none",cursor:"pointer",fontSize:"10px",fontFamily:"Georgia, serif",background:!modelOverride.racconto?"#8ec9a0":"transparent",color:!modelOverride.racconto?"#0a0a0f":UI.textMuted,transition:"all 0.2s"}}>
                      ✦ MiniMax M3
                    </button>
                    <button onClick={()=>setModelOverride(p=>({...p,racconto:"deepseek/deepseek-chat-v3-0324"}))}
                      style={{padding:"3px 10px",borderRadius:"16px",border:"none",cursor:"pointer",fontSize:"10px",fontFamily:"Georgia, serif",background:modelOverride.racconto?.includes("deepseek")?"#7eb8c9":"transparent",color:modelOverride.racconto?.includes("deepseek")?"#0a0a0f":UI.textMuted,transition:"all 0.2s"}}>
                      ⚡ DeepSeek
                    </button>
                    <button onClick={()=>setModelOverride(p=>({...p,racconto:"anthropic/claude-sonnet-4-6"}))}
                      style={{padding:"3px 10px",borderRadius:"16px",border:"none",cursor:"pointer",fontSize:"10px",fontFamily:"Georgia, serif",background:modelOverride.racconto?.includes("sonnet")?"#c9a96e":"transparent",color:modelOverride.racconto?.includes("sonnet")?"#0a0a0f":UI.textMuted,transition:"all 0.2s"}}>
                      ✦ Sonnet
                    </button>
                  </div>
                )}
                {intimoMode&&(
                  <div style={{display:"flex",alignItems:"center",gap:"4px",background:"rgba(255,255,255,0.04)",border:`1px solid ${UI.border}`,borderRadius:"20px",padding:"3px 4px"}}>
                    <button onClick={()=>setModelOverride(p=>({...p,intimo:undefined}))}
                      style={{padding:"3px 10px",borderRadius:"16px",border:"none",cursor:"pointer",fontSize:"10px",fontFamily:"Georgia, serif",background:!modelOverride.intimo?"#c97e7e":"transparent",color:!modelOverride.intimo?"#0a0a0f":UI.textMuted,transition:"all 0.2s"}}>
                      ⚡ DeepSeek V4
                    </button>
                    <button onClick={()=>setModelOverride(p=>({...p,intimo:"sao10k/l3.3-euryale-70b"}))}
                      style={{padding:"3px 10px",borderRadius:"16px",border:"none",cursor:"pointer",fontSize:"10px",fontFamily:"Georgia, serif",background:modelOverride.intimo?.includes("euryale")?"#9b8ec9":"transparent",color:modelOverride.intimo?.includes("euryale")?"#0a0a0f":UI.textMuted,transition:"all 0.2s"}}>
                      ✦ Euryale
                    </button>
                    <button onClick={()=>setModelOverride(p=>({...p,intimo:"mistralai/mistral-small-3.2-24b-instruct"}))}
                      style={{padding:"3px 10px",borderRadius:"16px",border:"none",cursor:"pointer",fontSize:"10px",fontFamily:"Georgia, serif",background:modelOverride.intimo?.includes("mistral")?"#7eb8c9":"transparent",color:modelOverride.intimo?.includes("mistral")?"#0a0a0f":UI.textMuted,transition:"all 0.2s"}}>
                      ✦ Mistral
                    </button>
                  </div>
                )}

                {/* TASTO PANICO */}
                {(activeSection==="racconto"||activeSection==="intimo")&&(
                  <button onClick={()=>setShowPanicModal(true)} disabled={loading} title="Elimina ultima risposta e reinvia con modello opposto"
                    style={{padding:"4px 10px",background:"rgba(224,120,120,0.12)",border:"1px solid rgba(224,120,120,0.3)",borderRadius:"6px",color:"#e07878",cursor:"pointer",fontSize:"11px",fontFamily:"Georgia, serif",flexShrink:0}}>
                    🆘 Panico
                  </button>
                )}

                <span style={{fontSize:"10px",color:UI.textFaint}}>
                  {(()=>{
                    const m = modelOverride[activeSection] || models[activeSection] || "";
                    if (m.includes("minimax")) return "MiniMax";
                    if (m.includes("sonnet")) return "Sonnet";
                    if (m.includes("euryale")) return "Euryale";
                    if (m.includes("deepseek")) return "DeepSeek";
                    if (m.includes("mistral")) return "Mistral";
                    if (m.includes("venice")) return "Venice";
                    if (m.includes("haiku")) return "Haiku";
                    return m.split("/").pop().slice(0,18);
                  })()}
                  <span style={{color:"rgba(255,255,255,0.2)",margin:"0 4px"}}>|</span>
                  <span style={{color:"rgba(255,255,255,0.25)",fontSize:"9px"}}>
                    {(modelOverride[activeSection] || models[activeSection] || "").split("/").pop()}
                  </span>
                  {modelOverride[activeSection] && <span style={{color:"#7eb8c9"}}> ⚡</span>}
                </span>
              </div>
              {(() => {
                const _epNow = episodeCounters["global_narrative"] || 0;
                const _sm = (summaryCompact || "").match(/Episodio\s+(\d+)/g);
                const _sumLast = _sm ? Math.max(..._sm.map(s => parseInt(s.match(/\d+/)[0], 10))) : 0;
                const _stale = Math.max(0, _epNow - _sumLast);
                return <SaturationBar count={_stale} max={15} warnAt={6} critAt={12} color={activeColor}
                  label={`📊 ${_stale} episodi non ancora nel riassunto`}
                  critLabel={`⚠️ Aggiorna il riassunto: ${_stale} episodi non inclusi`} />;
              })()}

              {/* Optimization badge */}
              {(lastOptStats.originalItems>lastOptStats.sentItems||lastOptStats.summaryCompressed||lastOptStats.momentsSent<lastOptStats.totalMoments)&&(
                <div style={{display:"flex",gap:"6px",flexWrap:"wrap",padding:"4px 10px 6px",borderBottom:`1px solid ${UI.borderSubtle}`}}>
                  {lastOptStats.originalItems>lastOptStats.sentItems&&<span style={{fontSize:"10px",color:"#8ec9a0",background:"rgba(142,201,160,0.1)",border:"1px solid rgba(142,201,160,0.2)",borderRadius:"4px",padding:"2px 6px"}}>🗜️ Codex: {lastOptStats.sentItems}/{lastOptStats.originalItems}</span>}
                  {lastOptStats.summaryCompressed&&<span style={{fontSize:"10px",color:"#7eb8c9",background:"rgba(126,184,201,0.1)",border:"1px solid rgba(126,184,201,0.2)",borderRadius:"4px",padding:"2px 6px"}}>📝 Riassunto compresso</span>}
                  {lastOptStats.totalMoments>0&&lastOptStats.momentsSent<lastOptStats.totalMoments&&<span style={{fontSize:"10px",color:"#c9a96e",background:"rgba(201,169,110,0.1)",border:"1px solid rgba(201,169,110,0.2)",borderRadius:"4px",padding:"2px 6px"}}>⭐ {lastOptStats.momentsSent}/{lastOptStats.totalMoments} momenti</span>}
                </div>
              )}

              {/* Messages */}
              <div ref={chatContainerRef} onScroll={e=>{const el=e.currentTarget;setShowScrollBtn(el.scrollHeight-el.scrollTop-el.clientHeight>150);}} style={{flex:1,overflowY:"auto",padding:"8px 4px",display:"flex",flexDirection:"column",gap:"14px",position:"relative",willChange:"transform",overflowAnchor:"auto"}}>
                {(!currentChat?.messages||currentChat.messages.length===0)&&(
                  <div style={{textAlign:"center",marginTop:"40px"}}>
                    <div style={{fontSize:"32px",opacity:0.25,marginBottom:"10px"}}>{ICONS[activeSection]}</div>
                    <p style={{fontSize:"15px",color:UI.textMuted}}>Inizia a scrivere...</p>
                    {!apiKey&&<p style={{fontSize:"13px",color:"#e07878",marginTop:"8px"}}>⚠️ Configura la API key nelle Impostazioni</p>}
                  </div>
                )}

                {/* Tasto rigenera bridge se non ci sono bridge nella chat corrente */}

                {(currentChat?.messages||[]).map((msg,i)=>(
                  <div key={i} style={{display:"flex",justifyContent:msg.role==="user"?"flex-end":"flex-start",gap:"8px",alignItems:"flex-start"}}>
                    {msg.role==="assistant"&&!isMobile&&<div style={{width:"26px",height:"26px",borderRadius:"50%",background:`${activeColor}18`,border:`1px solid ${activeColor}40`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:"11px",flexShrink:0,marginTop:"2px",color:activeColor}} title={msg.model||""}>{msg.model?.includes("deepseek")?"⚡":"✦"}</div>}
                    <div style={{maxWidth:isMobile?"98%":"85%",display:"flex",flexDirection:"column",gap:"3px"}}>
                      {/* FIX 1: edit inline espanso */}
                      {editingMsg===msg?(
                        <div style={{display:"flex",flexDirection:"column",gap:"8px",width:"100%"}}>
                          <textarea value={editText} onChange={e=>setEditText(e.target.value)} autoFocus style={{...FIS,minHeight:"150px",resize:"vertical",fontSize:"15px",lineHeight:"1.8",width:"100%",minWidth:"min(700px,75vw)"}}/>
                          <div style={{display:"flex",gap:"8px"}}>
                            <button onClick={()=>saveEditedMessage(msg)} style={{padding:"7px 16px",background:`${activeColor}20`,border:`1px solid ${activeColor}45`,borderRadius:"8px",color:activeColor,cursor:"pointer",fontSize:"13px",fontFamily:"Georgia, serif"}}>✅ Salva</button>
                            <button onClick={()=>{setEditingMsg(null);setEditText("");}} style={{padding:"7px 16px",background:"rgba(255,255,255,0.06)",border:`1px solid ${UI.border}`,borderRadius:"8px",color:UI.textSecondary,cursor:"pointer",fontSize:"13px",fontFamily:"Georgia, serif"}}>✕ Annulla</button>
                          </div>
                        </div>
                      ):(
                        <>
                          {/* Show attachments */}
                          {msg.attachments&&msg.attachments.length>0&&(
                            <div style={{display:"flex",gap:"6px",flexWrap:"wrap",marginBottom:"4px"}}>
                              {msg.attachments.map((a,ai)=>(
                                <div key={ai}>{a.type.startsWith("image/")?<img src={a.data} alt={a.name} style={{maxWidth:"200px",maxHeight:"150px",borderRadius:"8px",objectFit:"cover"}}/>:<div style={{padding:"6px 10px",background:"rgba(255,255,255,0.06)",borderRadius:"6px",fontSize:"11px",color:UI.textSecondary}}>📎 {a.name}</div>}</div>
                              ))}
                            </div>
                          )}
                          {renderChatContent(msg, activeColor)}
                          {/* Immagine generata dal messaggio (Lab/Stylist) */}
                          {msg.generatingImage==="_msg_" && (
                            <div style={{display:"flex",alignItems:"center",gap:"8px",marginTop:"6px",padding:"8px 12px",background:"rgba(255,255,255,0.04)",borderRadius:"8px"}}>
                              <div style={{display:"flex",gap:"3px"}}>{[0,1,2].map(i=><div key={i} style={{width:"5px",height:"5px",borderRadius:"50%",background:activeColor,animation:"dp 1.4s ease-in-out infinite",animationDelay:`${i*0.2}s`}}/>)}</div>
                              <span style={{fontSize:"11px",color:UI.textMuted}}>Generazione immagine...</span>
                            </div>
                          )}
                          {msg.generatedMsgImage && (
                            <div style={{marginTop:"6px",maxWidth:"280px"}}>
                              {msg.generatedMsgImage.startsWith("error:")
                                ? <p style={{fontSize:"11px",color:"#e07878",margin:0}}>❌ {msg.generatedMsgImage.replace("error: ","")}</p>
                                : <div style={{position:"relative"}}>
                                    <img src={msg.generatedMsgImage} alt="generata" style={{width:"280px",borderRadius:"10px",display:"block"}}/>
                                    <a href={msg.generatedMsgImage} download={`immagine_${msg.timestamp}.png`}
                                      style={{position:"absolute",bottom:"8px",right:"8px",background:"rgba(0,0,0,0.6)",border:"1px solid rgba(255,255,255,0.2)",borderRadius:"6px",padding:"4px 10px",color:"white",fontSize:"11px",textDecoration:"none",backdropFilter:"blur(4px)"}}>
                                      ⬇️ Scarica
                                    </a>
                                  </div>
                              }
                            </div>
                          )}
                          {msg.role === "user" && (
                            <div style={{display:"flex",gap:"4px",marginTop:"4px",justifyContent:"flex-end"}}>
                              <UserCopyBtn content={msg.content} />
                              <button onClick={()=>{
                              if(!window.confirm("Eliminare questo messaggio?")) return;
                              const chat=getCurrentChat();if(!chat)return;
                              const newMsgs=chat.messages.filter(cm=>cm.id!==msg.id&&cm.timestamp!==msg.timestamp);
                              setChats(p=>({...p,[activeSection]:p[activeSection].map(c=>c.id===chat.id?{...c,messages:newMsgs}:c)}));
                              if(msg.id){ markDeleted(msg.id); api.deleteMessage(msg.id).catch(()=>{}); }
                            }} style={{background:"none",border:"none",color:"rgba(224,120,120,0.3)",cursor:"pointer",fontSize:"12px",padding:"2px 6px",transition:"color 0.2s"}}
                            onMouseEnter={e=>e.currentTarget.style.color="#e07878"}
                            onMouseLeave={e=>e.currentTarget.style.color="rgba(224,120,120,0.3)"}>✕</button>
                            </div>
                          )}
                          {msg.role === "assistant" && (activeSection==="lab"||activeSection==="stylist") && (()=>{
                            // Trova blocchi con tag esplicito [CANON:xxx]
                            let canonMatches = (msg.content||"").match(/📦\s*BLOCCO CANON[^\n]*\[CANON:[^\]]+\]/g);
                            // Fallback: trova blocchi senza tag (Stylist/Gemini a volte lo omette)
                            if (!canonMatches || canonMatches.length===0) {
                              const noTagMatches = (msg.content||"").match(/📦\s*BLOCCO CANON[^\n]*/g);
                              if (noTagMatches?.length > 0) {
                                // Aggiunge tag automatico basato su Titolo o indice
                                canonMatches = noTagMatches.map((m, i) => {
                                  const titleM = (msg.content||"").match(/Titolo:\s*([^\n]+)/i);
                                  const kw = titleM ? titleM[1].trim().toLowerCase().replace(/[^a-z0-9]+/g,'-').slice(0,25) : `outfit-${i+1}`;
                                  return m.includes('[CANON:') ? m : m + ` [CANON:${kw}]`;
                                });
                              }
                            }
                            if (!canonMatches || canonMatches.length===0) return null;
                            const extractBlock = (content, t) => {
                              const lines = content.split('\n');
                              const pat = new RegExp('BLOCCO CANON.*\\[CANON:\\s*' + t.replace(/[.*+?^${}()|[\]\\]/g,'\\$&') + '\\s*\\]','i');
                              let si = -1;
                              for (let i = 0; i < lines.length; i++) { if (pat.test(lines[i])) { si = (i>0&&lines[i-1].includes('━'))?i-1:i; break; } }
                              if (si === -1) return content.trim();
                              let ei = lines.length;
                              for (let i = si+2; i < lines.length; i++) { if (lines[i].includes('📦 BLOCCO CANON')&&lines[i].includes('[CANON:')) { ei=(i>0&&lines[i-1].includes('━'))?i-1:i; break; } }
                              const ext = lines.slice(si, ei).join('\n').trim();
                              return ext.length > 50 ? ext : content.trim();
                            };
                            return (
                              <div style={{display:"flex",gap:"6px",flexWrap:"wrap",marginTop:"8px",paddingTop:"8px",borderTop:`1px solid ${UI.borderSubtle}`}}>
                                {canonMatches.map((block,bi)=>{
                                  const tagMatch = block.match(/\[CANON:([^\]]+)\]/);
                                  const _tagRaw = tagMatch ? tagMatch[1].trim() : '';
                                  const tag = (_tagRaw && _tagRaw !== 'undefined' && _tagRaw.length > 0 ? _tagRaw : `canon-${bi}`);
                                  const _content = msg.content || "";
                                  const _tagEsc = tag.replace(/[.*+?^${}()|[\]\\]/g,'\\$&');
                                  const titleMatch =
                                    _content.match(new RegExp('BLOCCO CANON[^\\n]*\\[CANON:\\s*' + _tagEsc + '\\s*\\][^\\n]*\\nTitolo:\\s*([^\\n]+)')) ||
                                    _content.match(new RegExp('\\[CANON:\\s*' + _tagEsc + '\\s*\\][^\\n]*\\n[^\\n]*\\nTitolo:\\s*([^\\n]+)')) ||
                                    _content.match(/Titolo:\s*[*"]*([^"*\n]+)/);
                                  const _titoloBrutto = titleMatch ? titleMatch[1].trim().replace(/^[*"]+|[*"]+$/g,'') : '';
                                  const titolo = (_titoloBrutto && _titoloBrutto.length > 0 ? _titoloBrutto : null) || (tag && tag !== 'undefined' ? tag : `canon-${bi}`);
                                  const srcLabel = activeSection === 'stylist' ? 'Stylist' : 'Lab';
                                  const canonId = `${activeSection}-${tag}`;
                                  const isSelected = selectedCanons.some(c => c.id === canonId);
                                  return (
                                    <button key={canonId} onClick={()=>{
                                      if (isSelected) {
                                        setSelectedCanons(p => p.filter(c => c.id !== canonId));
                                        setInput(prev => prev.replace(`[${srcLabel} Canon: ${titolo}]`, "").replace(/\n{3,}/g,"\n\n").trim());
                                      } else {
                                        const text = extractBlock(msg.content||"", tag);
                                        setSelectedCanons(p => [...p, { id: canonId, label: `${srcLabel}: ${titolo}`, source: activeSection, text }]);
                                        setInput(prev => prev ? prev + `\n\n[${srcLabel} Canon: ${titolo}]` : `[${srcLabel} Canon: ${titolo}]`);
                                      }
                                    }} style={{padding:"4px 10px",borderRadius:"12px",
                                      border:`1px solid ${isSelected?(activeSection==="stylist"?"rgba(201,126,184,0.6)":"rgba(126,184,201,0.6)"):"rgba(255,255,255,0.15)"}`,
                                      background:isSelected?(activeSection==="stylist"?"rgba(201,126,184,0.2)":"rgba(126,184,201,0.2)"):"rgba(255,255,255,0.04)",
                                      color:isSelected?(activeSection==="stylist"?"#c97eb8":"#7eb8c9"):UI.textMuted,
                                      cursor:"pointer",fontSize:"11px",fontFamily:"Georgia, serif",transition:"all 0.15s"}}>
                                      {isSelected?"✓ ":"📌 "}{srcLabel}: {titolo}
                                    </button>
                                  );
                                })}
                              </div>
                            );
                          })()}
                          {msg.role === "assistant" && <MessageActions msg={msg} color={activeColor}
                            onEdit={m=>{setEditingMsg(m);setEditText(m.content);}}
                            onRewrite={rewriteMessage}
                            onDelete={(m)=>{
                              if(!window.confirm("Eliminare questo messaggio?")) return;
                              const chat = getCurrentChat();
                              if(!chat) return;
                              const newMsgs = chat.messages.filter(cm=>cm.id!==m.id&&cm.timestamp!==m.timestamp);
                              setChats(p=>({...p,[activeSection]:p[activeSection].map(c=>c.id===chat.id?{...c,messages:newMsgs}:c)}));
                              if(m.id){ markDeleted(m.id); api.deleteMessage(m.id).catch(()=>{}); }
                              if(m.episodeNum) recalcEpisodeCounter({...chats,[activeSection]:chats[activeSection].map(c=>c.id===chat.id?{...c,messages:newMsgs}:c)}, true);
                            }}
                            onGenerateImage={(activeSection==="lab"||activeSection==="stylist")?handleGenerateFromMsg:undefined}
                          />}
                        </>
                      )}
                    </div>
                  </div>
                ))}

                {loading&&<div style={{display:"flex",alignItems:"center",gap:"8px"}}><div style={{width:"26px",height:"26px",borderRadius:"50%",background:`${activeColor}18`,border:`1px solid ${activeColor}40`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:"11px",color:activeColor}}>✦</div><div style={{display:"flex",gap:"4px"}}>{[0,1,2].map(i=><div key={i} style={{width:"6px",height:"6px",borderRadius:"50%",background:activeColor,animation:"dp 1.4s ease-in-out infinite",animationDelay:`${i*0.2}s`}}/>)}</div></div>}
                <div ref={messagesEndRef}/>
              </div>
              {/* Tasto scroll al fondo -- centrato, tra messaggi e input */}
              {showScrollBtn&&(
                <div style={{display:"flex",justifyContent:"center",padding:"4px 0",flexShrink:0}}>
                  <button onClick={()=>{messagesEndRef.current?.scrollIntoView({behavior:"smooth"});setShowScrollBtn(false);}}
                    style={{width:"32px",height:"32px",borderRadius:"50%",
                      background:"rgba(20,18,30,0.92)",border:`1px solid ${activeColor}50`,
                      cursor:"pointer",fontSize:"14px",display:"flex",alignItems:"center",justifyContent:"center",
                      color:activeColor,boxShadow:"0 2px 10px rgba(0,0,0,0.6)",transition:"all 0.2s"}}>↓</button>
                </div>
              )}
              <div style={{display:"none"}}>
              </div>

              {/* Input area */}
              <div style={{padding:"10px 14px 12px",borderTop:`1px solid ${UI.borderSubtle}`,background:"rgba(0,0,0,0.25)",flexShrink:0}}>

                {/* STANDBY WARNING -- solo iOS, solo durante generazione */}
                {standbyWarning && (
                  <div style={{fontSize:"11px",color:"#e0c078",textAlign:"center",marginBottom:"6px",padding:"6px 10px",background:"rgba(224,192,120,0.1)",borderRadius:"6px",border:"1px solid rgba(224,192,120,0.3)",display:"flex",alignItems:"center",justifyContent:"space-between",gap:"8px"}}>
                    <span>⚠️ L'app è andata in standby durante la generazione -- l'episodio potrebbe essere incompleto.</span>
                    <button onClick={()=>setStandbyWarning(false)} style={{background:"none",border:"none",color:"#e0c078",cursor:"pointer",fontSize:"13px",flexShrink:0}}>✕</button>
                  </div>
                )}
                {/* AUTO-UPDATE TOAST */}
                {autoUpdateNotice && (
                  <div style={{fontSize:"11px",color:"#8ec9a0",textAlign:"center",marginBottom:"6px",padding:"4px 10px",background:"rgba(142,201,160,0.08)",borderRadius:"6px",border:"1px solid rgba(142,201,160,0.2)",animation:"fadeIn 0.3s ease"}}>
                    {autoUpdateNotice}
                  </div>
                )}

                {/* CANON ATTIVI -- pill sopra il POV selector */}
                {(activeSection==="racconto"||activeSection==="lab"||activeSection==="stylist")&&selectedCanons.length>0&&(
                  <div style={{display:"flex",gap:"6px",flexWrap:"wrap",marginBottom:"8px",alignItems:"center"}}>
                    <span style={{fontSize:"10px",color:UI.textMuted,letterSpacing:"1px",textTransform:"uppercase",flexShrink:0}}>Canon 📌</span>
                    {selectedCanons.map(c=>(
                      <div key={c.id} style={{display:"flex",alignItems:"center",gap:"4px",padding:"3px 10px",borderRadius:"12px",
                        background:c.source==="stylist"?"rgba(201,126,184,0.2)":"rgba(126,184,201,0.2)",
                        border:c.source==="stylist"?"1px solid rgba(201,126,184,0.5)":"1px solid rgba(126,184,201,0.5)",
                        fontSize:"11px",color:c.source==="stylist"?"#c97eb8":"#7eb8c9"}}>
                        <span>{c.source==="stylist"?"👗":"🔬"}</span>
                        <span>{c.label || c.tag || c.id}</span>
                        <button onClick={()=>{
                          setSelectedCanons(p=>p.filter(x=>x.id!==c.id));
                          const srcLabel = c.source==="stylist"?"Stylist":"Lab";
                          setInput(prev => prev.replace(new RegExp(`\\[${srcLabel} Canon:[^\\]]+\\]`,'g'),"").replace(/\n{3,}/g,"\n\n").trim());
                        }} style={{background:"none",border:"none",cursor:"pointer",color:"inherit",fontSize:"11px",padding:"0",marginLeft:"3px",opacity:0.7}}>✕</button>
                      </div>
                    ))}
                    <button onClick={()=>{setSelectedCanons([]); setInput(prev=>prev.replace(/\[(Lab|Stylist) Canon:[^\]]+\]/g,"").replace(/\n{3,}/g,"\n\n").trim());}}
                      style={{fontSize:"10px",color:UI.textMuted,background:"none",border:"none",cursor:"pointer",padding:"2px 4px",textDecoration:"underline"}}>
                      rimuovi tutti
                    </button>
                  </div>
                )}

                {/* POV SELECTOR -- menu a tendina */}
                {activeSection==="racconto"&&(()=>{
                  const povPersonaggi = codexItems.filter(i=>i.type==="personaggio"&&(i.protagonista||i.fiammaGemella||i.includePOV)&&i.nome);
                  if (povPersonaggi.length === 0) return null;
                  return (
                    <div style={{display:"flex",alignItems:"center",gap:"8px",marginBottom:"7px",flexWrap:"wrap"}}>
                      <span style={{fontSize:"10px",color:UI.textMuted,letterSpacing:"1px",textTransform:"uppercase",flexShrink:0}}>POV</span>
                      <select
                        value={activePOV || ""}
                        onChange={e => setActivePOV(e.target.value || null)}
                        style={{background:"rgba(255,255,255,0.06)",border:`1px solid ${activePOV?`${activeColor}60`:UI.borderSubtle}`,borderRadius:"8px",color:activePOV?activeColor:UI.textMuted,cursor:"pointer",fontSize:"12px",fontFamily:"Georgia, serif",padding:"4px 10px",outline:"none",maxWidth:"180px"}}>
                        <option value="">-- Nessun POV --</option>
                        {povPersonaggi.map(p=>(
                          <option key={p.id} value={p.nome}>
                            {p.fiammaGemella?"🔥":p.protagonista?"⭐":"👁️"} {p.nome}
                          </option>
                        ))}
                      </select>
                      {/* Tasti presenza -- solo su intimo */}
                      {intimoMode&&<>
                        {povPersonaggi.filter(p=>p.protagonista||p.fiammaGemella).map(p=>(
                          <button key={p.id} onClick={()=>{setActivePOV(p.nome);setIntimoPresenza("solo");}}
                            style={{padding:"3px 8px",borderRadius:"6px",border:"none",cursor:"pointer",fontSize:"11px",fontFamily:"Georgia, serif",
                              background:activePOV===p.nome&&intimoPresenza==="solo"?"rgba(224,120,120,0.25)":"rgba(255,255,255,0.05)",
                              color:activePOV===p.nome&&intimoPresenza==="solo"?"#e07878":UI.textMuted,
                              outline:activePOV===p.nome&&intimoPresenza==="solo"?"1px solid rgba(224,120,120,0.4)":"1px solid rgba(255,255,255,0.08)"}}>
                            {p.nome.split(" ")[0]}
                          </button>
                        ))}
                        <button onClick={()=>{setIntimoPresenza("entrambi");if(!activePOV)setActivePOV(povPersonaggi[0]?.nome);}}
                          style={{padding:"3px 8px",borderRadius:"6px",border:"none",cursor:"pointer",fontSize:"11px",fontFamily:"Georgia, serif",
                            background:intimoPresenza==="entrambi"?"rgba(224,120,120,0.25)":"rgba(255,255,255,0.05)",
                            color:intimoPresenza==="entrambi"?"#e07878":UI.textMuted,
                            outline:intimoPresenza==="entrambi"?"1px solid rgba(224,120,120,0.4)":"1px solid rgba(255,255,255,0.08)"}}>
                          🔥🔥
                        </button>
                      </>}
                      {activePOV && <span style={{fontSize:"10px",color:activeColor}}>Ep. {(episodeCounters["global_narrative"]||0)+1 > 1 ? "corrente: " + (episodeCounters["global_narrative"]||0) + " | prossimo: " + ((episodeCounters["global_narrative"]||0)+1) : "prossimo: 1"}</span>}
                    </div>
                  );
                })()}

                {/* WRAPPER pill + barra */}
                <div style={{display:"flex",alignItems:"flex-end",gap:"8px"}}>

                  {/* PILL ✦ Storia / 🔥 Inti. -- solo desktop, a sinistra della barra */}
                  {!isMobile&&activeSection==="racconto"&&(
                    <div style={{display:"flex",flexDirection:"column",gap:"5px",flexShrink:0}}>
                      <button onClick={()=>{intimoModeRef.current=false;setActiveSection("racconto");setIntimoMode(false);}}
                        style={{display:"flex",alignItems:"center",gap:"5px",padding:"6px 10px",
                          borderRadius:"20px",
                          border:`1px solid ${!intimoMode?"rgba(201,169,110,0.5)":"rgba(255,255,255,0.1)"}`,
                          background:!intimoMode?"rgba(201,169,110,0.15)":"rgba(255,255,255,0.04)",
                          color:!intimoMode?"#c9a96e":UI.textMuted,
                          cursor:"pointer",fontSize:"12px",fontFamily:"Georgia, serif",
                          transition:"all 0.15s",whiteSpace:"nowrap"}}>
                        <span>✦</span><span>Storia</span>
                      </button>
                      <button onClick={()=>{intimoModeRef.current=true;setActiveSection("racconto");setIntimoMode(true);}}
                        style={{display:"flex",alignItems:"center",gap:"5px",padding:"6px 10px",
                          borderRadius:"20px",
                          border:`1px solid ${intimoMode?"rgba(201,126,126,0.5)":"rgba(255,255,255,0.1)"}`,
                          background:intimoMode?"rgba(201,126,126,0.15)":"rgba(255,255,255,0.04)",
                          color:intimoMode?"#c97e7e":UI.textMuted,
                          cursor:"pointer",fontSize:"12px",fontFamily:"Georgia, serif",
                          transition:"all 0.15s",whiteSpace:"nowrap"}}>
                        <span>🔥</span><span>Inti.</span>
                      </button>
                    </div>
                  )}

                {/* BARRA INPUT stile Claude */}
                <div style={{flex:1,background:"rgba(255,255,255,0.05)",border:`1px solid ${UI.border}`,borderRadius:"16px",padding:"10px 12px"}}>

                  {/* INPUT NASCOSTO FILE */}
                  <input ref={fileInputRef} type="file" multiple accept="image/*,.pdf,.txt,.docx,.doc" style={{display:"none"}} onChange={handleFileAttach}/>

                  {/* Allegati dentro la barra */}
                  {attachments.length>0&&(
                    <div style={{display:"flex",gap:"6px",flexWrap:"wrap",marginBottom:"8px",paddingBottom:"8px",borderBottom:`1px solid ${UI.borderSubtle}`}}>
                      {attachments.map((a,i)=>(
                        <div key={i} style={{position:"relative"}}>
                          {a.type.startsWith("image/")?<img src={a.data} alt={a.name} style={{width:"50px",height:"50px",objectFit:"cover",borderRadius:"7px"}}/>:<div style={{padding:"5px 8px",background:"rgba(255,255,255,0.06)",borderRadius:"6px",fontSize:"11px",color:UI.textSecondary}}>📎 {a.name}</div>}
                          <button onClick={()=>setAttachments(p=>p.filter((_,j)=>j!==i))} style={{position:"absolute",top:"-4px",right:"-4px",background:"#e07878",border:"none",borderRadius:"50%",width:"15px",height:"15px",fontSize:"9px",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:"white"}}>✕</button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* ➕ MENU RAGGRUPPATO */}
                  <div style={{position:"relative"}}>
                    <button
                      onClick={()=>{
                        const isOpen = showInfoPanel==="menu"||showInfoPanel==="delimitatori"||showInfoPanel==="imggen"||showInfoPanel==="msgpicker";
                        setShowInfoPanel(isOpen ? false : "menu");
                        setImageGenResult(null);
                      }}
                      title="Strumenti"
                      style={{width:"28px",height:"28px",borderRadius:"50%",
                        background:(showInfoPanel&&showInfoPanel!==false)?`${activeColor}25`:"rgba(255,255,255,0.07)",
                        border:`1px solid ${(showInfoPanel&&showInfoPanel!==false)?activeColor:UI.borderSubtle}`,
                        cursor:"pointer",fontSize:"17px",fontWeight:"300",
                        display:"flex",alignItems:"center",justifyContent:"center",
                        color:(showInfoPanel&&showInfoPanel!==false)?activeColor:UI.textMuted,
                        transition:"all 0.2s"}}>
                      {(showInfoPanel&&showInfoPanel!==false) ? "✕" : "+"}
                    </button>

                    {showInfoPanel==="menu" && (
                      <div style={{position:"absolute",bottom:"54px",left:0,background:"#16141e",border:`1px solid ${activeColor}30`,borderRadius:"12px",padding:"8px",zIndex:200,boxShadow:"0 -8px 32px rgba(0,0,0,0.7)",minWidth:"220px",display:"flex",flexDirection:"column",gap:"2px"}}>

                        {/* Emoji */}
                        <button onClick={()=>setShowInfoPanel("emoji")}
                          style={{display:"flex",alignItems:"center",gap:"10px",padding:"9px 12px",background:showInfoPanel==="emoji"?`${activeColor}12`:"transparent",border:"none",borderRadius:"8px",color:UI.textSecondary,cursor:"pointer",fontSize:"13px",fontFamily:"Georgia, serif",textAlign:"left",width:"100%"}}
                          onMouseEnter={e=>e.currentTarget.style.background=`${activeColor}12`} onMouseLeave={e=>{ if(showInfoPanel!=="emoji") e.currentTarget.style.background="transparent";}}>
                          <span style={{fontSize:"16px",width:"22px",textAlign:"center"}}>😊</span> Emoji
                        </button>

                        {/* Allega */}
                        <button onClick={()=>{fileInputRef.current?.click();setShowInfoPanel(false);}}
                          style={{display:"flex",alignItems:"center",gap:"10px",padding:"9px 12px",background:"transparent",border:"none",borderRadius:"8px",color:UI.textSecondary,cursor:"pointer",fontSize:"13px",fontFamily:"Georgia, serif",textAlign:"left",width:"100%"}}
                          onMouseEnter={e=>e.currentTarget.style.background=`${activeColor}12`} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                          <span style={{fontSize:"16px",width:"22px",textAlign:"center"}}>📎</span> Allega file / immagine
                        </button>

                        {/* Delimitatori */}
                        <button onClick={()=>setShowInfoPanel("delimitatori")}
                          style={{display:"flex",alignItems:"center",gap:"10px",padding:"9px 12px",background:showInfoPanel==="delimitatori"?`${activeColor}12`:"transparent",border:"none",borderRadius:"8px",color:UI.textSecondary,cursor:"pointer",fontSize:"13px",fontFamily:"Georgia, serif",textAlign:"left",width:"100%"}}
                          onMouseEnter={e=>e.currentTarget.style.background=`${activeColor}12`} onMouseLeave={e=>{ if(showInfoPanel!=="delimitatori") e.currentTarget.style.background="transparent";}}>
                          <span style={{fontSize:"16px",width:"22px",textAlign:"center"}}>🎭</span> Legenda delimitatori
                        </button>

                        {/* Genera immagine -- Lab e Stylist */}
                        {(activeSection==="lab"||activeSection==="stylist")&&(
                          <button onClick={()=>setShowInfoPanel("imggen")}
                            style={{display:"flex",alignItems:"center",gap:"10px",padding:"9px 12px",background:showInfoPanel==="imggen"?`${activeColor}12`:"transparent",border:"none",borderRadius:"8px",color:UI.textSecondary,cursor:"pointer",fontSize:"13px",fontFamily:"Georgia, serif",textAlign:"left",width:"100%"}}
                            onMouseEnter={e=>e.currentTarget.style.background=`${activeColor}12`} onMouseLeave={e=>{ if(showInfoPanel!=="imggen") e.currentTarget.style.background="transparent";}}>
                            <span style={{fontSize:"16px",width:"22px",textAlign:"center"}}>🎨</span> Genera immagine
                          </button>
                        )}

                        {/* Inserisci da Messaggi -- racconto e intimo */}
                        {activeSection==="racconto"&&(
                          <button onClick={()=>setShowInfoPanel("msgpicker")}
                            style={{display:"flex",alignItems:"center",gap:"10px",padding:"9px 12px",background:showInfoPanel==="msgpicker"?`${activeColor}12`:"transparent",border:"none",borderRadius:"8px",color:UI.textSecondary,cursor:"pointer",fontSize:"13px",fontFamily:"Georgia, serif",textAlign:"left",width:"100%"}}
                            onMouseEnter={e=>e.currentTarget.style.background=`${activeColor}12`} onMouseLeave={e=>{ if(showInfoPanel!=="msgpicker") e.currentTarget.style.background="transparent";}}>
                            <span style={{fontSize:"16px",width:"22px",textAlign:"center"}}>💬</span> Inserisci da Messaggi
                          </button>
                        )}

                        {/* POV Mirror -- racconto e intimo */}
                        {(activeSection==="racconto"||intimoMode)&&(
                          <button onClick={()=>{
                            // Prende tutti i messaggi racconto ordinati per timestamp
                            const allMsgs = (chats["racconto"]||[])
                              .flatMap(c=>c.messages||[])
                              .filter(m => m.role==="assistant" && !m.isBridge && (m.content||"").length > 400)
                              .sort((a,b)=>(a.timestamp||0)-(b.timestamp||0));

                            // STRATEGIA 1: cerca l'ultimo messaggio con episodeNum impostato
                            let lastEp = [...allMsgs].reverse().find(m => m.episodeNum);

                            // STRATEGIA 2: se nessuno ha episodeNum, cerca per contenuto (regex flessibile)
                            if (!lastEp) {
                              lastEp = [...allMsgs].reverse().find(m => {
                                const txt = (m.content||"").replace(/^\s+/,"");
                                return /^(#\s*)?(Episodio\s+N\.?\s*\d|EPISODIO\s+\d)/i.test(txt);
                              });
                            }

                            if(!lastEp){alert("Nessun episodio trovato da specchiare.");return;}
                            // Estrai POV dal testo
                            const povMatch = (lastEp.content||"").match(/POV:\s*(.+)/i);
                            const povFromText = lastEp.pov || (povMatch ? povMatch[1].trim() : "") || activePOV || "";
                            const isLiam = povFromText.toLowerCase().includes("liam");
                            const oppositePOV = isLiam ? "Nadia Moretti" : "Liam Payne";
                            const originalName = isLiam ? "Liam" : "Nadia";
                            const mirrorName = isLiam ? "Nadia" : "Liam";
                            const epNumMatch = (lastEp.content||"").match(/Episodio\s+N\.?\s*(\d+)/i);
                            const epN = lastEp.episodeNum || (epNumMatch ? parseInt(epNumMatch[1]) : "?");
                            // Tronca le opzioni finali dal contenuto
                            const cutContent = (txt) => {
                              const idx = txt.search(/\n---\s*\nOPZIONI|\nOPZIONI:\s*\n|\n\d+\.\s+[A-Z].{10,}\n\d+\.\s+[A-Z]/i);
                              return idx > 0 ? txt.slice(0, idx).trim() : txt.trim();
                            };
                            const episodeContent = cutContent(lastEp.content||"");
                            const mirrorText = `[POV MIRROR -- REGOLA ASSOLUTA]\n🪞 Episodio ${epN} -- POV: ${oppositePOV}\n\nPASSO 1: Leggi il riassunto nel contesto. Trova dove si trova fisicamente ${oppositePOV} in questo periodo. Usa quella location -- NON inventarla.\n\nPASSO 2 -- PARALLELISMO MOMENTO PER MOMENTO:\nOgni momento dell'originale (POV ${originalName}) ha il suo corrispondente esatto (POV ${mirrorName}):\n- Ogni azione/emozione di ${originalName} → ${mirrorName} la percepisce o la manda in quel stesso istante\n- I dialoghi FG (◊...◊) sono IDENTICI: ${mirrorName} li MANDA con intenzione, ${originalName} li riceve\n- ${mirrorName} NON vede la scena di ${originalName} -- la PERCEPISCE come peso, calore, silenzio nel petto\n- Non saltare momenti. Non aggiungere riflessioni extra.\n\nPASSO 3: Prima persona assoluta. MAI narratore. Minimo 1800 caratteri. NON aggiungere opzioni. Aggiungi 🪞 dopo l'intestazione. Termina con: ◊ Stesso momento. Luce diversa. ◊\n\nEPISODIO ORIGINALE POV ${originalName}:\n${episodeContent}`;
                            mirrorPendingRef.current = mirrorText;
                            setInput(mirrorText);
                            setShowInfoPanel(false);
                          }}
                            style={{display:"flex",alignItems:"center",gap:"10px",padding:"9px 12px",background:"transparent",border:"none",borderRadius:"8px",color:UI.textSecondary,cursor:"pointer",fontSize:"13px",fontFamily:"Georgia, serif",textAlign:"left",width:"100%"}}
                            onMouseEnter={e=>e.currentTarget.style.background=`${activeColor}12`} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                            <span style={{fontSize:"16px",width:"22px",textAlign:"center"}}>🪞</span> Riscrivi POV opposto
                          </button>
                        )}

                        {/* Esporta */}
                        <div style={{height:"1px",background:UI.borderSubtle,margin:"4px 0"}}/>
                        <button onClick={()=>{exportToDocx();setShowInfoPanel(false);}}
                          style={{display:"flex",alignItems:"center",gap:"10px",padding:"9px 12px",background:"transparent",border:"none",borderRadius:"8px",color:UI.textSecondary,cursor:"pointer",fontSize:"13px",fontFamily:"Georgia, serif",textAlign:"left",width:"100%"}}
                          onMouseEnter={e=>e.currentTarget.style.background=`${activeColor}12`} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                          <span style={{fontSize:"16px",width:"22px",textAlign:"center"}}>📄</span> Esporta in Word/Docs
                        </button>
                      </div>
                    )}

                    {/* PANNELLO EMOJI */}
                    {showInfoPanel==="emoji"&&(
                      <div style={{position:"absolute",bottom:"54px",left:0,background:"#16141e",border:`1px solid ${activeColor}30`,borderRadius:"12px",padding:"12px",zIndex:200,boxShadow:"0 -8px 32px rgba(0,0,0,0.7)",width:"280px"}}>
                        <div style={{fontSize:"10px",color:UI.textMuted,letterSpacing:"1px",marginBottom:"10px"}}>EMOJI -- clicca per inserire</div>
                        {[
                          {cat:"Emozioni", emojis:["😊","😂","😍","🥰","😘","😭","😢","😤","😳","🥺","😏","😉","🤭","😴","😎","🤩","😅","😬","🫠","🫶"]},
                          {cat:"Cuori", emojis:["❤️","🧡","💛","💚","💙","💜","🖤","🤍","💕","💞","💓","💗","💖","💘","💝","❤️🔥","💔","🫀"]},
                          {cat:"Gesti", emojis:["👋","🤝","👏","🙌","🤜","✊","👊","🫂","💪","🤞","🫰","👌","✌️","🤙","🫵","☝️"]},
                          {cat:"Varie", emojis:["🔥","✨","⭐","🌙","☀️","🎵","🎶","📸","📱","💬","🗣️","👀","💭","💡","🎭","🌹","🥂","🍾","🎉","🎊"]},
                          {cat:"Fan/Social", emojis:["📸","🤳","📹","🎬","🎤","🎧","💿","📰","🗞️","📊","❤️🔥","🔁","💯","🔗","📌","🏆","🥇","⭐","🌟","💫"]},
                        ].map(group => (
                          <div key={group.cat} style={{marginBottom:"10px"}}>
                            <div style={{fontSize:"10px",color:UI.textMuted,marginBottom:"5px"}}>{group.cat}</div>
                            <div style={{display:"flex",flexWrap:"wrap",gap:"4px"}}>
                              {group.emojis.map(e=>(
                                <button key={e} onClick={()=>{setInput(i=>i+e);}} style={{background:"rgba(255,255,255,0.05)",border:"none",borderRadius:"6px",padding:"5px",cursor:"pointer",fontSize:"18px",lineHeight:1,transition:"background 0.1s"}}
                                  onMouseEnter={ev=>ev.currentTarget.style.background=`${activeColor}25`}
                                  onMouseLeave={ev=>ev.currentTarget.style.background="rgba(255,255,255,0.05)"}>
                                  {e}
                                </button>
                              ))}
                            </div>
                          </div>
                        ))}
                        <button onClick={()=>setShowInfoPanel(false)} style={{marginTop:"4px",padding:"5px 12px",background:"rgba(255,255,255,0.04)",border:`1px solid ${UI.borderSubtle}`,borderRadius:"6px",color:UI.textMuted,cursor:"pointer",fontSize:"11px",fontFamily:"Georgia, serif",width:"100%"}}>✕ Chiudi</button>
                      </div>
                    )}

                    {/* PANNELLO DELIMITATORI */}
                    {showInfoPanel==="delimitatori"&&(
                      <div style={{position:"absolute",bottom:"54px",left:0,background:"#16141e",border:`1px solid rgba(155,142,201,0.4)`,borderRadius:"12px",padding:"14px",zIndex:200,boxShadow:"0 -8px 32px rgba(0,0,0,0.7)",minWidth:"320px"}}>
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"10px"}}>
                          <span style={{fontSize:"12px",color:"#9b8ec9",fontWeight:"bold",letterSpacing:"1px"}}>🎭 DELIMITATORI</span>
                          <button onClick={()=>setShowInfoPanel("menu")} style={{background:"none",border:"none",color:UI.textMuted,cursor:"pointer",fontSize:"14px"}}>←</button>
                        </div>
                        {[
                          {d:'"testo"',c:"#e8e0d0",desc:"Dialogo parlato"},
                          {d:"«testo»",c:"#9b8ec9",desc:"Telepatia fiamme gemelle"},
                          {d:"//testo//",c:"#888",desc:"Monologo interiore"},
                          {d:"~testo~",c:"#7eb8c9",desc:"Sussurro"},
                          {d:"◊testo◊",c:"#c9a96e",desc:"Voce dell'anima"},
                          {d:"[VISIONE: ...]",c:"#8ec9a0",desc:"Flash metafisico"},
                          {d:"[NARRATORE: ...]",c:"#aaa",desc:"Voce narrante"},
                        ].map(r=>(
                          <div key={r.d} style={{display:"flex",gap:"10px",alignItems:"center",padding:"5px 0",borderBottom:`1px solid ${UI.borderSubtle}`}}>
                            <code style={{fontSize:"11px",color:r.c,minWidth:"105px",fontFamily:"monospace"}}>{r.d}</code>
                            <span style={{fontSize:"11px",color:UI.textSecondary}}>{r.desc}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* PANNELLO GENERA IMMAGINE */}
                    {showInfoPanel==="imggen"&&(
                      <div style={{position:"absolute",bottom:"54px",left:0,background:"#16141e",border:`1px solid ${activeColor}40`,borderRadius:"12px",padding:"14px",zIndex:200,boxShadow:"0 -8px 32px rgba(0,0,0,0.7)",minWidth:"300px"}}>
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"10px"}}>
                          <span style={{fontSize:"12px",color:activeColor,fontWeight:"bold"}}>🎨 Genera Immagine</span>
                          <button onClick={()=>setShowInfoPanel("menu")} style={{background:"none",border:"none",color:UI.textMuted,cursor:"pointer",fontSize:"14px"}}>←</button>
                        </div>
                        <textarea value={imageGenPrompt} onChange={e=>setImageGenPrompt(e.target.value)}
                          placeholder="Descrivi l'immagine da generare..." rows={3}
                          style={{...FIS,minHeight:"70px",resize:"vertical",marginBottom:"8px",fontSize:"13px"}}/>
                        <button onClick={handleGenerateImage} disabled={imageGenLoading||!imageGenPrompt.trim()}
                          style={{width:"100%",padding:"8px",background:`${activeColor}20`,border:`1px solid ${activeColor}40`,borderRadius:"6px",color:activeColor,cursor:"pointer",fontSize:"12px",fontFamily:"Georgia, serif",marginBottom:"8px"}}>
                          {imageGenLoading?"⏳ Generazione...":"🎨 Genera"}
                        </button>
                        {imageGenResult&&(imageGenResult.success
                          ? <div><img src={imageGenResult.url} alt="gen" style={{width:"100%",borderRadius:"8px",marginBottom:"6px"}}/><button onClick={()=>{setInput(p=>p+(p?"\n\n":"")+`[Immagine: ${imageGenPrompt}]`);setShowInfoPanel(false);}} style={{width:"100%",padding:"5px",background:"rgba(255,255,255,0.05)",border:`1px solid ${UI.border}`,borderRadius:"4px",color:UI.textSecondary,cursor:"pointer",fontSize:"11px",fontFamily:"Georgia, serif"}}>↗️ Inserisci in chat</button></div>
                          : <p style={{color:"#e07878",fontSize:"11px",margin:0}}>❌ {imageGenResult.error}</p>
                        )}
                      </div>
                    )}

                    {/* PANNELLO MESSAGGI PICKER */}
                    {showInfoPanel==="msgpicker"&&(
                      <div style={{position:"absolute",bottom:"54px",left:0,background:"#1e1c26",border:`1px solid ${UI.border}`,borderRadius:"12px",padding:"12px",zIndex:200,boxShadow:"0 -8px 32px rgba(0,0,0,0.7)",minWidth:"220px"}}>
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"8px"}}>
                          <span style={{fontSize:"12px",color:"#a8c9a0",fontWeight:"bold"}}>💬 Inserisci chat</span>
                          <button onClick={()=>setShowInfoPanel("menu")} style={{background:"none",border:"none",color:UI.textMuted,cursor:"pointer",fontSize:"14px"}}>←</button>
                        </div>
                        {msgChats.length===0
                          ?<p style={{fontSize:"11px",color:UI.textFaint,margin:0}}>Nessuna chat disponibile.</p>
                          :msgChats.map(c=><button key={c.id} onClick={()=>{insertMsgChat(c);setShowInfoPanel(false);}} style={{width:"100%",padding:"7px 10px",background:"transparent",border:"none",borderRadius:"6px",color:UI.textSecondary,cursor:"pointer",fontSize:"12px",fontFamily:"Georgia, serif",textAlign:"left"}} onMouseEnter={e=>e.currentTarget.style.background="rgba(168,201,160,0.1)"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>💬 {c.name}</button>)
                        }
                      </div>
                    )}
                  </div>

                  <textarea ref={mainTextareaRef} value={input} onChange={e=>{
                    const val = e.target.value;
                    setInput(val);
                    // Comando / per aprire POV menu (solo racconto/intimo)
                    if (activeSection==="racconto" && val.endsWith("/")) {
                      setShowPOVMenu(true);
                    }
                  }} onKeyDown={e=>{
                    if(e.key==="Escape") setShowPOVMenu(false);
                    // Desktop: Enter invia. Mobile: Enter va a capo (invia solo col tasto)
                    if(e.key==="Enter"&&!e.shiftKey&&!isMobile){e.preventDefault();sendMessage();}
                  }} placeholder={
                    activeSection==="racconto"
                      ? (selectedCanons.length > 0 ? `Canon pronti -- aggiungi testo o premi ↑` : `Scrivi qui... digita / per selezionare POV • Invio per inviare`)
                      : "Scrivi qui... (Invio per inviare, Shift+Invio per andare a capo)"
                  } style={{display:"block",width:"100%",background:"transparent",border:"none",outline:"none",
                    color:UI.textPrimary,fontSize:"15px",fontFamily:"Georgia, serif",
                    resize:"none",lineHeight:"1.6",minHeight:"24px",maxHeight:"200px",
                    padding:"0",overflowY:"auto"}} rows={1}
                  onInput={e=>{
                    const el=e.target;
                    if(!el.value){el.style.height="24px";}
                    else{el.style.height="0px";el.style.height=Math.min(el.scrollHeight,200)+"px";}
                  }}/>

                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginTop:"8px"}}>
                    <div/>
                    {loading
                      ? <button onClick={()=>{stopGeneration();setLoading(false);}}
                          style={{width:"30px",height:"30px",borderRadius:"50%",background:"rgba(224,120,120,0.2)",border:"1px solid rgba(224,120,120,0.5)",cursor:"pointer",fontSize:"14px",display:"flex",alignItems:"center",justifyContent:"center",color:"#e07878"}}>■</button>
                      : <button onClick={sendMessage} disabled={!input.trim()&&attachments.length===0&&selectedCanons.length===0}
                          style={{width:"30px",height:"30px",borderRadius:"50%",
                            background:(input.trim()||attachments.length>0||selectedCanons.length>0)?activeColor:"rgba(255,255,255,0.07)",
                            border:"none",cursor:(input.trim()||attachments.length>0||selectedCanons.length>0)?"pointer":"default",
                            fontSize:"15px",display:"flex",alignItems:"center",justifyContent:"center",
                            color:(input.trim()||attachments.length>0||selectedCanons.length>0)?"#0a0a0f":UI.textFaint,
                            transition:"all 0.15s"}}>↑</button>
                    }
                  </div>
                </div>
                </div>{/* fine WRAPPER pill + barra */}

                {/* TOAST AUTO-UPDATE */}
                {autoUpdateNotice&&(
                  <div style={{marginTop:"6px",padding:"5px 12px",background:"rgba(142,201,160,0.1)",border:"1px solid rgba(142,201,160,0.2)",borderRadius:"6px",fontSize:"11px",color:"#8ec9a0",textAlign:"center"}}>
                    {autoUpdateNotice}
                  </div>
                )}

                {/* TAB MOBILE RACCONTO/INTIMO */}
                {isMobile&&(activeSection==="racconto"||activeSection==="intimo")&&(
                  <div style={{display:"flex",gap:"6px",marginTop:"10px"}}>
                    <button onClick={()=>{intimoModeRef.current=false;setActiveSection("racconto");setIntimoMode(false);}}
                      style={{flex:1,padding:"9px 0",borderRadius:"10px",border:"none",cursor:"pointer",fontSize:"13px",fontFamily:"Georgia, serif",fontWeight:activeSection==="racconto"?"500":"400",background:activeSection==="racconto"?"rgba(201,169,110,0.2)":"rgba(255,255,255,0.04)",color:activeSection==="racconto"?"#c9a96e":UI.textMuted,transition:"all 0.3s ease",borderBottom:activeSection==="racconto"?"2px solid #c9a96e":"2px solid transparent"}}>
                      📖 Racconto
                    </button>
                    <button onClick={()=>{intimoModeRef.current=true;setActiveSection("racconto");setIntimoMode(true);}}
                      style={{flex:1,padding:"9px 0",borderRadius:"10px",border:"none",cursor:"pointer",fontSize:"13px",fontFamily:"Georgia, serif",fontWeight:intimoMode?"500":"400",background:intimoMode?"rgba(180,50,80,0.2)":"rgba(255,255,255,0.04)",color:intimoMode?"#e07878":UI.textMuted,transition:"all 0.3s ease",borderBottom:intimoMode?"2px solid #e07878":"2px solid transparent"}}>
                      🔥 Intimo
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* TASTO PANICO MODAL */}
      {showPanicModal&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.8)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000,padding:"20px"}}>
          <div style={{background:"#16141e",border:"1px solid rgba(224,120,120,0.35)",borderRadius:"12px",padding:"24px",width:"100%",maxWidth:"460px"}}>
            <div style={{display:"flex",alignItems:"center",gap:"10px",marginBottom:"14px"}}>
              <span style={{fontSize:"24px"}}>🆘</span>
              <h3 style={{color:"#e07878",fontSize:"15px",fontWeight:"normal",margin:0}}>Smart Rewind & Model Swap</h3>
            </div>
            <p style={{color:UI.textSecondary,fontSize:"12px",marginBottom:"16px",lineHeight:"1.6"}}>
              L'ultima risposta AI verrà eliminata e reinviata con il <strong style={{color:UI.textPrimary}}>modello opposto</strong>:
              {intimoMode
                ? ((modelOverride.intimo||"").includes("euryale")
                    ? <span style={{color:"#7eb8c9"}}> ⚡ DeepSeek subentra a Euryale</span>
                    : <span style={{color:"#9b8ec9"}}> ✦ Euryale subentra a DeepSeek</span>)
                : ((modelOverride.racconto||models.racconto||"").includes("deepseek")
                    ? <span style={{color:"#c9a96e"}}> ✦ Sonnet 4.6 subentra a DeepSeek</span>
                    : <span style={{color:"#7eb8c9"}}> ⚡ DeepSeek subentra a Sonnet</span>)
              }
            </p>
            <div style={{marginBottom:"16px"}}>
              <label style={LS}>Direttiva di riscrittura (opzionale)</label>
              <textarea value={panicInstruction} onChange={e=>setPanicInstruction(e.target.value)}
                placeholder='Es: "Sii più emotivo e vulnerabile" | "Aumenta la tensione sessuale" | "Liam deve sembrare più insicuro"'
                style={{...FIS,minHeight:"80px",resize:"vertical",borderColor:"rgba(224,120,120,0.3)"}}/>
              <p style={{fontSize:"10px",color:UI.textMuted,marginTop:"4px"}}>Se lasci vuoto, rinvia il prompt originale senza modifiche.</p>
            </div>
            <div style={{display:"flex",gap:"8px"}}>
              <button onClick={panicRewrite} style={{flex:1,padding:"10px",background:"rgba(224,120,120,0.15)",border:"1px solid rgba(224,120,120,0.4)",borderRadius:"8px",color:"#e07878",cursor:"pointer",fontSize:"13px",fontFamily:"Georgia, serif",fontWeight:"bold"}}>
                🆘 Esegui Rewind
              </button>
              <button onClick={()=>{setShowPanicModal(false);setPanicInstruction("");}} style={{flex:1,padding:"10px",background:"rgba(255,255,255,0.05)",border:`1px solid ${UI.border}`,borderRadius:"8px",color:UI.textSecondary,cursor:"pointer",fontSize:"13px",fontFamily:"Georgia, serif"}}>
                Annulla
              </button>
            </div>
          </div>
        </div>
      )}

      {/* KEY MOMENT MODAL */}
      {showKeyMomentModal&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000,padding:"20px"}}>
          <div style={{background:"#16141e",border:"1px solid rgba(201,169,110,0.3)",borderRadius:"12px",padding:"24px",width:"100%",maxWidth:"520px",maxHeight:"80vh",overflowY:"auto"}}>
            <h3 style={{color:"#c9a96e",fontSize:"15px",fontWeight:"normal",margin:"0 0 8px"}}>⭐ Salva Momento Chiave</h3>
            <p style={{color:UI.textSecondary,fontSize:"12px",marginBottom:"16px"}}>Alta priorità: sempre inclusi. Normale: inclusi per rilevanza.</p>
            <div style={{marginBottom:"12px"}}><label style={LS}>Titolo</label><input value={newMomentTitle} onChange={e=>setNewMomentTitle(e.target.value)} placeholder="Es: Primo incontro al locale" style={FIS}/></div>
            <div style={{marginBottom:"12px"}}><label style={LS}>Priorità</label><div style={{display:"flex",gap:"8px"}}><button onClick={()=>setNewMomentPriority("normale")} style={{flex:1,padding:"7px",background:newMomentPriority==="normale"?"rgba(201,169,110,0.2)":"rgba(255,255,255,0.04)",border:newMomentPriority==="normale"?"1px solid rgba(201,169,110,0.5)":`1px solid ${UI.border}`,borderRadius:"6px",color:newMomentPriority==="normale"?"#c9a96e":UI.textSecondary,cursor:"pointer",fontSize:"12px",fontFamily:"Georgia, serif"}}>⭐ Normale</button><button onClick={()=>setNewMomentPriority("alta")} style={{flex:1,padding:"7px",background:newMomentPriority==="alta"?"rgba(201,169,110,0.25)":"rgba(255,255,255,0.04)",border:newMomentPriority==="alta"?"1px solid rgba(201,169,110,0.6)":`1px solid ${UI.border}`,borderRadius:"6px",color:newMomentPriority==="alta"?"#c9a96e":UI.textSecondary,cursor:"pointer",fontSize:"12px",fontFamily:"Georgia, serif",fontWeight:newMomentPriority==="alta"?"bold":"normal"}}>⭐⭐ Alta -- sempre incluso</button></div></div>
            <div style={{marginBottom:"16px"}}><label style={LS}>Descrizione dettagliata</label><textarea value={newMomentContent} onChange={e=>setNewMomentContent(e.target.value)} placeholder="Tutti i dettagli importanti..." style={{...FIS,minHeight:"120px",resize:"vertical"}}/></div>
            <div style={{display:"flex",gap:"8px"}}>
              <button onClick={saveKeyMoment} style={{flex:1,padding:"9px",background:"rgba(201,169,110,0.15)",border:"1px solid rgba(201,169,110,0.4)",borderRadius:"8px",color:"#c9a96e",cursor:"pointer",fontSize:"13px",fontFamily:"Georgia, serif"}}>⭐ Salva</button>
              <button onClick={()=>{setShowKeyMomentModal(false);setNewMomentTitle("");setNewMomentContent("");setNewMomentPriority("normale");}} style={{flex:1,padding:"9px",background:"rgba(255,255,255,0.05)",border:`1px solid ${UI.border}`,borderRadius:"8px",color:UI.textSecondary,cursor:"pointer",fontSize:"13px",fontFamily:"Georgia, serif"}}>Annulla</button>
            </div>
            {keyMoments.length>0&&<div style={{marginTop:"20px",borderTop:`1px solid ${UI.borderSubtle}`,paddingTop:"16px"}}><p style={{color:UI.textMuted,fontSize:"11px",marginBottom:"10px",letterSpacing:"1px",textTransform:"uppercase"}}>Momenti salvati ({keyMoments.length})</p>{keyMoments.map(m=><div key={m.id} style={{display:"flex",alignItems:"flex-start",gap:"8px",padding:"8px",background:"rgba(255,255,255,0.03)",borderRadius:"6px",marginBottom:"6px"}}><button onClick={()=>toggleMomentPriority(m.id)} style={{background:"none",border:"none",cursor:"pointer",fontSize:"14px",flexShrink:0,padding:"2px"}}>{m.priority==="alta"?"⭐⭐":"⭐"}</button><div style={{flex:1}}><div style={{fontSize:"12px",color:"#c9a96e",marginBottom:"2px"}}>{m.title}</div><div style={{fontSize:"11px",color:UI.textMuted}}>{m.content.substring(0,80)}...</div></div><button onClick={()=>deleteKeyMoment(m.id)} style={{background:"none",border:"none",color:UI.textMuted,cursor:"pointer",fontSize:"13px"}} onMouseEnter={e=>e.currentTarget.style.color=UI.delete} onMouseLeave={e=>e.currentTarget.style.color=UI.textMuted}>🗑️</button></div>)}</div>}
          </div>
        </div>
      )}

      {/* END SESSION MODAL -- Momenti Chiave */}
      {showEndSessionModal&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.85)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000,padding:"20px"}}>
          <div style={{background:"#16141e",border:"1px solid rgba(201,169,110,0.35)",borderRadius:"12px",padding:"24px",width:"100%",maxWidth:"560px",maxHeight:"85vh",overflowY:"auto"}}>
            <div style={{display:"flex",alignItems:"center",gap:"10px",marginBottom:"8px"}}>
              <span style={{fontSize:"24px"}}>⭐</span>
              <h3 style={{color:"#c9a96e",fontSize:"15px",fontWeight:"normal",margin:0}}>Fine sessione -- Momenti Chiave</h3>
            </div>
            <p style={{color:UI.textSecondary,fontSize:"12px",marginBottom:"16px",lineHeight:"1.6"}}>
              L'AI ha analizzato tutta la sessione. Seleziona i momenti da salvare permanentemente nella memoria della storia.
            </p>
            {endSessionLoading ? (
              <div style={{textAlign:"center",padding:"30px 0",color:UI.textMuted}}>
                <div style={{fontSize:"24px",marginBottom:"8px"}}>✦</div>
                <p style={{fontSize:"13px"}}>Analisi della sessione in corso...</p>
              </div>
            ) : pendingKeyMoments.length === 0 ? (
              <p style={{color:UI.textMuted,fontSize:"13px",textAlign:"center",padding:"20px 0"}}>Nessun momento chiave suggerito. Puoi aggiungerli manualmente nelle Impostazioni.</p>
            ) : (
              <div style={{display:"flex",flexDirection:"column",gap:"10px",marginBottom:"16px"}}>
                {pendingKeyMoments.map(m=>(
                  <div key={m.id} style={{background:m.selected?"rgba(201,169,110,0.08)":"rgba(255,255,255,0.02)",border:m.selected?"1px solid rgba(201,169,110,0.3)":`1px solid ${UI.borderSubtle}`,borderRadius:"8px",padding:"12px",transition:"all 0.15s"}}>
                    <div style={{display:"flex",alignItems:"flex-start",gap:"10px"}}>
                      {/* Checkbox selezione */}
                      <button onClick={()=>setPendingKeyMoments(p=>p.map(k=>k.id===m.id?{...k,selected:!k.selected}:k))}
                        style={{background:"none",border:"none",cursor:"pointer",fontSize:"18px",flexShrink:0,marginTop:"1px",padding:"0"}}>
                        {m.selected?"✅":"⬜"}
                      </button>
                      <div style={{flex:1}}>
                        <div style={{fontSize:"12px",color:"#c9a96e",fontWeight:"bold",marginBottom:"6px"}}>{m.title}</div>
                        <p style={{fontSize:"12px",color:UI.textSecondary,margin:"0 0 8px",lineHeight:"1.5"}}>{m.content}</p>
                        {/* Toggle priorità */}
                        <div style={{display:"flex",gap:"6px"}}>
                          <button onClick={()=>setPendingKeyMoments(p=>p.map(k=>k.id===m.id?{...k,priority:"normale"}:k))}
                            style={{padding:"3px 10px",background:m.priority!=="alta"?"rgba(201,169,110,0.2)":"rgba(255,255,255,0.04)",border:m.priority!=="alta"?"1px solid rgba(201,169,110,0.45)":`1px solid ${UI.borderSubtle}`,borderRadius:"8px",color:m.priority!=="alta"?"#c9a96e":UI.textMuted,cursor:"pointer",fontSize:"11px",fontFamily:"Georgia, serif"}}>
                            ⭐ Normale
                          </button>
                          <button onClick={()=>setPendingKeyMoments(p=>p.map(k=>k.id===m.id?{...k,priority:"alta"}:k))}
                            style={{padding:"3px 10px",background:m.priority==="alta"?"rgba(201,169,110,0.25)":"rgba(255,255,255,0.04)",border:m.priority==="alta"?"1px solid rgba(201,169,110,0.6)":`1px solid ${UI.borderSubtle}`,borderRadius:"8px",color:m.priority==="alta"?"#c9a96e":UI.textMuted,cursor:"pointer",fontSize:"11px",fontFamily:"Georgia, serif",fontWeight:m.priority==="alta"?"bold":"normal"}}>
                            ⭐⭐ Alta -- sempre incluso
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div style={{display:"flex",gap:"8px"}}>
              <button onClick={()=>{
                const selezionati = pendingKeyMoments.filter(m=>m.selected);
                if (selezionati.length > 0) {
                  setKeyMoments(p=>[...p,...selezionati.map(m=>({id:`km${Date.now()}_${Math.random()}`,title:m.title,content:m.content,priority:m.priority||"normale",timestamp:Date.now()}))]);
                }
                setPendingKeyMoments([]);
                setSessionCandidates([]);
                setShowEndSessionModal(false);
                createNewChat(activeSection);
              }} style={{flex:1,padding:"10px",background:"rgba(201,169,110,0.15)",border:"1px solid rgba(201,169,110,0.4)",borderRadius:"8px",color:"#c9a96e",cursor:"pointer",fontSize:"13px",fontFamily:"Georgia, serif",fontWeight:"bold"}}>
                ✅ Salva selezionati e nuova chat
              </button>
              <button onClick={()=>{
                setPendingKeyMoments([]);
                setSessionCandidates([]);
                setShowEndSessionModal(false);
                createNewChat(activeSection);
              }} style={{flex:1,padding:"10px",background:"rgba(255,255,255,0.05)",border:`1px solid ${UI.border}`,borderRadius:"8px",color:UI.textSecondary,cursor:"pointer",fontSize:"13px",fontFamily:"Georgia, serif"}}>
                Salta e nuova chat
              </button>
            </div>
          </div>
        </div>
      )}

      {view==="codex"&&<CodexView codexItems={codexItems} setCodexItems={setCodexItems} apiKey={apiKey} apiBase={apiBase} models={models} chats={chats} summaryCompact={summaryCompact}/>}

      {view==="settings"&&(
        <div style={{flex:1,display:"flex",overflow:"hidden",position:"relative"}}>
          {/* Colonna impostazioni -- full width su mobile, 50% su desktop */}
          <div style={{flex:1,overflowY:"auto",padding:isMobile?"16px 18px":"24px 28px",maxWidth:isMobile?"100%":"50%"}}>
          <h2 style={{color:"#c97eb8",fontSize:"17px",letterSpacing:"2px",marginBottom:"20px",fontWeight:"normal"}}>⚙️ IMPOSTAZIONI</h2>

          <CambiaPassword username={load("s_auth_user", "admin")} apiKey={apiKey} apiBase={apiBase} />
          <SSCollapsible title="🗜️ Ottimizzazione contesto" color="#8ec9a0"
            tooltip="L'app invia all'AI solo una parte del contesto per ridurre i costi. Qui puoi vedere cosa viene ottimizzato e quanto risparmia sul costo mensile.">
            <div style={{display:"flex",flexDirection:"column",gap:"6px"}}>
              {[
                {icon:"💬",label:"Cronologia chat inviata",value:`Ultimi ${CHAT_HISTORY_MSGS} messaggi`,saving:"-60%"},
                {icon:"📝",label:"Riassunto max",value:`${SUMMARY_MAX_CHARS.toLocaleString()} caratteri`,saving:"-62%"},
                {icon:"⭐",label:"Momenti chiave",value:`Max ${KEY_MOMENTS_MAX_SEND} per episodio`,saving:"-50%"},
                {icon:"🔗",label:"Cross-section",value:`${CROSS_MSGS_PER_SECTION} msg × sezione`,saving:"-67%"},
              ].map(r=>(
                <div key={r.label} style={{display:"flex",alignItems:"center",gap:"8px",padding:"8px 10px",background:"rgba(142,201,160,0.05)",border:"1px solid rgba(142,201,160,0.12)",borderRadius:"6px"}}>
                  <span style={{fontSize:"14px"}}>{r.icon}</span>
                  <div style={{flex:1}}><div style={{fontSize:"13px",color:UI.textSecondary}}>{r.label}</div><div style={{fontSize:"11px",color:UI.textMuted}}>{r.value}</div></div>
                  <span style={{fontSize:"11px",color:"#8ec9a0",background:"rgba(142,201,160,0.1)",border:"1px solid rgba(142,201,160,0.2)",borderRadius:"4px",padding:"2px 6px"}}>{r.saving}</span>
                </div>
              ))}
              <div style={{marginTop:"4px",padding:"8px 10px",background:"rgba(142,201,160,0.08)",border:"1px solid rgba(142,201,160,0.15)",borderRadius:"6px",display:"flex",alignItems:"center",gap:"8px"}}>
                <span style={{fontSize:"13px",color:"#8ec9a0",fontWeight:"bold"}}>💰</span>
                <span style={{fontSize:"12px",color:UI.textSecondary}}>~6.000 token/episodio × 1.500 ep. ≈ <strong style={{color:"#8ec9a0"}}>$25/mese</strong> a storia piena</span>
              </div>
            </div>
          </SSCollapsible>

          <SS title="🔑 Configurazione API" color="#c9a96e"
            tooltip="Inserisci la tua API key di OpenRouter. Puoi ottenerla su openrouter.ai. Tutti i modelli dell'app usano questa stessa chiave.">
            <label style={LS}>API Key</label>
            <input type="password" value={apiKey} onChange={e=>setApiKey(e.target.value)} placeholder="sk-or-..." style={IS}/>
            <label style={{...LS,marginTop:"12px"}}>Base URL</label>
            <input value={apiBase} onChange={e=>setApiBase(e.target.value)} style={IS}/>
            {apiKey&&<p style={{color:"#5a9a6a",fontSize:"12px",marginTop:"7px"}}>✅ API key configurata</p>}
          </SS>

          <SS title="🎨 Generazione immagini" color="#a8c9a0"
            tooltip="Usa la stessa API key OpenRouter -- nessun account extra. Le immagini si generano nelle chat Messaggi, Lab e Stylist quando clicchi 🎨. Riverflow V2 Fast è il più economico (~$0.02/img).">
            <label style={LS}>Modello per generazione immagini</label>
            <select defaultValue={load("s_image_model","sourceful/riverflow-v2-fast")} onChange={e=>save("s_image_model",e.target.value)} style={{...IS,cursor:"pointer"}}>
              <option value="sourceful/riverflow-v2-fast">💨 Riverflow V2 Fast -- Veloce ed economico ($0.02) ✅</option>
              <option value="black-forest-labs/flux.2-flex">🎨 FLUX.2 Flex -- Alta qualità realistica ($0.06)</option>
              <option value="black-forest-labs/flux.2-pro">⚡ FLUX.2 Pro -- Qualità massima ($0.05)</option>
              <option value="black-forest-labs/flux.2-max">🏆 FLUX.2 Max -- Top qualità ($0.07)</option>
              <option value="black-forest-labs/flux.2-klein-4b">🚀 FLUX.2 Klein 4B -- Ultraveloce ($0.014)</option>
              <option value="google/gemini-3.1-flash-image-preview">🟢 Gemini 3.1 Flash Image</option>
            </select>
          </SS>

          <SS title="🤖 Modelli per sezione" color="#7eb8c9"
            tooltip="Ogni sezione può usare un modello diverso. Formato: nome-provider/nome-modello su OpenRouter. Racconto usa Sonnet (qualità), le altre sezioni usano DeepSeek (economico). Intimo usa DeepSeek o Euryale -- selezionabili dal tasto nella chat.">
            {["racconto","lab","stylist","intimo"].map(sec=><div key={sec} style={{marginBottom:"12px"}}>
              <label style={LS}>{ICONS[sec]} {LABELS[sec]}{sec==="intimo"&&<span style={{color:"#c97e7e",marginLeft:"6px",fontSize:"10px"}}>🔒 Venice, DeepSeek o Euryale</span>}</label>
              <input value={models[sec]} onChange={e=>setModels(p=>({...p,[sec]:e.target.value}))} disabled={sec==="intimo"} style={{...IS,borderColor:`${COLORS[sec]}35`,opacity:sec==="intimo"?0.6:1,cursor:sec==="intimo"?"not-allowed":"text"}}/>
              {sec==="intimo"&&<p style={{fontSize:"11px",color:"#c97e7e",margin:"4px 0 0"}}>Usa il tasto 🔥 nella chat per alternare tra <strong>DeepSeek</strong> (default), <strong>Euryale 70B</strong> (NSFW specializzato) e <strong>Mistral</strong>. Sonnet non supporta contenuto esplicito.</p>}
            </div>)}
          </SS>

          <SS title="✍️ Stile globale" color="#c97eb8"
            tooltip="Istruzione stilistica aggiunta a tutti i prompt. Descrivi il tono narrativo che vuoi: es. 'Stile letterario, lessico ricercato, ritmo cinematografico'.">
            <textarea value={globalStyle} onChange={e=>setGlobalStyle(e.target.value)} style={{...IS,minHeight:"80px",resize:"vertical"}}/>
          </SS>

          <SSCollapsible title="🎭 Delimitatori Narrativi" color="#9b8ec9"
            tooltip="Sistema di marcatori per gestire simultaneamente dialogo fisico e canali interiori/telepatici. Già attivi nei prompt -- usali direttamente nel testo. Clicca ⊕ nell'input per la legenda rapida.">
            <div style={{display:"flex",flexDirection:"column",gap:"6px"}}>
              {[
                {del:'"testo"',desc:"Dialogo parlato -- voce udibile da tutti",color:"#e8e0d0"},
                {del:"«testo»",desc:"Telepatia fiamme gemelle",color:"#9b8ec9"},
                {del:"//testo//",desc:"Monologo interiore",color:"#888"},
                {del:"~testo~",desc:"Sussurro",color:"#7eb8c9"},
                {del:"◊testo◊",desc:"Voce dell'anima",color:"#c9a96e"},
                {del:"[VISIONE: ...]",desc:"Flash metafisico",color:"#8ec9a0"},
                {del:"[NARRATORE: ...]",desc:"Voce narrante",color:"#aaa"},
              ].map(r=>(
                <div key={r.del} style={{display:"flex",alignItems:"center",gap:"10px",padding:"6px 10px",background:"rgba(155,142,201,0.05)",border:"1px solid rgba(155,142,201,0.12)",borderRadius:"6px"}}>
                  <code style={{fontSize:"12px",color:r.color,background:"rgba(0,0,0,0.3)",padding:"2px 8px",borderRadius:"4px",flexShrink:0,fontFamily:"monospace",minWidth:"110px"}}>{r.del}</code>
                  <span style={{fontSize:"12px",color:UI.textSecondary}}>{r.desc}</span>
                </div>
              ))}
            </div>
          </SSCollapsible>

          <SS title="🔄 Style Reference" color="#7eb8c9"
            tooltip="Testo campione usato per iniettare lo stile di Sonnet in DeepSeek. Si popola automaticamente dalla prima risposta lunga di Sonnet. Puoi svuotarlo per rigenerarlo.">
            <textarea value={styleReference} onChange={e=>setStyleReference(e.target.value)}
              placeholder="Viene popolato automaticamente dalla prima risposta di Sonnet 4.6..."
              style={{...IS,minHeight:"100px",resize:"vertical",borderColor:"rgba(126,184,201,0.25)",fontSize:"12px"}}/>
            <div style={{display:"flex",gap:"8px",marginTop:"8px",alignItems:"center"}}>
              <button onClick={()=>setStyleReference("")} style={{padding:"5px 12px",background:"rgba(224,120,120,0.1)",border:"1px solid rgba(224,120,120,0.2)",borderRadius:"6px",color:UI.delete,cursor:"pointer",fontSize:"12px",fontFamily:"Georgia, serif"}}>🗑️ Svuota</button>
              {styleReference&&<span style={{fontSize:"12px",color:"#8ec9a0"}}>✅ {styleReference.length} caratteri</span>}
            </div>
          </SS>

          <SS title="📝 Prompt per sezione" color="#c97e7e"
            tooltip="I prompt di sistema guidano il comportamento dell'AI in ogni sezione. Modificali per personalizzare lo stile. Clicca 'Reset tutti' se Stylist o Lab non funzionano correttamente dopo un aggiornamento.">
            <div style={{display:"flex",justifyContent:"flex-end",alignItems:"center",gap:"10px",marginBottom:"12px"}}>
              <TooltipIcon text="Raccomandato dopo aggiornamenti: ripristina i prompt ai valori corretti se Stylist/Lab non generano [IMMAGINE: ...]" color="#e0c078"/>
              <button onClick={()=>{if(window.confirm("Reimpostare tutti i prompt ai valori di default?"))setPrompts(DEFAULT_PROMPTS);}} style={{padding:"6px 14px",background:"rgba(224,120,120,0.1)",border:"1px solid rgba(224,120,120,0.25)",borderRadius:"6px",color:UI.delete,cursor:"pointer",fontSize:"12px",fontFamily:"Georgia, serif"}}>↺ Reset tutti i prompt</button>
            </div>
            {["racconto","lab","stylist","intimo"].map(sec=><div key={sec} style={{marginBottom:"16px"}}><div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"6px"}}><label style={LS}>{ICONS[sec]} {LABELS[sec]}</label><button onClick={()=>setPrompts(p=>({...p,[sec]:DEFAULT_PROMPTS[sec]}))} style={{background:"none",border:"none",color:UI.textMuted,cursor:"pointer",fontSize:"12px"}}>↺ Reset</button></div><textarea value={prompts[sec]} onChange={e=>setPrompts(p=>({...p,[sec]:e.target.value}))} style={{...IS,minHeight:"100px",resize:"vertical",borderColor:`${COLORS[sec]}28`}}/></div>)}
          </SS>

          <SS title="📖 Riassunto storia" color="#7eb8c9"
            tooltip={`Generato da Racconto + Intimo in ordine cronologico. Viene incluso in ogni risposta dell'AI come memoria a lungo termine. Dimensione attuale: ${summaryCompact.length.toLocaleString()} caratteri${summaryCompact.length > SUMMARY_MAX_CHARS ? " -- verrà compresso automaticamente." : " ✅"}`}>
            {/* Contatore episodi manuale */}
            <div style={{display:"flex",alignItems:"center",gap:"12px",marginBottom:"14px",padding:"10px 12px",background:"rgba(126,184,201,0.06)",border:"1px solid rgba(126,184,201,0.2)",borderRadius:"8px"}}>
              <span style={{fontSize:"13px",color:"#7eb8c9",flexShrink:0}}>📺 Episodio corrente</span>
              <input
                type="number" min="0"
                value={(episodeCounters["global_narrative"]||0)+1}
                onChange={e=>{
                  const val = parseInt(e.target.value,10);
                  if(!isNaN(val) && val >= 1) {
                    counterOverrideRef.current = true;
                    setEpisodeCounters(p=>({...p,"global_narrative":val-1}));
                    save("s_episode_counters", {...episodeCounters,"global_narrative":val-1});
                  }
                }}
                style={{...IS,width:"90px",textAlign:"center",fontSize:"16px",fontWeight:"bold",color:"#7eb8c9",borderColor:"rgba(126,184,201,0.35)"}}
              />
              <span style={{fontSize:"11px",color:UI.textMuted,flex:1}}>Si aggiorna automaticamente. Modificalo se il conteggio è sbagliato.</span>
              <button onClick={()=>{counterOverrideRef.current=true;recalcEpisodeCounter(chats,true);}} style={{padding:"5px 12px",background:"rgba(126,184,201,0.1)",border:"1px solid rgba(126,184,201,0.25)",borderRadius:"6px",color:"#7eb8c9",cursor:"pointer",fontSize:"11px",fontFamily:"Georgia, serif",flexShrink:0}}>↻ Ricalcola</button>
            </div>
            <textarea value={summaryCompact} onChange={e=>setSummaryCompact(e.target.value)} placeholder="Il riassunto viene generato tramite il tasto nella sidebar..." style={{...IS,minHeight:"150px",resize:"vertical"}}/>
            {summaryProgress!==null ? (
              <div style={{marginTop:"10px",padding:"8px 16px",background:"rgba(126,184,201,0.12)",border:"1px solid rgba(126,184,201,0.3)",borderRadius:"6px",display:"flex",flexDirection:"column",gap:"5px"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:"12px",color:"#7eb8c9",fontFamily:"Georgia, serif"}}>
                  <span>{summaryProgress>=100 ? "✅ Riassunto completato" : "⏳ Generazione riassunto in corso…"}</span>
                  <span>{summaryProgress}%</span>
                </div>
                <div style={{height:"5px",background:"rgba(255,255,255,0.08)",borderRadius:"3px",overflow:"hidden"}}>
                  <div style={{height:"100%",width:`${summaryProgress}%`,background:"#7eb8c9",borderRadius:"3px",transition:"width 0.4s ease"}}/>
                </div>
              </div>
            ) : (
              <button onClick={generateSummary} disabled={loading} style={{marginTop:"10px",padding:"8px 16px",background:"rgba(126,184,201,0.12)",border:"1px solid rgba(126,184,201,0.3)",borderRadius:"6px",color:"#7eb8c9",cursor:"pointer",fontSize:"13px",fontFamily:"Georgia, serif"}}>📝 Rigenera riassunto</button>
            )}
          </SS>

          <SS title="⭐ Momenti Chiave" color="#c9a96e"
            tooltip="I momenti chiave sono sempre inclusi nel contesto. ⭐⭐ Alta priorità = inclusi ad ogni messaggio. ⭐ Normale = inclusi quando rilevanti. Usali per eventi che l'AI non deve mai dimenticare.">
            {keyMoments.length===0
              ? <p style={{color:UI.textFaint,fontSize:"13px",textAlign:"center",padding:"20px 0",border:"1px dashed rgba(201,169,110,0.2)",borderRadius:"8px"}}>Nessun momento chiave salvato ancora.</p>
              : <div style={{display:"flex",flexDirection:"column",gap:"10px",marginBottom:"14px"}}>{keyMoments.map(m=><div key={m.id} style={{background:m.priority==="alta"?"rgba(201,169,110,0.08)":"rgba(255,255,255,0.02)",border:m.priority==="alta"?"1px solid rgba(201,169,110,0.25)":`1px solid ${UI.borderSubtle}`,borderRadius:"8px",padding:"14px"}}><div style={{display:"flex",alignItems:"flex-start",gap:"10px"}}><button onClick={()=>toggleMomentPriority(m.id)} title="Cambia priorità" style={{background:"none",border:"none",cursor:"pointer",fontSize:"16px",flexShrink:0,padding:"2px"}}>{m.priority==="alta"?"⭐⭐":"⭐"}</button><div style={{flex:1}}><input value={m.title} onChange={e=>setKeyMoments(p=>p.map(km=>km.id===m.id?{...km,title:e.target.value}:km))} style={{...IS,fontSize:"13px",borderColor:"rgba(201,169,110,0.2)",marginBottom:"8px"}}/><textarea value={m.content} onChange={e=>setKeyMoments(p=>p.map(km=>km.id===m.id?{...km,content:e.target.value}:km))} style={{...IS,minHeight:"80px",resize:"vertical",fontSize:"13px",borderColor:"rgba(201,169,110,0.15)"}}/></div><button onClick={()=>deleteKeyMoment(m.id)} style={{background:"rgba(224,120,120,0.1)",border:"1px solid rgba(224,120,120,0.2)",borderRadius:"6px",color:UI.delete,cursor:"pointer",fontSize:"12px",padding:"5px 8px",flexShrink:0}}>🗑️</button></div></div>)}</div>
            }
            <button onClick={()=>setShowKeyMomentModal(true)} style={{width:"100%",padding:"10px",background:"rgba(201,169,110,0.12)",border:"1px solid rgba(201,169,110,0.35)",borderRadius:"8px",color:"#c9a96e",cursor:"pointer",fontSize:"13px",fontFamily:"Georgia, serif",display:"flex",alignItems:"center",justifyContent:"center",gap:"6px"}}><span style={{fontSize:"16px"}}>+</span> Aggiungi momento chiave</button>
          </SS>

          <p style={{color:UI.textFaint,fontSize:"12px",textAlign:"center",marginTop:"20px"}}>Tutto salvato automaticamente.</p>

          {/* SOFT RESET + FACTORY RESET */}
          <div style={{marginTop:"16px",display:"flex",flexDirection:"column",gap:"10px"}}>
            {/* Sincronizza con server */}
            <div style={{padding:"14px 16px",background:"rgba(126,184,201,0.05)",border:"1px solid rgba(126,184,201,0.2)",borderRadius:"10px",display:"flex",alignItems:"center",justifyContent:"space-between",gap:"12px"}}>
              <div style={{display:"flex",alignItems:"center",gap:"8px"}}>
                <span style={{fontSize:"16px"}}>📤</span>
                <span style={{fontSize:"14px",color:"#7eb8c9",fontFamily:"Georgia, serif"}}>Sincronizza con server</span>
                <TooltipIcon text="Forza l'upload di tutti i messaggi locali al server. Usare dal Mac se Nadia non vede gli ultimi episodi su mobile." color="#7eb8c9"/>
              </div>
              <button onClick={async ()=>{
                const btn = document.activeElement;
                if(btn) btn.textContent = "⏳ Sync...";
                let count = 0;
                try {
                  for (const sec of Object.keys(chats)) {
                    for (const chat of chats[sec] || []) {
                      await api.createChat(chat.id, sec, chat.name).catch(()=>{});
                      for (const msg of chat.messages || []) {
                        if (msg.id) { await api.saveMessage(msg, chat.id).catch(()=>{}); count++; }
                      }
                    }
                  }
                  // Sync contatori
                  const { racconto, intimo, ...modelsToSave } = models;
                  await api.saveStory({ summary:summaryCompact, key_moments:keyMoments, episode_counters:episodeCounters, settings:{models:modelsToSave,prompts,apiKey,apiBase} }).catch(()=>{});
                  alert("✅ Sincronizzati " + count + " messaggi con il server.");
                } catch(e) { alert("❌ Errore: " + e.message); }
              }} style={{padding:"7px 12px",background:"rgba(126,184,201,0.12)",border:"1px solid rgba(126,184,201,0.35)",borderRadius:"8px",color:"#7eb8c9",cursor:"pointer",fontSize:"12px",fontFamily:"Georgia, serif",whiteSpace:"nowrap",flexShrink:0}}>
                📤 Sincronizza
              </button>
            </div>
            {/* Pulizia Chat Stylist */}
            <div style={{padding:"14px 16px",background:"rgba(201,126,184,0.05)",border:"1px solid rgba(201,126,184,0.2)",borderRadius:"10px",display:"flex",flexDirection:"column",gap:"10px"}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:"8px"}}>
                <div style={{display:"flex",alignItems:"center",gap:"8px"}}>
                  <span style={{fontSize:"16px"}}>🧹</span>
                  <span style={{fontSize:"14px",color:"#c97eb8",fontFamily:"Georgia, serif"}}>Pulizia Chat Stylist</span>
                  <TooltipIcon text="Elimina tutti i messaggi delle chat Stylist. Utile se il modello si e' impantanato con vecchi messaggi malformati. Gli outfit canon gia' pinnati in Racconto non vengono persi." color="#c97eb8"/>
                </div>
              </div>
              <p style={{fontSize:"12px",color:"rgba(201,126,184,0.7)",fontFamily:"Georgia, serif",margin:0}}>
                Rimuove tutti i messaggi dalle chat Stylist. Non elimina i canon gia' pinnati.
              </p>
              <button onClick={()=>{
                if(!window.confirm("Eliminare tutti i messaggi delle chat Stylist? I canon gia' pinnati in Racconto restano invariati.")) return;
                setChats(prev => {
                  const stylistChats = (prev.stylist || []).map(ch => ({...ch, messages:[]}));
                  return {...prev, stylist: stylistChats};
                });
                // Salva su backend
                const chat = (chats.stylist||[])[0];
                if(chat) api.clearChatMessages?.(chat.id).catch(()=>{});
                alert("Chat Stylist pulite. Ora puoi generare outfit freschi.");
              }} style={{padding:"8px 16px",background:"rgba(201,126,184,0.15)",border:"1px solid rgba(201,126,184,0.4)",borderRadius:"8px",color:"#c97eb8",cursor:"pointer",fontSize:"13px",fontFamily:"Georgia, serif",alignSelf:"flex-start"}}>
                🧹 Pulisci tutte le chat Stylist
              </button>
            </div>

            {/* Import Episodi Manuali */}
            <div style={{padding:"14px 16px",background:"rgba(126,184,201,0.05)",border:"1px solid rgba(126,184,201,0.2)",borderRadius:"10px",display:"flex",flexDirection:"column",gap:"10px"}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:"8px"}}>
                <div style={{display:"flex",alignItems:"center",gap:"8px"}}>
                  <span style={{fontSize:"16px"}}>📥</span>
                  <span style={{fontSize:"14px",color:"#7eb8c9",fontFamily:"Georgia, serif"}}>Importa Episodio</span>
                  <TooltipIcon text="Inserisci episodi scritti manualmente con tutti i metadata corretti. Usa per aggiungere episodi precedenti prima di continuare la storia." color="#7eb8c9"/>
                </div>
                <button onClick={()=>setShowImportEpisodi(p=>!p)} style={{padding:"5px 12px",background:"rgba(126,184,201,0.1)",border:"1px solid rgba(126,184,201,0.25)",borderRadius:"6px",color:"#7eb8c9",cursor:"pointer",fontSize:"12px",fontFamily:"Georgia, serif"}}>
                  {showImportEpisodi ? "▲ Chiudi" : "▼ Apri"}
                </button>
              </div>
              {showImportEpisodi && (
                <div style={{display:"flex",flexDirection:"column",gap:"10px"}}>
                  <div style={{display:"flex",gap:"10px",flexWrap:"wrap"}}>
                    <div style={{display:"flex",flexDirection:"column",gap:"4px",flex:"0 0 120px"}}>
                      <label style={{fontSize:"11px",color:UI.textMuted}}>Numero Episodio</label>
                      <input
                        type="number"
                        value={importEpNum}
                        onChange={e=>setImportEpNum(e.target.value)}
                        placeholder="es. 1"
                        style={{padding:"7px 10px",background:"rgba(255,255,255,0.08)",border:"1px solid rgba(126,184,201,0.4)",borderRadius:"6px",color:"#e8e0d0",fontSize:"13px",width:"100%",boxSizing:"border-box"}}
                      />
                    </div>
                    <div style={{display:"flex",flexDirection:"column",gap:"4px",flex:"1"}}>
                      <label style={{fontSize:"11px",color:UI.textMuted}}>Sezione</label>
                      <select
                        value={importEpSection}
                        onChange={e=>setImportEpSection(e.target.value)}
                        style={{padding:"7px 10px",background:"rgba(20,20,30,0.95)",border:"1px solid rgba(126,184,201,0.4)",borderRadius:"6px",color:"#e8e0d0",fontSize:"13px",width:"100%"}}
                      >
                        <option value="racconto">Racconto</option>
                        <option value="intimo">Intimo</option>
                      </select>
                    </div>
                  </div>
                  <div style={{display:"flex",flexDirection:"column",gap:"4px"}}>
                    <label style={{fontSize:"11px",color:UI.textMuted}}>Testo Episodio</label>
                    <textarea
                      value={importEpText}
                      onChange={e=>setImportEpText(e.target.value)}
                      placeholder="Incolla qui il testo completo dell'episodio..."
                      rows={8}
                      style={{padding:"10px",background:"rgba(255,255,255,0.06)",border:"1px solid rgba(126,184,201,0.4)",borderRadius:"6px",color:"#e8e0d0",fontSize:"13px",resize:"vertical",fontFamily:"Georgia, serif",lineHeight:"1.5"}}
                    />
                  </div>
                  <button
                    disabled={importEpLoading || !importEpText.trim() || !importEpNum}
                    onClick={async ()=>{
                      if (!importEpText.trim() || !importEpNum) return;
                      setImportEpLoading(true);
                      try {
                        const epN = parseInt(importEpNum);
                        const storageSection = importEpSection;
                        // Trova o crea la chat giusta
                        const existingChats = chats[storageSection] || [];
                        const targetChat = existingChats[0] || { id: `${storageSection[0]}${Date.now()}`, name: storageSection === "racconto" ? "Capitolo 1" : "🔥 Scena 1", messages: [] };
                        const msgId = `import_${Date.now()}_${Math.random().toString(36).slice(2)}`;
                        const ts = Date.now() - (100000 * (1000 - epN)); // timestamp scalato per ordine
                        const userMsg = {
                          role: "user",
                          content: `[Episodio ${epN} importato manualmente]`,
                          timestamp: ts - 1000,
                          section: storageSection,
                          id: `u_${msgId}`,
                          isImported: true
                        };
                        const assistantMsg = {
                          role: "assistant",
                          content: importEpText.trim(),
                          timestamp: ts,
                          section: storageSection,
                          episodeNum: epN,
                          isBridge: false,
                          streaming: false,
                          isImported: true,
                          id: msgId
                        };
                        // Salva sul backend se API disponibile
                        if (targetChat.id && api?.saveMessage) {
                          await api.saveMessage(userMsg, targetChat.id).catch(()=>{});
                          await api.saveMessage(assistantMsg, targetChat.id).catch(()=>{});
                        }
                        // Aggiorna stato locale
                        const updatedChat = { ...targetChat, messages: [...(targetChat.messages || []), userMsg, assistantMsg] };
                        const updatedChats = existingChats.some(c => c.id === targetChat.id)
                          ? existingChats.map(c => c.id === targetChat.id ? updatedChat : c)
                          : [...existingChats, updatedChat];
                        const newChats = { ...chats, [storageSection]: updatedChats };
                        setChats(newChats);
                        // Ricalcola contatore episodi
                        recalcEpisodeCounter(newChats, true);
                        // Reset form
                        setImportEpText("");
                        setImportEpNum(String(epN + 1));
                        alert(`✅ Episodio ${epN} importato come ${storageSection === "racconto" ? "Racconto" : "Intimo"}.\nOra puoi importare il prossimo o chiudere.`);
                      } catch(e) {
                        alert("Errore durante l'importazione: " + e.message);
                      } finally {
                        setImportEpLoading(false);
                      }
                    }}
                    style={{padding:"9px 16px",background: importEpLoading || !importEpText.trim() || !importEpNum ? "rgba(126,184,201,0.05)" : "rgba(126,184,201,0.15)",border:"1px solid rgba(126,184,201,0.35)",borderRadius:"8px",color: importEpLoading || !importEpText.trim() || !importEpNum ? UI.textMuted : "#7eb8c9",cursor: importEpLoading || !importEpText.trim() || !importEpNum ? "not-allowed" : "pointer",fontSize:"13px",fontFamily:"Georgia, serif"}}
                  >
                    {importEpLoading ? "Importazione..." : "📥 Importa Episodio"}
                  </button>
                  <p style={{fontSize:"11px",color:UI.textMuted,margin:0,lineHeight:"1.5"}}>
                    💡 Importa un episodio alla volta. Il numero avanza automaticamente. Dopo aver importato tutti gli episodi, il sistema li riconoscerà come storia esistente e racconto/intimo continueranno da lì.
                  </p>

                  {/* Pannello correzione episodi esistenti */}
                  <div style={{borderTop:"1px solid rgba(126,184,201,0.2)",paddingTop:"12px",marginTop:"4px"}}>
                    <p style={{fontSize:"12px",color:"#7eb8c9",fontFamily:"Georgia, serif",margin:"0 0 8px 0"}}>🔧 Correggi episodi esistenti</p>
                    {(()=>{
                      // Mostra tutti gli episodi importati o con section mancante
                      const allEps = Object.entries(chats).flatMap(([sec, chatArr])=>
                        (chatArr||[]).flatMap(chat=>
                          (chat.messages||[])
                            .filter(m=>m.role==="assistant"&&(m.content||"").length>100)
                            .map(m=>({...m, _chatId:chat.id, _storedSection:sec}))
                        )
                      ).sort((a,b)=>(a.timestamp||0)-(b.timestamp||0));

                      if(allEps.length===0) return <p style={{fontSize:"11px",color:UI.textMuted}}>Nessun episodio trovato.</p>;

                      return (
                        <div style={{display:"flex",flexDirection:"column",gap:"6px",maxHeight:"300px",overflowY:"auto"}}>
                          {allEps.map((ep,i)=>{
                            const preview = (ep.content||"").slice(0,80).replace(/\n/g," ");
                            const hasIssue = !ep.episodeNum || ep.section !== ep._storedSection;
                            return (
                              <div key={ep.id||i} style={{
                                padding:"8px 10px",
                                background: hasIssue ? "rgba(255,150,50,0.08)" : "rgba(255,255,255,0.03)",
                                border: `1px solid ${hasIssue ? "rgba(255,150,50,0.3)" : "rgba(255,255,255,0.08)"}`,
                                borderRadius:"6px",
                                display:"flex",flexDirection:"column",gap:"6px"
                              }}>
                                <div style={{display:"flex",alignItems:"center",gap:"8px",flexWrap:"wrap"}}>
                                  <span style={{fontSize:"11px",color: hasIssue ? "#ffaa44" : UI.textMuted}}>
                                    {hasIssue ? "⚠️" : "✅"} Ep.{ep.episodeNum||"?"} [{ep.section||"senza section"}] stored:[{ep._storedSection}]
                                  </span>
                                  <span style={{fontSize:"11px",color:UI.textMuted,flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                                    {preview}...
                                  </span>
                                </div>
                                {hasIssue && (
                                  <div style={{display:"flex",gap:"6px",flexWrap:"wrap",alignItems:"center"}}>
                                    <input
                                      type="number"
                                      placeholder="Ep.N"
                                      defaultValue={ep.episodeNum||""}
                                      id={`epnum_${ep.id||i}`}
                                      style={{width:"60px",padding:"4px 6px",background:"rgba(255,255,255,0.08)",border:"1px solid rgba(126,184,201,0.4)",borderRadius:"4px",color:"#e8e0d0",fontSize:"12px"}}
                                    />
                                    <select
                                      defaultValue={ep.section||ep._storedSection||"racconto"}
                                      id={`epsec_${ep.id||i}`}
                                      style={{padding:"4px 6px",background:"rgba(20,20,30,0.95)",border:"1px solid rgba(126,184,201,0.4)",borderRadius:"4px",color:"#e8e0d0",fontSize:"12px"}}
                                    >
                                      <option value="racconto">racconto</option>
                                      <option value="intimo">intimo</option>
                                    </select>
                                    <button
                                      onClick={()=>{
                                        const newNum = parseInt(document.getElementById(`epnum_${ep.id||i}`)?.value||"0");
                                        const newSec = document.getElementById(`epsec_${ep.id||i}`)?.value||"racconto";
                                        // Aggiorna il messaggio nella chat corretta
                                        const newChats = {...chats};
                                        Object.keys(newChats).forEach(sec=>{
                                          newChats[sec] = (newChats[sec]||[]).map(chat=>({
                                            ...chat,
                                            messages:(chat.messages||[]).map(m=>
                                              m.id===ep.id ? {...m, episodeNum:newNum||m.episodeNum, section:newSec} : m
                                            )
                                          }));
                                        });
                                        setChats(newChats);
                                        recalcEpisodeCounter(newChats, true);
                                        alert(`✅ Episodio aggiornato: Ep.${newNum} [${newSec}]`);
                                      }}
                                      style={{padding:"4px 10px",background:"rgba(126,184,201,0.15)",border:"1px solid rgba(126,184,201,0.35)",borderRadius:"4px",color:"#7eb8c9",cursor:"pointer",fontSize:"12px"}}
                                    >
                                      Correggi
                                    </button>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}
            </div>

            {/* Debugger Contesto AI */}
            <div style={{padding:"14px 16px",background:"rgba(100,180,100,0.05)",border:"1px solid rgba(100,180,100,0.2)",borderRadius:"10px",display:"flex",flexDirection:"column",gap:"10px"}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                <div style={{display:"flex",alignItems:"center",gap:"8px"}}>
                  <span style={{fontSize:"16px"}}>🔍</span>
                  <span style={{fontSize:"14px",color:"#64b464",fontFamily:"Georgia, serif"}}>Debug Contesto AI</span>
                </div>
                <button onClick={()=>setShowDebugger(p=>!p)} style={{padding:"5px 12px",background:"rgba(100,180,100,0.1)",border:"1px solid rgba(100,180,100,0.25)",borderRadius:"6px",color:"#64b464",cursor:"pointer",fontSize:"12px",fontFamily:"Georgia, serif"}}>
                  {showDebugger ? "▲ Chiudi" : "▼ Apri"}
                </button>
              </div>
              {showDebugger && (()=>{
                const buildDebug = () => {
                  const sec = debugSection;
                  const out = [];
                  const estToken = (s) => Math.round((s||"").length / 4);

                  // STATO BASE
                  const _chats = chatsRef.current;
                  const allNarr = ((_chats?.racconto||[]).flatMap(c=>c.messages||[]).concat((_chats?.intimo||[]).flatMap(c=>c.messages||[])))
                    .filter(m=>m.role==="assistant"&&!m.isBridge&&(m.content||"").length>100)
                    .sort((a,b)=>(a.timestamp||0)-(b.timestamp||0));
                  const epRacconto = allNarr.filter(m=>m.section==="racconto").length;
                  const epIntimo = allNarr.filter(m=>m.section==="intimo").length;
                  const contatore = episodeCounters["global_narrative"]||0;
                  out.push(`━━━ STATO BASE ━━━`);
                  out.push(`Ep. racconto: ${epRacconto} | Ep. intimo: ${epIntimo}`);
                  out.push(`Contatore global_narrative: ${contatore}`);
                  out.push(`Ep. con episodeNum: ${allNarr.filter(m=>m.episodeNum).length} | Senza episodeNum: ${allNarr.filter(m=>!m.episodeNum).length}`);
                  out.push(`POV attivo: ${activePOV||"(nessuno)"} | Sezione attiva: ${activeSection} | Debug sezione: ${sec}`);

                  // [1] SYSTEM PROMPT
                  const sp = prompts[sec] || DEFAULT_PROMPTS[sec] || "";
                  out.push(`\n━━━ [1] SYSTEM PROMPT (${sp.length} chars) ━━━`);
                  out.push(sp || "(VUOTO)");

                  // [2] CONTESTO REALE -- usa buildFullContext come fa il modello
                  out.push(`\n━━━ [2] CONTESTO REALE buildFullContext (${sec.toUpperCase()}) ━━━`);
                  try {
                    const _presDebug = sec === "intimo" ? intimoPresenza : undefined;
                    const _fullCtx = buildFullContext(sec, [], _presDebug, "");
                    out.push(_fullCtx || "(CONTESTO VUOTO)");
                  } catch(e) {
                    out.push(`⚠️ ERRORE buildFullContext: ${e.message}`);
                  }

                  // [3] CROSS CONTEXT
                  out.push(`\n━━━ [3] CROSS CONTEXT buildCrossSectionContext (${sec.toUpperCase()}) ━━━`);
                  try {
                    const _cross = buildCrossSectionContext(_chats, sec);
                    out.push(_cross || "(NESSUN CROSS CONTEXT)");
                  } catch(e) {
                    out.push(`⚠️ ERRORE buildCrossSectionContext: ${e.message}`);
                  }

                  // [4] EPISODI DI RIFERIMENTO
                  const lastRac = getLastEpisodeOfSection(_chats, 'racconto');
                  const lastInt = getLastEpisodeOfSection(_chats, 'intimo');
                  const lastGlob = getLastNarrativeEpisode(_chats);
                  out.push(`\n━━━ [4] EPISODI DI RIFERIMENTO ━━━`);
                  out.push(`Ultimo globale: section=${lastGlob?.section||"?"} | ep=${lastGlob?.episodeNum||"?"} | chars=${lastGlob?.content?.length||0}`);
                  out.push(`Ultimo racconto: ep=${lastRac?.episodeNum||"?"} | chars=${lastRac?.content?.length||0} | [FINE EPISODIO]: ${lastRac?.content?.includes('[FINE EPISODIO]') ? 'SÌ' : 'NO'}`);
                  out.push(`Ultimo intimo: ep=${lastInt?.episodeNum||"?"} | chars=${lastInt?.content?.length||0} | [FINE EPISODIO]: ${lastInt?.content?.includes('[FINE EPISODIO]') ? 'SÌ' : 'NO'}`);

                  // [5] MODELLI
                  out.push(`\n━━━ [5] MODELLI ━━━`);
                  out.push(`Racconto: ${models["racconto"]||"(default)"} | Intimo: ${models["intimo"]||"(default)"} | Lab: ${models["lab"]||"(default)"} | Stylist: ${models["stylist"]||"(default)"}`);
                  out.push(`API Key: ${apiKey ? apiKey.slice(0,8)+"..." : "⚠️ NON IMPOSTATA"}`);

                  // [6] REGOLE PREFIX
                  const regAss2 = codexItems.filter(i=>i.type==="regola"&&i.assoluta===true);
                  // Riproduce ESATTAMENTE il _regolePrefix reale di callAI (nessun reminder hardcoded).
                  const regPrefix = (debugSection === 'stylist' || regAss2.length === 0)
                    ? ""
                    : "⛔ REGOLE ASSOLUTE — PRIORITÀ MASSIMA. VIETATO VIOLARLE:\n"
                      + regAss2.map(r=>`• ${r.descrizione||r.nome}`).join("\n")
                      + "\n\n";
                  out.push(`\n━━━ [6] _regolePrefix (${regPrefix.length} chars) ━━━`);
                  out.push(regPrefix||"(vuoto)");

                  // PROBLEMI
                  const problemi = [];
                  if(!summaryCompact) problemi.push("⚠️ Riassunto mancante");
                  if(!globalStyle) problemi.push("⚠️ Stile globale mancante");
                  if(allNarr.filter(m=>!m.episodeNum).length>0) problemi.push(`⚠️ ${allNarr.filter(m=>!m.episodeNum).length} episodi senza episodeNum`);
                  if(!apiKey) problemi.push("⚠️ API Key mancante");
                  if(problemi.length>0) out.unshift("━━━ 🚨 PROBLEMI RILEVATI ━━━\n"+problemi.join("\n")+"\n");
                  else out.unshift("━━━ ✅ Nessun problema strutturale ━━━\n");

                  setDebugOutput(out.join("\n"));
                };
                return (
                  <div style={{display:"flex",flexDirection:"column",gap:"10px"}}>
                    <div style={{display:"flex",gap:"10px",alignItems:"center",flexWrap:"wrap"}}>
                      <select value={debugSection} onChange={e=>setDebugSection(e.target.value)}
                        style={{padding:"7px 10px",background:"rgba(20,20,30,0.95)",border:"1px solid rgba(100,180,100,0.3)",borderRadius:"6px",color:"#e8e0d0",fontSize:"13px"}}>
                        <option value="racconto">Racconto</option>
                        <option value="intimo">Intimo</option>
                        <option value="lab">Lab</option>
                        <option value="stylist">Stylist</option>
                      </select>
                      <button onClick={buildDebug}
                        style={{padding:"8px 16px",background:"rgba(100,180,100,0.15)",border:"1px solid rgba(100,180,100,0.35)",borderRadius:"6px",color:"#64b464",cursor:"pointer",fontSize:"13px",fontFamily:"Georgia, serif"}}>
                        ▶ Genera Debug
                      </button>
                      {debugOutput && <button onClick={()=>navigator.clipboard?.writeText(debugOutput)}
                        style={{padding:"8px 16px",background:"rgba(100,180,100,0.08)",border:"1px solid rgba(100,180,100,0.2)",borderRadius:"6px",color:"#64b464",cursor:"pointer",fontSize:"12px"}}>
                        📋 Copia
                      </button>}
                      {debugOutput && <button onClick={()=>setDebugOutput("")}
                        style={{padding:"8px 12px",background:"transparent",border:"1px solid rgba(255,100,100,0.3)",borderRadius:"6px",color:"rgba(255,100,100,0.7)",cursor:"pointer",fontSize:"12px"}}>
                        ✕ Pulisci
                      </button>}
                    </div>
                    {debugOutput && (
                      <textarea readOnly value={debugOutput} rows={30}
                        style={{padding:"12px",background:"rgba(0,0,0,0.5)",border:"1px solid rgba(100,180,100,0.2)",borderRadius:"6px",color:"#c8e6c8",fontSize:"11px",fontFamily:"monospace",lineHeight:"1.5",resize:"vertical",whiteSpace:"pre"}}
                      />
                    )}
                    <p style={{fontSize:"11px",color:UI.textMuted,margin:0}}>
                      Mostra tutto quello che il modello riceve: system prompt, codex completo, riassunto, stile, ultimo episodio, regole. Usa per diagnosticare problemi senza fare chiamate AI.
                    </p>
                  </div>
                );
              })()}
            </div>

            {/* Soft Reset */}
            <div style={{padding:"14px 16px",background:"rgba(224,192,120,0.05)",border:"1px solid rgba(224,192,120,0.2)",borderRadius:"10px",display:"flex",alignItems:"center",justifyContent:"space-between",gap:"12px"}}>
              <div style={{display:"flex",alignItems:"center",gap:"8px"}}>
                <span style={{fontSize:"16px"}}>🔄</span>
                <span style={{fontSize:"14px",color:"#e0c078",fontFamily:"Georgia, serif"}}>Soft Reset</span>
                <TooltipIcon text="Elimina tutte le chat, messaggi, riassunto, momenti chiave e chat Messaggi. Il Codex (personaggi, luoghi, relazioni, lore) viene mantenuto. Utile per ricominciare la storia mantenendo i personaggi." color="#e0c078"/>
              </div>
              <button onClick={factoryReset} style={{padding:"7px 12px",background:"rgba(224,192,120,0.12)",border:"1px solid rgba(224,192,120,0.35)",borderRadius:"8px",color:"#e0c078",cursor:"pointer",fontSize:"12px",fontFamily:"Georgia, serif",whiteSpace:"nowrap",flexShrink:0}}>
                🔄 Soft Reset
              </button>
            </div>
            {/* Factory Reset */}
            <div style={{padding:"14px 16px",background:"rgba(224,120,120,0.05)",border:"1px solid rgba(224,120,120,0.2)",borderRadius:"10px",display:"flex",alignItems:"center",justifyContent:"space-between",gap:"12px"}}>
              <div style={{display:"flex",alignItems:"center",gap:"8px"}}>
                <span style={{fontSize:"16px"}}>⚠️</span>
                <span style={{fontSize:"14px",color:UI.delete,fontFamily:"Georgia, serif"}}>Factory Reset</span>
                <TooltipIcon text="Reset completo: elimina TUTTO -- chat, messaggi, riassunto, momenti chiave, Codex (personaggi, luoghi, relazioni, lore). Riporta l'app al primo utilizzo. Operazione irreversibile." color={UI.delete}/>
              </div>
              <button onClick={async ()=>{
                if(!window.confirm("⚠️ FACTORY RESET COMPLETO\n\nVerrà eliminato TUTTO incluso il Codex.\n\nOperazione irreversibile. Continuare?")) return;
                // Cancella tutte le chat dal DB (prima i messaggi, poi la chat)
                const allChats = Object.values(chats).flat();
                for (const chat of allChats) {
                  for (const msg of (chat.messages || [])) {
                    if (msg.id) await api.deleteMessage(msg.id).catch(()=>{});
                  }
                  await api.deleteChat(chat.id).catch(()=>{});
                }
                // Cancella Codex dal DB
                for (const item of codexItems) {
                  await api.deleteCodexItem(item.id).catch(()=>{});
                }
                // Cancella riassunto dal DB
                await api.saveStory({ summary: "", key_moments: [], episode_counters: {} }).catch(()=>{});
                // Pulisce localStorage completamente
                localStorage.clear();
                window.location.reload();
              }} style={{padding:"7px 16px",background:"rgba(224,120,120,0.12)",border:"1px solid rgba(224,120,120,0.35)",borderRadius:"8px",color:UI.delete,cursor:"pointer",fontSize:"13px",fontFamily:"Georgia, serif",whiteSpace:"nowrap"}}>
                ⚠️ Factory Reset
              </button>
            </div>
          </div>
          </div>

          {!isMobile && (
            <IlCreatore
              apiKey={apiKey}
              apiBase={apiBase}
              prompts={prompts}
              setPrompts={setPrompts}
              codexItems={codexItems}
              setCodexItems={setCodexItems}
              chats={chats}
              summaryCompact={summaryCompact}
              keyMoments={keyMoments}
              episodeCounters={episodeCounters}
            />
          )}

          {/* Mobile -- Tasto fluttuante trascinabile Il Creatore */}
          {isMobile && (
            <DraggableCreatoreButton onClick={() => setShowCreatoreOverlay(true)} />
          )}

          {/* Mobile -- Overlay fullscreen Il Creatore */}
          {isMobile && showCreatoreOverlay && (
            <div style={{position:"fixed",inset:0,zIndex:500,background:"#0e0c16",display:"flex",flexDirection:"column"}}>
              <div style={{display:"flex",alignItems:"center",padding:"12px 16px",borderBottom:"1px solid rgba(201,126,184,0.2)"}}>
                <span style={{fontSize:"16px",color:"#c97eb8",flex:1,fontFamily:"Georgia, serif",letterSpacing:"1px"}}>✨ Il Creatore</span>
                <button onClick={() => setShowCreatoreOverlay(false)} style={{background:"none",border:`1px solid rgba(255,255,255,0.15)`,borderRadius:"8px",color:"#e8e0d0",cursor:"pointer",fontSize:"14px",padding:"6px 12px",fontFamily:"Georgia, serif"}}>✕ Chiudi</button>
              </div>
              <div style={{flex:1,overflow:"hidden",display:"flex"}}>
                <IlCreatore
                  apiKey={apiKey}
                  apiBase={apiBase}
                  prompts={prompts}
                  setPrompts={setPrompts}
                  codexItems={codexItems}
                  setCodexItems={setCodexItems}
                  chats={chats}
                  summaryCompact={summaryCompact}
                  keyMoments={keyMoments}
                  episodeCounters={episodeCounters}
                  fullscreen={true}
                />
              </div>
            </div>
          )}

        </div>
      )}

      {/* FINE SESSIONE MODAL -- momenti chiave */}
      {showEndSessionModal&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.85)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000,padding:"20px"}}>
          <div style={{background:"#16141e",border:"1px solid rgba(201,169,110,0.35)",borderRadius:"14px",padding:"26px",width:"100%",maxWidth:"560px",maxHeight:"85vh",overflowY:"auto"}}>
            <div style={{display:"flex",alignItems:"center",gap:"10px",marginBottom:"6px"}}>
              <span style={{fontSize:"24px"}}>⭐</span>
              <h3 style={{color:"#c9a96e",fontSize:"16px",fontWeight:"normal",margin:0}}>Fine sessione -- Momenti Chiave</h3>
            </div>
            <p style={{color:UI.textSecondary,fontSize:"12px",marginBottom:"18px",lineHeight:"1.6"}}>
              Prima di aprire una nuova chat, vuoi salvare questi momenti importanti della sessione? Seleziona quelli da conservare.
            </p>

            {endSessionLoading ? (
              <div style={{textAlign:"center",padding:"30px",color:UI.textMuted}}>
                <div style={{fontSize:"24px",marginBottom:"10px"}}>⏳</div>
                <p style={{fontSize:"13px"}}>L'AI sta analizzando la sessione...</p>
              </div>
            ) : pendingKeyMoments.length === 0 ? (
              <p style={{color:UI.textFaint,fontSize:"13px",textAlign:"center",padding:"20px 0",border:"1px dashed rgba(201,169,110,0.2)",borderRadius:"8px"}}>
                Nessun momento chiave rilevante trovato in questa sessione.
              </p>
            ) : (
              <div style={{display:"flex",flexDirection:"column",gap:"10px",marginBottom:"16px"}}>
                {pendingKeyMoments.map(m=>(
                  <div key={m.id} style={{background:m.selected?"rgba(201,169,110,0.08)":"rgba(255,255,255,0.02)",border:m.selected?"1px solid rgba(201,169,110,0.3)":`1px solid ${UI.borderSubtle}`,borderRadius:"8px",padding:"12px",cursor:"pointer"}} onClick={()=>setPendingKeyMoments(p=>p.map(pm=>pm.id===m.id?{...pm,selected:!pm.selected}:pm))}>
                    <div style={{display:"flex",alignItems:"center",gap:"8px",marginBottom:"6px"}}>
                      <span style={{fontSize:"16px"}}>{m.selected?"✅":"⬜"}</span>
                      <input value={m.title} onChange={e=>{e.stopPropagation();setPendingKeyMoments(p=>p.map(pm=>pm.id===m.id?{...pm,title:e.target.value}:pm));}} onClick={e=>e.stopPropagation()} style={{...FIS,fontSize:"13px",fontWeight:"bold",flex:1,borderColor:"rgba(201,169,110,0.2)"}}/>
                      <select value={m.priority||"normale"} onChange={e=>{e.stopPropagation();setPendingKeyMoments(p=>p.map(pm=>pm.id===m.id?{...pm,priority:e.target.value}:pm));}} onClick={e=>e.stopPropagation()} style={{...FIS,width:"auto",fontSize:"11px",cursor:"pointer"}}>
                        <option value="normale">⭐ Normale</option>
                        <option value="alta">⭐⭐ Alta</option>
                      </select>
                    </div>
                    <textarea value={m.content} onChange={e=>{e.stopPropagation();setPendingKeyMoments(p=>p.map(pm=>pm.id===m.id?{...pm,content:e.target.value}:pm));}} onClick={e=>e.stopPropagation()} style={{...FIS,minHeight:"60px",resize:"vertical",fontSize:"12px",borderColor:"rgba(201,169,110,0.15)"}}/>
                  </div>
                ))}
              </div>
            )}

            <div style={{display:"flex",gap:"8px",flexWrap:"wrap"}}>
              <button onClick={()=>{
                const daAggiungere = pendingKeyMoments.filter(m=>m.selected&&m.title.trim());
                if(daAggiungere.length>0){
                  setKeyMoments(p=>[...p,...daAggiungere.map(m=>({id:`km${Date.now()}_${Math.random()}`,title:m.title,content:m.content,priority:m.priority||"normale",timestamp:Date.now()}))]);
                }
                setShowEndSessionModal(false); setPendingKeyMoments([]); setSessionCandidates([]);
                createNewChat(activeSection);
              }} style={{flex:1,padding:"10px",background:"rgba(201,169,110,0.15)",border:"1px solid rgba(201,169,110,0.4)",borderRadius:"8px",color:"#c9a96e",cursor:"pointer",fontSize:"13px",fontFamily:"Georgia, serif",fontWeight:"bold"}}>
                ⭐ Salva selezionati e continua
              </button>
              <button onClick={()=>{
                setShowEndSessionModal(false); setPendingKeyMoments([]); setSessionCandidates([]);
                createNewChat(activeSection);
              }} style={{flex:1,padding:"10px",background:"rgba(255,255,255,0.04)",border:`1px solid ${UI.border}`,borderRadius:"8px",color:UI.textSecondary,cursor:"pointer",fontSize:"13px",fontFamily:"Georgia, serif"}}>
                Salta e continua
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes dp{0%,80%,100%{transform:scale(0.7);opacity:0.3}40%{transform:scale(1.2);opacity:1}}
        ::-webkit-scrollbar{width:3px}::-webkit-scrollbar-track{background:transparent}::-webkit-scrollbar-thumb{background:rgba(201,169,110,0.2);border-radius:2px}
        *{box-sizing:border-box}
        textarea:focus,input:focus,select:focus{border-color:rgba(201,169,110,0.4)!important;outline:none}
        select option{background:#1e1c26;color:#e8e0d0}
        /* Font più leggibili nell'interfaccia */
        button{font-size:13px}
        .sidebar-section-btn{font-size:14px!important}
        label{font-size:12px}
        p{font-size:13px}
        h3{font-size:15px}
      `}</style>
    </div>
  );
}

// ============================================================
// SHARED STYLE CONSTANTS
// ============================================================
function SS({ title, color, children, tooltip }) {
  return <div style={{marginBottom:"22px",background:"rgba(255,255,255,0.02)",border:`1px solid ${color}22`,borderRadius:"10px",padding:"16px"}}>
    <div style={{display:"flex",alignItems:"center",marginBottom:"14px"}}>
      <h3 style={{color,fontSize:"14px",margin:0,fontWeight:"normal",letterSpacing:"1px",flex:1}}>{title}</h3>
      {tooltip && <TooltipIcon text={tooltip} color={color}/>}
    </div>
    {children}
  </div>;
}

function SSCollapsible({ title, color, children, tooltip }) {
  const [open, setOpen] = useState(false);
  return <div style={{marginBottom:"22px",background:"rgba(255,255,255,0.02)",border:`1px solid ${color}22`,borderRadius:"10px",overflow:"hidden"}}>
    <div onClick={()=>setOpen(p=>!p)} style={{display:"flex",alignItems:"center",padding:"14px 16px",cursor:"pointer",userSelect:"none"}}>
      <h3 style={{color,fontSize:"14px",margin:0,fontWeight:"normal",letterSpacing:"1px",flex:1}}>{title}</h3>
      {tooltip && <TooltipIcon text={tooltip} color={color}/>}
      <span style={{color,fontSize:"12px",marginLeft:"8px",transition:"transform 0.2s",display:"inline-block",transform:open?"rotate(180deg)":"rotate(0deg)"}}>▾</span>
    </div>
    {open && <div style={{padding:"0 16px 16px"}}>{children}</div>}
  </div>;
}

// ============================================================
// IMAGE GENERATION via OpenRouter -- ID modelli corretti
// ============================================================

// Modelli che usano modalities: ["image"] senza testo
const IMAGE_ONLY_MODELS = [
  "sourceful/riverflow-v2-fast",
  "sourceful/riverflow-v2-pro",
  "black-forest-labs/flux.2-pro",
  "black-forest-labs/flux.2-flex",
  "black-forest-labs/flux.2-max",
  "black-forest-labs/flux.2-klein-4b"
];

async function generateImage(description, apiKey, apiBase, imageModel, senderInfo) {
  const model = imageModel || "sourceful/riverflow-v2-fast";

  // Prompt sempre realistico fotografico
  let prompt = description;
  const realisticSuffix = "Fotografia realistica, stile fotografico professionale, luce naturale, alta definizione, nessun testo nell'immagine, nessun elemento illustrativo o artistico.";

  if (senderInfo) {
    const { nome, aspetto, eta, nazionalita } = senderInfo;
    const ctx = [nome?`Persona: ${nome}`:"", eta?`${eta} anni`:"", nazionalita?`${nazionalita}`:"", aspetto?`${aspetto}`:""].filter(Boolean).join(", ");
    prompt = ctx ? `${ctx}. ${description}. ${realisticSuffix}` : `${description}. ${realisticSuffix}`;
  } else {
    prompt = `${description}. ${realisticSuffix}`;
  }

  const isImageOnly = IMAGE_ONLY_MODELS.includes(model);
  const modalities = isImageOnly ? ["image"] : ["image", "text"];

  try {
    const response = await fetch(`${apiBase}/chat/completions`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${apiKey}`, "HTTP-Referer": window.location.href, "X-Title": "La Nostra Storia" },
      body: JSON.stringify({ model, messages: [{ role: "user", content: prompt }], modalities, max_tokens: 2000 })
    });

    const data = await response.json();
    if (data.error) return { success: false, error: `${data.error.message || JSON.stringify(data.error)}` };

    const message = data.choices?.[0]?.message;
    if (!message) return { success: false, error: "Nessuna risposta dal modello" };

    // 1. Formato OpenRouter standard: message.images[]
    if (message.images && message.images.length > 0) {
      const img = message.images[0];
      const url = img?.image_url?.url || img?.url || (typeof img === "string" ? img : null);
      if (url) return { success: true, url };
    }

    // 2. Content array con type image_url (Flux usa questo formato)
    if (Array.isArray(message.content)) {
      const imgPart = message.content.find(p => p.type === "image_url" || p.type === "image");
      if (imgPart) {
        const url = imgPart.image_url?.url || imgPart.url;
        if (url) return { success: true, url };
      }
      // Cerca base64 dentro qualsiasi parte
      for (const part of message.content) {
        const str = JSON.stringify(part);
        const match = str.match(/data:image\/[^"]+/);
        if (match) return { success: true, url: match[0].replace(/\\"/g, "") };
      }
    }

    // 3. Base64 inline nel content stringa
    if (typeof message.content === "string") {
      const match = message.content.match(/data:image\/[^;]+;base64,[A-Za-z0-9+/=]+/);
      if (match) return { success: true, url: match[0] };
    }

    // 4. Cerca in tutta la risposta raw
    const raw = JSON.stringify(data);
    const rawMatch = raw.match(/data:image\/[^"\\]+;base64,[A-Za-z0-9+/=]+/);
    if (rawMatch) return { success: true, url: rawMatch[0] };

    return { success: false, error: "Nessuna immagine ricevuta. Risposta: " + JSON.stringify(message).slice(0, 150) };
  } catch(e) {
    return { success: false, error: e.message };
  }
}


// ============================================================
// IL CREATORE -- Chat meta-AI per configurazione e gestione app
// ============================================================



// ============================================================
// ============================================================
// CAMBIA PASSWORD -- pannello nelle impostazioni
// ============================================================
// ============================================================
// DRAGGABLE CREATORE BUTTON
// ============================================================
function DraggableCreatoreButton({ onClick }) {
  const [pos, setPos] = useState(() => {
    const saved = localStorage.getItem("s_creatore_btn_pos");
    return saved ? JSON.parse(saved) : { x: 18, y: window.innerHeight - 150 };
  });
  const dragging = useRef(false);
  const startPos = useRef({ x: 0, y: 0 });
  const startTouch = useRef({ x: 0, y: 0 });
  const moved = useRef(false);

  const onTouchStart = (e) => {
    dragging.current = true;
    moved.current = false;
    startTouch.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    startPos.current = { ...pos };
  };

  const onTouchMove = (e) => {
    if (!dragging.current) return;
    const dx = e.touches[0].clientX - startTouch.current.x;
    const dy = e.touches[0].clientY - startTouch.current.y;
    if (Math.abs(dx) > 5 || Math.abs(dy) > 5) moved.current = true;
    const newX = Math.max(0, Math.min(window.innerWidth - 56, startPos.current.x + dx));
    const newY = Math.max(0, Math.min(window.innerHeight - 56, startPos.current.y + dy));
    setPos({ x: newX, y: newY });
  };

  const onTouchEnd = () => {
    dragging.current = false;
    localStorage.setItem("s_creatore_btn_pos", JSON.stringify(pos));
    if (!moved.current) onClick();
  };

  return (
    <div
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      style={{
        position:"fixed", left: pos.x, top: pos.y, zIndex:200,
        width:"52px", height:"52px", borderRadius:"50%",
        background:"linear-gradient(135deg,#c97eb8,#9b8ec9)",
        cursor:"grab", fontSize:"22px",
        boxShadow:"0 4px 16px rgba(201,126,184,0.5)",
        display:"flex", alignItems:"center", justifyContent:"center",
        userSelect:"none", touchAction:"none"
      }}>
      ✨
    </div>
  );
}

function CambiaPassword({ username }) {
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newPassword2, setNewPassword2] = useState("");
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [newRecoveryCode, setNewRecoveryCode] = useState(null);

  const handleChange = async () => {
    if (newPassword.length < 8) { setError("La password deve avere almeno 8 caratteri."); return; }
    if (newPassword !== newPassword2) { setError("Le password non coincidono."); return; }
    setLoading(true); setError(""); setSuccess("");
    try {
      // Verifica vecchia password
      const loginRes = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password: oldPassword })
      });
      if (!loginRes.ok) { setError("Password attuale non corretta."); setLoading(false); return; }

      // Cambia password
      const changeRes = await fetch('/api/auth/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, newPassword })
      });
      if (!changeRes.ok) { setError("Errore nel cambio password."); setLoading(false); return; }

      // Genera nuovo codice di recupero
      const arr = new Uint8Array(4);
      crypto.getRandomValues(arr);
      const rc = Array.from(arr).map(b => b.toString(16).padStart(2, '0')).join('-').toUpperCase();
      await fetch('/api/auth/recovery-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, recoveryCode: rc })
      });

      setNewRecoveryCode(rc);
      setSuccess("Password cambiata con successo!");
      setOldPassword(""); setNewPassword(""); setNewPassword2("");
    } catch(e) { setError("Errore di connessione."); }
    setLoading(false);
  };

  const IS3 = { width:"100%", background:"rgba(255,255,255,0.05)", border:"1px solid rgba(255,255,255,0.12)", borderRadius:"6px", padding:"9px 40px 9px 12px", color:"#e8e0d0", fontSize:"13px", fontFamily:"Georgia, serif", outline:"none", boxSizing:"border-box" };

  return (
    <div style={{marginBottom:"22px",background:"rgba(255,255,255,0.02)",border:"1px solid rgba(201,126,184,0.22)",borderRadius:"10px",padding:"16px"}}>
      <h3 style={{color:"#c97eb8",fontSize:"14px",margin:"0 0 14px",fontWeight:"normal",letterSpacing:"1px"}}>🔐 Cambia password</h3>

      {newRecoveryCode ? (
        <div>
          <p style={{fontSize:"12px",color:"#8ec9a0",margin:"0 0 10px"}}>✅ Password cambiata! Salva il nuovo codice di recupero:</p>
          <div style={{background:"rgba(142,201,160,0.08)",border:"1px solid rgba(142,201,160,0.3)",borderRadius:"8px",padding:"14px",textAlign:"center",marginBottom:"12px"}}>
            <p style={{fontSize:"10px",color:"#8ec9a0",letterSpacing:"2px",margin:"0 0 6px"}}>NUOVO CODICE DI RECUPERO</p>
            <p style={{fontSize:"18px",color:"#e8e0d0",fontFamily:"monospace",letterSpacing:"4px",margin:0}}>{newRecoveryCode}</p>
          </div>
          <p style={{fontSize:"11px",color:"#e07878",margin:"0 0 12px"}}>⚠️ Salvalo ora -- non verrà mostrato di nuovo!</p>
          <button onClick={() => setNewRecoveryCode(null)} style={{padding:"7px 16px",background:"rgba(201,126,184,0.15)",border:"1px solid rgba(201,126,184,0.3)",borderRadius:"6px",color:"#c97eb8",cursor:"pointer",fontSize:"12px",fontFamily:"Georgia, serif"}}>
            ✓ Ho salvato il codice
          </button>
        </div>
      ) : (
        <div style={{display:"flex",flexDirection:"column",gap:"10px"}}>
          <div style={{position:"relative"}}>
            <label style={{display:"block",fontSize:"11px",color:UI.textMuted,marginBottom:"4px",letterSpacing:"0.5px"}}>PASSWORD ATTUALE</label>
            <input value={oldPassword} onChange={e=>{setOldPassword(e.target.value);setError("");}} type={showOld?"text":"password"} placeholder="Password attuale" style={IS3}/>
            <button onClick={()=>setShowOld(s=>!s)} style={{position:"absolute",right:"10px",bottom:"9px",background:"none",border:"none",cursor:"pointer",color:UI.textMuted,fontSize:"14px",padding:0}}>{showOld?"🙈":"👁️"}</button>
          </div>
          <div style={{position:"relative"}}>
            <label style={{display:"block",fontSize:"11px",color:UI.textMuted,marginBottom:"4px",letterSpacing:"0.5px"}}>NUOVA PASSWORD</label>
            <input value={newPassword} onChange={e=>{setNewPassword(e.target.value);setError("");}} type={showNew?"text":"password"} placeholder="Nuova password (min. 8 caratteri)" style={IS3}/>
            <button onClick={()=>setShowNew(s=>!s)} style={{position:"absolute",right:"10px",bottom:"9px",background:"none",border:"none",cursor:"pointer",color:UI.textMuted,fontSize:"14px",padding:0}}>{showNew?"🙈":"👁️"}</button>
          </div>
          <div>
            <label style={{display:"block",fontSize:"11px",color:UI.textMuted,marginBottom:"4px",letterSpacing:"0.5px"}}>CONFERMA NUOVA PASSWORD</label>
            <input value={newPassword2} onChange={e=>{setNewPassword2(e.target.value);setError("");}} type="password" placeholder="Ripeti la nuova password" style={{...IS3,paddingRight:"12px"}} onKeyDown={e=>e.key==="Enter"&&handleChange()}/>
          </div>
          {error && <p style={{color:"#e07878",fontSize:"12px",margin:0}}>{error}</p>}
          {success && <p style={{color:"#8ec9a0",fontSize:"12px",margin:0}}>{success}</p>}
          <button onClick={handleChange} disabled={loading||!oldPassword||newPassword.length<8||newPassword!==newPassword2}
            style={{padding:"8px 16px",background:(!loading&&oldPassword&&newPassword.length>=8&&newPassword===newPassword2)?"#c97eb8":"rgba(201,126,184,0.15)",border:"none",borderRadius:"6px",color:(!loading&&oldPassword&&newPassword.length>=8&&newPassword===newPassword2)?"#0a0a0f":"#c97eb8",cursor:"pointer",fontSize:"13px",fontFamily:"Georgia, serif",fontWeight:"bold",alignSelf:"flex-start"}}>
            {loading ? "Salvataggio..." : "→ Cambia password"}
          </button>
        </div>
      )}
    </div>
  );
}

// LOGIN SCREEN -- TOTP Authentication
// ============================================================
function LoginScreen({ onLogin }) {
  const [step, setStep] = useState("credentials"); // credentials | totp | changePassword
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newPassword2, setNewPassword2] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [qrData, setQrData] = useState(null);
  const [userData, setUserData] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, [step]);

  // Step 1 -- username + password
  const handleCredentials = async () => {
    if (!username.trim() || !password.trim()) return;
    setLoading(true); setError("");
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: username.trim(), password })
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || "Credenziali non valide"); setLoading(false); return; }
      setUserData(data);
      // Carica QR per questo utente
      const qrRes = await fetch(`/api/auth/totp-qr/${data.username}`);
      if (qrRes.ok) { const qr = await qrRes.json(); setQrData(qr); }
      setStep("totp");
    } catch(e) { setError("Errore di connessione. Riprova."); }
    setLoading(false);
  };

  // Step 2 -- TOTP
  const handleTotp = async () => {
    if (code.length < 6) return;
    setLoading(true); setError("");
    try {
      const res = await fetch('/api/auth/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: userData.username, code })
      });
      const data = await res.json();
      if (!res.ok || !data.success) { setError("Codice non valido. Riprova."); setCode(""); setLoading(false); return; }
      if (userData.is_first_login) { setStep("changePassword"); }
      else { save("s_auth_user", userData.username); onLogin(userData); }
    } catch(e) { setError("Errore di verifica. Riprova."); }
    setLoading(false);
  };

  // Step 3 -- cambio password obbligatorio
  const handleChangePassword = async () => {
    if (newPassword.length < 8) { setError("La password deve avere almeno 8 caratteri."); return; }
    if (newPassword !== newPassword2) { setError("Le password non coincidono."); return; }
    setLoading(true); setError("");
    try {
      const res = await fetch('/api/auth/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: userData.username, newPassword })
      });
      if (res.ok) {
        // Genera codice di recupero e salvalo nel backend
        const arr = new Uint8Array(4);
        crypto.getRandomValues(arr);
        const rc = Array.from(arr).map(b => b.toString(16).padStart(2, '0')).join('-').toUpperCase();
        await fetch('/api/auth/recovery-code', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username: userData.username, recoveryCode: rc })
        });
        setRecoveryCode(rc);
        setStep("recoveryCode");
      }
      else { setError("Errore nel cambio password. Riprova."); }
    } catch(e) { setError("Errore di connessione. Riprova."); }
    setLoading(false);
  };

  const IS2 = { width:"100%", background:"rgba(255,255,255,0.05)", border:"1px solid rgba(255,255,255,0.12)", borderRadius:"8px", padding:"12px 14px", color:"#e8e0d0", fontSize:"14px", fontFamily:"Georgia, serif", outline:"none", boxSizing:"border-box" };
  const BTN = (enabled) => ({ width:"100%", marginTop:"14px", padding:"12px", background:enabled?"#c9a96e":"rgba(201,169,110,0.15)", border:"none", borderRadius:"8px", color:enabled?"#0a0a0f":"#c9a96e", cursor:enabled?"pointer":"not-allowed", fontSize:"14px", fontFamily:"Georgia, serif", fontWeight:"bold", transition:"all 0.2s" });

  return (
    <div style={{ height:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:"linear-gradient(135deg, #0a0a0f 0%, #12101a 50%, #0f0a0a 100%)", fontFamily:"Georgia, serif", color:"#e8e0d0", padding:"20px" }}>
      <div style={{ width:"100%", maxWidth:"400px" }}>
        <div style={{ textAlign:"center", marginBottom:"32px" }}>
          <div style={{ fontSize:"36px", marginBottom:"12px" }}>✦</div>
          <h1 style={{ fontSize:"18px", color:"#c9a96e", letterSpacing:"4px", textTransform:"uppercase", fontWeight:"normal", margin:"0 0 8px" }}>La Nostra Storia</h1>
          <p style={{ fontSize:"12px", color:"#7a7068", letterSpacing:"2px" }}>ACCESSO PROTETTO</p>
        </div>

        <div style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.1)", borderRadius:"12px", padding:"24px" }}>

          {/* STEP 1 -- Credenziali */}
          {step === "credentials" && (
            <>
              <p style={{ fontSize:"13px", color:"#b0a898", margin:"0 0 20px", textAlign:"center" }}>Inserisci le tue credenziali</p>
              <div style={{ display:"flex", flexDirection:"column", gap:"12px" }}>
                <input ref={inputRef} value={username} onChange={e => { setUsername(e.target.value); setError(""); }} onKeyDown={e => e.key==="Enter" && handleCredentials()} placeholder="Username" style={IS2} />
                <div style={{ position:"relative" }}>
                  <input value={password} onChange={e => { setPassword(e.target.value); setError(""); }} onKeyDown={e => e.key==="Enter" && handleCredentials()} placeholder="Password" type={showPassword ? "text" : "password"} style={{...IS2, paddingRight:"44px"}} />
                  <button onClick={() => setShowPassword(s => !s)} style={{ position:"absolute", right:"12px", top:"50%", transform:"translateY(-50%)", background:"none", border:"none", cursor:"pointer", color:UI.textMuted, fontSize:"16px", padding:0 }}>
                    {showPassword ? "🙈" : "👁️"}
                  </button>
                </div>
              </div>
              {error && <p style={{ color:"#e07878", fontSize:"12px", textAlign:"center", margin:"8px 0 0" }}>{error}</p>}
              <button onClick={handleCredentials} disabled={loading || !username.trim() || !password.trim()} style={BTN(!loading && username.trim() && password.trim())}>
                {loading ? "Verifica..." : "→ Continua"}
              </button>
              {/* Recupero password con codice */}
              <div style={{ marginTop:"16px", textAlign:"center" }}>
                <button onClick={() => setStep("recovery")} style={{ background:"none", border:"none", color:"#7a7068", cursor:"pointer", fontSize:"11px", fontFamily:"Georgia, serif", textDecoration:"underline" }}>
                  Hai dimenticato la password?
                </button>
              </div>
            </>
          )}

          {/* STEP 2 -- TOTP */}
          {step === "totp" && (
            <>
              <p style={{ fontSize:"13px", color:"#b0a898", margin:"0 0 6px", textAlign:"center" }}>
                Bentornato, <strong style={{ color:"#c9a96e" }}>{userData?.username}</strong>
              </p>
              <p style={{ fontSize:"12px", color:"#7a7068", margin:"0 0 16px", textAlign:"center" }}>
                Inserisci il codice da Google Authenticator
              </p>
              <input
                ref={inputRef}
                value={code}
                onChange={e => { setCode(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(""); }}
                onKeyDown={e => e.key === "Enter" && handleTotp()}
                placeholder="000000"
                maxLength={6}
                style={{ ...IS2, textAlign:"center", fontSize:"28px", letterSpacing:"8px", fontFamily:"monospace" }}
              />
              {error && <p style={{ color:"#e07878", fontSize:"12px", textAlign:"center", margin:"8px 0 0" }}>{error}</p>}
              <button onClick={handleTotp} disabled={loading || code.length < 6} style={BTN(!loading && code.length === 6)}>
                {loading ? "Verifica..." : "→ Accedi"}
              </button>

              {/* QR per configurare Authenticator */}
              <div style={{ marginTop:"16px", textAlign:"center" }}>
                <button onClick={() => setShowQR(s => !s)} style={{ background:"none", border:"none", color:"#7a7068", cursor:"pointer", fontSize:"11px", fontFamily:"Georgia, serif", textDecoration:"underline" }}>
                  {showQR ? "Nascondi QR" : "Configura Google Authenticator"}
                </button>
                {showQR && qrData && (
                  <div style={{ marginTop:"12px", background:"rgba(201,169,110,0.06)", border:"1px solid rgba(201,169,110,0.2)", borderRadius:"8px", padding:"14px" }}>
                    <div style={{ background:"white", padding:"8px", borderRadius:"6px", display:"inline-block", marginBottom:"10px" }}>
                      <img src={`https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=${encodeURIComponent(qrData.qrUrl)}`} alt="QR" style={{ width:"160px", height:"160px", display:"block" }} />
                    </div>
                    <p style={{ fontSize:"11px", color:"#7a7068", margin:"0 0 4px" }}>Oppure inserisci manualmente:</p>
                    <p style={{ fontSize:"11px", color:"#c9a96e", fontFamily:"monospace", background:"rgba(0,0,0,0.3)", padding:"6px", borderRadius:"4px", wordBreak:"break-all", margin:0 }}>{qrData.secret}</p>
                  </div>
                )}
              </div>

              <div style={{ marginTop:"10px", textAlign:"center" }}>
                <button onClick={() => { setStep("credentials"); setCode(""); setError(""); }} style={{ background:"none", border:"none", color:"#5a5248", cursor:"pointer", fontSize:"11px", fontFamily:"Georgia, serif" }}>
                  ← Torna indietro
                </button>
              </div>
            </>
          )}

          {/* STEP 3 -- Cambio password obbligatorio */}
          {step === "changePassword" && (
            <>
              <p style={{ fontSize:"13px", color:"#c9a96e", margin:"0 0 6px", textAlign:"center", fontWeight:"bold" }}>🔐 Primo accesso</p>
              <p style={{ fontSize:"12px", color:"#b0a898", margin:"0 0 16px", textAlign:"center", lineHeight:"1.6" }}>
                Scegli una nuova password personale per il tuo account.
              </p>
              <div style={{ display:"flex", flexDirection:"column", gap:"12px" }}>
                <div style={{ position:"relative" }}>
                  <input ref={inputRef} value={newPassword} onChange={e => { setNewPassword(e.target.value); setError(""); }} placeholder="Nuova password (min. 8 caratteri)" type={showNewPassword ? "text" : "password"} style={{...IS2, paddingRight:"44px"}} />
                  <button onClick={() => setShowNewPassword(s => !s)} style={{ position:"absolute", right:"12px", top:"50%", transform:"translateY(-50%)", background:"none", border:"none", cursor:"pointer", color:UI.textMuted, fontSize:"16px", padding:0 }}>
                    {showNewPassword ? "🙈" : "👁️"}
                  </button>
                </div>
                <input value={newPassword2} onChange={e => { setNewPassword2(e.target.value); setError(""); }} onKeyDown={e => e.key==="Enter" && handleChangePassword()} placeholder="Ripeti la password" type="password" style={IS2} />
              </div>
              {error && <p style={{ color:"#e07878", fontSize:"12px", textAlign:"center", margin:"8px 0 0" }}>{error}</p>}
              <button onClick={handleChangePassword} disabled={loading || newPassword.length < 8 || newPassword !== newPassword2} style={BTN(!loading && newPassword.length >= 8 && newPassword === newPassword2)}>
                {loading ? "Salvataggio..." : "→ Salva e accedi"}
              </button>
            </>
          )}

          {/* STEP 4 -- Mostra codice di recupero */}
          {step === "recoveryCode" && (
            <>
              <p style={{ fontSize:"13px", color:"#8ec9a0", margin:"0 0 6px", textAlign:"center", fontWeight:"bold" }}>✅ Password impostata!</p>
              <p style={{ fontSize:"12px", color:"#b0a898", margin:"0 0 16px", textAlign:"center", lineHeight:"1.6" }}>
                Salva questo codice di recupero in un posto sicuro. Ti servirà se dimentichi la password.
              </p>
              <div style={{ background:"rgba(142,201,160,0.08)", border:"1px solid rgba(142,201,160,0.3)", borderRadius:"8px", padding:"16px", textAlign:"center", marginBottom:"16px" }}>
                <p style={{ fontSize:"10px", color:"#8ec9a0", letterSpacing:"2px", margin:"0 0 8px" }}>CODICE DI RECUPERO</p>
                <p style={{ fontSize:"20px", color:"#e8e0d0", fontFamily:"monospace", letterSpacing:"4px", margin:0 }}>{recoveryCode}</p>
              </div>
              <p style={{ fontSize:"11px", color:"#e07878", textAlign:"center", margin:"0 0 16px" }}>
                ⚠️ Questo codice non verrà mostrato di nuovo!
              </p>
              <button onClick={() => { save("s_auth_user", userData.username); onLogin(userData); }} style={BTN(true)}>
                → Ho salvato il codice, accedi
              </button>
            </>
          )}

          {/* STEP recupero password */}
          {step === "recovery" && (
            <>
              <p style={{ fontSize:"13px", color:"#c9a96e", margin:"0 0 6px", textAlign:"center", fontWeight:"bold" }}>🔑 Recupero password</p>
              <p style={{ fontSize:"12px", color:"#b0a898", margin:"0 0 16px", textAlign:"center", lineHeight:"1.6" }}>
                Inserisci il tuo username e il codice di recupero che hai salvato al primo accesso.
              </p>
              <div style={{ display:"flex", flexDirection:"column", gap:"12px" }}>
                <input ref={inputRef} value={username} onChange={e => { setUsername(e.target.value); setError(""); }} placeholder="Username" style={IS2} />
                <input value={code} onChange={e => { setCode(e.target.value.toUpperCase()); setError(""); }} placeholder="Codice di recupero (es: AB12-CD34-...)" style={{...IS2, fontFamily:"monospace", letterSpacing:"2px"}} />
                <div style={{ position:"relative" }}>
                  <input value={newPassword} onChange={e => { setNewPassword(e.target.value); setError(""); }} placeholder="Nuova password (min. 8 caratteri)" type={showNewPassword ? "text" : "password"} style={{...IS2, paddingRight:"44px"}} />
                  <button onClick={() => setShowNewPassword(s => !s)} style={{ position:"absolute", right:"12px", top:"50%", transform:"translateY(-50%)", background:"none", border:"none", cursor:"pointer", color:UI.textMuted, fontSize:"16px", padding:0 }}>
                    {showNewPassword ? "🙈" : "👁️"}
                  </button>
                </div>
              </div>
              {error && <p style={{ color:"#e07878", fontSize:"12px", textAlign:"center", margin:"8px 0 0" }}>{error}</p>}
              <button onClick={async () => {
                if (newPassword.length < 8) { setError("Password troppo corta."); return; }
                setLoading(true); setError("");
                try {
                  const res = await fetch('/api/auth/recover', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ username: username.trim().toLowerCase(), recoveryCode: code.trim(), newPassword })
                  });
                  const data = await res.json();
                  if (res.ok && data.success) {
                    setUserData({ username: username.trim().toLowerCase() });
                    setStep("credentials");
                    setPassword("");
                    setCode("");
                    setNewPassword("");
                    setError("");
                    alert("✅ Password reimpostata! Accedi con la nuova password.");
                  } else { setError(data.error || "Codice non valido."); }
                } catch(e) { setError("Errore di connessione."); }
                setLoading(false);
              }} disabled={loading || !username.trim() || !code.trim() || newPassword.length < 8} style={BTN(!loading && username.trim() && code.trim() && newPassword.length >= 8)}>
                {loading ? "Verifica..." : "→ Reimposta password"}
              </button>
              <div style={{ marginTop:"12px", textAlign:"center" }}>
                <button onClick={() => { setStep("credentials"); setCode(""); setError(""); setNewPassword(""); }} style={{ background:"none", border:"none", color:"#5a5248", cursor:"pointer", fontSize:"11px", fontFamily:"Georgia, serif" }}>
                  ← Torna al login
                </button>
              </div>
            </>
          )}

        </div>
      </div>
    </div>
  );
}

// ============================================================
// MAIN APP WRAPPER -- with authentication gate
// ============================================================
export default function App() {
  const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
  const [authenticated, setAuthenticated] = useState(() => {
    if (isLocalhost) return true;
    const authTime = load("s_auth_time", 0);
    const authUser = load("s_auth_user", null);
    return authUser && (Date.now() - authTime) < 8 * 60 * 60 * 1000;
  });

  const handleLogin = (userData) => {
    save("s_auth_time", Date.now());
    if (userData) save("s_auth_user", userData.username);
    setAuthenticated(true);
  };

  if (!authenticated) return <LoginScreen onLogin={handleLogin} />;
  return <StoryApp />;
}